import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { toPng } from "html-to-image";
import { jsPDF } from "jspdf";
import LZString, { compressToUint8Array } from "lz-string";
import imageCompression from "browser-image-compression";
import axios from "axios";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  startTransition,
} from "react";
import React from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import useDebounce from "../../hooks/useDebounce";
import _ from "lodash"; // Add lodash for deep comparison
import { setStepTreeNodeId, fetchAllParts } from "../../redux/partSlice";
import { getAllTypes } from "../../redux/typeSlice";
import { getAllUnits } from "../../redux/unitSlice";
import {
  normalizeShapeType,
  normalizeShapes,
  isShapeType,
  isMarkerId,
} from "../../util/shapeTypeMapper";
import Canvas from "./components/canvas-core/Canvas";
import CanvasHeightEditor from "./components/canvas-height/CanvasHeightEditor";
import ShapeEditor from "./components/shape-editor/ShapeEditor";
import TextBoxWithButtons from "./components/text/TextBoxWithButtons";
import Toolbar from "./components/toolbar/Toolbar";
import WebcamPopup from "./components/webcam/WebcamPopup";
import ErrorSnackbar from "./components/snackbar/ErrorSnackbar";
import {
  persistCanvasPages,
  estimateKonvaTextHeight,
  measureHtmlHeight,
} from "../SectionElements/plugins/utils";
import { loadFromIndexedDB, openCanvasDB } from "../../util/indexedDBUtils";
import { DB_VERSION } from "../../util/canvas/canvasDbConfig";
import PartsPopup from "../../common/AppSpecificConpnents/PartsTable/PartsPopup";
import Loader from "../../common/Loader";
import html2canvas from "html2canvas";
import {
  generateSnapshotFromPageDataHTML,
  processHtmlContentForHighlighting,
  calculateTotalContentHeight,
} from "../../util/canvas/canvasHtmlSnapshot";
// Import extracted utility functions
import {
  createZoomChangeHandler,
  createResizeHandler,
  createTouchStartHandler,
  createTouchMoveHandler,
  setupBrowserZoomListeners,
} from "./utils/browserEventHandlers";
import {
  createScrollHandler,
  createMoveHandler,
  createUpHandler,
} from "./utils/scrollHandlers";
import {
  createCancelPartEditHandler,
  createInputChangeHandler,
  arePartsArraysEqual,
} from "./utils/partHandlers";
import {
  getTextStyle,
  deduplicateByName,
  sanitizeId,
} from "./utils/textDataUtils";
import { normalizeLoadedArrow } from "./utils/arrowUtils";
import {
  CANVAS_SCROLL_HORIZONTAL_GUTTER_PX,
  getCanvasStageHeightPx,
  getMainCanvasWidthPx,
  getOffCanvasStageWidthPx,
  getStageHeightPxFromCanvasHeight,
} from "./utils/mainCanvasLayout";
import {
  createUndoRedoKeyHandler,
  createZoomKeyHandler,
  createStateHash,
} from "./utils/keyboardStateUtils";
import {
  detectArrayChanges,
  detectSpecificChanges,
  restoreImagesFromHistory,
} from "./utils/changeDetectionUtils";
import {
  updateImageProperties,
  updateVideoProperties,
  createHandleImageDragEnd,
  createHandleImageClick,
} from "./utils/mediaUtils";
import {
  handleImageResize,
  clearSavedCanvasState,
  preserveTemplateStructure,
  resolveLineArrowPoints,
} from "./utils/canvasStateUtils";
import { getCanvasSnapshotFromStorage } from "./utils/canvasSnapshotStorage";
import {
  navigateBack,
  deduplicate,
  calculateDrawingBounds,
} from "./utils/navigationBoundsUtils";
import {
  convertImageToDataUrl,
  processImagesForStorage,
  deduplicateByIdOrMarkerId,
  prepareCanvasData,
} from "./utils/imageProcessingUtils";

import {
  createCompressedImageForApiData,
  compressImageIfNeeded,
  processCompressedImage,
  compressImage,
} from "./utils/imageCompressionUtils";
import {
  calculateBounds,
  normalizeArrowCoordinates,
  getDynamicCanvasBounds,
  scalePoints,
} from "./utils/canvasUtils";
import {
  convertSvgDataUrlToPng,
  generateOptimizedSVGSnapshot,
  optimizeSVGAttributes,
  rasterizeSVGToDataURL,
} from "./utils/svgUtils";
import {
  createResetZoom,
  createUpdateText,
  createAddImage,
} from "./utils/coreCanvasHandlers";
import {
  createNavigateWithState,
  createHandleSort,
  createHandleEditPart,
} from "./utils/navigationHandlers";
import {
  createShowSnackbar,
  createHandleReplacePart,
  CustomColorPicker,
} from "./utils/uiHandlers";
import {
  createAddText,
  createUpdateTextContent,
  createHandleZoom,
} from "./utils/textZoomHandlers";
import {
  clampImageCenterToStageBounds,
  clampPolylineGroupToStageBounds,
  clampRectangleTopLeftToStageBounds,
  createApplyDrawingRestrictions,
  createProcessHtmlContent,
  createProcessHtmlContentWithFormatting,
} from "./utils/drawingHtmlUtils";
import {
  applyOffCanvasMainColumnAvoidanceX,
  clampOffCanvasTopLeftXAwayFromMainColumn,
} from "./utils/boundaryUtils";
import { createExtractLinksFromText } from "./utils/linkProcessingUtils";
import {
  createRasterizeSvg,
  createRestoreTransformer,
  createOnDoubleClick,
} from "./utils/exportInteractionUtils";
import {
  createFindAndRemoveFromState,
  createUpdateTextProperties,
} from "./utils/stateDragUtils";
import { createHandleRectangleDragEnd } from "./utils/rectangleUtils";
import { createHandleCircleDragEnd } from "./utils/circleUtils";
import { createHandleArrowDragEnd } from "./utils/arrowUtils";
import { createHandleDoubleSidedArrowDragEnd } from "./utils/doubleSidedArrowUtils";
import { createHandleTextDragEnd } from "./utils/textUtils";
import {
  createHandleZoomInputChange,
  createHandleZoomInputKeyPress,
  createHandleManualZoom,
} from "./utils/zoomInputHandlers";
import { createHandleScribbleDragEnd } from "./utils/dragHandlers";
import {
  applyScrollZoomToPoint,
  computeHorizontallyCenteredScrollLeft,
  computeVerticallyCenteredScrollTop,
  logCanvasHScroll,
  settleScrollViewportOnMainStage,
} from "./utils/zoomViewportUtils";
import {
  createHandleWheel,
  createAddVideo,
  createHandleImageUpload,
} from "./utils/mediaInteractionHandlers";
import {
  useDetectBrowserZoom,
  useEnsureScrollToBottom,
  getRootFontSizePx,
} from "./utils/browserUtils";
import {
  useUpdateScrollMetrics,
  useThrottledUpdateScrollMetrics,
  useUpdateEffectiveContentSize,
} from "./utils/scrollUtils";
import {
  useVerticalThumbMouseDown,
  useHorizontalThumbMouseDown,
  useVerticalTrackMouseDown,
  useHorizontalTrackMouseDown,
} from "./utils/mouseHandlers";
import {
  createBlankPage,
  createEmptyCanvasPage,
  cleanupTransformerAndSelections,
  clearCanvasInIndexedDB,
  createClearCanvas,
} from "./utils/canvasPageUtils";
import { saveCanvasElementsToIndexedDB } from "./utils/indexedDBStorageUtils";
import { updateCanvasState } from "./utils/canvasStateManagementUtils";
import { storeMarkerInIndexedDB } from "./utils/markerStorageUtils";
import { handleLeaveCanvas } from "./utils/canvasLeaveHandler";
import { hasCanvasArraysChanged } from "./utils/arrayComparisonUtils";
import {
  createOnPointerDown,
  createOnPointerMove,
  createOnPointerUp,
} from "./utils/pointerHandlers";
import { createKeyboardHandler } from "./utils/keyboardHandlers";
import { computeNudgedCanvasState } from "./utils/nudgeSelectedElements";
import { createOnClick } from "./utils/clickHandler";
import { createImageLoadPromise } from "./utils/imageLoadingUtils";
import {
  getSvgTransform,
  processNode,
  processTextElement,
  convertStageToSVG as convertStageToSVGUtil,
} from "./utils/svgConversionUtils";
import {
  createHandleCopy,
  createHandleCut,
  createCopySelectedImage,
  createCutSelectedImage,
  createHandlePasteElements,
  createHandleNativePaste,
  createHandlePaste,
  isTextInputOrContentEditable,
  isInsideCanvasChrome,
  sanitizeElements as sanitizeElementsUtil,
  sanitizeForClone,
} from "./utils/clipboardUtils";
import {
  createHandleImportCanvas,
  createHandleDrop,
} from "./utils/canvasImportExportUtils";
import {
  generateMarkerId,
  createHandleDotDragMove,
  createLineDots,
  createAddLine,
  calculateLinePoints,
  createHandleLineDragEnd,
} from "./utils/lineUtils";
import {
  createUpdateSelectedShape,
  reassignIds,
  reassignTextIds,
  createUpdateLineProperties,
  createUpdateArrowProperties,
  createUpdateDoubleSidedArrowProperties,
} from "./utils/shapeUpdateUtils";
import { createDeleteSelectedObject } from "./utils/deletionUtils";
import { createOnAddText, createOnRemoveText } from "./utils/shapeTextUtils";
import {
  createStartElementEditing,
  createEndElementEditing,
} from "./utils/elementEditingUtils";
import {
  generateVideoThumbnail,
  convertVideoToDataUrl,
} from "./utils/exportUtils";
import {
  handleSvgExport as handleSvgExportUtil,
  handleExportPDF as handleExportPDFUtil,
  downloadFile,
} from "./utils/exportHandlers";
import { saveToHistory as saveToHistoryUtil } from "./utils/historyUtils";
import { prepareAndSaveCanvasData as prepareAndSaveCanvasDataUtil } from "./utils/saveUtils";
import {
  dedupeCanvasTextsByEntity,
  mergeIndexedDbTextsWithNavigationState,
  registerCanvasTextKeysInMap,
  textShapeAlreadyLoaded,
} from "./utils/canvasTextEntityUtils";
import { clearCrossCanvasCloneServerIdentity } from "./utils/canvasCrossCanvasIdentityUtils";
import { createUndo, createRedo } from "./utils/undoRedoUtils";
import {
  createHandleSelectPart,
  createToggleImageCompression,
} from "./utils/partImageUtils";
import { BASE_URL } from "../../config";
import { refreshTextPartsByTypeWithLatestParts } from "../../util/canvas/refreshCanvasPartsWithSearch";

/** Off-canvas flag may exist only on shape_properties / shape_info / image_properties (saved JSON). */
function itemIsOffCanvas(item) {
  if (!item || typeof item !== "object") return false;
  const sp = item.shape_properties;
  const si = item.shape_info;
  const ip = item.image_properties;
  const topLevelOff =
    item.isOffcanvas === true ||
    item.isOffCanvas === true ||
    item.is_off_canvas === true;

  // Clone-of-clone rows on main canvas can keep stale nested template flags
  // (shape_properties/shape_info). If top-level off-canvas flags are NOT set and
  // this is a user/marker clone, treat it as main-canvas data.
  const isMarkerLikeClone =
    item.createdOnCanvas === true ||
    isMarkerId(item.id) ||
    isMarkerId(item.shape_id);
  if (!topLevelOff && isMarkerLikeClone) {
    return false;
  }

  return (
    topLevelOff ||
    sp?.isOffcanvas === true ||
    sp?.isOffCanvas === true ||
    sp?.is_off_canvas === true ||
    si?.isOffcanvas === true ||
    si?.isOffCanvas === true ||
    si?.is_off_canvas === true ||
    ip?.isOffcanvas === true ||
    ip?.isOffCanvas === true ||
    ip?.is_off_canvas === true
  );
}

const applyCtmPartDetailsToPartsByType = (partsByType, partDetailsMap) => {
  if (!partsByType || typeof partsByType !== "object" || partDetailsMap.size === 0) {
    return partsByType;
  }
  const updated = {};
  Object.entries(partsByType).forEach(([typeName, group]) => {
    if (!group || !Array.isArray(group.parts)) {
      updated[typeName] = group;
      return;
    }
    updated[typeName] = {
      ...group,
      parts: group.parts.map((partItem) => {
        const matched = partDetailsMap.get(partItem?.part_ref);
        if (!matched) return partItem;
        let newPart = "";
        if (matched.part_number) newPart += `(${matched.part_number}) `;
        if (matched.description) newPart += matched.description;
        return {
          ...partItem,
          part: newPart || partItem.part,
          textbox_note: matched.textbox_note || partItem.textbox_note,
          itemCodeNote: matched.textbox_note || partItem.itemCodeNote,
          type_name: matched.type_name || partItem.type_name,
        };
      }),
    };
  });
  return updated;
};

const applyCtmPartDetailsToTexts = (texts = [], ctmPartDetails = []) => {
  if (!Array.isArray(texts) || texts.length === 0 || !Array.isArray(ctmPartDetails)) {
    return texts;
  }
  const map = new Map();
  ctmPartDetails.forEach((part) => {
    if (part?.part_ref) map.set(part.part_ref, part);
  });
  if (map.size === 0) return texts;

  return texts.map((text) => {
    const updatedPartsByType = applyCtmPartDetailsToPartsByType(
      text.partsByType,
      map,
    );
    const updatedTextBoxContent = Array.isArray(text.text_box_content_data)
      ? text.text_box_content_data.map((entry) => {
          const matched = map.get(entry?.part_ref);
          if (!matched) return entry;
          let newPart = "";
          if (matched.part_number) newPart += `(${matched.part_number}) `;
          if (matched.description) newPart += matched.description;
          return {
            ...entry,
            part: newPart || entry.part,
            textbox_note: matched.textbox_note || entry.textbox_note,
            itemCodeNote: matched.textbox_note || entry.itemCodeNote,
            type_name: matched.type_name || entry.type_name,
          };
        })
      : text.text_box_content_data;

    return {
      ...text,
      partsByType: updatedPartsByType,
      text_box_content_data: updatedTextBoxContent,
    };
  });
};

/** All ids that might refer to the same shape (top-level + nested API fields). */
function collectCanvasElementIdentityKeys(item) {
  const keys = new Set();
  const add = (v) => {
    if (v == null) return;
    const s = String(v).trim();
    if (s) keys.add(s);
  };
  if (!item || typeof item !== "object") return keys;
  add(item.shape_id);
  add(item.id);
  add(item.image_id);
  const sp = item.shape_properties;
  if (sp && typeof sp === "object") {
    add(sp.shape_id);
    add(sp.id);
  }
  const si = item.shape_info;
  if (si && typeof si === "object") {
    add(si.shape_id);
    add(si.id);
  }
  const ip = item.image_properties;
  if (ip && typeof ip === "object") {
    add(ip.shape_id);
    add(ip.id);
    add(ip.image_id);
  }
  return keys;
}

const OFF_CANVAS_GEOMETRY_ROUND = 1;

function roundOffCanvasGeo(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return 0;
  return Math.round(x / OFF_CANVAS_GEOMETRY_ROUND) * OFF_CANVAS_GEOMETRY_ROUND;
}

/**
 * When one copy uses DB shape_id as id and another only has Konva marker id, identity
 * dedupe misses. Same position + geometry ⇒ one shape.
 */
function offCanvasElementGeometryKey(item, arrayKey) {
  if (!item || typeof item !== "object") return "";
  if (arrayKey === "lines") {
    const pts = Array.isArray(item.points) ? item.points : [];
    const ptStr = pts.map((v) => roundOffCanvasGeo(v)).join(",");
    const t = item.type || "";
    const he = item.hasEndArrow === true ? "1" : "0";
    const hs = item.hasStartArrow === true ? "1" : "0";
    return `L|${roundOffCanvasGeo(item.x)}|${roundOffCanvasGeo(item.y)}|${t}|${hs}|${he}|${ptStr}`;
  }
  if (arrayKey === "circles") {
    const r = item.radius ?? item.radiusX ?? 0;
    return `C|${roundOffCanvasGeo(item.x)}|${roundOffCanvasGeo(item.y)}|${roundOffCanvasGeo(r)}`;
  }
  if (arrayKey === "rectangles") {
    return `R|${roundOffCanvasGeo(item.x)}|${roundOffCanvasGeo(item.y)}|${roundOffCanvasGeo(item.width)}|${roundOffCanvasGeo(item.height)}`;
  }
  if (arrayKey === "arrows") {
    const pts = Array.isArray(item.points) ? item.points : [];
    const ptStr = pts.map((v) => roundOffCanvasGeo(v)).join(",");
    return `A|${roundOffCanvasGeo(item.x)}|${roundOffCanvasGeo(item.y)}|${ptStr}`;
  }
  if (arrayKey === "doubleSidedArrows") {
    const pts = Array.isArray(item.points) ? item.points : [];
    const ptStr = pts.map((v) => roundOffCanvasGeo(v)).join(",");
    return `D|${roundOffCanvasGeo(item.x)}|${roundOffCanvasGeo(item.y)}|${ptStr}`;
  }
  if (arrayKey === "scribbles") {
    const pts = Array.isArray(item.points) ? item.points : [];
    if (pts.length < 4) return "";
    return `S|${roundOffCanvasGeo(item.x)}|${roundOffCanvasGeo(item.y)}|${pts
      .slice(0, 32)
      .map((v) => roundOffCanvasGeo(v))
      .join(",")}`;
  }
  if (arrayKey === "texts") {
    const loc = item.canvas_page_location_and_size || {};
    const x = item.x ?? loc.x ?? 0;
    const y = item.y ?? loc.y ?? 0;
    const w = item.width ?? loc.width ?? 0;
    const h = item.height ?? loc.height ?? 0;
    const rot = item.rotation ?? loc.rotation ?? 0;
    return `T|${roundOffCanvasGeo(x)}|${roundOffCanvasGeo(y)}|${roundOffCanvasGeo(w)}|${roundOffCanvasGeo(h)}|${roundOffCanvasGeo(rot)}`;
  }
  if (arrayKey === "images" || arrayKey === "videos") {
    const prefix = arrayKey === "videos" ? "V" : "I";
    const x = item.x ?? 0;
    const y = item.y ?? 0;
    const w = item.width ?? 0;
    const h = item.height ?? 0;
    const rot = item.rotation ?? 0;
    const src = String(
      item.src ||
        item.image_path ||
        item.referenceUrl ||
        item.video_path ||
        item.url ||
        "",
    ).slice(0, 160);
    return `${prefix}|${roundOffCanvasGeo(x)}|${roundOffCanvasGeo(y)}|${roundOffCanvasGeo(w)}|${roundOffCanvasGeo(h)}|${roundOffCanvasGeo(rot)}|${src}`;
  }
  return "";
}

function dedupeOffCanvasArrayByGeometry(arr, arrayKey) {
  if (!Array.isArray(arr) || arr.length < 2) return arr;
  const seen = new Set();
  const seenLoose = new Set();
  const out = [];
  for (const item of arr) {
    const gk = offCanvasElementGeometryKey(item, arrayKey);
    let looseKey = "";
    if (
      (arrayKey === "images" || arrayKey === "videos") &&
      item &&
      typeof item === "object"
    ) {
      const prefix = arrayKey === "videos" ? "V" : "I";
      const sp = item.shape_properties || {};
      const si = item.shape_info || {};
      const ip = item.image_properties || {};
      const x = item.x ?? ip.x ?? sp.x ?? si.x ?? 0;
      const y = item.y ?? ip.y ?? sp.y ?? si.y ?? 0;
      const w = item.width ?? ip.width ?? sp.width ?? si.width ?? 0;
      const h = item.height ?? ip.height ?? sp.height ?? si.height ?? 0;
      const rot = item.rotation ?? ip.rotation ?? sp.rotation ?? si.rotation ?? 0;
      // Loose key intentionally ignores src so duplicates from images[] + shapes[]
      // collapse even when one copy has src/imageDataUrl and the other doesn't.
      looseKey = `${prefix}|${roundOffCanvasGeo(x)}|${roundOffCanvasGeo(y)}|${roundOffCanvasGeo(w)}|${roundOffCanvasGeo(h)}|${roundOffCanvasGeo(rot)}`;
    }
    if (gk) {
      if (seen.has(gk)) continue;
      if (looseKey && seenLoose.has(looseKey)) continue;
      seen.add(gk);
      if (looseKey) seenLoose.add(looseKey);
    }
    out.push(item);
  }
  return out;
}

/** Regions that should disable "Leave Canvas" while focused or pointer-active (see focus/pointer listeners). */
const BLOCK_LEAVE_CANVAS_SELECTOR = "[data-block-leave-canvas]";

const ACTIONS = {
  PAN: "PAN",
  SELECT: "SELECT",
  RECTANGLE: "RECTANGLE",
  CIRCLE: "CIRCLE",
  ARROW: "ARROW",
  DOUBLE_SIDED_ARROW: "DOUBLE_SIDED_ARROW",
  SCRIBBLE: "SCRIBBLE",
  TEXT: "TEXT",
  LINE: "line",
  IMAGE: "IMAGE",
};

/** Toolbar focus blocks leave only while a non-Select tool is active; Select keeps focus on toolbar without sticking leave disabled. */
function isNodeInsideBlockLeaveRegion(node, currentAction) {
  if (!node || typeof node.closest !== "function") return false;
  try {
    // Height editor: block Leave only while focus is in a data-block-leave-canvas region (inputs).
    // Buttons (Auto Size, presets, Save/Cancel) should not keep Leave disabled.
    const heightEditor = node.closest(".canvas-height-editor");
    if (heightEditor && node.closest("button")) {
      return false;
    }

    const region = node.closest(BLOCK_LEAVE_CANVAS_SELECTOR);
    if (!region) return false;
    if (
      currentAction === ACTIONS.SELECT &&
      region.classList?.contains("toolbar-wrapper")
    ) {
      return false;
    }
    return true;
  } catch {
    return false;
  }
}
const MIN_CANVAS_HEIGHT_PERCENT = 5;

const PART_SEARCH_FIELDS = [
  "part",
  "itemCodeNote",
  "defaultUnit",
  "typeName",
];

const buildPartLabel = (partData) => {
  const partNumber = partData?.part_number || "";
  const description = partData?.description || "";
  if (!partNumber && !description) return "";
  return `(${partNumber}) ${description}`.trim();
};

const getDefaultUnitFromPart = (partData) => {
  if (Array.isArray(partData?.units) && partData.units.length > 0) {
    return partData.units[0]?.unit_name || "";
  }
  return "";
};

const getPartTypeName = (partData, fallbackType = "Other") =>
  partData?.type_details?.name ||
  partData?.type_name ||
  partData?.type_info?.name ||
  fallbackType;

const normalizeComparisonValue = (value) => {
  const raw = (value ?? "").toString();
  return raw
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
};

const extractPartNumberFromTextLabel = (value = "") => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const bracketMatch = raw.match(/\(([^)]+)\)/);
  if (bracketMatch?.[1]) return bracketMatch[1].trim();
  return raw;
};

const updateImportedTextWithLatestPartDetails = (text, latestPartMap) => {
  if (!text?.partsByType || typeof text.partsByType !== "object") return text;

  let hasAnyChange = false;
  const nextPartsByType = {};

  Object.entries(text.partsByType).forEach(([originalTypeName, group]) => {
    const sourceParts = Array.isArray(group?.parts) ? group.parts : [];
    sourceParts.forEach((partItem) => {
      const latestPart = latestPartMap.get(
        partItem?.part_ref != null && partItem.part_ref !== ""
          ? String(partItem.part_ref)
          : "",
      );
      if (!latestPart) {
        if (!nextPartsByType[originalTypeName]) {
          nextPartsByType[originalTypeName] = {
            order: group?.order,
            parts: [],
          };
        }
        nextPartsByType[originalTypeName].parts.push(partItem);
        return;
      }

      const currentTypeName = partItem?.typeName || originalTypeName || "Other";
      const updatedTypeName = getPartTypeName(latestPart, currentTypeName);
      const updatedOrder =
        latestPart?.type_details?.orders ??
        latestPart?.type_info?.order ??
        group?.order ??
        9999;
      const updatedPartLabel = buildPartLabel(latestPart) || partItem.part || "";
      const updatedItemCodeNote =
        latestPart?.textbox_note !== undefined && latestPart?.textbox_note !== null
          ? latestPart.textbox_note
          : partItem.itemCodeNote || "";
      const updatedDefaultUnit = getDefaultUnitFromPart(latestPart);
      const previousDefaultUnit = partItem.defaultUnit || "";

      const nextPartItem = {
        ...partItem,
        part: updatedPartLabel,
        itemCodeNote: updatedItemCodeNote,
        textbox_note: updatedItemCodeNote,
        defaultUnit: updatedDefaultUnit,
        units: latestPart?.units?.[0]?.unit_id || partItem.units || "",
        unit_type_id: latestPart?.unit_type_id_ref || partItem.unit_type_id || null,
        typeName: updatedTypeName,
      };

      // Match refreshCanvasPartsWithSearch: only sync to new default when still on old default.
      if (partItem.unit === previousDefaultUnit) {
        nextPartItem.unit = updatedDefaultUnit;
      } else {
        nextPartItem.unit = partItem.unit ?? "";
      }

      if (
        partItem.part !== nextPartItem.part ||
        (partItem.itemCodeNote || "") !== (nextPartItem.itemCodeNote || "") ||
        (partItem.defaultUnit || "") !== (nextPartItem.defaultUnit || "") ||
        (partItem.typeName || originalTypeName) !== (nextPartItem.typeName || updatedTypeName)
      ) {
        hasAnyChange = true;
      }

      if (!nextPartsByType[updatedTypeName]) {
        nextPartsByType[updatedTypeName] = {
          order: updatedOrder,
          parts: [],
        };
      }
      nextPartsByType[updatedTypeName].parts.push(nextPartItem);
    });
  });

  if (!hasAnyChange) return text;
  return {
    ...text,
    partsByType: nextPartsByType,
  };
};

const resolveTextElementId = (raw = {}, merged = {}) => {
  const candidates = [
    raw?.shape_id,
    raw?.shapeId,
    raw?.text_id,
    raw?.textId,
    raw?.canvas_text_id,
    raw?.canvasTextId,
    raw?.id,
    raw?.text_box_id,
    raw?.textBoxId,
    raw?.canvas_text_box_id,
    raw?.canvasTextBoxId,
    raw?.shape_properties?.shape_id,
    raw?.shape_properties?.shapeId,
    raw?.shape_properties?.text_id,
    raw?.shape_properties?.textId,
    raw?.shape_properties?.id,
    raw?.shape_properties?.canvas_text_id,
    raw?.shape_properties?.canvasTextId,
    raw?.shape_info?.shape_id,
    raw?.shape_info?.shapeId,
    raw?.shape_info?.text_id,
    raw?.shape_info?.textId,
    raw?.shape_info?.id,
    raw?.shape_info?.canvas_text_id,
    raw?.shape_info?.canvasTextId,
    merged?.shape_id,
    merged?.shapeId,
    merged?.text_id,
    merged?.textId,
    merged?.id,
  ]
    .map((value) =>
      value === undefined || value === null
        ? null
        : typeof value === "number"
          ? String(value)
          : String(value),
    )
    .filter(
      (value) =>
        value && value.trim() !== "" && !value.toLowerCase().includes("marker"),
    );

  return candidates.length > 0 ? candidates[0] : null;
};

/**
 * Merge one text row from IndexedDB/API (shape_properties + shape_info + partsByType).
 * Used for main-canvas texts and off-canvas texts so clones keep partObjs / partsByType after leave/return.
 */
const mergeLoadedCanvasTextRow = (r) => {
  const shapeProperties = r.shape_properties || {};
  const shapeInfo = r.shape_info || {};

  const partsByType =
    r.partsByType !== null && r.partsByType !== undefined
      ? r.partsByType
      : shapeProperties.partsByType !== null &&
          shapeProperties.partsByType !== undefined
        ? shapeProperties.partsByType
        : shapeInfo.partsByType;

  const preservedContent =
    r.content !== null && r.content !== undefined && r.content !== ""
      ? r.content
      : shapeProperties.content !== null &&
          shapeProperties.content !== undefined &&
          shapeProperties.content !== ""
        ? shapeProperties.content
        : shapeInfo.content !== null &&
            shapeInfo.content !== undefined &&
            shapeInfo.content !== ""
          ? shapeInfo.content
          : r.content;

  const preservedTask =
    r.task !== null && r.task !== undefined && r.task !== ""
      ? r.task
      : shapeProperties.task !== null &&
          shapeProperties.task !== undefined &&
          shapeProperties.task !== ""
        ? shapeProperties.task
        : shapeInfo.task !== null &&
            shapeInfo.task !== undefined &&
            shapeInfo.task !== ""
          ? shapeInfo.task
          : r.task;

  const preservedNote =
    r.note !== null && r.note !== undefined && r.note !== ""
      ? r.note
      : shapeProperties.note !== null &&
          shapeProperties.note !== undefined &&
          shapeProperties.note !== ""
        ? shapeProperties.note
        : shapeInfo.note !== null &&
            shapeInfo.note !== undefined &&
            shapeInfo.note !== ""
          ? shapeInfo.note
          : r.note;

  const merged = {
    ...r,
    ...shapeProperties,
    ...shapeInfo,
    ...(partsByType !== undefined && partsByType !== null
      ? { partsByType }
      : {}),
    ...(preservedContent !== undefined && preservedContent !== null
      ? { content: preservedContent }
      : {}),
    ...(preservedTask !== undefined && preservedTask !== null
      ? { task: preservedTask }
      : {}),
    ...(preservedNote !== undefined && preservedNote !== null
      ? { note: preservedNote }
      : {}),
  };

  const fromR = {
    id: r.id,
    shape_id: r.shape_id,
    x: r.x,
    y: r.y,
    content: r.content,
    note: r.note,
    task: r.task,
    name: r.name,
    plainText: r.plainText,
    notePlainText: r.notePlainText,
    taskPlainText: r.taskPlainText,
    partsByType: r.partsByType,
    text_box_content_data: r.text_box_content_data,
    text_box_id: r.text_box_id,
  };
  Object.keys(fromR).forEach((k) => {
    if (fromR[k] !== undefined && fromR[k] !== null) {
      merged[k] = fromR[k];
    }
  });
  if (r.id != null) merged.id = r.id;
  if (r.shape_id != null) merged.shape_id = r.shape_id;
  if (r.x != null || r.y != null) {
    merged.x = r.x ?? merged.x;
    merged.y = r.y ?? merged.y;
    if (merged.canvas_page_location_and_size) {
      merged.canvas_page_location_and_size = {
        ...merged.canvas_page_location_and_size,
        x: merged.x,
        y: merged.y,
      };
    }
  }

  if (partsByType && !merged.partsByType) {
  }

  const preferredId = resolveTextElementId(r, merged);
  if (preferredId) {
    merged.id = preferredId;
    if (
      !merged.shape_id ||
      (typeof merged.shape_id === "string" &&
        merged.shape_id.toLowerCase().includes("marker"))
    ) {
      merged.shape_id = preferredId;
    }
  }

  if (merged.strokeWidth === undefined || merged.strokeWidth === null) {
    merged.strokeWidth =
      merged.stroke_size !== undefined && merged.stroke_size !== null
        ? merged.stroke_size
        : 1;
  }

  return merged;
};

// Normalize loaded line/arrow geometry to a stable local coordinate system.
// 1) Ensure points are relative to (x, y)
// 2) Shift local points so minX/minY start at 0
// 3) Move x/y by that shift to preserve absolute on-canvas position
const normalizeLoadedLineGeometry = (lineLike) => {
  const x = Number(lineLike?.x) || 0;
  const y = Number(lineLike?.y) || 0;
  const src = Array.isArray(lineLike?.points) ? lineLike.points : null;

  if (!src || src.length < 4) {
    return {
      x,
      y,
      points: [0, 0, 100, 0],
      width: 100,
      height: 0,
      skipOffset: true,
    };
  }

  const firstX = Number(src[0]) || 0;
  const firstY = Number(src[1]) || 0;
  const looksAbsolute =
    Math.abs(firstX - x) < 0.1 && Math.abs(firstY - y) < 0.1;
  const skipOffset = lineLike?.skipOffset === true;

  // Some persisted edit-mode lines/arrows carry skipOffset=true and very large
  // negative local points while x/y is already the intended anchor.
  // Rebase points to start at (0,0) while preserving x/y in this case.
  const shouldRebaseAroundFirstPoint =
    skipOffset &&
    !looksAbsolute &&
    Number.isFinite(x) &&
    Number.isFinite(y) &&
    (Math.abs(firstX) > 100 || Math.abs(firstY) > 100) &&
    Math.abs(x + firstX) < 200 &&
    Math.abs(y + firstY) < 200;

  const relativePoints = shouldRebaseAroundFirstPoint
    ? src.map((value, index) =>
        index % 2 === 0
          ? (Number(value) || 0) - firstX
          : (Number(value) || 0) - firstY,
      )
    : looksAbsolute
      ? src.map((value, index) =>
          index % 2 === 0
            ? (Number(value) || 0) - x
            : (Number(value) || 0) - y,
        )
      : src.map((value) => Number(value) || 0);

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (let i = 0; i < relativePoints.length; i += 2) {
    const px = relativePoints[i];
    const py = relativePoints[i + 1];
    minX = Math.min(minX, px);
    minY = Math.min(minY, py);
    maxX = Math.max(maxX, px);
    maxY = Math.max(maxY, py);
  }

  const shiftedPoints = relativePoints.map((value, index) =>
    index % 2 === 0 ? value - minX : value - minY,
  );

  const normalized = {
    x: x + minX,
    y: y + minY,
    points: shiftedPoints,
    width: Math.max(0, maxX - minX),
    height: Math.max(0, maxY - minY),
    // Mark as canonicalized to avoid later unintended offset recalculation.
    skipOffset: true,
  };

  if (shouldRebaseAroundFirstPoint) {
  }

  return normalized;
};

const isValidLoadedUnifiedLineLike = (line) =>
  line &&
  line.x !== undefined &&
  line.y !== undefined &&
  Array.isArray(line.points) &&
  line.points.length >= 4;

/**
 * Normalize one persisted unified line/arrow (main or off-canvas) after IndexedDB/API load.
 * Must match main-canvas `lines` processing so off-canvas rows are not left with `points: null`.
 */
const processLoadedUnifiedLineLike = (l) => {
  const originalShapeId = l.shape_id;
  const originalId = l.id;
  const originalPoints = l.points;
  const originalHasStartArrow = l.hasStartArrow;
  const originalHasEndArrow = l.hasEndArrow;
  const originalPointerAtBeginning = l.pointerAtBeginning;
  const originalPointerAtEnd = l.pointerAtEnd;
  const originalType = l.type;

  const line = {
    ...l,
    ...(l.shape_properties || {}),
    ...(l.shape_info || {}),
  };

  const resolvedPts = resolveLineArrowPoints(line);
  console.log("[MyCanvasLoadDebug][processLoadedUnifiedLineLike][before]", {
    id: originalShapeId || originalId || line.id,
    type: originalType || line.type,
    originalPoints: Array.isArray(originalPoints) ? originalPoints : null,
    mergedRootPoints: Array.isArray(line.points) ? line.points : null,
    mergedShapeInfoPoints: Array.isArray(line.shape_info?.points)
      ? line.shape_info.points
      : null,
    mergedShapePropertiesPoints: Array.isArray(line.shape_properties?.points)
      ? line.shape_properties.points
      : null,
    resolvedPoints: Array.isArray(resolvedPts) ? resolvedPts : null,
    width: line.width,
    height: line.height,
    x: line.x,
    y: line.y,
  });
  const isLikelyDefaultPoints = (arr) => {
    if (!Array.isArray(arr) || arr.length !== 4) return false;
    const [x1, y1, x2, y2] = arr.map((v) => Number(v) || 0);
    return (
      (Math.abs(x1) < 0.001 &&
        Math.abs(y1) < 0.001 &&
        Math.abs(x2 - 100) < 0.001 &&
        Math.abs(y2) < 0.001) ||
      (Math.abs(x1) < 0.001 &&
        Math.abs(y1) < 0.001 &&
        Math.abs(x2 - 100) < 0.001 &&
        Math.abs(y2 - 100) < 0.001)
    );
  };
  const isDegeneratePoints = (arr) => {
    if (!Array.isArray(arr) || arr.length < 4) return true;
    let totalLength = 0;
    for (let i = 0; i < arr.length - 2; i += 2) {
      const x1 = Number(arr[i]) || 0;
      const y1 = Number(arr[i + 1]) || 0;
      const x2 = Number(arr[i + 2]) || 0;
      const y2 = Number(arr[i + 3]) || 0;
      totalLength += Math.hypot(x2 - x1, y2 - y1);
    }
    return totalLength < 0.5;
  };
  if (
    Array.isArray(originalPoints) &&
    originalPoints.length >= 4 &&
    !(
      resolvedPts &&
      resolvedPts.length >= 4 &&
      (isLikelyDefaultPoints(originalPoints) || isDegeneratePoints(originalPoints))
    )
  ) {
    line.points = originalPoints;
  } else if (resolvedPts && resolvedPts.length >= 4) {
    line.points = resolvedPts;
  } else if (!Array.isArray(line.points)) {
    line.points = line.points || [0, 0, 100, 100];
  }

  const normalizedType = originalType || line.type;
  const isArrowType =
    normalizedType === "Arrow" || normalizedType === "arrow";
  const isDoubleSidedArrowType =
    normalizedType === "DoubleSidedArrow" ||
    normalizedType === "double_sided_arrow";

  const shouldBeArrow =
    isArrowType ||
    originalType === "Arrow" ||
    (originalHasEndArrow === true && line.type !== "DoubleSidedArrow") ||
    l.shape_properties?.type === "Arrow" ||
    l.shape_info?.type === "Arrow";

  const shouldBeDoubleSidedArrow =
    isDoubleSidedArrowType ||
    originalType === "DoubleSidedArrow" ||
    (originalHasStartArrow === true &&
      originalHasEndArrow === true &&
      line.type !== "Arrow") ||
    l.shape_properties?.type === "DoubleSidedArrow" ||
    l.shape_info?.type === "DoubleSidedArrow";

  if (shouldBeArrow) {
    line.type = "Arrow";
    if (line.pointerAtEnd === false || originalPointerAtEnd === false) {
      line.hasEndArrow = false;
    } else {
      line.hasEndArrow = true;
    }
    line.hasStartArrow = originalHasStartArrow === true ? true : false;
  } else if (shouldBeDoubleSidedArrow) {
    line.type = "DoubleSidedArrow";
    line.hasStartArrow = originalHasStartArrow === false ? false : true;
    line.hasEndArrow = originalHasEndArrow === false ? false : true;
  } else {
    if (originalHasStartArrow !== undefined) {
      line.hasStartArrow = originalHasStartArrow;
    }
    if (originalHasEndArrow !== undefined) {
      line.hasEndArrow = originalHasEndArrow;
    }
  }

  if (originalPointerAtBeginning !== undefined) {
    line.pointerAtBeginning = originalPointerAtBeginning;
  }
  if (originalPointerAtEnd !== undefined) {
    line.pointerAtEnd = originalPointerAtEnd;
  }

  if (originalShapeId) {
    line.id = originalShapeId;
  } else if (originalId && !isMarkerId(originalId)) {
    line.id = originalId;
  } else if (!line.id) {
    line.id = `line_${Date.now()}_${Math.random()}`;
  }

  const normalizedGeometry = normalizeLoadedLineGeometry(line);
  line.x = normalizedGeometry.x;
  line.y = normalizedGeometry.y;
  line.points = normalizedGeometry.points;
  line.width =
    normalizedGeometry.width > 0 ? normalizedGeometry.width : line.width;
  line.height = normalizedGeometry.height;
  line.skipOffset = true;
  console.log("[MyCanvasLoadDebug][processLoadedUnifiedLineLike][after]", {
    id: line.id,
    type: line.type,
    finalPoints: Array.isArray(line.points) ? line.points : null,
    finalX: line.x,
    finalY: line.y,
    finalWidth: line.width,
    finalHeight: line.height,
  });
  return line;
};

const applyResolvedLinePointsFromShape = (shapeLike, mergedLineLike) => {
  const resolvedPoints = resolveLineArrowPoints(shapeLike) || resolveLineArrowPoints(mergedLineLike);
  if (Array.isArray(resolvedPoints) && resolvedPoints.length >= 4) {
    mergedLineLike.points = [...resolvedPoints];
  }
  if (
    typeof window !== "undefined" &&
    window.localStorage?.getItem("debugMyCanvasLoadLinePoints") === "1"
  ) {
    const rootPts = shapeLike?.points;
    const infoPts = shapeLike?.shape_info?.points;
    const propsPts = shapeLike?.shape_properties?.points;
    const pickSource = Array.isArray(mergedLineLike?.points)
      ? Array.isArray(propsPts) && mergedLineLike.points === propsPts
        ? "shape_properties.points"
        : Array.isArray(infoPts) && mergedLineLike.points === infoPts
          ? "shape_info.points"
          : "root.points"
      : "none";
    console.log("[MyCanvasLoadDebug][line-points-source]", {
      shapeId: shapeLike?.shape_id || shapeLike?.id,
      type: shapeLike?.type,
      rootPointsLen: Array.isArray(rootPts) ? rootPts.length : null,
      shapeInfoPointsLen: Array.isArray(infoPts) ? infoPts.length : null,
      shapePropertiesPointsLen: Array.isArray(propsPts) ? propsPts.length : null,
      resolvedPointsLen: Array.isArray(mergedLineLike?.points)
        ? mergedLineLike.points.length
        : null,
      source: pickSource,
    });
  }
  return mergedLineLike;
};

const mapLegacyLoadedArrowToUnifiedLine = (a) => {
  const normalized = normalizeLoadedArrow(a);
  const canonical = normalizeLoadedLineGeometry(normalized);
  return {
    ...normalized,
    x: canonical.x,
    y: canonical.y,
    points: canonical.points,
    width: canonical.width,
    height: canonical.height,
    skipOffset: true,
    hasStartArrow:
      normalized.hasStartArrow !== undefined
        ? normalized.hasStartArrow
        : normalized.pointerAtBeginning || false,
    hasEndArrow:
      normalized.hasEndArrow !== undefined
        ? normalized.hasEndArrow
        : normalized.pointerAtEnd !== undefined
          ? normalized.pointerAtEnd
          : true,
    type: "Arrow",
  };
};

const mapLegacyLoadedDoubleSidedToUnifiedLine = (d) => {
  const normalized = normalizeLoadedArrow(d);
  const canonical = normalizeLoadedLineGeometry(normalized);
  return {
    ...normalized,
    x: canonical.x,
    y: canonical.y,
    points: canonical.points,
    width: canonical.width,
    height: canonical.height,
    skipOffset: true,
    hasStartArrow:
      normalized.hasStartArrow !== undefined
        ? normalized.hasStartArrow
        : normalized.pointerAtBeginning !== undefined
          ? normalized.pointerAtBeginning
          : true,
    hasEndArrow:
      normalized.hasEndArrow !== undefined
        ? normalized.hasEndArrow
        : normalized.pointerAtEnd !== undefined
          ? normalized.pointerAtEnd
          : true,
    type: "DoubleSidedArrow",
  };
};

const MyCanvas = () => {
  // Always open this screen from the top
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }
  }, []); // Initialize canvasHeight (vh). If coming from response/layout_config, use that.
  const location = useLocation(); // Get location object to check for passed state
  const navigate = useNavigate(); // Initialize navigate early to avoid initialization errors
  const initialCanvasHeight = useMemo(() => {
    const clampPct = (raw) => {
      const n = parseInt(raw, 10);
      if (Number.isNaN(n)) return null;
      return String(Math.max(MIN_CANVAS_HEIGHT_PERCENT, Math.min(100, n)));
    };
    // Prefer height from navigation state (canvasHeight from EditCanvasPage)
    const pageHeightFromState = location.state?.canvasHeight;
    if (
      pageHeightFromState != null &&
      !Number.isNaN(parseInt(pageHeightFromState))
    ) {
      return clampPct(pageHeightFromState) ?? "100";
    }
    // Then try layout_config
    const pageHeightFromLayout =
      location.state?.layout_config?.layout?.page_height_vh ??
      location.state?.page_height_vh;
    if (
      pageHeightFromLayout != null &&
      !Number.isNaN(parseInt(pageHeightFromLayout))
    ) {
      return clampPct(pageHeightFromLayout) ?? "100";
    }
    // Else try IndexedDB-loaded elements (set later in load), fallback to 100
    return "100";
  }, [
    location.state?.canvasHeight,
    location.state?.layout_config,
    location.state?.page_height_vh,
  ]);
  const [canvasHeight, setCanvasHeight] = useState(initialCanvasHeight);
  const [canvasViewportWidth, setCanvasViewportWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1024,
  );
  const [canvasStageHeightPx, setCanvasStageHeightPx] = useState(() =>
    getStageHeightPxFromCanvasHeight(canvasHeight),
  );
  const SHAPE_BOTTOM_PADDING_PX = 14;
  const AUTO_SIZE_TOP_PADDING_PX = 10;
  // Ref to track current canvasHeight for saveToHistory to avoid stale closure values
  const canvasHeightRef = useRef(canvasHeight);
  const saveToHistoryRef = useRef(null);
  const stageRef = useRef();
  const offCanvasStageRef = useRef(null);
  const topLayerStageRef = useRef(null); // For compositing main + top-layer in leave snapshot
  /** Same pointer click can invoke canvas onClick twice (e.g. Text Group then inner Text); prevents toggle-off from undoing multi-add. */
  const multiSelectCanvasClickDedupeRef = useRef(null);

  // Keep ref in sync with state
  useEffect(() => {
    canvasHeightRef.current = canvasHeight;
  }, [canvasHeight]);

  const [snapshot, setSnapshot] = useState(null); // State to hold the snapshot
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false); // Track if there are unsaved changes
  const [isLeavingCanvas, setIsLeavingCanvas] = useState(false);
  const [isExportingPng, setIsExportingPng] = useState(false); // Track PNG export loading state
  const [isExportingPdf, setIsExportingPdf] = useState(false); // Track PDF export loading state
  const [isExportingSvg, setIsExportingSvg] = useState(false); // Track SVG export loading state
  const [isImportingCanvas, setIsImportingCanvas] = useState(false); // Track canvas import loading state
  const [isPastingFromClipboard, setIsPastingFromClipboard] = useState(false); // Track toolbar paste loading state
  const [importedCjsonPartChanges, setImportedCjsonPartChanges] = useState([]);
  
  // CRITICAL: Ref to capture selection count before Stage.onMouseDown clears it
  const preMouseDownSelectionCountRef = useRef(0);

  const [isApplyingImportedPartUpdates, setIsApplyingImportedPartUpdates] =
    useState(false);
  const importedTextIdsWithPartChangesRef = useRef(new Set());
  const latestImportedPartMapRef = useRef(new Map());
  const [isLoadingFromIndexedDB, setIsLoadingFromIndexedDB] = useState(false); // Track IndexedDB loading state
  const [loadingMessage, setLoadingMessage] = useState(
    "Loading canvas data...",
  );
  const [videos, setVideos] = useState([]);
  const [isDrawingRect, setIsDrawingRectState] = useState(false);
  const isDrawingRectRef = useRef(false);
  const setIsDrawingRect = useCallback((value) => {
    setIsDrawingRectState((prev) => {
      const next = typeof value === "function" ? value(prev) : value;
      isDrawingRectRef.current = next;
      return next;
    });
  }, []);
  const [isDrawingCircle, setIsDrawingCircle] = useState(false);
  const [isDrawingText, setIsDrawingText] = useState(false);
  const [newRectStart, setNewRectStartState] = useState(null);
  const newRectStartRef = useRef(null);
  const setNewRectStart = useCallback((value) => {
    setNewRectStartState((prev) => {
      const next = typeof value === "function" ? value(prev) : value;
      newRectStartRef.current = next;
      return next;
    });
  }, []);
  const [newCircleStart, setNewCircleStart] = useState(null);
  const [newTextStart, setNewTextStart] = useState(null);
  const [selectedImage, setSelectedImageState] = useState(null);

  const [errorSnackbar, setErrorSnackbar] = useState({
    isVisible: false,
    message: "",
  });
  const pageId = location.state?.pageId; // Get pageId from location state
  const expectsInitialContent =
    Boolean(pageId) ||
    Boolean(location.state?.fromCanvasPage) ||
    Boolean(location.state?.fromMyCanvas) ||
    Boolean(location.state?.canvasData) ||
    (Array.isArray(location.state?.canvasPages) &&
      location.state.canvasPages.length > 0) ||
    (Array.isArray(location.state?.shapeData) &&
      location.state.shapeData.length > 0) ||
    (Array.isArray(location.state?.imageData) &&
      location.state.imageData.length > 0);
  const initialDataLoadInProgress = useRef(expectsInitialContent);
  const historyInitialized = useRef(false);
  const keepHistoryEmpty = useRef(false); // Flag to prevent history re-initialization after intentional clearing
  const isUndoingRedoing = useRef(false);
  const lastUndoRedoCompletionTime = useRef(null);
  const offCanvasImagesRef = useRef([]); // Current off-canvas images at undo time (avoids stale closure)
  // Shape states
  const [texts, setTexts] = useState([]);
  const [rectangles, setRectangles] = useState([]);
  const [circles, setCircles] = useState([]);
  const [scribbles, setScribbles] = useState([]);
  const [arrows, setArrows] = useState([]);
  const [lines, setLines] = useState([]);
  const [doubleSidedArrows, setDoubleSidedArrows] = useState([]);
  const [images, setImages] = useState([]);

  // Track initial arrays state after loadCanvasElements completes
  // This is used to detect if any changes were made when leaving canvas
  const initialArraysStateRef = useRef(null);
  // Track if loadCanvasElements has completed (for this page)
  const loadCanvasElementsCompletedRef = useRef(false);
  // Track if user has made any changes after loading
  const hasUserMadeChangesRef = useRef(false);
  // Track when we should start monitoring for changes (after system effects settle)
  const shouldStartMonitoringRef = useRef(false);
  // Track previous pageId to detect actual changes (not just re-renders)
  const previousPageIdRef = useRef(pageId);

  // Callback to show error snackbar
  const showErrorSnackbar = useCallback((message) => {
    setErrorSnackbar({
      isVisible: true,
      message: message,
    });
  }, []);

  // Track global mouse position for Ctrl+A canvas detection
  useEffect(() => {
    const handleMouseMove = (e) => {
      window.lastMouseX = e.clientX;
      window.lastMouseY = e.clientY;
    };

    document.addEventListener("mousemove", handleMouseMove, { passive: true });

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
    };
  }, []);

  const computeCanvasContentBottomPx = useCallback(() => {
    const stage = stageRef.current;
    let maxBottom = 0;
    let maxBottomForPaddedShapes = 0;
    let hasElements = false;

    const considerBottom = (value) => {
      if (typeof value === "number" && Number.isFinite(value)) {
        hasElements = true;
        if (value > maxBottom) {
          maxBottom = value;
        }
      }
    };
    const considerPaddedShapeBottom = (value) => {
      if (typeof value === "number" && Number.isFinite(value)) {
        if (value > maxBottomForPaddedShapes) {
          maxBottomForPaddedShapes = value;
        }
      }
    };

    const fallbackBottom = (element, type) => {
      if (!element) return null;
      switch (type) {
        case "Rect":
        case "Rectangle": {
          const height =
            Math.abs(element.height ?? 0) * Math.abs(element.scaleY ?? 1);
          return (element.y ?? 0) + height;
        }
        case "Circle": {
          const radiusY = element.radiusY ?? element.radius ?? 0;
          const diameter =
            Math.abs(radiusY) * 2 * Math.abs(element.scaleY ?? 1);
          return (element.y ?? 0) + diameter;
        }
        case "Image": {
          const height =
            Math.abs(element.height ?? 0) * Math.abs(element.scaleY ?? 1);
          // CRITICAL FIX: Images use center coordinates (with offsetX/offsetY for centering)
          // So we need to add half the height to get the bottom position
          // Bottom = centerY + (height / 2)
          return (element.y ?? 0) + height / 2;
        }
        case "Line":
        case "Arrow":
        case "DoubleSidedArrow": {
          const points = element.points || [];
          if (points.length < 2) {
            return element.y ?? 0;
          }
          let maxY = -Infinity;
          for (let i = 1; i < points.length; i += 2) {
            const relativeY = points[i] ?? 0;
            const absoluteY = relativeY + (element.y ?? 0);
            if (absoluteY > maxY) {
              maxY = absoluteY;
            }
          }
          return Number.isFinite(maxY) ? maxY : null;
        }
        case "Text": {
          // For text elements, recalculate totalContentHeight using the same logic as TextElement
          // This ensures we always have the correct height even when content changes
          // We detect the bottom stroke of the text element and restrict canvas resize rect there
          const canvasWidth =
            stageRef?.current?.width?.() || getMainCanvasWidthPx();
          // Recalculate height using the exact same logic as TextElement.jsx
          const calculatedHeight = calculateTotalContentHeight(
            element,
            canvasWidth,
          );
          // Get minimum height based on font size (matching TextElement logic)
          const getMinHeight = () => {
            if (element.fontSize === 11) return 30;
            if (element.fontSize === 14 || element.fontSize === "14") return 35;
            return 43;
          };
          const totalHeight = Math.max(calculatedHeight, getMinHeight());
          // Use the full calculated height without any reduction
          // This matches the actual visual bounds of the text element including its stroke
          const height = Math.abs(totalHeight) * Math.abs(element.scaleY ?? 1);
          return (element.y ?? 0) + height;
        }
        default: {
          const height = Math.abs(element.height ?? 0);
          return (element.y ?? 0) + height;
        }
      }
    };

    const processElement = (element, type) => {
      if (!element) return;
      const id = element.id;
      let bottom = null;

      // For text elements, skip getClientRect() as it may return clipped bounds due to HTML content clipping
      // Use fallback calculation with adjusted padding instead
      // For other elements (rectangles, circles, etc.), use getClientRect() for accurate bounds
      if (type !== "Text" && stage && id) {
        const node = stage.findOne(`#${id}`);
        if (node) {
          try {
            const rect = node.getClientRect({ relativeTo: stage });
            if (
              rect &&
              Number.isFinite(rect.y) &&
              Number.isFinite(rect.height)
            ) {
              bottom = rect.y + rect.height;
            }
          } catch (err) {
            // Silently ignore measurement errors and fall back to state-derived dimensions
          }
        }
      }

      if (bottom == null) {
        bottom = fallbackBottom(element, type);
      }

      considerBottom(bottom);
      if (
        type === "Rect" ||
        type === "Rectangle" ||
        type === "Circle" ||
        type === "Image"
      ) {
        considerPaddedShapeBottom(bottom);
      }
    };

    texts.forEach((text) => processElement(text, "Text"));
    rectangles.forEach((rect) => processElement(rect, "Rect"));
    circles.forEach((circle) => processElement(circle, "Circle"));
    images.forEach((img) => processElement(img, "Image"));
    lines.forEach((line) => processElement(line, "Line"));
    arrows.forEach((arrow) => processElement(arrow, "Arrow"));
    doubleSidedArrows.forEach((doubleArrow) =>
      processElement(doubleArrow, "DoubleSidedArrow"),
    );

    if (!hasElements) {
      return 0;
    }

    return maxBottom + (maxBottomForPaddedShapes > 0 ? SHAPE_BOTTOM_PADDING_PX : 0);
  }, [
    arrows,
    circles,
    doubleSidedArrows,
    images,
    lines,
    rectangles,
    SHAPE_BOTTOM_PADDING_PX,
    stageRef,
    texts,
  ]);

  const computeMinCanvasHeightPercent = useCallback(() => {
    const bottomPx = computeCanvasContentBottomPx();
    const paddingPx = 0;
    const minPx = Math.max(0, bottomPx + paddingPx);
    // Use the same fixed stage baseline used by canvasHeight% -> px mapping.
    // This keeps percentages consistent across different screen/window heights.
    const stageBaselineHeightPx = Math.max(1, getCanvasStageHeightPx());
    if (stageBaselineHeightPx <= 0) {
      return 0;
    }
    return (minPx / stageBaselineHeightPx) * 100;
  }, [computeCanvasContentBottomPx]);

  // Cache for min canvas height to avoid expensive recalculations
  const minCanvasHeightCacheRef = useRef({ value: null, timestamp: 0 });
  const MIN_CACHE_DURATION = 100; // Cache for 100ms

  const applyCanvasHeight = useCallback(
    (nextHeight) => {
      let numeric =
        typeof nextHeight === "string" ? parseFloat(nextHeight) : nextHeight;

      if (Number.isNaN(numeric)) {
        const currentNumeric = parseFloat(canvasHeight);
        return Number.isNaN(currentNumeric)
          ? MIN_CANVAS_HEIGHT_PERCENT
          : Math.max(MIN_CANVAS_HEIGHT_PERCENT, currentNumeric);
      }

      // Use cached min percent if available and recent, otherwise compute (with timeout protection)
      let minPercent = 0;
      const now = Date.now();
      if (
        minCanvasHeightCacheRef.current.value !== null &&
        now - minCanvasHeightCacheRef.current.timestamp < MIN_CACHE_DURATION
      ) {
        minPercent = minCanvasHeightCacheRef.current.value;
      } else {
        try {
          // Wrap in try-catch to prevent blocking if computation is too expensive
          minPercent = computeMinCanvasHeightPercent();
          minCanvasHeightCacheRef.current = {
            value: minPercent,
            timestamp: now,
          };
        } catch (err) {
          // Fallback if calculation fails (e.g., too many elements causing timeout)
          minPercent = minCanvasHeightCacheRef.current.value || 0;
        }
      }

      const maxAllowedPercent = 100;
      const minAllowedPercent = Math.min(
        maxAllowedPercent,
        Math.max(MIN_CANVAS_HEIGHT_PERCENT, Math.ceil(minPercent || 0)),
      );
      const candidatePercent = Math.min(
        maxAllowedPercent,
        Math.max(MIN_CANVAS_HEIGHT_PERCENT, Math.round(numeric)),
      );
      const clampedPercent = Math.max(minAllowedPercent, candidatePercent);

      setCanvasHeight(String(clampedPercent));
      return clampedPercent;
    },
    [canvasHeight, computeMinCanvasHeightPercent, setCanvasHeight],
  );

  const getMainCanvasVerticalBounds = useCallback(() => {
    const stage = stageRef?.current;
    if (!stage) {
      return null;
    }

    let minY = Infinity;
    let maxY = -Infinity;

    const elementIds = [
      ...images.map((item) => item?.id).filter(Boolean),
      ...rectangles.map((item) => item?.id).filter(Boolean),
      ...circles.map((item) => item?.id).filter(Boolean),
      ...lines.map((item) => item?.id).filter(Boolean),
      ...arrows.map((item) => item?.id).filter(Boolean),
      ...doubleSidedArrows.map((item) => item?.id).filter(Boolean),
      ...texts.map((item) => item?.id).filter(Boolean),
    ];

    elementIds.forEach((id) => {
      try {
        const node = stage.findOne(`#${id}`);
        if (!node || typeof node.visible !== "function" || !node.visible()) {
          return;
        }
        const rect = node.getClientRect({ relativeTo: stage });
        if (!rect || !Number.isFinite(rect.y) || !Number.isFinite(rect.height)) {
          return;
        }
        minY = Math.min(minY, rect.y);
        maxY = Math.max(maxY, rect.y + rect.height);
      } catch (e) {}
    });

    if (!Number.isFinite(minY) || !Number.isFinite(maxY)) {
      return null;
    }

    return { minY, maxY };
  }, [
    arrows,
    circles,
    doubleSidedArrows,
    images,
    lines,
    rectangles,
    stageRef,
    texts,
  ]);

  const autoSizeMainCanvasToContent = useCallback(() => {
    const bounds = getMainCanvasVerticalBounds();
    if (!bounds || typeof window === "undefined") {
      return;
    }

    const shiftY = Number.isFinite(bounds.minY)
      ? bounds.minY - AUTO_SIZE_TOP_PADDING_PX
      : 0;
    const contentHeightPx = Math.max(0, bounds.maxY - bounds.minY);
    const hasPaddedShape =
      images.length > 0 || rectangles.length > 0 || circles.length > 0;
    const contentHeightWithPaddingPx =
      contentHeightPx +
      AUTO_SIZE_TOP_PADDING_PX +
      (hasPaddedShape ? SHAPE_BOTTOM_PADDING_PX : 0);

    const moveElementUp = (element) => {
      if (!element || typeof element !== "object") {
        return element;
      }
      const next = { ...element };

      if (typeof next.y === "number" && Number.isFinite(next.y)) {
        next.y = next.y - shiftY;
      }

      if (Array.isArray(next.points) && next.points.length > 1) {
        if (typeof next.y !== "number" || !Number.isFinite(next.y)) {
          next.points = next.points.map((val, index) =>
            index % 2 === 1 && Number.isFinite(val) ? val - shiftY : val,
          );
        }
      }

      return next;
    };

    const shiftedImages = images.map(moveElementUp);
    const shiftedRectangles = rectangles.map(moveElementUp);
    const shiftedCircles = circles.map(moveElementUp);
    const shiftedLines = lines.map(moveElementUp);
    const shiftedArrows = arrows.map(moveElementUp);
    const shiftedDoubleSidedArrows = doubleSidedArrows.map(moveElementUp);
    const shiftedTexts = texts.map(moveElementUp);

    setImages(shiftedImages);
    setRectangles(shiftedRectangles);
    setCircles(shiftedCircles);
    setLines(shiftedLines);
    setArrows(shiftedArrows);
    setDoubleSidedArrows(shiftedDoubleSidedArrows);
    setTexts(shiftedTexts);

    // Convert px -> % using the SAME baseline as getStageHeightPxFromCanvasHeight.
    // Using window.innerHeight here caused percent/px mismatch, so elements shifted
    // but the main stage card height often appeared unchanged.
    const stageBaselineHeightPx = Math.max(1, getCanvasStageHeightPx());
    const nextPercent = Math.max(
      MIN_CANVAS_HEIGHT_PERCENT,
      Math.min(
        100,
        Math.ceil((contentHeightWithPaddingPx / stageBaselineHeightPx) * 100),
      ),
    );
    // Auto-size already computes the content-fit percent deterministically.
    // Set it directly to avoid first-click variance from cached min-height
    // clamping inside applyCanvasHeight().
    setCanvasHeight(String(nextPercent));

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (typeof saveToHistoryRef.current === "function") {
          saveToHistoryRef.current("auto_size_main_canvas");
        }
      });
    });
  }, [
    arrows,
    circles,
    doubleSidedArrows,
    getMainCanvasVerticalBounds,
    images,
    lines,
    rectangles,
    saveToHistoryRef,
    setArrows,
    setCanvasHeight,
    setCircles,
    setDoubleSidedArrows,
    setImages,
    setLines,
    setRectangles,
    setTexts,
    AUTO_SIZE_TOP_PADDING_PX,
    SHAPE_BOTTOM_PADDING_PX,
    texts,
  ]);

  useEffect(() => {
    const minPercent = computeMinCanvasHeightPercent();
    const maxAllowedPercent = 100;
    const minAllowedPercent = Math.min(
      maxAllowedPercent,
      Math.max(MIN_CANVAS_HEIGHT_PERCENT, Math.ceil(minPercent || 0)),
    );
    const currentPercent = parseInt(canvasHeight, 10);

    if (!Number.isNaN(currentPercent) && currentPercent < minAllowedPercent) {
      setCanvasHeight(String(minAllowedPercent));
    }
  }, [canvasHeight, computeMinCanvasHeightPercent, setCanvasHeight]);

  // Offcanvas state variables for elements that are outside the main canvas bounds
  const [offCanvasRectangles, setOffCanvasRectangles] = useState([]);
  const [offCanvasCircles, setOffCanvasCircles] = useState([]);
  const [offCanvasArrows, setOffCanvasArrows] = useState([]);
  const [offCanvasLines, setOffCanvasLines] = useState([]);
  const [offCanvasDoubleSidedArrows, setOffCanvasDoubleSidedArrows] = useState(
    [],
  );
  const [offCanvasTexts, setOffCanvasTexts] = useState([]);
  const [offCanvasImages, setOffCanvasImages] = useState([]);
  const [offCanvasVideos, setOffCanvasVideos] = useState([]);
  const [offCanvasScribbles, setOffCanvasScribbles] = useState([]);
  const shapeData = location.state?.shapeData || [];
  const imageData = location.state?.imageData || [];
  const { fromCanvasTemplate } = location.state || {};
  // Function to load canvas elements

  useEffect(() => {
    if (fromCanvasTemplate) {
      setFilterType("all");
    }
  }, [fromCanvasTemplate]);

  // Helper function to create compressed image for API-loaded images

  const loadCanvasElements = async (
    version = DB_VERSION,
    maxVersion = DB_VERSION + 5,
  ) => {
    // Set loading state at the beginning
    initialDataLoadInProgress.current = true;
    setIsLoadingFromIndexedDB(true);
    setLoadingMessage("Loading canvas data from cache...");

    // Sanitize the pageId to match what we save

    const sanitizedPageId = sanitizeId(pageId);

    // Verbose off-canvas load traces: localStorage.setItem('debugMyCanvasOffCanvas','1'); reload; filter console by [MyCanvas:offCanvasLoad]
    const logOffCanvasLoad = (step, mainEl, offEl, extra = {}) => {
      try {
        if (
          typeof window === "undefined" ||
          window.localStorage?.getItem("debugMyCanvasOffCanvas") !== "1"
        ) {
          return;
        }
        const layerKeys = [
          "texts",
          "rectangles",
          "circles",
          "arrows",
          "lines",
          "doubleSidedArrows",
          "scribbles",
          "images",
          "videos",
        ];
        const pack = (obj) => {
          const counts = {};
          const nestedOff = {};
          const lineSample = [];
          layerKeys.forEach((k) => {
            const arr = obj?.[k];
            const len = Array.isArray(arr) ? arr.length : 0;
            counts[k] = len;
            if (Array.isArray(arr) && len) {
              const onOff = arr.filter((i) => itemIsOffCanvas(i)).length;
              if (onOff) nestedOff[k] = onOff;
            }
            if (k === "lines" && Array.isArray(arr)) {
              arr.slice(0, 12).forEach((i) => {
                lineSample.push({
                  shape_id: i?.shape_id,
                  id: i?.id,
                  itemIsOffCanvas: itemIsOffCanvas(i),
                });
              });
            }
          });
          return { counts, nestedOff, lineSample };
        };
      } catch (e) {
      }
    };

    try {
      let elementsResult = null;
      let offCanvasElementsResult = null;
      if (sanitizedPageId) {
        setLoadingMessage("Loading canvas elements...");
        const keyCandidates = Array.from(
          new Set(
            [pageId, sanitizedPageId, sanitizeId(pageId, pageId)]
              .filter(Boolean)
              .map((k) => String(k)),
          ),
        );
        const pickLatestRecord = (records = []) => {
          let best = null;
          let bestTs = -Infinity;
          records.forEach((r) => {
            if (!r) return;
            const ts = Number(new Date(r?.lastModified || 0).getTime()) || 0;
            if (!best || ts > bestTs) {
              best = r;
              bestTs = ts;
            }
          });
          return best || null;
        };
        const mainRows = [];
        const offRows = [];
        for (const key of keyCandidates) {
          try {
            const row = await loadFromIndexedDB("canvasElements", key);
            if (row) mainRows.push(row);
          } catch (_e) {}
          try {
            const row = await loadFromIndexedDB("offCanvasElements", key);
            if (row) offRows.push(row);
          } catch (_e) {}
        }
        elementsResult = pickLatestRecord(mainRows);
        offCanvasElementsResult = pickLatestRecord(offRows);
      }

      let elements = elementsResult?.data || elementsResult || {};
      let offCanvasElements =
        offCanvasElementsResult?.data || offCanvasElementsResult || {};

      // Hard guard: apply persisted removed IDs before any merge/split logic so
      // stale rows cannot resurrect deleted elements on reopen.
      let removedMainTextIds = new Set();
      let removedOffTextIds = new Set();
      let removedImageIds = new Set();
      let removedOffImageIds = new Set();
      let removedRectangleIds = new Set();
      let removedCircleIds = new Set();
      let removedArrowIds = new Set();
      let removedLineIds = new Set();
      let removedDoubleSidedArrowIds = new Set();
      let removedScribbleIds = new Set();
      let removedVideoIds = new Set();
      
      try {
        const removedKeyCandidates = Array.from(
          new Set(
            [pageId, sanitizedPageId, sanitizeId(pageId, pageId)]
              .filter(Boolean)
              .map((k) => `${String(k)}_removed_shapes`),
          ),
        );
        let removedShapesRow = null;
        for (const rk of removedKeyCandidates) {
          try {
            const row = await loadFromIndexedDB("canvasElements", rk);
            if (row?.data) {
              removedShapesRow = row;
              break;
            }
          } catch (_e) {}
        }
        const removedData = removedShapesRow?.data || {};
        removedMainTextIds = new Set(
          (removedData.removedTexts || []).map((id) => String(id)),
        );
        removedOffTextIds = new Set(
          (removedData.removedOffCanvasTexts || []).map((id) => String(id)),
        );
        
        // Get all removed element types
        removedImageIds = new Set(
          (removedData.removedImages || []).map((id) => String(id)),
        );
        removedOffImageIds = new Set(
          (removedData.removedOffCanvasImages || []).map((id) => String(id)),
        );
        removedRectangleIds = new Set(
          (removedData.removedRectangles || []).map((id) => String(id)),
        );
        removedCircleIds = new Set(
          (removedData.removedCircles || []).map((id) => String(id)),
        );
        removedArrowIds = new Set(
          (removedData.removedArrows || []).map((id) => String(id)),
        );
        removedLineIds = new Set(
          (removedData.removedLines || []).map((id) => String(id)),
        );
        removedDoubleSidedArrowIds = new Set(
          (removedData.removedDoubleSidedArrows || []).map((id) => String(id)),
        );
        removedScribbleIds = new Set(
          (removedData.removedScribbles || []).map((id) => String(id)),
        );
        removedVideoIds = new Set(
          (removedData.removedVideos || []).map((id) => String(id)),
        );
        
        const filterElementsByRemoved = (arr, removedSet) =>
          Array.isArray(arr)
            ? arr.filter((element) => {
                const candidateIds = [
                  element?.id,
                  element?.shape_id,
                  element?.image_id,
                  element?.shape_info?.id,
                  element?.shape_properties?.id,
                ]
                  .map((v) => String(v || "").trim())
                  .filter(Boolean);
                return !candidateIds.some((id) => removedSet.has(id));
              })
            : [];
        
        // Filter main canvas elements
        if (removedMainTextIds.size > 0) {
          elements = {
            ...elements,
            texts: filterElementsByRemoved(elements?.texts, removedMainTextIds),
          };
        }
        if (removedImageIds.size > 0) {
          elements = {
            ...elements,
            images: filterElementsByRemoved(elements?.images, removedImageIds),
          };
        }
        if (removedRectangleIds.size > 0) {
          elements = {
            ...elements,
            rectangles: filterElementsByRemoved(elements?.rectangles, removedRectangleIds),
          };
        }
        if (removedCircleIds.size > 0) {
          elements = {
            ...elements,
            circles: filterElementsByRemoved(elements?.circles, removedCircleIds),
          };
        }
        if (removedArrowIds.size > 0) {
          elements = {
            ...elements,
            arrows: filterElementsByRemoved(elements?.arrows, removedArrowIds),
          };
        }
        if (removedLineIds.size > 0) {
          elements = {
            ...elements,
            lines: filterElementsByRemoved(elements?.lines, removedLineIds),
          };
        }
        if (removedDoubleSidedArrowIds.size > 0) {
          elements = {
            ...elements,
            doubleSidedArrows: filterElementsByRemoved(elements?.doubleSidedArrows, removedDoubleSidedArrowIds),
          };
        }
        if (removedScribbleIds.size > 0) {
          elements = {
            ...elements,
            scribbles: filterElementsByRemoved(elements?.scribbles, removedScribbleIds),
          };
        }
        if (removedVideoIds.size > 0) {
          elements = {
            ...elements,
            videos: filterElementsByRemoved(elements?.videos, removedVideoIds),
          };
        }
        
        // Filter off-canvas elements
        if (removedOffTextIds.size > 0) {
          offCanvasElements = {
            ...offCanvasElements,
            texts: filterElementsByRemoved(
              offCanvasElements?.texts,
              removedOffTextIds,
            ),
          };
          if (elements?.offCanvasElements) {
            elements = {
              ...elements,
              offCanvasElements: {
                ...elements.offCanvasElements,
                texts: filterElementsByRemoved(
                  elements.offCanvasElements?.texts,
                  removedOffTextIds,
                ),
              },
            };
          }
          if (Array.isArray(elements?.texts)) {
            // Also clean misplaced off-canvas texts leaked into main text array.
            elements = {
              ...elements,
              texts: filterElementsByRemoved(elements.texts, removedOffTextIds),
            };
          }
        }
        
        // Filter off-canvas images
        if (removedOffImageIds.size > 0) {
          offCanvasElements = {
            ...offCanvasElements,
            images: filterElementsByRemoved(
              offCanvasElements?.images,
              removedOffImageIds,
            ),
          };
          if (elements?.offCanvasElements) {
            elements = {
              ...elements,
              offCanvasElements: {
                ...elements.offCanvasElements,
                images: filterElementsByRemoved(
                  elements.offCanvasElements?.images,
                  removedOffImageIds,
                ),
              },
            };
          }
          if (Array.isArray(elements?.images)) {
            // Also clean misplaced off-canvas images leaked into main array.
            elements = {
              ...elements,
              images: filterElementsByRemoved(elements.images, removedOffImageIds),
            };
          }
        }
      } catch (_e) {}

      logOffCanvasLoad("1-after-IDB-read", elements, offCanvasElements);

      // Check if the loaded data has template elements nested under an 'elements' property
      // This happens when loading template data saved from CanvasForm.js
      if (elements.elements && typeof elements.elements === "object") {
        elements = elements.elements;
      }
      if (
        offCanvasElements.elements &&
        typeof offCanvasElements.elements === "object"
      ) {
        offCanvasElements = offCanvasElements.elements;
      }
      const splitOffCanvasElements = (source = {}) => {
        const hasTopLevelOffCanvasFlag = (item) =>
          !!(
            item &&
            typeof item === "object" &&
            (item.isOffcanvas === true ||
              item.isOffCanvas === true ||
              item.is_off_canvas === true)
          );
        const keys = [
          "texts",
          "rectangles",
          "circles",
          "arrows",
          "lines",
          "doubleSidedArrows",
          "scribbles",
          "images",
          "videos",
        ];
        const main = { ...source };
        const off = {};
        keys.forEach((key) => {
          const arr = Array.isArray(source[key]) ? source[key] : [];
          const mainArr = [];
          const offArr = [];
          arr.forEach((item) => {
            // IMPORTANT: while splitting main payload into main/off-canvas arrays,
            // trust only top-level flags. Nested flags can be stale template metadata
            // and were causing main-canvas clones to be reclassified as off-canvas.
            // If the payload explicitly says off-canvas at top-level, always route
            // it to off-canvas even when createdOnCanvas/marker IDs are present.
            const isOff = hasTopLevelOffCanvasFlag(item);
            if (isOff) {
              offArr.push({ ...item, isOffCanvas: true });
            } else {
              mainArr.push(item);
            }
          });
          main[key] = mainArr;
          if (offArr.length > 0) {
            off[key] = offArr;
          }
        });
        return { main, off };
      };

      const registerCanvasElementIdentity = (item, seen) => {
        collectCanvasElementIdentityKeys(item).forEach((k) => seen.add(k));
      };

      const itemMatchesCanvasIdentitySeen = (item, seen) => {
        for (const k of collectCanvasElementIdentityKeys(item)) {
          if (seen.has(k)) return true;
        }
        return false;
      };

      const mergeOffCanvasArraysDeduped = (preferFirst, preferSecond) => {
        const seen = new Set();
        const out = [];
        for (const item of [...(preferFirst || []), ...(preferSecond || [])]) {
          if (!item || typeof item !== "object") {
            if (item) out.push(item);
            continue;
          }
          if (itemMatchesCanvasIdentitySeen(item, seen)) continue;
          registerCanvasElementIdentity(item, seen);
          out.push(item);
        }
        return out;
      };

      const stripMainElementsDuplicatedOnOffCanvas = (main, off) => {
        const keys = [
          "rectangles",
          "circles",
          "arrows",
          "lines",
          "doubleSidedArrows",
          "scribbles",
          "images",
          "videos",
        ];
        // Omit rectangles/circles from geometry strip: a deliberate main-canvas copy can share
        // the same size/position as the off-canvas original; identity dedupe is enough for rects.
        const geoKeysForStrip = new Set([
          "lines",
          "arrows",
          "doubleSidedArrows",
          "scribbles",
          "images",
          "videos",
        ]);
        const out = { ...main };
        keys.forEach((key) => {
          const offArr = off[key];
          if (!Array.isArray(offArr) || offArr.length === 0) return;
          const identitySeen = new Set();
          offArr.forEach((item) =>
            registerCanvasElementIdentity(item, identitySeen),
          );
          const geoSeen = new Set();
          if (geoKeysForStrip.has(key)) {
            offArr.forEach((item) => {
              const gk = offCanvasElementGeometryKey(item, key);
              if (gk) geoSeen.add(gk);
            });
          }
          if (identitySeen.size === 0 && geoSeen.size === 0) return;
          const mainArr = out[key];
          if (!Array.isArray(mainArr) || mainArr.length === 0) return;
          out[key] = mainArr.filter((item) => {
            // Clones keep template shape_id / nested ids but get a new top-level `id`. Do not treat
            // them as duplicates of off-canvas rows when their id is not any off shape's id.
            if (
              item &&
              typeof item === "object" &&
              (key === "texts" ||
                key === "rectangles" ||
                key === "circles" ||
                key === "images" ||
                key === "videos")
            ) {
              const mainPid =
                item.id != null ? String(item.id).trim() : "";
              const offPrimaryIds = new Set(
                offArr
                  .map((o) =>
                    o?.id != null ? String(o.id).trim() : "",
                  )
                  .filter(Boolean),
              );
              if (
                mainPid &&
                offPrimaryIds.size > 0 &&
                !offPrimaryIds.has(mainPid) &&
                itemMatchesCanvasIdentitySeen(item, identitySeen)
              ) {
                try {
                  if (
                    typeof window !== "undefined" &&
                    window.localStorage?.getItem("debugMyCanvasOffCanvas") ===
                      "1"
                  ) {
                  }
                } catch (_e) {}
                return true;
              }
            }
            if (itemMatchesCanvasIdentitySeen(item, identitySeen)) return false;
            if (geoKeysForStrip.has(key)) {
              const gk = offCanvasElementGeometryKey(item, key);
              if (gk && geoSeen.has(gk)) return false;
            }
            return true;
          });
        });
        return out;
      };

      const mergeOffCanvasStoresAfterSplit = (splitOff, existingOff) => ({
        ...existingOff,
        ...splitOff,
        texts: dedupeOffCanvasArrayByGeometry(
          mergeOffCanvasArraysDeduped(
            existingOff?.texts,
            splitOff.texts,
          ),
          "texts",
        ),
        rectangles: dedupeOffCanvasArrayByGeometry(
          mergeOffCanvasArraysDeduped(
            existingOff?.rectangles,
            splitOff.rectangles,
          ),
          "rectangles",
        ),
        circles: dedupeOffCanvasArrayByGeometry(
          mergeOffCanvasArraysDeduped(
            existingOff?.circles,
            splitOff.circles,
          ),
          "circles",
        ),
        arrows: dedupeOffCanvasArrayByGeometry(
          mergeOffCanvasArraysDeduped(
            existingOff?.arrows,
            splitOff.arrows,
          ),
          "arrows",
        ),
        lines: dedupeOffCanvasArrayByGeometry(
          mergeOffCanvasArraysDeduped(
            existingOff?.lines,
            splitOff.lines,
          ),
          "lines",
        ),
        doubleSidedArrows: dedupeOffCanvasArrayByGeometry(
          mergeOffCanvasArraysDeduped(
            existingOff?.doubleSidedArrows,
            splitOff.doubleSidedArrows,
          ),
          "doubleSidedArrows",
        ),
        scribbles: dedupeOffCanvasArrayByGeometry(
          mergeOffCanvasArraysDeduped(
            existingOff?.scribbles,
            splitOff.scribbles,
          ),
          "scribbles",
        ),
        images: dedupeOffCanvasArrayByGeometry(
          mergeOffCanvasArraysDeduped(
            existingOff?.images,
            splitOff.images,
          ),
          "images",
        ),
        videos: dedupeOffCanvasArrayByGeometry(
          mergeOffCanvasArraysDeduped(
            existingOff?.videos,
            splitOff.videos,
          ),
          "videos",
        ),
      });

      // Debug IndexedDB data
      if (elements && Object.keys(elements).length > 0) {
        // Debug first text element properties
        if (elements.texts?.length > 0) {
        }

        // Debug all element counts from IndexedDB
      }

      // CRITICAL FIX: Merge location.state.canvasData with IndexedDB data.
      // Prefer the source that has MORE elements per array so we never overwrite
      // IndexedDB (e.g. 4 images) with incomplete API conversion (e.g. 0 images).
      if (
        location.state?.canvasData &&
        Object.keys(location.state.canvasData).length > 0
      ) {
        const incoming = location.state.canvasData;
        const incomingTexts = Array.isArray(incoming?.texts) ? incoming.texts : [];
        const idbElements = { ...elements };
        const hasIdbMainPayload = !!elementsResult?.data;
        const hasIdbOffPayload = !!offCanvasElementsResult?.data;
        
        // Apply deletion filtering to incoming API elements
        // This prevents deleted API elements from resurrecting when navigating back
        const filteredIncoming = { ...incoming };
        if (removedMainTextIds.size > 0) {
          filteredIncoming.texts = Array.isArray(incoming.texts) 
            ? incoming.texts.filter((t) => !removedMainTextIds.has(String(t?.id)))
            : incoming.texts;
        }
        if (removedImageIds.size > 0) {
          filteredIncoming.images = Array.isArray(incoming.images) 
            ? incoming.images.filter((img) => !removedImageIds.has(String(img?.id)))
            : incoming.images;
        }
        if (removedRectangleIds.size > 0) {
          filteredIncoming.rectangles = Array.isArray(incoming.rectangles) 
            ? incoming.rectangles.filter((rect) => !removedRectangleIds.has(String(rect?.id)))
            : incoming.rectangles;
        }
        if (removedCircleIds.size > 0) {
          filteredIncoming.circles = Array.isArray(incoming.circles) 
            ? incoming.circles.filter((circle) => !removedCircleIds.has(String(circle?.id)))
            : incoming.circles;
        }
        if (removedArrowIds.size > 0) {
          filteredIncoming.arrows = Array.isArray(incoming.arrows) 
            ? incoming.arrows.filter((arrow) => !removedArrowIds.has(String(arrow?.id)))
            : incoming.arrows;
        }
        if (removedLineIds.size > 0) {
          filteredIncoming.lines = Array.isArray(incoming.lines) 
            ? incoming.lines.filter((line) => !removedLineIds.has(String(line?.id)))
            : incoming.lines;
        }
        if (removedDoubleSidedArrowIds.size > 0) {
          filteredIncoming.doubleSidedArrows = Array.isArray(incoming.doubleSidedArrows) 
            ? incoming.doubleSidedArrows.filter((arrow) => !removedDoubleSidedArrowIds.has(String(arrow?.id)))
            : incoming.doubleSidedArrows;
        }
        if (removedScribbleIds.size > 0) {
          filteredIncoming.scribbles = Array.isArray(incoming.scribbles) 
            ? incoming.scribbles.filter((scribble) => !removedScribbleIds.has(String(scribble?.id)))
            : incoming.scribbles;
        }
        if (removedVideoIds.size > 0) {
          filteredIncoming.videos = Array.isArray(incoming.videos) 
            ? incoming.videos.filter((video) => !removedVideoIds.has(String(video?.id)))
            : incoming.videos;
        }
        
        const arrayKeys = [
          "texts",
          "rectangles",
          "circles",
          "arrows",
          "lines",
          "doubleSidedArrows",
          "scribbles",
          "images",
          "videos",
        ];
        elements = {
          ...elements,
          ...filteredIncoming,
          canvasHeight: filteredIncoming.canvasHeight || elements.canvasHeight,
        };
        arrayKeys.forEach((key) => {
          const fromIdb = idbElements[key];
          const fromState = filteredIncoming[key];
          if (
            Array.isArray(fromIdb) &&
            Array.isArray(fromState) &&
            fromIdb.length > fromState.length
          ) {
            elements[key] = fromIdb;
          }
        });
        // When navigating from CanvasForm/MyCanvas: navigation canvasData often drops
        // polyline `points` (null) for unified lines/arrows while IndexedDB has the bends.
        // Prefer IDB for these arrays when we actually loaded a main row (same idea as texts).
        if (location.state?.fromCanvasPage || location.state?.fromMyCanvas) {
          if (hasIdbMainPayload) {
            ["lines", "arrows", "doubleSidedArrows"].forEach((key) => {
              if (Array.isArray(idbElements[key])) {
                elements[key] = idbElements[key];
              }
            });
          } else {
            ["lines", "arrows", "doubleSidedArrows"].forEach((key) => {
              if (Array.isArray(filteredIncoming[key])) {
                elements[key] = filteredIncoming[key];
              }
            });
          }
          // Returning from canvas flow: never resurrect text from navigation payload.
          // If IndexedDB was loaded, keep IDB geometry/identity but still merge metadata
          // (partsByType/text_box_content_data/content) from latest navigation payload.
          if (hasIdbMainPayload && Array.isArray(idbElements.texts)) {
            // Filter incoming texts to remove deleted API elements before merging
            const filteredIncomingTexts = removedMainTextIds.size > 0
              ? incomingTexts.filter((t) => !removedMainTextIds.has(String(t?.id)))
              : incomingTexts;
            
            elements.texts = mergeIndexedDbTextsWithNavigationState(
              idbElements.texts,
              filteredIncomingTexts,
              {
                appendUnmatchedIncoming: false,
              },
            );
          }
          if (hasIdbOffPayload) {
            const idbOffTexts = Array.isArray(offCanvasElements?.texts)
              ? offCanvasElements.texts
              : Array.isArray(idbElements?.offCanvasElements?.texts)
                ? idbElements.offCanvasElements.texts
                : [];
            elements.offCanvasElements = {
              ...(elements.offCanvasElements || {}),
              texts: idbOffTexts,
            };
          }
        }
        // Texts: merge entity-by-entity so IndexedDB (updated on canvas leave) wins geometry over stale navigation state when counts match
        if (
          Array.isArray(idbElements.texts) &&
          idbElements.texts.length > 0 &&
          Array.isArray(elements.texts)
        ) {
          elements.texts = mergeIndexedDbTextsWithNavigationState(
            idbElements.texts,
            elements.texts,
            {
              // Returning from CanvasForm/MyCanvas should treat IndexedDB as source of truth.
              // Otherwise stale get-by-id/navigation texts can resurrect deleted off-canvas texts.
              appendUnmatchedIncoming: false,
            },
          );
        }
        // When coming from form, always use form's validTextShapeIds so we show 2 API texts (never overwrite with IndexedDB single text_box id)
        if (
          location.state?.fromCanvasPage &&
          Array.isArray(location.state?.validTextShapeIds) &&
          location.state.validTextShapeIds.length > 0
        ) {
          elements.validTextShapeIds = location.state.validTextShapeIds;
        }
        logOffCanvasLoad(
          "2-after-canvasData-merge",
          elements,
          offCanvasElements,
        );
      }
      // When navigating from form, always stamp state.validTextShapeIds onto elements so filter shows 2 texts even when canvasData wasn't merged
      if (
        location.state?.fromCanvasPage &&
        Array.isArray(location.state?.validTextShapeIds) &&
        location.state.validTextShapeIds.length > 0
      ) {
        elements.validTextShapeIds = location.state.validTextShapeIds;
      }

      // Retry if elements not found
      if (!elements || Object.keys(elements).length === 0) {
        if (version < maxVersion) {
          return await loadCanvasElements(version + 1, maxVersion);
        } else {
          elements = {};
        }
      }
      if (!offCanvasElements || typeof offCanvasElements !== "object") {
        offCanvasElements = {};
      }

      // Always split isOffcanvas items out of main element arrays and merge with the
      // off-canvas store (deduped). Without this, Edit-in-full-screen only merged when
      // canvasData was present, so IDB could keep off-canvas shapes in main arrays while
      // offCanvasElements also had copies → duplicates on the off-canvas stage.
      {
        logOffCanvasLoad(
          "3-before-split-merge-strip",
          elements,
          offCanvasElements,
        );
        const splitAll = splitOffCanvasElements(elements);
        elements = splitAll.main;
        offCanvasElements = mergeOffCanvasStoresAfterSplit(
          splitAll.off,
          offCanvasElements,
        );
        elements = stripMainElementsDuplicatedOnOffCanvas(
          elements,
          offCanvasElements,
        );
        logOffCanvasLoad("4-after-split-merge-strip", elements, offCanvasElements, {
          splitOffCounts: [
            "texts",
            "rectangles",
            "circles",
            "arrows",
            "lines",
            "doubleSidedArrows",
            "scribbles",
            "images",
            "videos",
          ].reduce((acc, k) => {
            acc[k] = Array.isArray(splitAll.off[k])
              ? splitAll.off[k].length
              : 0;
            return acc;
          }, {}),
        });
      }

      // IMPORTANT: Prioritize navigation state canvas height first (specific to this page)
      // Then fallback to IndexedDB elements, then default
      // This ensures each page maintains its own independent canvas height

      if (
        location.state?.canvasHeight != null &&
        !Number.isNaN(parseInt(location.state?.canvasHeight))
      ) {
        // Use canvasHeight passed via navigation state for selected page (highest priority)
        setCanvasHeight(String(parseInt(location.state.canvasHeight)));
      } else if (
        elements?.canvasHeight != null &&
        !Number.isNaN(parseInt(elements.canvasHeight))
      ) {
        // Fallback to saved canvasHeight from IndexedDB

        setCanvasHeight(String(parseInt(elements.canvasHeight)));
      }

      // Step 1: Normalize legacy shapes if present - MERGE by shape_id so we never duplicate (API has 2 texts; don't add 2 more from shapes)
      const existingTextsByShapeId = new Map();
      (elements.texts || []).forEach((t) =>
        registerCanvasTextKeysInMap(existingTextsByShapeId, t),
      );
      if (elements?.shapes?.length) {
        elements.shapes.forEach((shape) => {
          const normalizedShape = {
            ...shape,
            type: normalizeShapeType(shape.type),
          };
          switch (normalizedShape.type) {
            case "Text": {
              // CRITICAL: Skip shapes that only have a marker id (layout_config / parts) - never add 3rd text from marker
              if (
                isMarkerId(normalizedShape.shape_id) ||
                (!normalizedShape.shape_id &&
                  normalizedShape.shape_properties?.id &&
                  isMarkerId(normalizedShape.shape_properties.id))
              )
                break;
              // CRITICAL: Use shape_id first - shape_properties.id can be a shared marker id (never use as identity)
              const textIdFromShape =
                normalizedShape.shape_id ||
                (normalizedShape.shape_properties?.id &&
                  !isMarkerId(normalizedShape.shape_properties.id)
                  ? normalizedShape.shape_properties.id
                  : null) ||
                normalizedShape.id;
              const shapeProperties = normalizedShape.shape_properties || {};
              // Skip if IndexedDB already has this text (match shape_id, inner id, or text_box_id)
              if (textShapeAlreadyLoaded(existingTextsByShapeId, textIdFromShape, shapeProperties))
                break;
              if (!textIdFromShape || isMarkerId(textIdFromShape)) break;

              const shapeInfo = normalizedShape.shape_info || {};
              const preservedPartsByType =
                shapeProperties.partsByType !== null &&
                  shapeProperties.partsByType !== undefined
                  ? shapeProperties.partsByType
                  : shapeInfo.partsByType;
              const preservedContent =
                shapeProperties.content !== null &&
                  shapeProperties.content !== undefined &&
                  shapeProperties.content !== ""
                  ? shapeProperties.content
                  : shapeInfo.content !== null &&
                    shapeInfo.content !== undefined &&
                    shapeInfo.content !== ""
                    ? shapeInfo.content
                    : normalizedShape.content;
              const preservedTask =
                shapeProperties.task !== null &&
                  shapeProperties.task !== undefined &&
                  shapeProperties.task !== ""
                  ? shapeProperties.task
                  : shapeInfo.task !== null &&
                    shapeInfo.task !== undefined &&
                    shapeInfo.task !== ""
                    ? shapeInfo.task
                    : normalizedShape.task;
              const preservedNote =
                shapeProperties.note !== null &&
                  shapeProperties.note !== undefined &&
                  shapeProperties.note !== ""
                  ? shapeProperties.note
                  : shapeInfo.note !== null &&
                    shapeInfo.note !== undefined &&
                    shapeInfo.note !== ""
                    ? shapeInfo.note
                    : normalizedShape.note;

              const textDataFromShape = normalizedShape.shape_properties
                ? {
                  ...normalizedShape.shape_properties,
                  ...normalizedShape,
                  id: textIdFromShape,
                  shape_id: normalizedShape.shape_id || textIdFromShape,
                  ...(preservedPartsByType !== undefined &&
                    preservedPartsByType !== null
                    ? { partsByType: preservedPartsByType }
                    : {}),
                  ...(preservedContent !== undefined &&
                    preservedContent !== null
                    ? { content: preservedContent }
                    : {}),
                  ...(preservedTask !== undefined && preservedTask !== null
                    ? { task: preservedTask }
                    : {}),
                  ...(preservedNote !== undefined && preservedNote !== null
                    ? { note: preservedNote }
                    : {}),
                }
                : normalizedShape;

              if (
                normalizedShape.canvas_page_location_and_size?.rotation !==
                undefined
              ) {
                textDataFromShape.rotation =
                  normalizedShape.canvas_page_location_and_size.rotation;
              } else if (
                normalizedShape.shape_properties?.canvas_page_location_and_size
                  ?.rotation !== undefined
              ) {
                textDataFromShape.rotation =
                  normalizedShape.shape_properties.canvas_page_location_and_size.rotation;
              } else if (
                normalizedShape.shape_info?.canvas_page_location_and_size
                  ?.rotation !== undefined
              ) {
                textDataFromShape.rotation =
                  normalizedShape.shape_info.canvas_page_location_and_size.rotation;
              }
              if (textDataFromShape.rotation === undefined) {
                textDataFromShape.rotation = normalizedShape.rotation ?? 0;
              }

              (elements.texts = elements.texts || []).push(textDataFromShape);
              registerCanvasTextKeysInMap(existingTextsByShapeId, textDataFromShape);
              break;
            }
            case "Rect": {
              // Extract rectangle properties from shape_properties
              const rectDataFromShape = normalizedShape.shape_properties
                ? {
                  ...normalizedShape.shape_properties,
                  ...normalizedShape,
                  id:
                    normalizedShape.shape_id ||
                    normalizedShape.shape_properties.id ||
                    normalizedShape.id,
                }
                : normalizedShape;
              (elements.rectangles = elements.rectangles || []).push(
                rectDataFromShape,
              );
              break;
            }
            case "Circle": {
              // Extract circle properties from shape_properties
              const circleDataFromShape = normalizedShape.shape_properties
                ? {
                  ...normalizedShape.shape_properties,
                  ...normalizedShape,
                  id:
                    normalizedShape.shape_id ||
                    normalizedShape.shape_properties.id ||
                    normalizedShape.id,
                }
                : normalizedShape;
              (elements.circles = elements.circles || []).push(
                circleDataFromShape,
              );
              break;
            }
            case "Scribble": {
              // Extract scribble properties from shape_properties
              const scribbleDataFromShape = normalizedShape.shape_properties
                ? {
                  ...normalizedShape.shape_properties,
                  ...normalizedShape,
                  id:
                    normalizedShape.shape_id ||
                    normalizedShape.shape_properties.id ||
                    normalizedShape.id,
                }
                : normalizedShape;
              (elements.scribbles = elements.scribbles || []).push(
                scribbleDataFromShape,
              );
              break;
            }
            case "Arrow": {
              // Extract arrow properties from shape_properties
              const arrowDataFromShape = normalizedShape.shape_properties
                ? {
                  ...normalizedShape.shape_properties,
                  ...normalizedShape,
                  id:
                    normalizedShape.shape_id ||
                    normalizedShape.shape_properties.id ||
                    normalizedShape.id,
                }
                : normalizedShape;
              applyResolvedLinePointsFromShape(normalizedShape, arrowDataFromShape);
              // Convert to unified format: Arrow has arrow at end by default (1 pointer)
              // Push to lines array with hasEndArrow: true, hasStartArrow: false
              arrowDataFromShape.hasStartArrow =
                arrowDataFromShape.hasStartArrow !== undefined
                  ? arrowDataFromShape.hasStartArrow
                  : arrowDataFromShape.pointerAtBeginning || false;
              arrowDataFromShape.hasEndArrow =
                arrowDataFromShape.hasEndArrow !== undefined
                  ? arrowDataFromShape.hasEndArrow
                  : arrowDataFromShape.pointerAtEnd !== undefined
                    ? arrowDataFromShape.pointerAtEnd
                    : true;
              // CRITICAL: Set type to "Arrow" so click handler can identify it correctly
              arrowDataFromShape.type = "Arrow";
              // CRITICAL: Push to lines array for unified rendering with UnifiedLineArrowElement
              (elements.lines = elements.lines || []).push(arrowDataFromShape);
              break;
            }
            case "Line": {
              // Extract line properties from shape_properties
              const lineDataFromShape = normalizedShape.shape_properties
                ? {
                  ...normalizedShape.shape_properties,
                  ...normalizedShape,
                  id:
                    normalizedShape.shape_id ||
                    normalizedShape.shape_properties.id ||
                    normalizedShape.id,
                }
                : normalizedShape;
              applyResolvedLinePointsFromShape(normalizedShape, lineDataFromShape);
              // CRITICAL: If API sends type "Line" but shape_properties has DoubleSidedArrow (or both arrow flags),
              // treat as DoubleSidedArrow so it renders correctly in MyCanvas (not as collapsed double heads)
              const spType = normalizedShape.shape_properties?.type != null
                ? normalizeShapeType(normalizedShape.shape_properties.type)
                : null;
              const hasBothArrows =
                lineDataFromShape.hasStartArrow === true && lineDataFromShape.hasEndArrow === true;
              if (spType === "DoubleSidedArrow" || (hasBothArrows && spType !== "Arrow")) {
                lineDataFromShape.type = "DoubleSidedArrow";
                lineDataFromShape.hasStartArrow = true;
                lineDataFromShape.hasEndArrow = true;
              }
              // Ensure points have valid length for arrows (avoid zero-length double-sided arrow)
              if (
                (lineDataFromShape.hasStartArrow || lineDataFromShape.hasEndArrow) &&
                Array.isArray(lineDataFromShape.points) &&
                lineDataFromShape.points.length >= 4
              ) {
                const [x1, y1, x2, y2] = lineDataFromShape.points;
                const len = Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2);
                if (len < 1) {
                  const w = lineDataFromShape.width > 0 ? lineDataFromShape.width : 100;
                  lineDataFromShape.points = [0, 0, w, 0];
                }
              }
              (elements.lines = elements.lines || []).push(lineDataFromShape);
              break;
            }
            case "DoubleSidedArrow": {
              // Extract double-sided arrow properties from shape_properties
              const doubleArrowDataFromShape = normalizedShape.shape_properties
                ? {
                  ...normalizedShape.shape_properties,
                  ...normalizedShape,
                  id:
                    normalizedShape.shape_id ||
                    normalizedShape.shape_properties.id ||
                    normalizedShape.id,
                }
                : normalizedShape;
              applyResolvedLinePointsFromShape(
                normalizedShape,
                doubleArrowDataFromShape,
              );
              // Convert to unified format: DoubleSidedArrow has arrows at both ends (2 pointers)
              // Push to lines array with hasStartArrow: true, hasEndArrow: true
              doubleArrowDataFromShape.hasStartArrow =
                doubleArrowDataFromShape.hasStartArrow !== undefined
                  ? doubleArrowDataFromShape.hasStartArrow
                  : true;
              doubleArrowDataFromShape.hasEndArrow =
                doubleArrowDataFromShape.hasEndArrow !== undefined
                  ? doubleArrowDataFromShape.hasEndArrow
                  : true;
              // CRITICAL: Set type to "DoubleSidedArrow" so click handler can identify it correctly
              doubleArrowDataFromShape.type = "DoubleSidedArrow";
              // CRITICAL: Push to lines array for unified rendering with UnifiedLineArrowElement
              (elements.lines = elements.lines || []).push(
                doubleArrowDataFromShape,
              );
              break;
            }
            case "Image":
              (elements.images = elements.images || []).push(normalizedShape);
              break;
            default:
          }
        });
      }

      // Step 2: Add shapeData and imageData from router state - only add Text if not already present (merge by shape_id)
      shapeData?.forEach((shape) => {
        const normalizedShape = {
          ...shape,
          type: normalizeShapeType(shape.type),
        };
        switch (normalizedShape.type) {
          case "Text": {
            // CRITICAL: Skip shapes that only have a marker id (layout_config / parts)
            if (
              isMarkerId(normalizedShape.shape_id) ||
              (!normalizedShape.shape_id &&
                normalizedShape.shape_properties?.id &&
                isMarkerId(normalizedShape.shape_properties.id))
            )
              break;
            const textIdFromState =
              normalizedShape.shape_id ||
              (normalizedShape.shape_properties?.id &&
                !isMarkerId(normalizedShape.shape_properties.id)
                ? normalizedShape.shape_properties.id
                : null) ||
              normalizedShape.id;
            const shapeProperties = normalizedShape.shape_properties || {};
            if (textShapeAlreadyLoaded(existingTextsByShapeId, textIdFromState, shapeProperties))
              break;
            if (!textIdFromState || isMarkerId(textIdFromState)) break;

            const shapeInfo = normalizedShape.shape_info || {};
            const preservedPartsByType =
              shapeProperties.partsByType !== null &&
                shapeProperties.partsByType !== undefined
                ? shapeProperties.partsByType
                : shapeInfo.partsByType;
            const preservedContent =
              shapeProperties.content !== null &&
                shapeProperties.content !== undefined &&
                shapeProperties.content !== ""
                ? shapeProperties.content
                : shapeInfo.content !== null &&
                  shapeInfo.content !== undefined &&
                  shapeInfo.content !== ""
                  ? shapeInfo.content
                  : normalizedShape.content;
            const preservedTask =
              shapeProperties.task !== null &&
                shapeProperties.task !== undefined &&
                shapeProperties.task !== ""
                ? shapeProperties.task
                : shapeInfo.task !== null &&
                  shapeInfo.task !== undefined &&
                  shapeInfo.task !== ""
                  ? shapeInfo.task
                  : normalizedShape.task;
            const preservedNote =
              shapeProperties.note !== null &&
                shapeProperties.note !== undefined &&
                shapeProperties.note !== ""
                ? shapeProperties.note
                : shapeInfo.note !== null &&
                  shapeInfo.note !== undefined &&
                  shapeInfo.note !== ""
                  ? shapeInfo.note
                  : normalizedShape.note;

            const textData = normalizedShape.shape_properties
              ? {
                ...normalizedShape.shape_properties,
                ...normalizedShape,
                id: textIdFromState,
                shape_id: normalizedShape.shape_id || textIdFromState,
                ...(preservedPartsByType !== undefined &&
                  preservedPartsByType !== null
                  ? { partsByType: preservedPartsByType }
                  : {}),
                ...(preservedContent !== undefined &&
                  preservedContent !== null
                  ? { content: preservedContent }
                  : {}),
                ...(preservedTask !== undefined && preservedTask !== null
                  ? { task: preservedTask }
                  : {}),
                ...(preservedNote !== undefined && preservedNote !== null
                  ? { note: preservedNote }
                  : {}),
              }
              : normalizedShape;

            if (
              normalizedShape.canvas_page_location_and_size?.rotation !==
              undefined
            ) {
              textData.rotation =
                normalizedShape.canvas_page_location_and_size.rotation;
            } else if (
              normalizedShape.shape_properties?.canvas_page_location_and_size
                ?.rotation !== undefined
            ) {
              textData.rotation =
                normalizedShape.shape_properties.canvas_page_location_and_size.rotation;
            } else if (
              normalizedShape.shape_info?.canvas_page_location_and_size
                ?.rotation !== undefined
            ) {
              textData.rotation =
                normalizedShape.shape_info.canvas_page_location_and_size.rotation;
            }
            if (textData.rotation === undefined) {
              textData.rotation =
                textData.rotation ?? normalizedShape.rotation ?? 0;
            }

            (elements.texts = elements.texts || []).push(textData);
            registerCanvasTextKeysInMap(existingTextsByShapeId, textData);
            break;
          }
          case "Rect": {
            // Extract rectangle properties from shape_properties
            const rectData = normalizedShape.shape_properties
              ? {
                ...normalizedShape.shape_properties,
                ...normalizedShape,
                id:
                  normalizedShape.shape_id ||
                  normalizedShape.shape_properties.id ||
                  normalizedShape.id,
              }
              : normalizedShape;
            (elements.rectangles = elements.rectangles || []).push(rectData);
            break;
          }
          case "Circle": {
            // Extract circle properties from shape_properties
            const circleData = normalizedShape.shape_properties
              ? {
                ...normalizedShape.shape_properties,
                ...normalizedShape,
                id:
                  normalizedShape.shape_id ||
                  normalizedShape.shape_properties.id ||
                  normalizedShape.id,
              }
              : normalizedShape;
            (elements.circles = elements.circles || []).push(circleData);
            break;
          }
          case "Scribble": {
            // Extract scribble properties from shape_properties
            const scribbleData = normalizedShape.shape_properties
              ? {
                ...normalizedShape.shape_properties,
                ...normalizedShape,
                id:
                  normalizedShape.shape_id ||
                  normalizedShape.shape_properties.id ||
                  normalizedShape.id,
              }
              : normalizedShape;
            (elements.scribbles = elements.scribbles || []).push(scribbleData);
            break;
          }
          case "Arrow": {
            // Extract arrow properties from shape_properties
            const arrowData = normalizedShape.shape_properties
              ? {
                ...normalizedShape.shape_properties,
                ...normalizedShape,
                id:
                  normalizedShape.shape_id ||
                  normalizedShape.shape_properties.id ||
                  normalizedShape.id,
              }
              : normalizedShape;
            applyResolvedLinePointsFromShape(normalizedShape, arrowData);
            // Convert to unified format: Arrow has arrow at end by default (1 pointer)
            // Push to lines array with hasEndArrow: true, hasStartArrow: false
            // CRITICAL: For Arrow type from API, always default hasEndArrow to true
            // Only use shape_properties value if pointerAtEnd is explicitly false
            arrowData.hasStartArrow =
              arrowData.hasStartArrow !== undefined
                ? arrowData.hasStartArrow
                : arrowData.pointerAtBeginning || false;
            // CRITICAL: For Arrow types, hasEndArrow should default to true
            // Only respect shape_properties if pointerAtEnd is explicitly set to false
            if (arrowData.pointerAtEnd === false) {
              arrowData.hasEndArrow = false;
            } else {
              arrowData.hasEndArrow =
                arrowData.hasEndArrow !== undefined
                  ? arrowData.hasEndArrow
                  : true; // Default to true for Arrow type
            }
            // CRITICAL: Set type to "Arrow" AFTER all spreading to ensure it's not overwritten
            // This must be set last to override any type from shape_properties
            arrowData.type = "Arrow";
            // CRITICAL: Ensure arrow head properties are explicitly set as booleans
            arrowData.hasStartArrow = Boolean(arrowData.hasStartArrow);
            arrowData.hasEndArrow = Boolean(arrowData.hasEndArrow);
            // CRITICAL: Push to lines array for unified rendering with UnifiedLineArrowElement
            (elements.lines = elements.lines || []).push(arrowData);
            break;
          }
          case "Line": {
            // Extract line properties from shape_properties
            const lineData = normalizedShape.shape_properties
              ? {
                ...normalizedShape.shape_properties,
                ...normalizedShape,
                id:
                  normalizedShape.shape_id ||
                  normalizedShape.shape_properties.id ||
                  normalizedShape.id,
              }
              : normalizedShape;
            applyResolvedLinePointsFromShape(normalizedShape, lineData);
            // CRITICAL: If API sends type "Line" but shape_properties has DoubleSidedArrow (or both arrow flags),
            // treat as DoubleSidedArrow so it renders correctly in MyCanvas (not as collapsed double heads)
            const spTypeLine = normalizedShape.shape_properties?.type != null
              ? normalizeShapeType(normalizedShape.shape_properties.type)
              : null;
            const hasBothArrowsLine =
              lineData.hasStartArrow === true && lineData.hasEndArrow === true;
            if (spTypeLine === "DoubleSidedArrow" || (hasBothArrowsLine && spTypeLine !== "Arrow")) {
              lineData.type = "DoubleSidedArrow";
              lineData.hasStartArrow = true;
              lineData.hasEndArrow = true;
            }
            // Ensure points have valid length for arrows (avoid zero-length double-sided arrow)
            if (
              (lineData.hasStartArrow || lineData.hasEndArrow) &&
              Array.isArray(lineData.points) &&
              lineData.points.length >= 4
            ) {
              const [x1, y1, x2, y2] = lineData.points;
              const len = Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2);
              if (len < 1) {
                const w = lineData.width > 0 ? lineData.width : 100;
                lineData.points = [0, 0, w, 0];
              }
            }
            (elements.lines = elements.lines || []).push(lineData);
            break;
          }
          case "DoubleSidedArrow": {
            // Extract double-sided arrow properties from shape_properties
            const doubleArrowData = normalizedShape.shape_properties
              ? {
                ...normalizedShape.shape_properties,
                ...normalizedShape,
                id:
                  normalizedShape.shape_id ||
                  normalizedShape.shape_properties.id ||
                  normalizedShape.id,
              }
              : normalizedShape;
            applyResolvedLinePointsFromShape(normalizedShape, doubleArrowData);
            // Convert to unified format: DoubleSidedArrow has arrows at both ends (2 pointers)
            // Push to lines array with hasStartArrow: true, hasEndArrow: true
            doubleArrowData.hasStartArrow =
              doubleArrowData.hasStartArrow !== undefined
                ? doubleArrowData.hasStartArrow
                : true;
            doubleArrowData.hasEndArrow =
              doubleArrowData.hasEndArrow !== undefined
                ? doubleArrowData.hasEndArrow
                : true;
            // CRITICAL: Set type to "DoubleSidedArrow" so click handler can identify it correctly
            doubleArrowData.type = "DoubleSidedArrow";
            // CRITICAL: Push to lines array for unified rendering with UnifiedLineArrowElement
            (elements.lines = elements.lines || []).push(doubleArrowData);
            break;
          }
          default:
        }
      });

      // Merge imageData into existing image so originalCropRotation/originalWidth/originalHeight are present (fixes crop cut in MyCanvas)
      imageData?.forEach((img) => {
        const src = img.image_path || img.src;
        const id = img.id || img.image_id || img.shape_id;
        const imageProps = img.image_properties || {};
        const existingIndex = (elements.images || []).findIndex(
          (e) =>
            (e.id || e.image_id || e.shape_id) === id ||
            (e.src || e.image_path) === src,
        );
        if (existingIndex >= 0) {
          const existing = elements.images[existingIndex];
          elements.images[existingIndex] = {
            ...existing,
            ...img,
            ...imageProps,
            src: src || existing.src || existing.image_path,
            image_properties: {
              ...(existing.image_properties || {}),
              ...imageProps,
            },
          };
        } else {
          (elements.images = elements.images || []).push({
            ...img,
            ...imageProps,
            src,
          });
        }
      });

      elements = stripMainElementsDuplicatedOnOffCanvas(
        elements,
        offCanvasElements,
      );
      logOffCanvasLoad(
        "5-after-legacy-shapes-strip",
        elements,
        offCanvasElements,
      );

      // Include API text shapes and user-created texts (createdOnCanvas or marker ids present in loaded data).
      const validTextShapeIds =
        location.state?.validTextShapeIds ??
        (elements?.validTextShapeIds || []);
      let textsForDedup = elements.texts || [];
      // Marker ids that were in the loaded payload (IndexedDB) – keep these so user-added texts persist even if createdOnCanvas was stripped
      const loadedMarkerTextIds = new Set(
        (elements.texts || [])
          .filter((t) => {
            const sid = t.shape_id || t.id;
            return sid && isMarkerId(sid);
          })
          .map((t) => t.shape_id || t.id),
      );
      const nonMarkerOrUserCreatedTexts = textsForDedup.filter((t) => {
        const sid = t.shape_id || t.id;
        return (
          sid &&
          (!isMarkerId(sid) ||
            t.createdOnCanvas === true ||
            loadedMarkerTextIds.has(sid))
        );
      });
      if (Array.isArray(validTextShapeIds) && validTextShapeIds.length > 0) {
        textsForDedup = textsForDedup.filter((t) => {
          if (t.createdOnCanvas === true) return true;
          const sid = t.shape_id || t.id;
          if (sid && loadedMarkerTextIds.has(sid)) return true;
          return sid && validTextShapeIds.includes(sid);
        });
        if (
          textsForDedup.length < nonMarkerOrUserCreatedTexts.length &&
          nonMarkerOrUserCreatedTexts.length > 0
        ) {
          textsForDedup = nonMarkerOrUserCreatedTexts;
        }
      } else if (nonMarkerOrUserCreatedTexts.length > 0) {
        textsForDedup = nonMarkerOrUserCreatedTexts;
      }
      // Keep user-created texts (createdOnCanvas or marker id present in loaded data)
      textsForDedup = textsForDedup.filter((t) => {
        const idStr = t.id != null ? String(t.id) : "";
        const sidStr = t.shape_id != null ? String(t.shape_id) : "";
        if (t.createdOnCanvas === true) return true;
        const sid = t.shape_id || t.id;
        if (sid && loadedMarkerTextIds.has(sid)) return true;
        if (idStr.includes("marker") && (!sidStr || sidStr.includes("marker")))
          return false;
        return true;
      });

      // CRITICAL: Deduplicate texts by shape_id so one shape never appears twice (avoids merged props from canvasData/text_boxes)
      const textsDedupedByShapeId = dedupeCanvasTextsByEntity(
        textsForDedup.reduce((acc, t) => {
          const sid = t.shape_id || t.id;
          if (!sid) {
            acc.push(t);
            return acc;
          }
          const existing = acc.find((a) => (a.shape_id || a.id) === sid);
          if (!existing) {
            acc.push(t);
          } else if (t.id === sid && existing.id !== sid) {
            const idx = acc.findIndex((a) => (a.shape_id || a.id) === sid);
            acc[idx] = t;
          }
          return acc;
        }, []),
      );

      // Process and set shapes (main + off-canvas texts use the same merge so partsByType survives IndexedDB)
      const processedTexts = (textsDedupedByShapeId || []).map(
        mergeLoadedCanvasTextRow,
      );
      const processedOffCanvasTexts = dedupeCanvasTextsByEntity(
        offCanvasElements.texts || [],
      ).map(mergeLoadedCanvasTextRow);
      const mainTextRowCount = processedTexts.length;
      const textsForPartsRefresh = [
        ...processedTexts,
        ...processedOffCanvasTexts,
      ];
      const refreshedAllTextsRaw =
        await refreshTextPartsByTypeWithLatestParts(textsForPartsRefresh, dispatch);
      const refreshedAllTexts = applyCtmPartDetailsToTexts(
        refreshedAllTextsRaw,
        location.state?.ctmPartDetails || [],
      );
      setTexts(refreshedAllTexts.slice(0, mainTextRowCount));
      setOffCanvasTexts(refreshedAllTexts.slice(mainTextRowCount));
      const processedRectangles = (elements.rectangles || [])
        .map((r) => {
          // Preserve original IDs before spreading shape_properties/shape_info
          // which may contain marker IDs that should not override the real shape_id
          const originalShapeId = r.shape_id;
          const originalId = r.id;

          // Extract properties from shape_properties or shape_info if they exist
          const rect = {
            ...r,
            ...(r.shape_properties || {}),
            ...(r.shape_info || {}),
          };

          // CRITICAL: Prioritize shape_id from API over id from shape_properties
          // shape_id is the authoritative ID from the database
          // shape_properties.id may contain marker IDs that should be ignored
          if (originalShapeId) {
            rect.id = originalShapeId;
          } else if (originalId && !isMarkerId(originalId)) {
            rect.id = originalId;
          } else if (!rect.id) {
            rect.id = `rect_${Date.now()}_${Math.random()}`;
          }
          return rect;
        })
        // Filter out rectangles that don't have valid dimensions
        .filter(
          (rect) =>
            rect.width !== undefined &&
            rect.height !== undefined &&
            rect.width > 0 &&
            rect.height > 0 &&
            rect.x !== undefined &&
            rect.y !== undefined,
        );

      setRectangles(processedRectangles);

      const processedCirclesFirst = (elements.circles || [])
        .map((c) => {
          // Preserve original IDs before spreading shape_properties/shape_info
          // which may contain marker IDs that should not override the real shape_id
          const originalShapeId = c.shape_id;
          const originalId = c.id;

          const circle = {
            ...c,
            ...(c.shape_properties || {}),
            ...(c.shape_info || {}),
          };

          // CRITICAL: Prioritize shape_id from API over id from shape_properties
          // shape_id is the authoritative ID from the database
          // shape_properties.id may contain marker IDs that should be ignored
          if (originalShapeId) {
            circle.id = originalShapeId;
          } else if (originalId && !isMarkerId(originalId)) {
            circle.id = originalId;
          } else if (!circle.id) {
            circle.id = `circle_${Date.now()}_${Math.random()}`;
          }
          return circle;
        })
        .filter(
          (circle) =>
            circle.x !== undefined &&
            circle.y !== undefined &&
            circle.radius !== undefined &&
            circle.radius > 0,
        );
      setCircles(processedCirclesFirst);

      const processedScribbles = (elements.scribbles || []).map((s) => ({
        ...s,
        ...(s.shape_properties || {}),
        ...(s.shape_info || {}),
      }));
      setScribbles(processedScribbles);

      // CRITICAL: Convert legacy arrows to unified format and merge into lines array
      const processedArrows = (elements.arrows || []).map(
        mapLegacyLoadedArrowToUnifiedLine,
      );
      // Keep arrows array empty for backward compatibility (but don't use it)
      setArrows([]);

      const processedLinesFirst = (elements.lines || [])
        .map(processLoadedUnifiedLineLike)
        .filter(isValidLoadedUnifiedLineLike);

      // CRITICAL: Convert legacy doubleSidedArrows to unified format and merge into lines array
      const processedDoubleSidedArrows = (
        elements.doubleSidedArrows || []
      ).map(mapLegacyLoadedDoubleSidedToUnifiedLine);
      // Keep doubleSidedArrows array empty for backward compatibility (but don't use it)
      setDoubleSidedArrows([]);

      // Merge all unified elements (lines, arrows, doubleSidedArrows) into lines array
      const allUnifiedLines = [
        ...processedLinesFirst,
        ...processedArrows,
        ...processedDoubleSidedArrows,
      ];
      console.log("[MyCanvasLoadDebug][branch-A][allUnifiedLines]", {
        total: allUnifiedLines.length,
        sample: allUnifiedLines.slice(0, 8).map((l) => ({
          id: l?.id || l?.shape_id,
          type: l?.type,
          x: l?.x,
          y: l?.y,
          pointsLen: Array.isArray(l?.points) ? l.points.length : null,
          firstPoints: Array.isArray(l?.points) ? l.points.slice(0, 6) : null,
        })),
      });
      try {
        const unresolved = (elements.lines || []).filter((raw) => {
          const rootPts = raw?.points;
          const infoPts = raw?.shape_info?.points;
          const propsPts = raw?.shape_properties?.points;
          return (
            !Array.isArray(rootPts) &&
            !Array.isArray(infoPts) &&
            !Array.isArray(propsPts)
          );
        });
        if (unresolved.length > 0) {
          console.warn("[MyCanvasLoadDebug][unresolved-line-points][branch-A]", {
            count: unresolved.length,
            ids: unresolved.map((r) => r?.shape_id || r?.id).filter(Boolean),
          });
        }
      } catch (e) {}
      const processedOffCanvasLinesOnly = (offCanvasElements.lines || [])
        .map(processLoadedUnifiedLineLike)
        .filter(isValidLoadedUnifiedLineLike);
      const processedOffArrows = (offCanvasElements.arrows || []).map(
        mapLegacyLoadedArrowToUnifiedLine,
      );
      const processedOffDoubleSidedArrows = (
        offCanvasElements.doubleSidedArrows || []
      ).map(mapLegacyLoadedDoubleSidedToUnifiedLine);
      const allUnifiedOffCanvasLines = [
        ...processedOffCanvasLinesOnly,
        ...processedOffArrows,
        ...processedOffDoubleSidedArrows,
      ];
      try {
        if (
          typeof window !== "undefined" &&
          window.localStorage?.getItem("debugMyCanvasOffCanvas") === "1"
        ) {
          const offLineIds = new Set();
          allUnifiedOffCanvasLines.forEach((l) =>
            collectCanvasElementIdentityKeys(l).forEach((k) =>
              offLineIds.add(k),
            ),
          );
          const mainOverlap = (allUnifiedLines || []).filter((l) => {
            for (const k of collectCanvasElementIdentityKeys(l)) {
              if (offLineIds.has(k)) return true;
            }
            return false;
          });
          const offLineKeyCounts = new Map();
          allUnifiedOffCanvasLines.forEach((l) => {
            collectCanvasElementIdentityKeys(l).forEach((k) => {
              offLineKeyCounts.set(k, (offLineKeyCounts.get(k) || 0) + 1);
            });
          });
          const offLineDupedKeys = [...offLineKeyCounts.entries()].filter(
            ([, n]) => n > 1,
          );
        }
      } catch (e) {
      }
      setLines(allUnifiedLines);
      setOffCanvasRectangles(offCanvasElements.rectangles || []);
      setOffCanvasCircles(offCanvasElements.circles || []);
      setOffCanvasArrows([]);
      setOffCanvasDoubleSidedArrows([]);
      setOffCanvasScribbles(offCanvasElements.scribbles || []);
      setOffCanvasLines(allUnifiedOffCanvasLines);
      setOffCanvasVideos(offCanvasElements.videos || []);

      // CRITICAL: Capture processed arrays RIGHT BEFORE setting state
      // This ensures we capture exactly what will be set, before any other effects modify them
      // We'll set this after images are loaded too

      // Variable to hold processed images (needed outside if block for initial state capture)
      let processedImagesArray = [];

      // Load images
      if (elements.images?.length) {
        setLoadingMessage(`Loading ${elements.images.length} image(s)...`);
        const loadImagePromises = elements.images.map((imgData) => {
          const info = imgData.shape_info || imgData.image_properties || {};
          return createImageLoadPromise({ imgData, info });
        });

        const loadedImages = await Promise.all(loadImagePromises);

        // Filter out images that failed to load and log them
        const successfulImages = loadedImages.filter(
          (img) => img.image !== null,
        );
        const failedImages = loadedImages.filter((img) => img.image === null);

        if (failedImages.length > 0) {
          failedImages.forEach((img) => { });
        }

        const recoveryImages = await Promise.all(
          successfulImages.map((img) => {
            if (!img.image && img.src?.startsWith("data:image/")) {
              return new Promise((resolve) => {
                const fallback = new Image();
                fallback.crossOrigin = "anonymous";
                fallback.onload = () => {
                  resolve({ ...img, image: fallback, originalImage: fallback });
                };
                fallback.onerror = () => {
                  resolve(img);
                };
                fallback.src = img.src;
              });
            }
            return Promise.resolve(img);
          }),
        );

        // Deduplicate images by ID before setting state
        const uniqueImages = [];
        const seenIds = new Set();
        for (const img of recoveryImages) {
          if (!seenIds.has(img.id)) {
            seenIds.add(img.id);
            uniqueImages.push(img);
          }
        }

        processedImagesArray = uniqueImages;
        setImages(processedImagesArray);

        // DOWNSCALING DISABLED: Keep full-resolution images so advanced crop modal is not blurry
        // const { downscaleAllImages } = await import('./utils/downscaleExistingImages');
        // setTimeout(async () => {
        //   try {
        //     await downscaleAllImages(processedImagesArray, setImages);
        //   } catch (error) {
        //     console.error('Failed to downscale existing images:', error);
        //   }
        // }, 100);
      }

      if (offCanvasElements.images?.length) {
        const offCanvasImagePromises = offCanvasElements.images.map((imgData) => {
          const info = imgData.shape_info || imgData.image_properties || {};
          return createImageLoadPromise({ imgData, info });
        });
        const loadedOffCanvasImages = await Promise.all(offCanvasImagePromises);
        const successfulOffCanvasImages = loadedOffCanvasImages.filter(
          (img) => img.image !== null,
        );
        setOffCanvasImages(successfulOffCanvasImages);
      } else {
        setOffCanvasImages([]);
      }

      // CRITICAL: Mark loadCanvasElements as completed and capture initial state IMMEDIATELY
      // We capture from the processed data right after setting state, then wait a bit to let system effects finish
      // But we have the initial state ready immediately in case user leaves quickly
      if (pageId) {
        loadCanvasElementsCompletedRef.current = true;
        hasUserMadeChangesRef.current = false; // Reset flag for new page
        shouldStartMonitoringRef.current = false; // Don't start monitoring yet

        // CRITICAL: Reset history and redo stack when opening existing canvas for editing.
        // Set history to [initialState] immediately (from loaded data) so the first user action
        // (e.g. replacePart) pushes [initial, newState] and undo is enabled. If we only setHistory([]),
        // replacePart can run before the init effect and push to empty history, leaving [singleState] and undo disabled.
        if (location.state?.fromCanvasPage) {
          setRedoStack([]);
          historyInitialized.current = false;

          try {
            const initialCanvasHeightValue =
              elements?.canvasHeight != null &&
                !Number.isNaN(parseInt(elements.canvasHeight))
                ? String(parseInt(elements.canvasHeight))
                : location.state?.canvasHeight != null &&
                  !Number.isNaN(parseInt(location.state.canvasHeight))
                  ? String(parseInt(location.state.canvasHeight))
                  : canvasHeight;

            initialArraysStateRef.current = {
              texts: JSON.parse(JSON.stringify(processedTexts || [])),
              rectangles: JSON.parse(JSON.stringify(processedRectangles || [])),
              circles: JSON.parse(JSON.stringify(processedCirclesFirst || [])),
              arrows: [],
              lines: JSON.parse(JSON.stringify(allUnifiedLines || [])),
              doubleSidedArrows: [],
              scribbles: JSON.parse(JSON.stringify(processedScribbles || [])),
              images: JSON.parse(JSON.stringify(processedImagesArray || [])),
              canvasHeight: initialCanvasHeightValue,
            };

            const ref = initialArraysStateRef.current;
            const initialState = {
              ...ref,
              offCanvasTexts: [],
              offCanvasRectangles: [],
              offCanvasCircles: [],
              offCanvasArrows: [],
              offCanvasDoubleSidedArrows: [],
              offCanvasImages: [],
              offCanvasScribbles: [],
              offCanvasLines: [],
              offCanvasVideos: [],
              selectedText: null,
              selectedTextId: null,
              operationType: "initial",
              timestamp: Date.now(),
            };
            setHistory([initialState]);
            historyInitialized.current = true;
          } catch (e) {
            console.error("🔄 [HISTORY-RESET] Error building initial state", e);
            setHistory([]);
          }
        }

        try {
          // Determine initial canvasHeight - prefer from elements, then from state
          const initialCanvasHeightValue =
            elements?.canvasHeight != null &&
              !Number.isNaN(parseInt(elements.canvasHeight))
              ? String(parseInt(elements.canvasHeight))
              : location.state?.canvasHeight != null &&
                !Number.isNaN(parseInt(location.state.canvasHeight))
                ? String(parseInt(location.state.canvasHeight))
                : canvasHeight;

          // Capture initial state in ref for other consumers (keep in sync with reset block when fromCanvasPage)
          initialArraysStateRef.current = {
            texts: JSON.parse(JSON.stringify(processedTexts || [])),
            rectangles: JSON.parse(JSON.stringify(processedRectangles || [])),
            circles: JSON.parse(JSON.stringify(processedCirclesFirst || [])),
            arrows: [],
            lines: JSON.parse(JSON.stringify(allUnifiedLines || [])),
            doubleSidedArrows: [],
            scribbles: JSON.parse(JSON.stringify(processedScribbles || [])),
            images: JSON.parse(JSON.stringify(processedImagesArray || [])),
            canvasHeight: initialCanvasHeightValue,
          };


          shouldStartMonitoringRef.current = true;
        } catch (error) {
          console.error(
            "❌ [LOAD-CANVAS] Error capturing initial arrays state:",
            error,
          );
        }
      }

      // Load videos (similar to images)
      if (elements.videos?.length) {
        const loadVideoPromises = elements.videos.map((videoData) => {
          return new Promise((resolve) => {
            const info =
              videoData.shape_info || videoData.video_properties || {};
            const videoSrc =
              videoData.src ||
              videoData.videoDataUrl ||
              info.videoDataUrl ||
              videoData.video_path ||
              "";

            if (!videoSrc) {
              resolve({
                ...info,
                ...videoData,
                id: videoData.shape_id || videoData.id || videoData.video_id,
                x: videoData.x ?? info.x ?? 0,
                y: videoData.y ?? info.y ?? 0,
                width: videoData.width ?? info.width ?? 320,
                height: videoData.height ?? info.height ?? 180,
                src: "",
                duration: videoData.duration || 0,
                videoNumber: videoData.videoNumber || 1,
                name: videoData.name || "Video",
              });
              return;
            }

            const video = document.createElement("video");
            video.crossOrigin = "anonymous";
            video.preload = "metadata";

            video.onloadedmetadata = async () => {
              // Generate thumbnail for loaded video
              const thumbnail = await generateVideoThumbnail(
                videoSrc,
                videoData.width ?? info.width ?? 320,
                videoData.height ?? info.height ?? 180,
              );

              resolve({
                ...info,
                ...videoData,
                id: videoData.shape_id || videoData.id || videoData.video_id,
                x: videoData.x ?? info.x ?? 0,
                y: videoData.y ?? info.y ?? 0,
                width: videoData.width ?? info.width ?? 320,
                height: videoData.height ?? info.height ?? 180,
                src: videoSrc,
                duration: isFinite(video.duration)
                  ? video.duration
                  : videoData.duration || 0,
                videoNumber: videoData.videoNumber || 1,
                name: videoData.name || "Video",
                thumbnail: thumbnail,
              });
            };

            video.onerror = (error) => {
              resolve({
                ...info,
                ...videoData,
                id: videoData.shape_id || videoData.id || videoData.video_id,
                x: videoData.x ?? info.x ?? 0,
                y: videoData.y ?? info.y ?? 0,
                width: videoData.width ?? info.width ?? 320,
                height: videoData.height ?? info.height ?? 180,
                src: videoSrc,
                duration: videoData.duration || 0,
                videoNumber: videoData.videoNumber || 1,
                name: videoData.name || "Video",
              });
            };

            // Add timeout for video loading
            const timeout = setTimeout(() => {
              video.onloadedmetadata = null;
              video.onerror = null;
              resolve({
                ...info,
                ...videoData,
                id: videoData.shape_id || videoData.id || videoData.video_id,
                x: videoData.x ?? info.x ?? 0,
                y: videoData.y ?? info.y ?? 0,
                width: videoData.width ?? info.width ?? 320,
                height: videoData.height ?? info.height ?? 180,
                src: videoSrc,
                duration: videoData.duration || 0,
                videoNumber: videoData.videoNumber || 1,
                name: videoData.name || "Video",
              });
            }, 10000); // 10 second timeout

            video.src = videoSrc;
          });
        });

        const loadedVideos = await Promise.all(loadVideoPromises);

        // Filter out videos that failed to load and log them
        const successfulVideos = loadedVideos.filter(
          (video) => video.src && video.src !== "",
        );
        const failedVideos = loadedVideos.filter(
          (video) => !video.src || video.src === "",
        );

        if (failedVideos.length > 0) {
          failedVideos.forEach((video) => { });
        }

        setVideos(successfulVideos);
      }

      // Load markers
      setLoadingMessage("Loading location markers...");
      const markersResult = await loadFromIndexedDB(
        "canvasMarkers",
        sanitizedPageId,
      );
      const markers = markersResult?.data || markersResult;
      // setLocationMarkers(markers); // Uncomment if needed
    } catch (error) {
      // Clear loading state on error
    } finally {
      setIsLoadingFromIndexedDB(false);
      initialDataLoadInProgress.current = false;
    }
  };

  // Effect to reset canvas height when pageId changes
  useEffect(() => {
    // Only reset if pageId actually changed (not just on mount or re-render)
    const pageIdChanged = previousPageIdRef.current !== pageId;

    if (pageIdChanged && pageId) {
      // Reset initial state and flags only when pageId actually changes

      initialArraysStateRef.current = null;
      loadCanvasElementsCompletedRef.current = false;
      hasUserMadeChangesRef.current = false;
      shouldStartMonitoringRef.current = false;
      previousPageIdRef.current = pageId;
    } else if (!pageId && previousPageIdRef.current) {
      // If pageId becomes null/undefined (after having a value), reset

      initialArraysStateRef.current = null;
      loadCanvasElementsCompletedRef.current = false;
      hasUserMadeChangesRef.current = false;
      shouldStartMonitoringRef.current = false;
      previousPageIdRef.current = pageId;
    } else if (pageId && !pageIdChanged) {
      // PageId exists and hasn't changed - don't reset (preserve initial state)
      // This is important - we don't want to reset after loadCanvasElements has captured state
    }

    const pageHeightFromState = location.state?.canvasHeight;
    if (
      pageHeightFromState != null &&
      !Number.isNaN(parseInt(pageHeightFromState))
    ) {
      setCanvasHeight(String(parseInt(pageHeightFromState)));
    } else {
      const pageHeightFromLayout =
        location.state?.layout_config?.layout?.page_height_vh ??
        location.state?.page_height_vh;
      if (
        pageHeightFromLayout != null &&
        !Number.isNaN(parseInt(pageHeightFromLayout))
      ) {
        setCanvasHeight(String(parseInt(pageHeightFromLayout)));
      } else {
        // Default to 100 if no height information is provided
        setCanvasHeight("100");
      }
    }
  }, [
    pageId,
    location.state?.canvasHeight,
    location.state?.layout_config,
    location.state?.page_height_vh,
  ]);

  // NOTE: Initial arrays state is now captured directly in loadCanvasElements
  // from the processed data BEFORE it's set to state. This ensures we capture
  // exactly what will be set, avoiding timing issues with React state updates.

  // NOTE: Initial state is now captured IMMEDIATELY in loadCanvasElements
  // We also update it after 2 seconds (after system effects) to get a more accurate baseline
  // But we always have an initial state available for comparison, even if user leaves quickly

  // Effect to load canvas elements when returning from CanvasPage or MyCanvas
  useEffect(() => {
    if (location.state?.fromCanvasPage && pageId) {
      const version =
        Number(localStorage.getItem("canvasDBVersion")) || DB_VERSION;
      loadCanvasElements(version);
    } else if (location.state?.fromCanvasPage) {
      // Waiting for pageId
    } else if (location.state?.fromMyCanvas && pageId) {
      // CRITICAL FIX: Load data when returning from MyCanvas with recent changes
      const version =
        Number(localStorage.getItem("canvasDBVersion")) || DB_VERSION;
      loadCanvasElements(version);
    } else if (location.state?.pageId && pageId) {
      const version =
        Number(localStorage.getItem("canvasDBVersion")) || DB_VERSION;
      loadCanvasElements(version);
    } else {
      // No conditions met for loading elements
    }
  }, [
    location.state?.fromCanvasPage,
    location.state?.fromMyCanvas,
    location.state?.pageId,
    pageId,
  ]);

  // CRITICAL: Track if user has made ANY changes to the canvas
  // This is set to true only when USER explicitly makes changes (not during load/undo/redo)
  const userMadeChangesRef = useRef(false);
  const isLoadingRef = useRef(true); // Track if we're still loading initial data
  const isUndoRedoRef = useRef(false); // Track if we're in undo/redo operation
  const isSelectionOperationRef = useRef(false); // Track if we're in a selection-only operation

  // Helper to mark that user made changes (only during actual user interactions)
  const markUserChanges = useCallback(() => {
    // Only mark changes if we're not loading, not in undo/redo, and not in a selection-only operation
    if (
      !isLoadingRef.current &&
      !isUndoRedoRef.current &&
      !isSelectionOperationRef.current
    ) {
      userMadeChangesRef.current = true;
    } else {
    }
  }, []);

  // Reset the flag when pageId changes (new page loaded)
  // BUT: Check if there's a persistent unsaved changes flag for this canvas
  // If user made changes before, restore the flag so it persists across visits
  useEffect(() => {
    // Get canvas ID from localStorage (set by CanvasForm when navigating to MyCanvas)
    const canvasId = localStorage.getItem("currentCanvasId");
    // Check for persistent unsaved changes flag (persists across MyCanvas visits)
    // Use canvas ID if available, otherwise fallback to pageId
    const persistentFlagKey = canvasId
      ? `canvasHasUnsavedChanges_${canvasId}`
      : `canvasHasUnsavedChanges_${pageId}`;
    const hasPersistentChanges =
      localStorage.getItem(persistentFlagKey) === "true";

    if (hasPersistentChanges) {
      // Restore the flag if persistent changes exist
      userMadeChangesRef.current = true;
    } else {
      // Only reset if no persistent changes
      userMadeChangesRef.current = false;
    }

    isLoadingRef.current = true; // Mark as loading
  }, [pageId]);

  // Mark loading as complete after initial elements are loaded
  useEffect(() => {
    if (loadCanvasElementsCompletedRef.current && isLoadingRef.current) {
      // Wait a bit for all post-processing to complete
      const timer = setTimeout(() => {
        isLoadingRef.current = false;
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [loadCanvasElementsCompletedRef.current]);

  // CRITICAL: Capture stable initial state AFTER React has finished rendering
  // This ensures we capture the actual state that's rendered, not intermediate states
  // REMOVED: This effect was causing the initial state to be captured AFTER user edits,
  // resulting in false negatives for change detection. The initial state is now captured
  // immediately in loadCanvasElements (line 1670) which is the correct baseline.
  // Keeping this effect would overwrite the correct baseline with a state that includes user changes.

  // CRITICAL: Helper to wrap setTexts calls that might be selection-only (clearing selectedContent)
  // This prevents markUserChanges from being called during selection operations
  // We detect selection operations by checking if the update only clears selectedContent
  const setTextsForSelection = useCallback(
    (updater) => {
      let shouldSetFlag = false;

      // Try to detect if this is a selection-only operation (only clearing selectedContent)
      if (typeof updater === "function") {
        try {
          // Test the updater with current texts (read from ref or state)
          // We'll use a simple heuristic: if updater maps over texts and only clears selectedContent
          // For now, we'll be conservative and always set the flag, then clear it quickly
          // This ensures we catch selection operations without blocking real changes
          shouldSetFlag = true; // Conservative approach: assume it might be selection
        } catch (e) {
          shouldSetFlag = false;
        }
      }

      // Always set flag temporarily to catch immediate markUserChanges calls
      // We'll clear it quickly so real changes can still be marked
      isSelectionOperationRef.current = true;

      // Call the original setTexts
      const result = setTexts(updater);

      // Clear the flag quickly (50ms) - this allows real changes to be marked
      // but prevents selection operations from marking changes
      setTimeout(() => {
        isSelectionOperationRef.current = false;
      }, 50);

      return result;
    },
    [setTexts],
  );

  // Add state variables for infinite scrolling in Parts table
  const [partsOffset, setPartsOffset] = useState(0);
  const [partsLimit] = useState(100);
  const [localParts, setLocalParts] = useState([]);
  const [isLoadingMoreParts, setIsLoadingMoreParts] = useState(false);
  const [allPartsLoaded, setAllPartsLoaded] = useState(false);
  const [partsScrollPosition, setPartsScrollPosition] = useState(0);
  const [hoverPoint, setHoverPoint] = useState(null); // where mouse is hovering during line drawing

  const [action, setAction] = useState(ACTIONS.SELECT);
  const actionRef = useRef(action);
  actionRef.current = action;
  const [lockedAction, setLockedAction] = useState(null); // Track which action is locked
  /** Bumped so Leave-blocking chrome recomputes from live `document.activeElement` (avoids stale state). */
  const [leaveChromeSync, setLeaveChromeSync] = useState(0);
  const [isCloneMode, setIsCloneMode] = useState(false); // Track if clone mode is active
  const [cloneTemplate, setCloneTemplate] = useState(null); // Template object for cloning (single element - backward compatibility)
  const [cloneTemplates, setCloneTemplates] = useState([]); // Array of template objects for cloning multiple elements
  const lastClonePlacementRef = useRef({
    ts: 0,
    x: null,
    y: null,
    templateCount: 0,
    templateType: "",
  });
  const [fillColor, setFillColor] = useState("#ff0000");

  const [isDrawingLine, setIsDrawingLine] = useState(false);
  const [lineStartPoint, setLineStartPoint] = useState(null);
  const [isDrawingImage, setIsDrawingImage] = useState(false);
  const [imageStartPoint, setImageStartPoint] = useState(null);
  const [pendingImageData, setPendingImageData] = useState(null); // Stores imageSrc, originalBlob, and image object
  const LARGE_IMAGE_WARNING_THRESHOLD_BYTES = 10 * 1024 * 1024;
  const shouldShowLargeImageWarning =
    !!pendingImageData &&
    !!pendingImageData.originalBlob &&
    pendingImageData.originalBlob.size >= LARGE_IMAGE_WARNING_THRESHOLD_BYTES &&
    action === ACTIONS.IMAGE;
  const [selectedTextId, setSelectedTextId] = useState(null);
  const [selectedText, setSelectedText] = useState(null);
  const [selectedObject, setSelectedObjectState] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);

  // Ref to track the element ID being cloned (for updating clone template when properties change)
  const cloneTemplateElementIdRef = useRef(null);

  // Handler to toggle clone mode (must be after selectedObject declaration)
  // Works like toolbar locking: single click toggles, keeps editor open
  // Accepts optional selectedObjectsArray parameter for multi-select cloning
  const handleToggleCloneMode = useCallback(
    (selectedObjectsArray = null) => {
      const hydrateCloneSourceObject = (obj) => {
        if (!obj) return obj;
        if (!isShapeType(obj.type, "Text") || !obj.id) {
          return obj;
        }
        const latestText =
          texts.find((t) => t?.id === obj.id) ||
          offCanvasTexts.find((t) => t?.id === obj.id);
        if (!latestText) {
          return obj;
        }
        const merged = {
          ...latestText,
          ...obj,
          type: obj.type || "Text",
        };
        // Keep full text payload fields from latest text row when selectedObject carries
        // partial/selection-only snapshots (common in duplicate flows).
        if (
          merged.partsByType == null &&
          latestText.partsByType != null
        ) {
          merged.partsByType = latestText.partsByType;
        }
        if (
          (!Array.isArray(merged.text_box_content_data) ||
            merged.text_box_content_data.length === 0) &&
          Array.isArray(latestText.text_box_content_data) &&
          latestText.text_box_content_data.length > 0
        ) {
          merged.text_box_content_data = latestText.text_box_content_data;
        }
        if (
          merged.selectedContent == null &&
          latestText.selectedContent != null
        ) {
          merged.selectedContent = latestText.selectedContent;
        }
        return merged;
      };

      if (isCloneMode) {
        // Disable clone mode - unlock
        // Ensure action is reset to SELECT and lockedAction is cleared
        // This ensures dragging works correctly after disabling clone mode
        setLockedAction(null);
        setAction(ACTIONS.SELECT);
        setIsCloneMode(false);
        setCloneTemplate(null);
        setCloneTemplates([]);
      } else {
        // Determine which objects to clone
        const objectsToClone =
          selectedObjectsArray && selectedObjectsArray.length > 0
            ? selectedObjectsArray.map(hydrateCloneSourceObject)
            : selectedObject
              ? [hydrateCloneSourceObject(selectedObject)]
              : [];

        if (objectsToClone.length === 0) return;

        // If multiple objects are selected, use multi-clone mode
        if (objectsToClone.length > 1) {
          setIsCloneMode(true);

          const snapshotLineLikeGeometry = (t) => ({
            ...t,
            ...(Array.isArray(t.points) ? { points: [...t.points] } : {}),
            ...(Array.isArray(t._presetBasePoints)
              ? { _presetBasePoints: [...t._presetBasePoints] }
              : {}),
          });

          // Create templates for all selected objects
          const templates = objectsToClone.map((obj) => {
            const { id, isNew, isSelected, isHighlighted, ...template } = obj;
            return {
              ...snapshotLineLikeGeometry(template),
              type: obj.type || template.type, // Ensure type is set
              originalX: obj.x,
              originalY: obj.y,
            };
          });

          // Calculate bounding box to maintain relative positions
          const minX = Math.min(...objectsToClone.map((obj) => obj.x || 0));
          const minY = Math.min(...objectsToClone.map((obj) => obj.y || 0));

          // Store templates with relative positions
          const templatesWithRelativePos = templates.map((template, index) => ({
            ...template,
            relativeX: (objectsToClone[index].x || 0) - minX,
            relativeY: (objectsToClone[index].y || 0) - minY,
          }));

          setCloneTemplates(templatesWithRelativePos);
          setCloneTemplate(null); // Clear single template
          setAction(ACTIONS.SELECT); // Use SELECT action for multi-clone
          setLockedAction(ACTIONS.SELECT); // Lock to enable crosshair cursor
        } else {
          // Single element clone (original behavior)
          let currentSelectedObject = hydrateCloneSourceObject(
            objectsToClone[0],
          );

          // CRITICAL: For unified arrow elements (stored in lines array), get the latest element from lines array
          // This ensures we have the most up-to-date properties (e.g., hasStartArrow, hasEndArrow)
          // even if selectedObject hasn't been updated yet
          if (
            (isShapeType(currentSelectedObject.type, "Line") ||
              isShapeType(currentSelectedObject.type, "Arrow") ||
              isShapeType(currentSelectedObject.type, "DoubleSidedArrow")) &&
            currentSelectedObject.id
          ) {
            // Check if it's a unified element in the main or off-canvas lines array
            const lineElement =
              lines.find((l) => l.id === currentSelectedObject.id) ||
              offCanvasLines.find((l) => l.id === currentSelectedObject.id);
            if (lineElement) {
              // Use the latest element from lines array, but preserve the type from selectedObject
              currentSelectedObject = {
                ...lineElement,
                type: currentSelectedObject.type, // Preserve the type (Arrow, DoubleSidedArrow, or Line)
              };
            } else {
              // Check if it's in the arrows array (legacy)
              const arrowElement = arrows.find(
                (a) => a.id === currentSelectedObject.id,
              );
              if (arrowElement) {
                // Use the latest element from arrows array
                currentSelectedObject = {
                  ...arrowElement,
                  type: currentSelectedObject.type, // Preserve the type
                };
              } else {
              }
            }
          }
          // CRITICAL: For images, get the latest element from images array
          // This ensures we have the most up-to-date properties even if selectedObject hasn't been updated yet
          else if (
            isShapeType(currentSelectedObject.type, "Image") &&
            currentSelectedObject.id
          ) {
            // Check if it's an image in the images array
            const imageElement = images.find(
              (img) => img.id === currentSelectedObject.id,
            );
            if (imageElement) {
              // Use the latest element from images array, but preserve the type from selectedObject
              currentSelectedObject = {
                ...imageElement,
                type: currentSelectedObject.type, // Preserve the type (Image)
              };
            }
          }

          // Determine the action type based on selected object type
          // CRITICAL: Check Arrow and DoubleSidedArrow BEFORE Line, because unified arrow elements
          // have type "Arrow" or "DoubleSidedArrow", not "Line"
          let cloneAction = null;
          if (isShapeType(currentSelectedObject.type, "Rect")) {
            cloneAction = ACTIONS.RECTANGLE;
          } else if (isShapeType(currentSelectedObject.type, "Circle")) {
            cloneAction = ACTIONS.CIRCLE;
          } else if (isShapeType(currentSelectedObject.type, "Arrow")) {
            cloneAction = ACTIONS.ARROW;
          } else if (
            isShapeType(currentSelectedObject.type, "DoubleSidedArrow")
          ) {
            cloneAction = ACTIONS.DOUBLE_SIDED_ARROW;
          } else if (isShapeType(currentSelectedObject.type, "Line")) {
            // Check Line LAST - only for actual lines without arrow heads
            cloneAction = ACTIONS.LINE;
          } else if (isShapeType(currentSelectedObject.type, "Image")) {
            cloneAction = ACTIONS.IMAGE;
          }

          if (cloneAction) {
            setIsCloneMode(true);
            // Create a clean template without id, isNew, etc.
            const { id, isNew, isSelected, isHighlighted, ...template } =
              currentSelectedObject;
            const templateSnapshot = {
              ...template,
              ...(Array.isArray(template.points)
                ? { points: [...template.points] }
                : {}),
              ...(Array.isArray(template._presetBasePoints)
                ? { _presetBasePoints: [...template._presetBasePoints] }
                : {}),
            };
            // CRITICAL: Store original x, y for point conversion when cloning unified elements
            // This allows us to convert absolute points to relative coordinates
            templateSnapshot.originalX = currentSelectedObject.x;
            templateSnapshot.originalY = currentSelectedObject.y;
            setCloneTemplate(templateSnapshot);
            setCloneTemplates([]); // Clear multi templates
            setAction(cloneAction);
            // Keep single-element clone mode locked to the shape tool so both
            // click-to-duplicate and drag-to-duplicate are supported.
            setLockedAction(cloneAction);
            // Store the element ID for tracking when updating clone template
            cloneTemplateElementIdRef.current = id;
          }
        }
        // Don't clear selectedObject - keep editor open!
      }
    },
    [isCloneMode, selectedObject, lines, offCanvasLines, images, texts, offCanvasTexts],
  );

  /** ShapeEditor: same "Stop duplicating" control also clears toolbar tool lock (lockedAction). */
  const handleShapeEditorStopDuplicatingOrUnlock = useCallback(() => {
    if (isCloneMode) {
      handleToggleCloneMode();
      return;
    }
    if (lockedAction) {
      setLockedAction(null);
      setAction(ACTIONS.SELECT);
    }
  }, [isCloneMode, lockedAction, handleToggleCloneMode]);

  // Disable clone mode when selectedObject changes or becomes null
  // BUT: Don't disable if lockedAction is set (clone mode is actively creating clones)
  // This allows continuous cloning without the mode being disabled after each clone
  useEffect(() => {
    if (isCloneMode && selectedObject === null && !lockedAction) {
      // Only disable clone mode if there's no locked action
      // This means the user explicitly deselected, not that we're in the middle of cloning

      setIsCloneMode(false);
      setCloneTemplate(null);
      setCloneTemplates([]);
      if (action !== ACTIONS.SELECT) {
        setAction(ACTIONS.SELECT);
        setLockedAction(null);
      }
    }
  }, [selectedObject, isCloneMode, action, lockedAction]);

  // Update clone template when selectedObject changes while clone mode is active
  // This ensures that if user modifies element properties (e.g., arrow properties) after enabling clone mode,
  // the clone template is updated with the latest properties
  useEffect(() => {
    if (isCloneMode && cloneTemplate) {
      // Store the element ID when clone template is first created
      if (!cloneTemplateElementIdRef.current && selectedObject) {
        cloneTemplateElementIdRef.current = selectedObject.id;
      }
    } else {
      // Clear the ref when clone mode is disabled
      cloneTemplateElementIdRef.current = null;
    }
  }, [isCloneMode, cloneTemplate, selectedObject]);

  // DISABLED: This useEffect was causing infinite loops
  // The clone template is already set correctly when clone mode is enabled in handleToggleCloneMode
  // If properties change after enabling clone mode, the user can disable and re-enable clone mode
  // or we can handle it differently if needed
  // useEffect(() => {
  //   if (
  //     isCloneMode &&
  //     selectedObject &&
  //     cloneTemplateElementIdRef.current === selectedObject.id
  //   ) {
  //     // Update clone template logic removed to prevent infinite loops
  //   }
  // }, [selectedObject, isCloneMode, lines]);

  // Clear pending image data when switching away from IMAGE action
  useEffect(() => {
    if (action !== ACTIONS.IMAGE && pendingImageData) {
      setPendingImageData(null);
      setIsDrawingImage(false);
      setImageStartPoint(null);
    }
  }, [action, pendingImageData]);

  // Auto-fix duplicate names across all element arrays
  // This handles race conditions where multiple clones are created before state updates complete
  useEffect(() => {
    // Helper to generate next unique name for a base name
    const generateNextUniqueName = (baseName, existingNames) => {
      const nameMatch = baseName.match(/^(.+?)\s+(\d+)$/);
      const base = nameMatch ? nameMatch[1] : baseName;

      // Find the highest number for this base name
      let maxNumber = 0;
      existingNames.forEach((existingName) => {
        const existingMatch = existingName.match(
          new RegExp(
            `^${base.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\s+(\\d+)$`,
          ),
        );
        if (existingMatch) {
          const num = parseInt(existingMatch[1]);
          if (num > maxNumber) {
            maxNumber = num;
          }
        }
      });

      return `${base} ${maxNumber + 1}`;
    };

    // Helper to fix duplicates in an array
    const fixDuplicatesInArray = (array, setter, arrayName) => {
      if (!array || array.length === 0) return;

      const nameCounts = new Map();
      const duplicates = [];

      // Find all duplicates
      array.forEach((item, index) => {
        if (!item.name) return;
        if (!nameCounts.has(item.name)) {
          nameCounts.set(item.name, []);
        }
        nameCounts.get(item.name).push({ index, item });
      });

      // Identify which items need renaming (keep first occurrence, rename others)
      nameCounts.forEach((occurrences, name) => {
        if (occurrences.length > 1) {
          // Keep first, rename the rest
          for (let i = 1; i < occurrences.length; i++) {
            duplicates.push(occurrences[i]);
          }
        }
      });

      if (duplicates.length > 0) {
        setter((prev) => {
          const updated = [...prev];
          const existingNames = new Set(prev.map((item) => item.name));

          duplicates.forEach(({ index, item }) => {
            const newName = generateNextUniqueName(item.name, existingNames);
            updated[index] = { ...item, name: newName };
            existingNames.add(newName); // Add to set so next duplicate gets next number
          });

          return updated;
        });
      }
    };

    // For arrows, check for duplicates WITHIN each array separately
    // Arrows in different arrays can have the same name - they're separate storage systems
    // We only fix duplicates within the same array
    const fixArrowDuplicates = () => {
      // Fix duplicates in lines array (unified arrows only)
      setLines((prev) => {
        const linesArrows = prev.filter(
          (l) =>
            l.hasStartArrow === true ||
            l.hasEndArrow === true ||
            l.pointerAtBeginning === true ||
            l.pointerAtEnd === true ||
            l.pointerAtEnding === true,
        );

        if (linesArrows.length === 0) return prev;

        const nameCounts = new Map();
        const duplicates = [];

        linesArrows.forEach((line) => {
          const actualIndex = prev.findIndex((l) => l.id === line.id);
          if (actualIndex === -1 || !line.name) return;

          if (!nameCounts.has(line.name)) {
            nameCounts.set(line.name, []);
          }
          nameCounts.get(line.name).push({ index: actualIndex, item: line });
        });

        nameCounts.forEach((occurrences, name) => {
          if (occurrences.length > 1) {
            // Keep first, rename the rest
            for (let i = 1; i < occurrences.length; i++) {
              duplicates.push(occurrences[i]);
            }
          }
        });

        if (duplicates.length === 0) return prev;

        const updated = [...prev];
        const existingNames = new Set(linesArrows.map((l) => l.name));

        duplicates.forEach(({ index, item }) => {
          const newName = generateNextUniqueName(item.name, existingNames);
          updated[index] = { ...item, name: newName };
          existingNames.add(newName);
        });

        return updated;
      });

      // Fix duplicates in arrows array (legacy) - check only within this array
      fixDuplicatesInArray(arrows, setArrows, "arrows");

      // Fix duplicates in doubleSidedArrows array (legacy) - check only within this array
      fixDuplicatesInArray(
        doubleSidedArrows,
        setDoubleSidedArrows,
        "doubleSidedArrows",
      );
    };

    // Fix duplicates in each array
    fixDuplicatesInArray(rectangles, setRectangles, "rectangles");
    fixDuplicatesInArray(circles, setCircles, "circles");
    fixDuplicatesInArray(images, setImages, "images");
    fixDuplicatesInArray(texts, setTexts, "texts");

    // Special handling for arrows (check across lines, arrows, and doubleSidedArrows)
    fixArrowDuplicates();

    // For lines that are NOT arrows, check separately
    setLines((prev) => {
      const nonArrowLines = prev.filter(
        (l) =>
          !(l.hasStartArrow === true || l.hasEndArrow === true) &&
          !(
            l.pointerAtBeginning === true ||
            l.pointerAtEnd === true ||
            l.pointerAtEnding === true
          ),
      );

      if (nonArrowLines.length === 0) return prev;

      const nameCounts = new Map();
      const duplicates = [];

      // Find duplicates in non-arrow lines
      nonArrowLines.forEach((line, originalIndex) => {
        const actualIndex = prev.findIndex((l) => l.id === line.id);
        if (actualIndex === -1 || !line.name) return;

        if (!nameCounts.has(line.name)) {
          nameCounts.set(line.name, []);
        }
        nameCounts.get(line.name).push({ index: actualIndex, item: line });
      });

      // Identify which items need renaming
      nameCounts.forEach((occurrences, name) => {
        if (occurrences.length > 1) {
          for (let i = 1; i < occurrences.length; i++) {
            duplicates.push(occurrences[i]);
          }
        }
      });

      if (duplicates.length === 0) return prev;

      const updated = [...prev];
      const existingNames = new Set(nonArrowLines.map((item) => item.name));

      duplicates.forEach(({ index, item }) => {
        const newName = generateNextUniqueName(item.name, existingNames);
        updated[index] = { ...item, name: newName };
        existingNames.add(newName);
      });

      return updated;
    });
  }, [
    rectangles,
    circles,
    images,
    texts,
    lines,
    arrows,
    doubleSidedArrows,
    setRectangles,
    setCircles,
    setImages,
    setTexts,
    setLines,
    setArrows,
    setDoubleSidedArrows,
  ]);

  const [showWebcamPopup, setShowWebcamPopup] = useState(null);
  const quillRef = useRef(null);
  const [capturedImage, setCapturedImage] = useState(null);
  const [selectedArrow, setSelectedArrow] = useState(null);
  const [selectedDoubleSidedArrow, setSelectedDoubleSidedArrow] =
    useState(null);
  const [selectedObjects, setSelectedObjects] = useState([]);
  
  // DEBUG: Wrap setSelectedObjects to track calls
  const debugSetSelectedObjects = (newSelectedObjects) => {
    console.log("🔵 [MYCANVAS] setSelectedObjects called", {
      newSelectedObjectsLength: Array.isArray(newSelectedObjects) ? newSelectedObjects.length : 'not-array',
      newSelectedObjects,
      stack: new Error().stack,
    });
    setSelectedObjects(newSelectedObjects);
  };
  const selectedObjectsRef = useRef([]);
  useEffect(() => {
    console.log("🔵 [MYCANVAS] selectedObjectsRef updated", {
      selectedObjectsLength: selectedObjects?.length || 0,
      selectedObjects: selectedObjects,
      selectedObjectsRefCurrent: selectedObjectsRef.current,
    });
    selectedObjectsRef.current = selectedObjects;
  }, [selectedObjects]);
  const prevSelectedObjectsLenRef = useRef(0);
  // During group drag from off-canvas to main (or vice versa), show x,y in SelectionEditor relative to target stage
  const [groupDragDisplay, setGroupDragDisplay] = useState(null);
  // Multi-select mode: when enabled, users can individually select/deselect items in SelectionEditor
  const [isMultiSelectMode, setIsMultiSelectMode] = useState(false);
  // Ref to track current isMultiSelectMode value to avoid stale closures
  const isMultiSelectModeRef = useRef(isMultiSelectMode);

  // Keep ref in sync with state
  useEffect(() => {
    isMultiSelectModeRef.current = isMultiSelectMode;
  }, [isMultiSelectMode]);

  // Individually selected items (Set of IDs) - used when multi-select mode is enabled
  const [individuallySelectedIds, setIndividuallySelectedIds] = useState(
    new Set(),
  );
  // Ref to track if we just finished a selection box drag (to prevent onClick from clearing selection)
  // Using ref instead of state so it's immediately available (state updates are async)
  const justFinishedSelectionRef = useRef(false);
  // Ref to suppress post-draw synthetic background click from clearing selection.
  const justFinishedShapeDrawRef = useRef(false);

  const [selectedLine, setSelectedLine] = useState(null);

  // Grouped undo/redo state tracking
  const [isElementBeingEdited, setIsElementBeingEdited] = useState(false);
  const [initialEditState, setInitialEditState] = useState(null);
  const [pendingChanges, setPendingChanges] = useState(false);

  // Refs to track current values for endElementEditing to avoid stale closure issues
  const isElementBeingEditedRef = useRef(false);
  const pendingChangesRef = useRef(false);
  // Ref to track text editing mode - persists across renders
  const isTextEditingModeRef = useRef(false);
  // Ref to prevent duplicate element_edit_complete calls
  const isSavingRef = useRef(false);
  // Ref to store latest updated images arrays from transform operations
  // This ensures element_edit_complete can access the updated state with rotation
  // Stores both main canvas and off-canvas images arrays
  const latestUpdatedImagesRef = useRef({ main: null, offCanvas: null });

  // Ref updated by ImageEditor with current image id + referenceUrl so when user clicks
  // off-canvas before blur/debounce flushes, endElementEditing can preserve referenceUrl
  const latestImageEditorRefUrlRef = useRef({
    imageId: null,
    referenceUrl: "",
  });

  // Ref to store latest updated texts arrays from transform operations
  // This ensures element_edit_complete can access the updated state with rotation
  // Stores both main canvas and off-canvas texts arrays
  const latestUpdatedTextsRef = useRef({ main: null, offCanvas: null });

  // Ref updated by historyUtils on each push; endElementEditing skips save-from-ref when last op is add_image so 1 undo removes main image
  const lastHistoryOperationTypeRef = useRef(null);

  // Ref to track if we're currently transforming (used by Canvas.jsx and imageRenderer.js)
  // Also stores initial transform state for comparing changes across multiple transforms
  // Structure: { isTransforming: boolean, initialTransformState: { id, rotation, scaleX, scaleY, width, height } | null }
  const isTransformingRef = useRef({
    isTransforming: false,
    initialTransformState: null,
  });

  // Ref to read latest selectedObject in timeouts (avoids stale closure; updated every render)
  const selectedObjectRef = useRef(null);
  selectedObjectRef.current = selectedObject;
  const selectedImageRef = useRef(null);
  selectedImageRef.current = selectedImage;
  const lastDragAutoExpandAtRef = useRef(0);

  // Ensure canvas grows live while dragging, for all element types (including top-layer images),
  // using the real Konva node bounds instead of waiting for state array updates.
  // Supports both single drag (selectedObject) and group drag (selectedObjects).
  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const isMainCanvasElement = (obj) => {
      if (!obj?.id || !obj?.type) return false;
      const id = obj.id;
      const type = obj.type;
      if (type === "Rect" || type === "Rectangle") {
        return rectangles.some((r) => r.id === id);
      }
      if (type === "Circle") {
        return circles.some((c) => c.id === id);
      }
      if (type === "Image") {
        return images.some((img) => img.id === id);
      }
      if (type === "Text") {
        return texts.some((t) => t.id === id);
      }
      if (type === "Line") {
        return lines.some((l) => l.id === id);
      }
      if (type === "Arrow") {
        return arrows.some((a) => a.id === id) || lines.some((l) => l.id === id);
      }
      if (type === "DoubleSidedArrow") {
        return (
          doubleSidedArrows.some((a) => a.id === id) ||
          lines.some((l) => l.id === id)
        );
      }
      return false;
    };

    const candidateObjects = [];
    if (
      Array.isArray(selectedObjects) &&
      selectedObjects.length > 0
    ) {
      selectedObjects.forEach((obj) => {
        if (isMainCanvasElement(obj)) candidateObjects.push(obj);
      });
    }
    if (
      selectedObject &&
      isMainCanvasElement(selectedObject) &&
      !candidateObjects.some(
        (obj) => obj.id === selectedObject.id && obj.type === selectedObject.type,
      )
    ) {
      candidateObjects.push(selectedObject);
    }

    if (candidateObjects.length === 0) {
      return;
    }

    const now = Date.now();
    if (now - lastDragAutoExpandAtRef.current < 33) {
      return;
    }
    lastDragAutoExpandAtRef.current = now;

    const mainStage = stageRef?.current;
    const topStage = topLayerStageRef?.current;
    if (!mainStage && !topStage) {
      return;
    }

    let bottomPx = null;
    try {
      let hasDraggingNode = false;
      for (const obj of candidateObjects) {
        const selector = `#${obj.id}`;
        const node =
          mainStage?.findOne?.(selector) || topStage?.findOne?.(selector) || null;
        const nodeStage = node?.getStage?.() || mainStage || topStage;
        const nodeIsDragging =
          (typeof node?.isDragging === "function" && node.isDragging()) ||
          node?.getAttr?.("_isDragging") === true ||
          obj?._isDragging === true ||
          selectedObject?._isDragging === true;
        if (!nodeIsDragging) {
          continue;
        }
        hasDraggingNode = true;
        if (node && nodeStage && typeof node.getClientRect === "function") {
          const rect = node.getClientRect({ relativeTo: nodeStage });
          if (
            rect &&
            Number.isFinite(rect.y) &&
            Number.isFinite(rect.height)
          ) {
            const nodeBottom = rect.y + rect.height;
            bottomPx =
              bottomPx == null ? nodeBottom : Math.max(bottomPx, nodeBottom);
          }
        }
      }
      if (!hasDraggingNode) {
        // Fallback: selection state can lag/miss in some drag paths (e.g. group drag wrappers).
        // Scan live stage nodes for active drags so canvas expansion still works.
        const collectDraggingNodeBottoms = (stage) => {
          if (!stage || typeof stage.find !== "function") return;
          const groups = stage.find("Group") || [];
          const shapes = stage.find("Shape") || [];
          const nodes = [...groups, ...shapes];
          for (const node of nodes) {
            const dragging =
              (typeof node?.isDragging === "function" && node.isDragging()) ||
              node?.getAttr?.("_isDragging") === true;
            if (!dragging || typeof node.getClientRect !== "function") {
              continue;
            }
            hasDraggingNode = true;
            const rect = node.getClientRect({ relativeTo: stage });
            if (
              rect &&
              Number.isFinite(rect.y) &&
              Number.isFinite(rect.height)
            ) {
              const nodeBottom = rect.y + rect.height;
              bottomPx =
                bottomPx == null ? nodeBottom : Math.max(bottomPx, nodeBottom);
            }
          }
        };

        collectDraggingNodeBottoms(mainStage);
        collectDraggingNodeBottoms(topStage);

        if (!hasDraggingNode) {
          return;
        }
      }
    } catch (e) {
      // Ignore lookup failures during rapid drag/remount.
    }

    if (!Number.isFinite(bottomPx)) {
      return;
    }

    const viewportHeight = window.innerHeight || 0;
    if (viewportHeight <= 0) {
      return;
    }

    const requiredPercent = Math.max(
      MIN_CANVAS_HEIGHT_PERCENT,
      Math.min(
        100,
        Math.ceil(((bottomPx + SHAPE_BOTTOM_PADDING_PX) / viewportHeight) * 100),
      ),
    );
    const currentPercent = parseInt(canvasHeight, 10);
    if (!Number.isNaN(currentPercent) && requiredPercent > currentPercent) {
      applyCanvasHeight(requiredPercent);
    }
  }, [
    applyCanvasHeight,
    arrows,
    canvasHeight,
    circles,
    doubleSidedArrows,
    images,
    lines,
    rectangles,
    selectedObject,
    selectedObjects,
    stageRef,
    topLayerStageRef,
    texts,
    SHAPE_BOTTOM_PADDING_PX,
  ]);

  // Keep refs in sync with state
  useEffect(() => {
    isElementBeingEditedRef.current = isElementBeingEdited;
  }, [isElementBeingEdited]);

  // CRITICAL: Start editing mode when pendingChanges becomes true
  // This ensures startElementEditing is only called when actual property changes are made
  useEffect(() => {
    if (pendingChanges && !isElementBeingEdited && selectedObject) {
      startElementEditing();
    }
  }, [pendingChanges, isElementBeingEdited, selectedObject]);

  // REMOVED: This useEffect was causing issues where manually setting pendingChangesRef.current
  // would be immediately overridden by the state value before the state update took effect
  // Now we update the ref manually in the same places where we call setPendingChanges
  // useEffect(() => {
  //   pendingChangesRef.current = pendingChanges;
  // }, [pendingChanges]);

  // Reset individually selected IDs when selection box changes
  useEffect(() => {
    if (selectedObjects.length > 0) {
      // When new selection box is created, initialize all items as selected
      setIndividuallySelectedIds(new Set(selectedObjects.map((obj) => obj.id)));
    } else {
      // Clear when selection is cleared
      setIndividuallySelectedIds(new Set());
    }
  }, [selectedObjects]);

  const [waypointCount, setWaypointCount] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(0.8);
  const zoomLevelRef = useRef(zoomLevel);
  zoomLevelRef.current = zoomLevel;
  const [zoomInputValue, setZoomInputValue] = useState(80);
  const [browserZoomLevel, setBrowserZoomLevel] = useState(1);
  const [effectiveZoomLevel, setEffectiveZoomLevel] = useState(1);
  const [textStyles, setTextStyles] = useState({});
  const [activeInput, setActiveInput] = useState(null);
  const [showTextBox, setShowTextBox] = useState(false);
  const [history, setHistory] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const [type, setType] = useState("");
  const [editing, setEditing] = useState(false);

  // Effect to sync showTextBox with selectedText and selectedObject
  // Ensures showTextBox is false when no text element is selected
  // CRITICAL: Also set showTextBox to true when selectedText is set (e.g., via drag selection box)
  // CRITICAL FIX: Handle timing issue where selectedObject is set before selectedText during drag selection
  useEffect(() => {
    // Always log to debug what's happening

    // Check if selectedObject is a Text type (even if selectedText isn't set yet)
    // This handles the timing issue where selectedObject is set before selectedText
    const hasTextObject =
      selectedObject !== null &&
      (selectedObject.type === "Text" || selectedObject.type?.includes("Text"));

    // Check if selectedText is set (for when both are available)
    const hasTextSelected =
      selectedText !== null &&
      selectedObject !== null &&
      (selectedObject.type === "Text" || selectedObject.type?.includes("Text"));

    // CRITICAL: If selectedObject is a Text type, we should show TextBoxWithButtons
    // This handles the case where selectedObject is set before selectedText (timing issue during drag selection)
    if (hasTextObject) {
      // Text object is selected - ensure showTextBox is true
      // Don't require selectedText to be set yet (it might be set in the next render due to React batching)
      if (!showTextBox) {
        setShowTextBox(true);
      } else {
      }
    } else if (!hasTextSelected && showTextBox) {
      // No text selected at all - ensure showTextBox is false
      // Only clear if we're sure there's no text (both selectedText and selectedObject are null or not Text)
      // CRITICAL: Don't clear if selectedObject is a Text type but selectedText isn't set yet (timing issue)
      const isTextObjectButNoSelectedText =
        selectedObject !== null &&
        (selectedObject.type === "Text" ||
          selectedObject.type?.includes("Text")) &&
        selectedText === null;

      if (!isTextObjectButNoSelectedText) {
        setShowTextBox(false);
      } else {
      }
    } else {
    }
  }, [selectedText, selectedObject, showTextBox, selectedObjects]);

  // Effect to clear selectedContent when selectedText becomes null
  // This fixes issues with off-canvas texts not clearing selectedContent properly
  useEffect(() => {
    if (selectedText === null) {
      // Clear selectedContent from both main and off-canvas texts
      setTextsForSelection((prevTexts) =>
        prevTexts.map((text) => ({
          ...text,
          selectedContent: undefined,
        }))
      );
      
      setOffCanvasTexts((prevTexts) =>
        prevTexts.map((text) => ({
          ...text,
          selectedContent: undefined,
        }))
      );
    }
  }, [selectedText, setTextsForSelection, setOffCanvasTexts]);

  // History size limit to prevent memory issues
  const MAX_HISTORY_SIZE = Infinity;

  // Refs needed for saveToHistory
  const lastSaveTimestamp = useRef(0);
  const saveDebounceTimeout = useRef(null);

  // Ref to track if a history save is in progress (prevents batching)
  const isHistorySaveInProgress = useRef(false);
  const historySaveQueue = useRef([]);

  // Ref to store object with commit function and hasPendingChange check
  const heightChangeCommitRef = useRef(null);

  // saveToHistory function (needed for many function creators)
  const saveToHistory = useCallback(
    (
      operationType = "general",
      currentImagesOverride = null,
      currentArrowsOverride = null,
      currentDoubleSidedArrowsOverride = null,
      currentLinesOverride = null,
      currentTextsOverride = null,
      currentSelectedTextOverride = null,
      currentSelectedTextIdOverride = null,
      currentRectanglesOverride = null,
      currentOffCanvasRectanglesOverride = null,
      currentCirclesOverride = null,
      currentOffCanvasCirclesOverride = null,
      currentOffCanvasArrowsOverride = null,
      currentOffCanvasDoubleSidedArrowsOverride = null,
      currentOffCanvasLinesOverride = null,
      currentOffCanvasTextsOverride = null,
      currentOffCanvasImagesOverride = null,
      deletedObjectId = null,
      deletedObjectType = null,
      optionsOrExtra = null,
    ) => {
      // Options object may be passed as 16th (currentOffCanvasImagesOverride), 17th (deletedObjectId), or last (optionsOrExtra)
      const optsObj =
        optionsOrExtra &&
          typeof optionsOrExtra === "object" &&
          !Array.isArray(optionsOrExtra) &&
          ("currentOffCanvasImagesOverride" in optionsOrExtra ||
            "currentCanvasHeightOverride" in optionsOrExtra)
          ? optionsOrExtra
          : deletedObjectId &&
            typeof deletedObjectId === "object" &&
            !Array.isArray(deletedObjectId) &&
            "currentOffCanvasImagesOverride" in deletedObjectId
            ? deletedObjectId
            : currentOffCanvasImagesOverride &&
              typeof currentOffCanvasImagesOverride === "object" &&
              !Array.isArray(currentOffCanvasImagesOverride) &&
              "currentOffCanvasImagesOverride" in
              currentOffCanvasImagesOverride
              ? currentOffCanvasImagesOverride
              : null;
      const finalOffCanvasImagesOverride = optsObj
        ? optsObj.currentOffCanvasImagesOverride
        : currentOffCanvasImagesOverride;
      const finalCanvasHeightOverride = optsObj?.currentCanvasHeightOverride;
      const finalDeletedObjectId = optsObj ? null : deletedObjectId;
      const finalDeletedObjectType = optsObj ? null : deletedObjectType;

      // CRITICAL: Reset keepHistoryEmpty flag when user makes their first change
      // This allows history to start being tracked again after intentional clearing
      if (keepHistoryEmpty.current) {
        keepHistoryEmpty.current = false;
        historyInitialized.current = false; // Allow history to be initialized with this change
      }

      // Mark user changes if this is not an undo/redo operation
      // CRITICAL: Only mark changes for operations that actually modify the canvas
      // Selection-only operations should NOT mark user changes
      // Exclude operations that are explicitly selection-related
      // Also check if we're currently in a selection operation (via ref)
      // Note: "general" operations are allowed as they may represent actual modifications
      const isSelectionOnlyOperation =
        operationType &&
        (operationType.includes("select") || operationType.includes("SELECT"));

      if (
        !isUndoingRedoing.current &&
        !isSelectionOnlyOperation &&
        !isSelectionOperationRef.current
      ) {
        markUserChanges();
      }

      saveToHistoryUtil({
        operationType,
        currentImagesOverride,
        currentArrowsOverride,
        currentDoubleSidedArrowsOverride,
        currentLinesOverride,
        currentTextsOverride,
        currentSelectedTextOverride,
        currentSelectedTextIdOverride,
        currentRectanglesOverride,
        currentOffCanvasRectanglesOverride,
        currentCirclesOverride,
        currentOffCanvasCirclesOverride,
        currentOffCanvasArrowsOverride,
        currentOffCanvasDoubleSidedArrowsOverride,
        currentOffCanvasLinesOverride,
        currentOffCanvasTextsOverride,
        currentOffCanvasImagesOverride: finalOffCanvasImagesOverride,
        currentCanvasHeightOverride: finalCanvasHeightOverride,
        deletedObjectId: finalDeletedObjectId,
        deletedObjectType: finalDeletedObjectType,
        images,
        texts,
        rectangles,
        circles,
        arrows,
        lines,
        doubleSidedArrows,
        videos,
        scribbles,
        offCanvasTexts,
        offCanvasRectangles,
        offCanvasCircles,
        offCanvasArrows,
        offCanvasDoubleSidedArrows,
        offCanvasImages,
        offCanvasScribbles,
        offCanvasLines,
        offCanvasVideos,
        selectedText,
        selectedTextId,
        canvasHeight: canvasHeightRef.current, // Use ref to always get the latest value
        history, // History is now accessed via functional updates in historyUtils.js
        setHistory,
        setRedoStack,
        setPendingChanges,
        pendingChangesRef,
        isUndoingRedoing,
        lastUndoRedoCompletionTime,
        isElementBeingEdited,
        lastSaveTimestamp,
        saveDebounceTimeout,
        MAX_HISTORY_SIZE,
        commitPendingHeightChange:
          heightChangeCommitRef.current?.commit || null,
        hasPendingHeightChange:
          heightChangeCommitRef.current?.hasPendingChange || (() => false),
        lastHistoryOperationTypeRef,
      });
    },
    [
      images,
      texts,
      rectangles,
      circles,
      arrows,
      lines,
      doubleSidedArrows,
      videos,
      scribbles,
      offCanvasTexts,
      offCanvasRectangles,
      offCanvasCircles,
      offCanvasArrows,
      offCanvasDoubleSidedArrows,
      offCanvasImages,
      offCanvasScribbles,
      offCanvasLines,
      offCanvasVideos,
      selectedText,
      selectedTextId,
      // Note: canvasHeight is not in dependencies because we use canvasHeightRef.current
      history,
      setHistory,
      setRedoStack,
      setPendingChanges,
      isUndoingRedoing,
      isElementBeingEdited,
      lastSaveTimestamp,
      saveDebounceTimeout,
      MAX_HISTORY_SIZE,
      markUserChanges,
    ],
  );
  saveToHistoryRef.current = saveToHistory;

  // Add periodic memory cleanup
  useEffect(() => {
    const cleanupInterval = setInterval(() => {
      // Check if we have too many history items and aggressively clean them
      setHistory((prev) => {
        if (prev.length > MAX_HISTORY_SIZE) {
          // Keep only the most recent items
          return prev.slice(prev.length - MAX_HISTORY_SIZE);
        }
        return prev;
      });

      // No periodic cleanup for redo stack - let it grow as needed
      // No emergency memory cleanup - let undo/redo grow without limits
    }, 30000); // Run every 30 seconds

    return () => clearInterval(cleanupInterval);
  }, [MAX_HISTORY_SIZE]);

  // Browser zoom detection
  const detectBrowserZoom = useDetectBrowserZoom(setBrowserZoomLevel);

  // Calculate effective zoom level (only canvas zoom, not browser zoom)
  useEffect(() => {
    setEffectiveZoomLevel(zoomLevel);
  }, [zoomLevel]);

  // Browser zoom handles scrolling automatically, no need for complex calculations

  // Function to ensure smooth scrolling to bottom elements
  const ensureScrollToBottom = useEnsureScrollToBottom();

  // Removed auto-scroll to bottom on zoom to keep top content reachable

  // Initialize zoom functionality
  useEffect(() => {
    // Initialize zoom level
    if (!zoomLevel) {
      setZoomLevel(0.8);
      setZoomInputValue(80);
    }

    // Detect initial browser zoom
    detectBrowserZoom();
  }, [zoomLevel, detectBrowserZoom]);

  // Browser zoom detection event listeners
  useEffect(() => {
    const handleZoomChange = createZoomChangeHandler(detectBrowserZoom);
    const handleResize = createResizeHandler(detectBrowserZoom);

    // Listen for visual viewport changes (modern browsers)
    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", handleZoomChange);
      window.visualViewport.addEventListener("scroll", handleZoomChange);
    }

    // Listen for window resize (fallback)
    window.addEventListener("resize", handleResize);

    // Listen for orientation change (mobile)
    window.addEventListener("orientationchange", handleZoomChange);

    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener("resize", handleZoomChange);
        window.visualViewport.removeEventListener("scroll", handleZoomChange);
      }
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleZoomChange);
    };
  }, [detectBrowserZoom]);

  // Pinch-to-zoom detection for mobile devices
  useEffect(() => {
    let initialDistance = 0;
    let initialZoom = 1;

    const handleTouchStart = createTouchStartHandler(
      (distance) => {
        initialDistance = distance;
      },
      (zoom) => {
        initialZoom = zoom;
      },
      zoomLevel,
    );

    const handleTouchMove = createTouchMoveHandler(
      initialDistance,
      initialZoom,
      setZoomLevel,
      setZoomInputValue,
    );

    // Add touch event listeners
    document.addEventListener("touchstart", handleTouchStart, {
      passive: false,
    });
    document.addEventListener("touchmove", handleTouchMove, { passive: false });

    return () => {
      document.removeEventListener("touchstart", handleTouchStart);
      document.removeEventListener("touchmove", handleTouchMove);
    };
  }, [zoomLevel]);

  // Removed skipNextHistory reset effect (no longer used)

  const [canvasResizeTrigger, setCanvasResizeTrigger] = useState(0);
  const { parts } = useSelector((state) => state.parts);
  const [typesModalOpen, setTypesModalOpen] = useState(false);
  const [showShortcutsModal, setShowShortcutsModal] = useState(false);
  const [newPartRow, setNewPartRow] = useState(null);
  const partTableContnrRef = useRef(null);
  const typeTableContnrRef = useRef(null);
  const [editingPartId, setEditingPartId] = useState(null);
  const [editedPartData, setEditedPartData] = useState({});

  // Sort state (needed for handleSort creation)
  const [sortDirection, setSortDirection] = useState("asc");
  const [sortField, setSortField] = useState(null);

  // Snackbar state (needed for showSnackbar creation)
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    type: "",
  });

  // Parts modal state (needed for handleReplacePart creation)
  const [isPartsModalOpen, setPartsModalOpen] = useState(false);

  // Wrapper function to set step ID before opening parts modal
  const handleOpenPartsModal = () => {
    // Set step ID synchronously before opening modal if available
    if (location.state?.stepId) {
      dispatch(setStepTreeNodeId(location.state.stepId));
    }
    setPartsModalOpen(true);
  };

  // Text editing states (needed for addText creation)
  const [editTextBoxContent, setEditTextBoxContent] = useState(false);
  const [showEditTask, setShowEditTask] = useState(false);
  const [showEditNote, setShowEditNote] = useState(false);
  const [showQtyUnitOptions, setShowQtyUnitOptions] = useState(false);

  // Image loading states (needed for addImage creation)
  const [isImageLoading, setIsImageLoading] = useState(false);
  const [imageLoadingProgress, setImageLoadingProgress] = useState(0);

  // Crop modal state (needed for createOnClick and Canvas/ShapeEditor - must be declared before onClick useMemo)
  const [isCropModalOpen, setIsCropModalOpenState] = useState(false);
  // Ref set synchronously when Crop is clicked so same-tick/early events never clear selection (fixes first-time close)
  const isCropModalOpenRef = useRef(false);
  const setIsCropModalOpen = useCallback((open) => {
    const value = !!open;
    isCropModalOpenRef.current = value;
    setIsCropModalOpenState(value);
  }, []);
  const setCropModalOpenRefOnly = useCallback(() => {
    isCropModalOpenRef.current = true;
  }, []);

  // Guarded setters: when crop modal is open, never clear selection (any code path that tries to set null will no-op)
  const setSelectedObject = useCallback(
    (value) => {
      if (value === null && (isCropModalOpenRef?.current || isCropModalOpen)) {
       
        return;
      }
      
      setSelectedObjectState(value);
    },
    [isCropModalOpen],
  );
  const setSelectedImage = useCallback(
    (value) => {
      const prevValue = selectedImageRef?.current ?? null;
      const prevId = prevValue?.id ?? null;
      const nextId = value?.id ?? null;
      const prevTopLayer = prevValue?.isOnTopLayer === true;
      const nextTopLayer = value?.isOnTopLayer === true;
      const isSameSelection =
        (prevValue === null && value === null) ||
        (prevId !== null &&
          prevId === nextId &&
          prevTopLayer === nextTopLayer);

      if (isSameSelection) {
        
        return;
      }

      if (value === null && (isCropModalOpenRef?.current || isCropModalOpen)) {
        
        return;
      }
    
      selectedImageRef.current = value;
      setSelectedImageState(value);
    },
    [isCropModalOpen],
  );

  const clearCanvasSelectionState = useCallback(() => {
    if (isCropModalOpenRef?.current || isCropModalOpen) return;
    if ((selectedObjects?.length || 0) > 1) return;
    if (pendingSelection.current) {
      pendingSelection.current = null;
    }
    transformerRef?.current?.nodes([]);
    setSelectedText(null);
    setShowTextBox(false);
    setSelectedTextId(null);
    setSelectedArrow(null);
    setSelectedLine(null);
    setSelectedDoubleSidedArrow(null);
    setSelectedObject(null);
    setSelectedImage(null);
    setSelectedObjects([]);
    setIsMultiSelectMode(false);
    setIndividuallySelectedIds(new Set());
  }, [
    isCropModalOpen,
    setSelectedText,
    setShowTextBox,
    setSelectedTextId,
    setSelectedArrow,
    setSelectedLine,
    setSelectedDoubleSidedArrow,
    setSelectedObject,
    setSelectedImage,
    setSelectedObjects,
    setIsMultiSelectMode,
    setIndividuallySelectedIds,
    selectedObjects,
  ]);

  const cropModalRootRef = useRef(null);
  const setCropModalRootRef = useCallback((el) => {
    cropModalRootRef.current = el;
  }, []);

  // When crop modal is open, block ALL pointer/click events from reaching the app unless target is inside the modal (stops canvas/image handlers from clearing selection)
  useEffect(() => {
    if (!isCropModalOpen && !isCropModalOpenRef?.current) {
      cropModalRootRef.current = null;
      return;
    }
    const blockUnlessInsideModal = (e) => {
      const root = cropModalRootRef.current;
      if (root && root.contains(e.target)) return;
      e.stopImmediatePropagation();
      e.preventDefault();
    };
    const opts = { capture: true };
    document.addEventListener("click", blockUnlessInsideModal, opts);
    document.addEventListener("mousedown", blockUnlessInsideModal, opts);
    document.addEventListener("mouseup", blockUnlessInsideModal, opts);
    document.addEventListener("pointerdown", blockUnlessInsideModal, opts);
    document.addEventListener("pointerup", blockUnlessInsideModal, opts);
    return () => {
      document.removeEventListener("click", blockUnlessInsideModal, opts);
      document.removeEventListener("mousedown", blockUnlessInsideModal, opts);
      document.removeEventListener("mouseup", blockUnlessInsideModal, opts);
      document.removeEventListener("pointerdown", blockUnlessInsideModal, opts);
      document.removeEventListener("pointerup", blockUnlessInsideModal, opts);
      cropModalRootRef.current = null;
    };
  }, [isCropModalOpen]);

  // Background-like deselect for clicks/taps outside both stages.
  useEffect(() => {
    const handleOutsideStagePointerDown = (e) => {
      if (isCropModalOpenRef?.current || isCropModalOpen) return;
      if (e?.target == null || !(e.target instanceof Element)) return;
      if (e.pointerType === "mouse" && e.button !== 0) return;
      // Respect canvas chrome controls (shape editor, zoom controls, textbox buttons, toolbars, etc.).
      // Those should not clear selection even though they are outside the stage containers.
      if (isInsideCanvasChrome(e.target)) return;

      const mainStageContainer = stageRef?.current?.container?.() || null;
      const offStage =
        offCanvasStageRef?.current ||
        (typeof window !== "undefined" && window.__offCanvasStageRef?.current);
      const offStageContainer = offStage?.container?.() || null;
      const topLayerStageContainer =
        topLayerStageRef?.current?.container?.() || null;
      const offCanvasTopLayerWrapper =
        typeof document !== "undefined"
          ? document.querySelector('[data-off-canvas-top-layer-wrapper="true"]')
          : null;
      const clickedInsideMain = !!mainStageContainer?.contains(e.target);
      const clickedInsideOff = !!offStageContainer?.contains(e.target);
      const clickedInsideTopLayer = !!topLayerStageContainer?.contains(e.target);
      const clickedInsideOffTopLayer =
        !!offCanvasTopLayerWrapper?.contains(e.target) ||
        !!e.target.closest?.('[data-off-canvas-top-layer-wrapper="true"]');


      if (
        clickedInsideMain ||
        clickedInsideOff ||
        clickedInsideTopLayer ||
        clickedInsideOffTopLayer
      )
        return;
      clearCanvasSelectionState();
    };

    document.addEventListener("pointerdown", handleOutsideStagePointerDown, true);
    return () => {
      document.removeEventListener(
        "pointerdown",
        handleOutsideStagePointerDown,
        true,
      );
    };
  }, [clearCanvasSelectionState, isCropModalOpen]);

  // Clipboard state (needed for clipboard operations)
  const [clipboard, setClipboard] = useState([]);

  // Paste mode state - tracks if we're waiting for user to click position for paste
  const [isWaitingForPaste, setIsWaitingForPaste] = useState(false);
  const [pasteModeType, setPasteModeType] = useState(null); // 'copy' or 'cut'
  const [pendingClipboard, setPendingClipboard] = useState([]); // Store clipboard data while waiting for paste
  const isToolbarPasteInProgressRef = useRef(false);
  const toolShortcutPressRef = useRef({});

  // Removed elements states (needed for deleteSelectedObject creation)
  const [removedRectangles, setRemovedRectangles] = useState([]);
  const [removedCircles, setRemovedCircles] = useState([]);
  const [removedArrows, setRemovedArrows] = useState([]);
  const [removedDoubleSidedArrows, setRemovedDoubleSidedArrows] = useState([]);
  const [removedScribbles, setRemovedScribbles] = useState([]);
  const [removedTexts, setRemovedTexts] = useState([]);
  const [removedImages, setRemovedImages] = useState([]);
  const [removedVideos, setRemovedVideos] = useState([]);
  const [removedLines, setRemovedLines] = useState([]);
  const [removedOffCanvasRectangles, setRemovedOffCanvasRectangles] = useState(
    [],
  );
  const [removedOffCanvasCircles, setRemovedOffCanvasCircles] = useState([]);
  const [removedOffCanvasArrows, setRemovedOffCanvasArrows] = useState([]);
  const [
    removedOffCanvasDoubleSidedArrows,
    setRemovedOffCanvasDoubleSidedArrows,
  ] = useState([]);
  const [removedOffCanvasScribbles, setRemovedOffCanvasScribbles] = useState(
    [],
  );
  const [removedOffCanvasTexts, setRemovedOffCanvasTexts] = useState([]);
  const [removedOffCanvasImages, setRemovedOffCanvasImages] = useState([]);
  const [removedOffCanvasVideos, setRemovedOffCanvasVideos] = useState([]);
  const [removedOffCanvasLines, setRemovedOffCanvasLines] = useState([]);
  // Track removed part references (for parts removed from text boxes)
  const [removedPartReferences, setRemovedPartReferences] = useState([]);

  // Refs needed for function creators (stageRef is declared earlier at line 300)
  const transformerRef = useRef();
  const scrollAreaRef = useRef(null);
  /** Synced from Canvas `#main-canvas-stage-container` — preferred for zoom scroll centering */
  const mainStageContainerRef = useRef(null);
  /** Before +/- zoom or manual zoom %, snapshot scroll so thumb ratio stays fixed after layout. */
  const preZoomHScrollRef = useRef(null);
  const contentRef = useRef(null);
  const scaledContentRef = useRef(null);
  const hasCenteredInitialHorizontalScrollRef = useRef(false);

  // Ref to track pending selection after state update (needed for createAddText)
  const pendingSelection = useRef(null);

  // Create function instances from utility factories
  const navigateWithState = createNavigateWithState(pageId, navigate);
  const handleSort = createHandleSort(
    sortField,
    sortDirection,
    setSortField,
    setSortDirection,
  );
  const handleEditPart = createHandleEditPart(
    setEditingPartId,
    setEditedPartData,
  );
  const showSnackbar = createShowSnackbar(setSnackbar);
  const handleReplacePart = createHandleReplacePart(
    setSelectedText,
    setTexts,
    setPartsModalOpen,
    saveToHistory,
    generateMarkerId,
    // Callback to track replaced part references (similar to onPartRemoved)
    (replacedPartData) => {
      setRemovedPartReferences((prev) => [...prev, replacedPartData]);
    },
    // Return the array that contains the selected text (main or off-canvas) so we update and save the right one
    () => {
      if (!selectedText?.id) return texts;
      const inOff = (offCanvasTexts || []).some((t) => t?.id === selectedText.id);
      return inOff ? offCanvasTexts : texts;
    },
    () => {
      if (!selectedText?.id) return setTexts;
      const inOff = (offCanvasTexts || []).some((t) => t?.id === selectedText.id);
      return inOff ? setOffCanvasTexts : setTexts;
    },
    (updatedTexts, updatedSelectedText, selectedTextId) => ({
      currentTextsOverride: (offCanvasTexts || []).some((t) => t?.id === selectedTextId) ? null : updatedTexts,
      currentOffCanvasTextsOverride: (offCanvasTexts || []).some((t) => t?.id === selectedTextId) ? updatedTexts : null,
      currentSelectedTextOverride: updatedSelectedText,
      currentSelectedTextIdOverride: selectedTextId,
    }),
    setPendingChanges,
    pendingChangesRef,
  );
  const addText = createAddText(
    setTexts,
    setSelectedObject,
    setSelectedObjects,
    setSelectedImage,
    setSelectedArrow,
    setSelectedLine,
    setSelectedDoubleSidedArrow,
    setEditTextBoxContent,
    setShowEditTask,
    setShowEditNote,
    setSelectedText,
    setSelectedTextId,
    setShowTextBox,
    saveToHistory,
    generateMarkerId,
    transformerRef,
    pendingSelection,
    texts, // Pass texts array so we can calculate count directly
  );
  const updateTextContent = createUpdateTextContent(setTexts, saveToHistory);
  const handleZoom = createHandleZoom(
    setZoomLevel,
    setZoomInputValue,
    saveToHistory,
    scrollAreaRef,
    scaledContentRef,
    stageRef,
    preZoomHScrollRef,
  );
  const handleZoomInputChange = createHandleZoomInputChange(setZoomInputValue);
  const handleManualZoom = createHandleManualZoom(
    setZoomLevel,
    setZoomInputValue,
    saveToHistory,
    scrollAreaRef,
    scaledContentRef,
    stageRef,
    preZoomHScrollRef,
  );
  const handleZoomInputKeyPress =
    createHandleZoomInputKeyPress(handleManualZoom);
  const resetZoom = createResetZoom(
    setZoomLevel,
    setZoomInputValue,
    scrollAreaRef,
    scaledContentRef,
    stageRef,
    mainStageContainerRef,
  );
  const updateText = createUpdateText(setTexts, saveToHistory);
  const applyDrawingRestrictions = createApplyDrawingRestrictions(
    canvasHeight,
    canvasViewportWidth,
  );

  const clampPastedImagePlacement = useCallback(
    ({ centerX, centerY, width, height, pasteTarget }) => {
      const mainStage = stageRef?.current;
      const offStage =
        offCanvasStageRef?.current ||
        (typeof window !== "undefined" && window.__offCanvasStageRef?.current);
      const stage = pasteTarget === "off" ? offStage : mainStage;
      const sw = stage?.width?.() ?? 0;
      const sh = stage?.height?.() ?? 0;
      if (sw <= 0 || sh <= 0) {
        return { centerX, centerY, width, height };
      }

      const restricted = applyDrawingRestrictions("image", {
        x: centerX - width / 2,
        y: centerY - height / 2,
        width,
        height,
      });

      let cx = restricted.x + restricted.width / 2;
      let cy = restricted.y + restricted.height / 2;
      let cw = restricted.width;
      let ch = restricted.height;

      let clamped = clampImageCenterToStageBounds(cx, cy, cw, ch, sw, sh, 0);

      if (pasteTarget === "off") {
        const shifted = applyOffCanvasMainColumnAvoidanceX(
          { width: cw, height: ch, useTopLeftAnchor: false },
          "Image",
          clamped.x,
          clamped.y,
          sw,
        );
        clamped = clampImageCenterToStageBounds(
          shifted.x,
          shifted.y,
          cw,
          ch,
          sw,
          sh,
          0,
        );
      }

      return {
        centerX: clamped.x,
        centerY: clamped.y,
        width: cw,
        height: ch,
      };
    },
    [applyDrawingRestrictions, stageRef, offCanvasStageRef],
  );

  const addImage = createAddImage(
    setIsImageLoading,
    setImageLoadingProgress,
    setImages,
    setOffCanvasImages,
    setSelectedObject,
    setSelectedObjects,
    setSelectedImage,
    setSelectedText,
    setSelectedTextId,
    setSelectedArrow,
    setSelectedLine,
    setSelectedDoubleSidedArrow,
    saveToHistory,
    generateMarkerId,
    compressImageIfNeeded,
    processCompressedImage,
    transformerRef,
    showErrorSnackbar,
    clampPastedImagePlacement,
  );

  /** Stage coordinates and target for pasting images at the last known pointer (main vs off-canvas). */
  const resolvePasteImagePosition = useCallback(() => {
    const clientX =
      typeof window.lastMouseX === "number"
        ? window.lastMouseX
        : window.innerWidth / 2;
    const clientY =
      typeof window.lastMouseY === "number"
        ? window.lastMouseY
        : window.innerHeight / 2;

    const mainStage = stageRef?.current;
    const offStage =
      offCanvasStageRef?.current ||
      (typeof window !== "undefined" && window.__offCanvasStageRef?.current);

    const clientToStage = (stage, cx, cy) => {
      try {
        const container = stage.container();
        if (!container) return null;
        const rect = container.getBoundingClientRect();
        const scaleX = stage.width() / rect.width;
        const scaleY = stage.height() / rect.height;
        return {
          x: (cx - rect.left) * scaleX,
          y: (cy - rect.top) * scaleY,
        };
      } catch {
        return null;
      }
    };

    // Toolbar button paste should always target main stage (not cursor-derived off-canvas).
    // Ctrl/Cmd+V keeps cursor-based behavior below.
    if (isToolbarPasteInProgressRef.current && mainStage) {
      const centerX = (mainStage?.width?.() || getMainCanvasWidthPx()) / 2;
      const centerY = (mainStage?.height?.() || getCanvasStageHeightPx()) / 2;
      return { x: centerX, y: centerY, target: "main" };
    }

    const rectContains = (rect, cx, cy) =>
      !!rect &&
      cx >= rect.left &&
      cx <= rect.right &&
      cy >= rect.top &&
      cy <= rect.bottom;

    let mainRect = null;
    let offRect = null;
    try {
      mainRect = mainStage?.container?.()?.getBoundingClientRect() ?? null;
    } catch {
      mainRect = null;
    }
    try {
      offRect = offStage?.container?.()?.getBoundingClientRect() ?? null;
    } catch {
      offRect = null;
    }

    let stageCoords = null;
    let target = "main";

    if (mainStage && rectContains(mainRect, clientX, clientY)) {
      stageCoords = clientToStage(mainStage, clientX, clientY);
      target = "main";
    } else if (offStage && rectContains(offRect, clientX, clientY)) {
      stageCoords = clientToStage(offStage, clientX, clientY);
      target = "off";
    } else if (mainStage && mainRect) {
      const cx = Math.max(mainRect.left, Math.min(mainRect.right, clientX));
      const cy = Math.max(mainRect.top, Math.min(mainRect.bottom, clientY));
      stageCoords = clientToStage(mainStage, cx, cy);
      target = "main";
    }

    if (!stageCoords) return null;
    return { x: stageCoords.x, y: stageCoords.y, target };
  }, [stageRef, offCanvasStageRef]);

  const addImageAtPastePosition = useCallback(
    (
      imageSrc,
      referenceUrl = "",
      originalBlob = null,
      x = null,
      y = null,
      width = null,
      height = null,
      useTopLeftAnchor = false,
      imageAddOptions = null,
    ) => {
      const pos = resolvePasteImagePosition();
      const pasteTarget = pos?.target === "off" ? "off" : "main";
      return addImage(
        imageSrc,
        referenceUrl,
        originalBlob,
        pos != null ? pos.x : x,
        pos != null ? pos.y : y,
        width,
        height,
        useTopLeftAnchor,
        {
          ...(imageAddOptions && typeof imageAddOptions === "object"
            ? imageAddOptions
            : {}),
          pasteTarget,
        },
      );
    },
    [addImage, resolvePasteImagePosition],
  );

  const handleWheel = createHandleWheel(
    handleZoom,
    scrollAreaRef,
    () => zoomLevelRef.current,
  );
  const addVideo = createAddVideo(
    setVideos,
    setSelectedObject,
    setSelectedObjects,
    setSelectedImage,
    setSelectedText,
    setSelectedTextId,
    setSelectedArrow,
    setSelectedLine,
    setSelectedDoubleSidedArrow,
    setSelectedVideo,
    saveToHistory,
    generateMarkerId,
    generateVideoThumbnail,
    transformerRef,
  );
  const handleImageUpload = createHandleImageUpload(
    setAction,
    setIsImageLoading,
    setImageLoadingProgress,
    addImage,
    ACTIONS,
    setPendingImageData,
    showErrorSnackbar,
  );
  const processHtmlContent = createProcessHtmlContent();
  const processHtmlContentWithFormatting =
    createProcessHtmlContentWithFormatting();
  const extractLinksFromText = createExtractLinksFromText();
  const rasterizeSvg = createRasterizeSvg();
  const restoreTransformer = createRestoreTransformer();
  const onDoubleClick = createOnDoubleClick(setArrows, setDoubleSidedArrows);
  const findAndRemoveFromState = createFindAndRemoveFromState();
  const updateTextProperties = createUpdateTextProperties(
    setTexts,
    setOffCanvasTexts,
    offCanvasTexts,
    setSelectedText,
    saveToHistory,
  );
  const handleRectangleDragEnd = createHandleRectangleDragEnd(
    rectangles,
    setRectangles,
    saveToHistory,
    isUndoingRedoing,
  );
  const handleCircleDragEnd = createHandleCircleDragEnd(
    circles,
    setCircles,
    saveToHistory,
    isUndoingRedoing,
  );
  const handleArrowDragEnd = createHandleArrowDragEnd(
    arrows,
    setArrows,
    saveToHistory,
    isUndoingRedoing,
  );
  const handleDoubleSidedArrowDragEnd = createHandleDoubleSidedArrowDragEnd(
    doubleSidedArrows,
    setDoubleSidedArrows,
    saveToHistory,
    isUndoingRedoing,
  );
  const handleLineDragEnd = createHandleLineDragEnd(
    lines,
    setLines,
    saveToHistory,
    isUndoingRedoing,
    setSelectedObject, // Pass setSelectedObject to update selection after drag
    setSelectedObjects, // Pass setSelectedObjects to update selection after drag
    offCanvasLines, // Pass off-canvas lines array
    setOffCanvasLines, // Pass setter for off-canvas lines array
  );
  const handleScribbleDragEnd = createHandleScribbleDragEnd(
    setScribbles,
    saveToHistory,
    scribbles,
  );
  const handleTextDragEnd = createHandleTextDragEnd(
    texts,
    setTexts,
    setSelectedText,
    saveToHistory,
    isUndoingRedoing,
  );

  // Function to prepare clipboard data with relative positions for group pasting
  const prepareClipboardData = (objects) => {
    if (objects.length === 0) return null;

    // Calculate bounding box of selected objects
    const minX = Math.min(...objects.map((obj) => obj.x || 0));
    const minY = Math.min(...objects.map((obj) => obj.y || 0));

    return objects.map((obj) => {
      const sanitized = sanitizeForClone(obj);
      return {
        ...sanitized,
        relativeX: (obj.x || 0) - minX,
        relativeY: (obj.y || 0) - minY,
        originalX: obj.x || 0,
        originalY: obj.y || 0,
        originalId: obj.id,
      };
    });
  };

  // Function to paste elements at a specific position
  const pasteElementsAtPosition = (
    pasteX,
    pasteY,
    clipboardData,
    isCut = false,
  ) => {
    if (!clipboardData || clipboardData.length === 0) return;

    const newItems = clipboardData.map((item) => {
      // Generate new unique ID
      const newId = `pasted_${Date.now()}_${Math.random()}_${item.originalId}`;

      // Calculate new position maintaining relative positioning within the group
      const newX = pasteX + (item.relativeX || 0);
      const newY = pasteY + (item.relativeY || 0);

      // Create new item with all preserved properties
      const newItem = {
        ...item,
        id: newId,
        x: newX,
        y: newY,
      };

      // Remove temporary properties
      delete newItem.relativeX;
      delete newItem.relativeY;
      delete newItem.originalX;
      delete newItem.originalY;
      delete newItem.originalId;

      return newItem;
    });

    // Add elements to their respective state arrays
    newItems.forEach((item) => {
      // CRITICAL: Normalize element type to handle variations like "rect", "rectangle", "Rect", "img", "image", etc.
      const elementType = normalizeShapeType(item.type);
      if (elementType === "Image") {
        setImages((prev) => {
          // CRITICAL: Check if ID already exists to prevent duplicates
          const idExists = prev.some((img) => img.id === item.id);
          if (idExists) {
            console.error(
              "❌ [CLONE] Duplicate image ID detected, generating new ID",
              {
                duplicateId: item.id,
                existingIds: prev.map((i) => i.id).slice(-5),
              },
            );
            item.id = generateMarkerId();
          }
          return [...prev, item];
        });
      } else if (elementType === "Rect") {
        setRectangles((prev) => {
          // CRITICAL: Check if ID already exists to prevent duplicates
          const idExists = prev.some((rect) => rect.id === item.id);
          if (idExists) {
            console.error(
              "❌ [CLONE] Duplicate rectangle ID detected, generating new ID",
              {
                duplicateId: item.id,
                existingIds: prev.map((r) => r.id).slice(-5),
              },
            );
            // Generate a new ID if duplicate is found
            item.id = generateMarkerId();
          }
          return [...prev, item];
        });
      } else if (elementType === "Circle") {
        setCircles((prev) => {
          // CRITICAL: Check if ID already exists to prevent duplicates
          const idExists = prev.some((circle) => circle.id === item.id);
          if (idExists) {
            console.error(
              "❌ [CLONE] Duplicate circle ID detected, generating new ID",
              {
                duplicateId: item.id,
                existingIds: prev.map((c) => c.id).slice(-5),
              },
            );
            item.id = generateMarkerId();
          }
          return [...prev, item];
        });
      } else if (elementType === "Arrow") {
        setArrows((prev) => {
          // CRITICAL: Check if ID already exists to prevent duplicates
          const idExists = prev.some((arrow) => arrow.id === item.id);
          if (idExists) {
            console.error(
              "❌ [CLONE] Duplicate arrow ID detected, generating new ID",
              {
                duplicateId: item.id,
                existingIds: prev.map((a) => a.id).slice(-5),
              },
            );
            item.id = generateMarkerId();
          }
          return [...prev, item];
        });
      } else if (elementType === "DoubleSidedArrow") {
        setDoubleSidedArrows((prev) => {
          // CRITICAL: Check if ID already exists to prevent duplicates
          const idExists = prev.some((arrow) => arrow.id === item.id);
          if (idExists) {
            console.error(
              "❌ [CLONE] Duplicate double-sided arrow ID detected, generating new ID",
              {
                duplicateId: item.id,
                existingIds: prev.map((a) => a.id).slice(-5),
              },
            );
            item.id = generateMarkerId();
          }
          return [...prev, item];
        });
      } else if (elementType === "Line") {
        // Normalize arrow properties for unified line/arrow elements
        const normalizedHasStartArrow =
          item.hasStartArrow === true || item.pointerAtBeginning === true;
        const normalizedHasEndArrow =
          item.hasEndArrow === true ||
          item.pointerAtEnd === true ||
          item.pointerAtEnding === true;

        // Destructure to exclude legacy properties
        const {
          pointerAtBeginning,
          pointerAtEnd,
          pointerAtEnding,
          ...itemWithoutLegacy
        } = item;
        const normalizedItem = {
          ...itemWithoutLegacy,
          hasStartArrow: normalizedHasStartArrow,
          hasEndArrow: normalizedHasEndArrow,
        };
        setLines((prev) => {
          // CRITICAL: Check if ID already exists to prevent duplicates
          const idExists = prev.some((line) => line.id === normalizedItem.id);
          if (idExists) {
            console.error(
              "❌ [CLONE] Duplicate line ID detected, generating new ID",
              {
                duplicateId: normalizedItem.id,
                existingIds: prev.map((l) => l.id).slice(-5),
              },
            );
            normalizedItem.id = generateMarkerId();
          }
          return [...prev, normalizedItem];
        });
      } else if (elementType === "Text") {
        setTexts((prev) => {
          // CRITICAL: Check if ID already exists to prevent duplicates
          const idExists = prev.some((text) => text.id === item.id);
          if (idExists) {
            console.error(
              "❌ [CLONE] Duplicate text ID detected, generating new ID",
              {
                duplicateId: item.id,
                existingIds: prev.map((t) => t.id).slice(-5),
              },
            );
            item.id = generateMarkerId();
          }
          return [...prev, item];
        });
      }
    });

    // If cut, remove original objects
    if (isCut) {
      const idsToRemove = new Set(clipboardData.map((item) => item.originalId));
      setImages((prev) => prev.filter((img) => !idsToRemove.has(img.id)));
      setRectangles((prev) => prev.filter((rect) => !idsToRemove.has(rect.id)));
      setCircles((prev) => prev.filter((c) => !idsToRemove.has(c.id)));
      setLines((prev) => prev.filter((line) => !idsToRemove.has(line.id)));
      setArrows((prev) => prev.filter((arrow) => !idsToRemove.has(arrow.id)));
      setDoubleSidedArrows((prev) =>
        prev.filter((arrow) => !idsToRemove.has(arrow.id)),
      );
      setTexts((prev) => prev.filter((txt) => !idsToRemove.has(txt.id)));
    }

    // Select the pasted items
    setSelectedObjects(newItems);
    setSelectedObject(newItems[0] || null);

    // Save to history
    saveToHistory();
  };

  // Clone elements at clicked position (similar to pasteElementsAtPosition)
  // clickedStage: the stage that was clicked (main stageRef or offCanvasStageRef)
  // clientX, clientY: client coordinates to determine which canvas to add elements to
  const cloneElementsAtPosition = (
    cloneX,
    cloneY,
    clickedStage = null,
    clientX = null,
    clientY = null,
  ) => {
    const TRANSFORMER_ANCHOR_BUFFER_PX = 10;
    const DEBUG_CLONE_GROUP_ALIGNMENT = true;
    const clickedStageInstance = clickedStage?.current || clickedStage;
    const mainStage = stageRef?.current;
    const offStage = offCanvasStageRef?.current;

    // Decide drop target from viewport first so client→stage conversion uses the SAME
    // stage as where elements will be inserted. Otherwise off→main (or mis-attributed
    // clicks) can map clientX/Y through the wrong container, explode scale, or clamp
    // to the far edge.
    const resolveTargetCanvasFromClient = () => {
      if (clientX === null || clientY === null) return null;
      const inRect = (rect) =>
        rect &&
        clientX >= rect.left &&
        clientX <= rect.right &&
        clientY >= rect.top &&
        clientY <= rect.bottom;

      const mainRect = mainStage?.container?.()?.getBoundingClientRect?.();
      const offRect = offStage?.container?.()?.getBoundingClientRect?.();

      const inMain = inRect(mainRect);
      const inOff = inRect(offRect);

      if (inMain && !inOff) return "main";
      if (inOff && !inMain) return "offCanvas";
      if (inMain && inOff) {
        if (clickedStageInstance === mainStage) return "main";
        if (clickedStageInstance === offStage) return "offCanvas";
        return "main";
      }
      return null;
    };

    let targetCanvas = "main";
    const targetFromClient = resolveTargetCanvasFromClient();
    if (targetFromClient) {
      targetCanvas = targetFromClient;
    } else if (clickedStage) {
      if (clickedStageInstance && mainStage && clickedStageInstance === mainStage) {
        targetCanvas = "main";
      } else if (clickedStageInstance && offStage && clickedStageInstance === offStage) {
        targetCanvas = "offCanvas";
      } else if (clientX !== null && clientY !== null && mainStage) {
        const mainBBox = mainStage.container()?.getBoundingClientRect();

        if (mainBBox) {
          const mainCanvasRight = mainBBox.right;
          const mainCanvasBottom = mainBBox.bottom;

          const isInMainCanvas =
            clientX >= mainBBox.left &&
            clientX <= mainCanvasRight &&
            clientY >= mainBBox.top &&
            clientY <= mainCanvasBottom;

          if (isInMainCanvas) {
            targetCanvas = "main";
          } else if (clickedStageInstance && clickedStageInstance !== mainStage) {
            targetCanvas = "offCanvas";
          } else {
            targetCanvas = "offCanvas";
          }
        } else if (clickedStageInstance && clickedStageInstance !== mainStage) {
          targetCanvas = "offCanvas";
        }
      } else if (clickedStageInstance && clickedStageInstance !== mainStage) {
        targetCanvas = "offCanvas";
      }
    }

    const stageForCoords =
      targetCanvas === "offCanvas"
        ? offStage || clickedStageInstance
        : mainStage || clickedStageInstance;

    let resolvedCloneX = Number(cloneX);
    let resolvedCloneY = Number(cloneY);

    // Prefer client->stage conversion on the *target* stage's container.
    // Rationale: Konva pointerPos can be stale on the first click after interacting with
    // the other stage (main ↔ off-canvas). client coords are the ground truth; the stage
    // used for conversion must match targetCanvas.
    if (
      stageForCoords &&
      clientX !== null &&
      clientY !== null &&
      typeof stageForCoords.container === "function"
    ) {
      const stageContainer = stageForCoords.container();
      const stageRect = stageContainer?.getBoundingClientRect?.();
      if (stageRect) {
        const stageWidth = Number(stageForCoords.width?.()) || 0;
        const stageHeight = Number(stageForCoords.height?.()) || 0;
        const domW = Math.max(1, Number(stageRect.width) || 1);
        const domH = Math.max(1, Number(stageRect.height) || 1);
        // When the canvas host is being scrolled, there can be a transient frame
        // where DOM rect width/height are tiny (1px). Converting client -> stage in
        // that frame explodes coordinates and pins first clone to the right edge.
        // In that case, keep the incoming pointerPos-based cloneX/cloneY.
        const hasUsableDomRect = domW > 8 && domH > 8;
        if (hasUsableDomRect) {
          const sx = stageWidth > 0 ? stageWidth / domW : 1;
          const sy = stageHeight > 0 ? stageHeight / domH : 1;
          const rawX = (clientX - stageRect.left) * sx;
          const rawY = (clientY - stageRect.top) * sy;
          if (Number.isFinite(rawX) && Number.isFinite(rawY)) {
            resolvedCloneX =
              stageWidth > 0 ? Math.max(0, Math.min(stageWidth, rawX)) : rawX;
            resolvedCloneY =
              stageHeight > 0 ? Math.max(0, Math.min(stageHeight, rawY)) : rawY;
          }
        }
      }
    }
    if (!Number.isFinite(resolvedCloneX)) resolvedCloneX = 0;
    if (!Number.isFinite(resolvedCloneY)) resolvedCloneY = 0;
    if (DEBUG_CLONE_GROUP_ALIGNMENT) {
      console.log("[CLONE-GROUP][ENTRY]", {
        cloneX,
        cloneY,
        resolvedCloneX,
        resolvedCloneY,
        clientX,
        clientY,
        targetCanvas,
        templateCount: cloneTemplates?.length || (cloneTemplate ? 1 : 0),
      });
    }

    // Handle both single-element clone (cloneTemplate) and multi-element clone (cloneTemplates)
    let templatesToUse =
      cloneTemplates && cloneTemplates.length > 0
        ? cloneTemplates
        : cloneTemplate
          ? [cloneTemplate]
          : [];

    if (!templatesToUse || templatesToUse.length === 0) {
      return;
    }

    // Guard duplicate invocations from overlapping event paths (notably top-layer image clone draw flow)
    // so one user action creates only one clone.
    const now = Date.now();
    const roundedX = Math.round(resolvedCloneX);
    const roundedY = Math.round(resolvedCloneY);
    const templateCount = templatesToUse.length;
    const templateType = templatesToUse[0]?.type || "";
    const lastPlacement = lastClonePlacementRef.current;
    const nearSamePointFast =
      now - (lastPlacement.ts || 0) < 140 &&
      Math.abs(roundedX - (lastPlacement.x ?? roundedX)) <= 2 &&
      Math.abs(roundedY - (lastPlacement.y ?? roundedY)) <= 2;
    if (nearSamePointFast) {
      return;
    }
    if (
      now - (lastPlacement.ts || 0) < 120 &&
      templateCount === lastPlacement.templateCount &&
      templateType === lastPlacement.templateType &&
      Math.abs(roundedX - (lastPlacement.x ?? roundedX)) <= 2 &&
      Math.abs(roundedY - (lastPlacement.y ?? roundedY)) <= 2
    ) {
      return;
    }
    lastClonePlacementRef.current = {
      ts: now,
      x: roundedX,
      y: roundedY,
      templateCount,
      templateType,
    };

    const getMainStageDimensions = () => {
      const mainStage = stageRef?.current;
      if (mainStage) {
        return { width: mainStage.width(), height: mainStage.height() };
      }
      return {
        width: getMainCanvasWidthPx(),
        height: canvasStageHeightPx,
      };
    };

    const constrainInsideMainStage = (item) => {
      if (targetCanvas !== "main") return item;
      const { width: stageWidth, height: stageHeight } = getMainStageDimensions();
      const strokeWidth = Math.max(0, Number(item?.strokeWidth ?? 2));
      const strokeHalf = strokeWidth / 2;
      const transformerEdgeInset = TRANSFORMER_ANCHOR_BUFFER_PX;
      const edgePad = strokeHalf + transformerEdgeInset;
      const nextItem = { ...item };

      // Unified lines/arrows: keep full polyline (including stroke) inside stage by shifting x/y.
      if (Array.isArray(nextItem.points) && nextItem.points.length >= 4) {
        return clampPolylineGroupToStageBounds(
          nextItem,
          stageWidth,
          stageHeight,
          strokeWidth,
          transformerEdgeInset,
        );
      }

      // Circle center-based positioning.
      if (normalizeShapeType(nextItem.type) === "Circle") {
        const radius = Math.max(0, Number(nextItem.radius) || 0);
        const maxInsetX = Math.max(
          0,
          (stageWidth - radius * 2 - strokeHalf * 2) / 2,
        );
        const maxInsetY = Math.max(
          0,
          (stageHeight - radius * 2 - strokeHalf * 2) / 2,
        );
        const edgeInsetX = Math.min(transformerEdgeInset, maxInsetX);
        const edgeInsetY = Math.min(transformerEdgeInset, maxInsetY);
        const minX = radius + strokeHalf + edgeInsetX;
        const maxX = stageWidth - radius - strokeHalf - edgeInsetX;
        const minY = radius + strokeHalf + edgeInsetY;
        const maxY = stageHeight - radius - strokeHalf - edgeInsetY;
        nextItem.x = Math.max(minX, Math.min(Number(nextItem.x) || 0, maxX));
        nextItem.y = Math.max(minY, Math.min(Number(nextItem.y) || 0, maxY));
        return nextItem;
      }

      // Rectangles: rendered with x/y as top-left (RectangleElement); keep inside main stage.
      if (normalizeShapeType(nextItem.type) === "Rect") {
        return clampRectangleTopLeftToStageBounds(
          nextItem,
          stageWidth,
          stageHeight,
          nextItem.strokeWidth ?? 2,
          transformerEdgeInset,
        );
      }

      // Text uses top-left anchored x/y and its rendered height can grow near the
      // stage edge because available width shrinks and content wraps more.
      if (normalizeShapeType(nextItem.type) === "Text") {
        const textWidth = Math.max(1, Number(nextItem.width) || 200);
        const getMinHeight = () => {
          if (nextItem.fontSize === 11) return 30;
          if (nextItem.fontSize === 14 || nextItem.fontSize === "14") return 35;
          return 43;
        };

        // Match TextElement's content-height logic for the *current* x position so
        // right-edge placements account for extra wrapping before clamping.
        const textHeight = Math.max(
          calculateTotalContentHeight(
            { ...nextItem, width: textWidth },
            stageWidth,
          ),
          getMinHeight(),
        );

        return clampRectangleTopLeftToStageBounds(
          {
            ...nextItem,
            width: textWidth,
            height: textHeight,
          },
          stageWidth,
          stageHeight,
          nextItem.strokeWidth ?? 2,
          transformerEdgeInset,
        );
      }

      const itemWidth = Math.max(0, Number(nextItem.width) || 0);
      const itemHeight = Math.max(0, Number(nextItem.height) || 0);
      const useTopLeftAnchor = nextItem.useTopLeftAnchor === true;

      if (useTopLeftAnchor) {
        const maxInsetX = Math.max(
          0,
          (stageWidth - itemWidth - strokeHalf * 2) / 2,
        );
        const maxInsetY = Math.max(
          0,
          (stageHeight - itemHeight - strokeHalf * 2) / 2,
        );
        const edgeInsetX = Math.min(transformerEdgeInset, maxInsetX);
        const edgeInsetY = Math.min(transformerEdgeInset, maxInsetY);
        const minX = strokeHalf + edgeInsetX;
        const maxX = Math.max(minX, stageWidth - itemWidth - strokeHalf - edgeInsetX);
        const minY = strokeHalf + edgeInsetY;
        const maxY = Math.max(minY, stageHeight - itemHeight - strokeHalf - edgeInsetY);
        nextItem.x = Math.max(minX, Math.min(Number(nextItem.x) || 0, maxX));
        nextItem.y = Math.max(minY, Math.min(Number(nextItem.y) || 0, maxY));
      } else {
        const halfW = itemWidth / 2;
        const halfH = itemHeight / 2;
        const maxInsetX = Math.max(0, (stageWidth - itemWidth - strokeHalf * 2) / 2);
        const maxInsetY = Math.max(0, (stageHeight - itemHeight - strokeHalf * 2) / 2);
        const edgeInsetX = Math.min(transformerEdgeInset, maxInsetX);
        const edgeInsetY = Math.min(transformerEdgeInset, maxInsetY);
        const minX = halfW + strokeHalf + edgeInsetX;
        const maxX = Math.max(minX, stageWidth - halfW - strokeHalf - edgeInsetX);
        const minY = halfH + strokeHalf + edgeInsetY;
        const maxY = Math.max(minY, stageHeight - halfH - strokeHalf - edgeInsetY);
        nextItem.x = Math.max(minX, Math.min(Number(nextItem.x) || 0, maxX));
        nextItem.y = Math.max(minY, Math.min(Number(nextItem.y) || 0, maxY));
      }

      return nextItem;
    };
    const constrainInsideOffCanvasStage = (item) => {
      if (targetCanvas !== "offCanvas") return item;
      if (normalizeShapeType(item?.type) !== "Image") return item;
      const offStage = offCanvasStageRef?.current;
      const stageWidth = offStage?.width?.() || getOffCanvasStageWidthPx();
      const stageHeight = offStage?.height?.() || canvasStageHeightPx;
      if (stageWidth <= 0 || stageHeight <= 0) return item;
      const nextItem = { ...item };
      const strokeWidth = Math.max(0, Number(nextItem?.strokeWidth ?? 0));
      const strokeHalf = strokeWidth / 2;
      const edgeInset = TRANSFORMER_ANCHOR_BUFFER_PX;
      const itemWidth = Math.max(0, Number(nextItem.width) || 0);
      const itemHeight = Math.max(0, Number(nextItem.height) || 0);
      const useTopLeftAnchor = nextItem.useTopLeftAnchor === true;
      if (useTopLeftAnchor) {
        const minY = strokeHalf + edgeInset;
        const maxY = Math.max(minY, stageHeight - itemHeight - strokeHalf - edgeInset);
        nextItem.x = clampOffCanvasTopLeftXAwayFromMainColumn(
          Number(nextItem.x) || 0,
          itemWidth,
          stageWidth,
          strokeHalf + edgeInset,
        );
        nextItem.y = Math.max(minY, Math.min(Number(nextItem.y) || 0, maxY));
        return nextItem;
      }
      const halfW = itemWidth / 2;
      const halfH = itemHeight / 2;
      const clampedTopLeftX = clampOffCanvasTopLeftXAwayFromMainColumn(
        (Number(nextItem.x) || 0) - halfW,
        itemWidth,
        stageWidth,
        strokeHalf + edgeInset,
      );
      const minY = halfH + strokeHalf + edgeInset;
      const maxY = Math.max(minY, stageHeight - halfH - strokeHalf - edgeInset);
      nextItem.x = clampedTopLeftX + halfW;
      nextItem.y = Math.max(minY, Math.min(Number(nextItem.y) || 0, maxY));
      return nextItem;
    };

    const getRotatedAabb = (cx, cy, width, height, rotationDeg = 0) => {
      const w = Math.max(0, Number(width) || 0);
      const h = Math.max(0, Number(height) || 0);
      const r = ((Number(rotationDeg) || 0) * Math.PI) / 180;
      if (!r) {
        return {
          minX: cx - w / 2,
          minY: cy - h / 2,
          maxX: cx + w / 2,
          maxY: cy + h / 2,
        };
      }
      const cos = Math.cos(r);
      const sin = Math.sin(r);
      const hw = w / 2;
      const hh = h / 2;
      const corners = [
        { x: -hw, y: -hh },
        { x: hw, y: -hh },
        { x: hw, y: hh },
        { x: -hw, y: hh },
      ];
      let minX = Infinity;
      let minY = Infinity;
      let maxX = -Infinity;
      let maxY = -Infinity;
      corners.forEach((p) => {
        const rx = cx + p.x * cos - p.y * sin;
        const ry = cy + p.x * sin + p.y * cos;
        if (rx < minX) minX = rx;
        if (ry < minY) minY = ry;
        if (rx > maxX) maxX = rx;
        if (ry > maxY) maxY = ry;
      });
      return { minX, minY, maxX, maxY };
    };

    const getItemBounds = (item) => {
      const strokeHalf = Math.max(0, Number(item?.strokeWidth ?? 2)) / 2;
      // Unified polyline/arrow-like element
      if (Array.isArray(item?.points) && item.points.length >= 4) {
        const baseX = Number(item.x) || 0;
        const baseY = Number(item.y) || 0;
        let minX = Infinity;
        let minY = Infinity;
        let maxX = -Infinity;
        let maxY = -Infinity;
        for (let i = 0; i < item.points.length; i += 2) {
          const px = baseX + (Number(item.points[i]) || 0);
          const py = baseY + (Number(item.points[i + 1]) || 0);
          if (px < minX) minX = px;
          if (py < minY) minY = py;
          if (px > maxX) maxX = px;
          if (py > maxY) maxY = py;
        }
        if (!Number.isFinite(minX) || !Number.isFinite(minY)) {
          minX = baseX;
          minY = baseY;
          maxX = baseX;
          maxY = baseY;
        }
        return {
          minX: minX - strokeHalf,
          minY: minY - strokeHalf,
          maxX: maxX + strokeHalf,
          maxY: maxY + strokeHalf,
        };
      }

      if (normalizeShapeType(item?.type) === "Circle") {
        const r = Math.max(0, Number(item?.radius) || 0);
        const cx = Number(item?.x) || 0;
        const cy = Number(item?.y) || 0;
        return {
          minX: cx - r - strokeHalf,
          minY: cy - r - strokeHalf,
          maxX: cx + r + strokeHalf,
          maxY: cy + r + strokeHalf,
        };
      }

      if (normalizeShapeType(item?.type) === "Rect") {
        const w = Math.max(0, Number(item?.width) || 0);
        const h = Math.max(0, Number(item?.height) || 0);
        const x = Number(item?.x) || 0;
        const y = Number(item?.y) || 0;
        const rotation = Number(item?.rotation) || 0;
        const aabb = getRotatedAabb(x + w / 2, y + h / 2, w, h, rotation);
        return {
          minX: aabb.minX - strokeHalf,
          minY: aabb.minY - strokeHalf,
          maxX: aabb.maxX + strokeHalf,
          maxY: aabb.maxY + strokeHalf,
        };
      }

      if (normalizeShapeType(item?.type) === "Text") {
        const stageWidthForText =
          targetCanvas === "offCanvas"
            ? offCanvasStageRef?.current?.width?.() || getOffCanvasStageWidthPx()
            : getMainStageDimensions().width;
        const textWidth = Math.max(1, Number(item?.width) || 200);
        const getMinHeight = () => {
          if (item?.fontSize === 11) return 30;
          if (item?.fontSize === 14 || item?.fontSize === "14") return 35;
          return 43;
        };
        const textHeight = Math.max(
          calculateTotalContentHeight(
            { ...item, width: textWidth },
            stageWidthForText,
          ),
          getMinHeight(),
        );
        const x = Number(item?.x) || 0;
        const y = Number(item?.y) || 0;
        const rotation = Number(item?.rotation) || 0;
        const aabb = getRotatedAabb(
          x + textWidth / 2,
          y + textHeight / 2,
          textWidth,
          textHeight,
          rotation,
        );
        return {
          minX: aabb.minX - strokeHalf,
          minY: aabb.minY - strokeHalf,
          maxX: aabb.maxX + strokeHalf,
          maxY: aabb.maxY + strokeHalf,
        };
      }

      const w = Math.max(0, Number(item?.width) || 0);
      const h = Math.max(0, Number(item?.height) || 0);
      const useTopLeftAnchor = item?.useTopLeftAnchor === true;
      const rotation = Number(item?.rotation) || 0;
      const cx = Number(item?.x) || 0;
      const cy = Number(item?.y) || 0;
      if (useTopLeftAnchor) {
        const aabb = getRotatedAabb(cx + w / 2, cy + h / 2, w, h, rotation);
        return {
          minX: aabb.minX - strokeHalf,
          minY: aabb.minY - strokeHalf,
          maxX: aabb.maxX + strokeHalf,
          maxY: aabb.maxY + strokeHalf,
        };
      }
      const aabb = getRotatedAabb(cx, cy, w, h, rotation);
      return {
        minX: aabb.minX - strokeHalf,
        minY: aabb.minY - strokeHalf,
        maxX: aabb.maxX + strokeHalf,
        maxY: aabb.maxY + strokeHalf,
      };
    };

    const logCloneGroupSnapshot = (label, items) => {
      if (!DEBUG_CLONE_GROUP_ALIGNMENT || !Array.isArray(items) || items.length === 0) {
        return;
      }
      const first = items[0];
      const firstX = Number(first?.x) || 0;
      const firstY = Number(first?.y) || 0;
      const rows = items.map((it) => {
        const x = Number(it?.x) || 0;
        const y = Number(it?.y) || 0;
        const b = getItemBounds(it);
        return {
          id: it?.id,
          type: it?.type,
          x,
          y,
          dxFromFirst: x - firstX,
          dyFromFirst: y - firstY,
          bounds: b,
        };
      });
      const groupBounds = rows.reduce(
        (acc, row) => ({
          minX: Math.min(acc.minX, row.bounds.minX),
          minY: Math.min(acc.minY, row.bounds.minY),
          maxX: Math.max(acc.maxX, row.bounds.maxX),
          maxY: Math.max(acc.maxY, row.bounds.maxY),
        }),
        { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity },
      );
      console.log(`[CLONE-GROUP][${label}]`, {
        count: rows.length,
        groupBounds,
        rows,
      });
    };

    // Anchor clones at the pointer like paste/drag: group bounding-box center at (cloneX, cloneY),
    // not the top-left of the selection's min x/y (which felt "cursor = top-left corner").
    let groupCenterX = 0;
    let groupCenterY = 0;
    {
      let gminX = Infinity;
      let gminY = Infinity;
      let gmaxX = -Infinity;
      let gmaxY = -Infinity;
      templatesToUse.forEach((t) => {
        const relX = t.relativeX || 0;
        const relY = t.relativeY || 0;
        const b = getItemBounds({ ...t, x: relX, y: relY });
        if (Number.isFinite(b.minX) && Number.isFinite(b.minY)) {
          gminX = Math.min(gminX, b.minX);
          gminY = Math.min(gminY, b.minY);
          gmaxX = Math.max(gmaxX, b.maxX);
          gmaxY = Math.max(gmaxY, b.maxY);
        }
      });
      if (
        Number.isFinite(gminX) &&
        Number.isFinite(gminY) &&
        Number.isFinite(gmaxX) &&
        Number.isFinite(gmaxY)
      ) {
        groupCenterX = (gminX + gmaxX) / 2;
        groupCenterY = (gminY + gmaxY) / 2;
      }
    }

    const isMainMultiCloneGroup =
      targetCanvas === "main" && templatesToUse.length > 1;
    const normalizeCloneForTargetCanvas = (item) => {
      const base = clearCrossCanvasCloneServerIdentity(item);
      const forceOffCanvasFlags = (obj, isOffCanvasTarget) => {
        if (!obj || typeof obj !== "object") return obj;
        if (isOffCanvasTarget) {
          return {
            ...obj,
            isOffcanvas: true,
            isOffCanvas: true,
            is_off_canvas: true,
          };
        }
        const next = { ...obj };
        delete next.isOffcanvas;
        delete next.isOffCanvas;
        delete next.is_off_canvas;
        return next;
      };
      const isOffCanvasTarget = targetCanvas === "offCanvas";
      const normalized = forceOffCanvasFlags(base, isOffCanvasTarget);
      if (normalized.shape_properties) {
        normalized.shape_properties = forceOffCanvasFlags(
          normalized.shape_properties,
          isOffCanvasTarget,
        );
      }
      if (normalized.shape_info) {
        normalized.shape_info = forceOffCanvasFlags(
          normalized.shape_info,
          isOffCanvasTarget,
        );
      }
      if (normalized.image_properties) {
        normalized.image_properties = forceOffCanvasFlags(
          normalized.image_properties,
          isOffCanvasTarget,
        );
      }
      return normalized;
    };
    const deriveTextBoxContentFromPartsByType = (partsByType, textBoxId = null) => {
      if (!partsByType || typeof partsByType !== "object") return [];
      const derived = [];
      Object.values(partsByType).forEach((group) => {
        const parts = Array.isArray(group?.parts) ? group.parts : [];
        parts.forEach((partItem) => {
          if (!partItem?.part_ref) return;
          derived.push({
            part_ref: partItem.part_ref,
            inline_note: partItem.notePlainText || partItem.note || "",
            qty: parseInt(partItem.qty) || 0,
            ...(partItem.units ? { units: partItem.units } : {}),
            ...(textBoxId ? { text_box_id: textBoxId } : {}),
          });
        });
      });
      return derived;
    };
    let newItems = templatesToUse.map((template) => {
      // Generate new unique ID using generateMarkerId to ensure uniqueness
      const newId = generateMarkerId();

      // Calculate new position maintaining relative positioning within the group
      let templateOffsetX = template.relativeX || 0;
      let templateOffsetY = template.relativeY || 0;

      // Keep unified line/arrow offsets in template-relative coordinates.
      // IMPORTANT: do NOT replace with per-element geometry center. Bent polylines
      // can have asymmetric local bounds, which makes clones jump inconsistently.

      let newX = resolvedCloneX + templateOffsetX - groupCenterX;
      let newY = resolvedCloneY + templateOffsetY - groupCenterY;

      // Copy before mutating — `cloneTemplate` / `cloneTemplates[]` are React state; in-place
      // writes (e.g. converting points to relative) were mutating the stored template so the
      // next placement cloned the last clone's geometry instead of the original.
      const { id, ...templateRest } = template;
      const cleanTemplateRaw = {
        ...templateRest,
        ...(Array.isArray(templateRest.points)
          ? { points: [...templateRest.points] }
          : {}),
        ...(Array.isArray(templateRest._presetBasePoints)
          ? { _presetBasePoints: [...templateRest._presetBasePoints] }
          : {}),
      };
      const cleanTemplate = normalizeCloneForTargetCanvas(cleanTemplateRaw);

      // CRITICAL: Normalize unified line/arrow flags.
      // We support both new flags (hasStartArrow/hasEndArrow) and legacy flags
      // (pointerAtBeginning/pointerAtEnd/pointerAtEnding). If these disagree, cloning can
      // resurrect arrowheads even when user unchecked them in the editor.
      const normalizedHasStartArrow =
        cleanTemplate.hasStartArrow === true ||
        cleanTemplate.pointerAtBeginning === true;
      const normalizedHasEndArrow =
        cleanTemplate.hasEndArrow === true ||
        cleanTemplate.pointerAtEnd === true ||
        cleanTemplate.pointerAtEnding === true;
      // Always write both representations so downstream code stays consistent.
      cleanTemplate.hasStartArrow = normalizedHasStartArrow;
      cleanTemplate.hasEndArrow = normalizedHasEndArrow;
      cleanTemplate.pointerAtBeginning = normalizedHasStartArrow;
      cleanTemplate.pointerAtEnd = normalizedHasEndArrow;
      cleanTemplate.pointerAtEnding = normalizedHasEndArrow;

      // CRITICAL: For unified line-arrow elements (elements with points array), convert absolute points to relative
      // The template points might be absolute coordinates (from selectedObject), but they need to be relative
      // to the new element's x, y position
      // Check for unified elements: those with points array (could be Line, Arrow, or DoubleSidedArrow type)
      const isUnifiedElementForPoints =
        cleanTemplate.points &&
        Array.isArray(cleanTemplate.points) &&
        cleanTemplate.points.length >= 4 &&
        (cleanTemplate.type === "Line" ||
          cleanTemplate.hasStartArrow !== undefined ||
          cleanTemplate.hasEndArrow !== undefined);

      if (isUnifiedElementForPoints) {
        // Use originalX/Y if available (stored when template was created), otherwise use template.x/y
        // template.x/y should contain the original position since we haven't modified the template object yet
        const templateX =
          template.originalX !== undefined
            ? template.originalX
            : template.x !== undefined
              ? template.x
              : 0;
        const templateY =
          template.originalY !== undefined
            ? template.originalY
            : template.y !== undefined
              ? template.y
              : 0;
        const templatePoints = cleanTemplate.points;

        // Check if points are absolute by comparing first point with template position
        // If the first point is very close to templateX/Y, points are definitely absolute
        const firstPointX = templatePoints[0] || 0;
        const firstPointY = templatePoints[1] || 0;
        const tolerance = 1.0;
        const firstPointMatchesPosition =
          templateX !== undefined &&
          templateY !== undefined &&
          Math.abs(firstPointX - templateX) < tolerance &&
          Math.abs(firstPointY - templateY) < tolerance;

        // CRITICAL: Convert absolute points to relative if:
        // 1. First point matches template position (clear sign of absolute coordinates)
        // NOTE: Do not infer absoluteness from "large" values; bent polylines can
        // legitimately have large/negative relative coordinates.
        const shouldConvert =
          firstPointMatchesPosition &&
          templateX !== undefined &&
          templateY !== undefined;

        if (shouldConvert) {
          // Convert absolute points to relative by subtracting template position
          cleanTemplate.points = templatePoints.map((coord, i) =>
            i % 2 === 0 ? coord - templateX : coord - templateY,
          );
        } else {
          // Points are already relative, log for debugging
        }
      }

      // Create new item with all preserved properties
      // IMPORTANT: Explicitly set id after spreading clean template to ensure it's always unique
      const newItem = {
        ...cleanTemplate,
        id: newId, // Always set a fresh unique ID
        x: newX,
        y: newY,
        isNew: false,
        isSelected: false,
        isHighlighted: false,
      };
      if (normalizeShapeType(newItem.type) === "Text") {
        // Duplicate text should remain a user-created text and keep payload data.
        newItem.createdOnCanvas = true;
        if (
          (!Array.isArray(newItem.text_box_content_data) ||
            newItem.text_box_content_data.length === 0) &&
          Array.isArray(cleanTemplate.text_box_content_data) &&
          cleanTemplate.text_box_content_data.length > 0
        ) {
          newItem.text_box_content_data = cleanTemplate.text_box_content_data;
        }
        if (
          (newItem.partsByType == null || typeof newItem.partsByType !== "object") &&
          cleanTemplate.partsByType &&
          typeof cleanTemplate.partsByType === "object"
        ) {
          newItem.partsByType = cleanTemplate.partsByType;
        }
        if (
          (!Array.isArray(newItem.text_box_content_data) ||
            newItem.text_box_content_data.length === 0) &&
          newItem.partsByType &&
          typeof newItem.partsByType === "object"
        ) {
          newItem.text_box_content_data = deriveTextBoxContentFromPartsByType(
            newItem.partsByType,
            newItem.text_box_id ||
              newItem.textboxId ||
              newItem.textBoxId ||
              newItem.canvas_text_box_id ||
              newItem.canvasTextBoxId ||
              null,
          );
        }
        if (
          !newItem?.selectedContent?.part_ref &&
          Array.isArray(newItem.text_box_content_data) &&
          newItem.text_box_content_data.length > 0
        ) {
          const firstPartRef = newItem.text_box_content_data[0]?.part_ref || null;
          if (firstPartRef) {
            newItem.selectedContent = {
              ...(newItem.selectedContent || {}),
              type: "part",
              part_ref: firstPartRef,
            };
          }
        }
      }

      // Remove temporary properties
      delete newItem.relativeX;
      delete newItem.relativeY;
      delete newItem.originalX;
      delete newItem.originalY;

      const stageConstrained = isMainMultiCloneGroup
        ? newItem
        : constrainInsideMainStage(newItem);
      // CRITICAL FIX: Skip individual image clamping for multi-clone groups
      // Group clamping will handle the positioning to maintain relative arrangement
      const isOffCanvasMultiCloneGroup = targetCanvas === "offCanvas" && templatesToUse.length > 1;
      return (isMainMultiCloneGroup || isOffCanvasMultiCloneGroup) ? stageConstrained : constrainInsideOffCanvasStage(stageConstrained);
    });

    // Multi-clone needs group clamp (blue selection box), not only per-item clamp.
    // Per-item clamp can still let combined bounds cross the stage.
    if (isMainMultiCloneGroup && newItems.length > 1) {
      logCloneGroupSnapshot("MAIN-BEFORE-CLAMP", newItems);
      const { width: stageWidth, height: stageHeight } = getMainStageDimensions();
      let groupMinX = Infinity;
      let groupMinY = Infinity;
      let groupMaxX = -Infinity;
      let groupMaxY = -Infinity;
      newItems.forEach((item) => {
        const b = getItemBounds(item);
        groupMinX = Math.min(groupMinX, b.minX);
        groupMinY = Math.min(groupMinY, b.minY);
        groupMaxX = Math.max(groupMaxX, b.maxX);
        groupMaxY = Math.max(groupMaxY, b.maxY);
      });
      if (
        Number.isFinite(groupMinX) &&
        Number.isFinite(groupMinY) &&
        Number.isFinite(groupMaxX) &&
        Number.isFinite(groupMaxY)
      ) {
        // Keep a tiny safety inset to avoid float/render rounding showing the box slightly outside.
        const GROUP_CLAMP_MARGIN_PX = TRANSFORMER_ANCHOR_BUFFER_PX;
        const EPS = 0.001;
        const minAllowedX = GROUP_CLAMP_MARGIN_PX;
        const minAllowedY = GROUP_CLAMP_MARGIN_PX;
        const maxAllowedX = stageWidth - GROUP_CLAMP_MARGIN_PX;
        const maxAllowedY = stageHeight - GROUP_CLAMP_MARGIN_PX;
        let shiftX = 0;
        let shiftY = 0;
        if (groupMinX < minAllowedX + EPS) shiftX = minAllowedX - groupMinX;
        else if (groupMaxX > maxAllowedX - EPS) shiftX = maxAllowedX - groupMaxX;
        if (groupMinY < minAllowedY + EPS) shiftY = minAllowedY - groupMinY;
        else if (groupMaxY > maxAllowedY - EPS) shiftY = maxAllowedY - groupMaxY;
        if (shiftX !== 0 || shiftY !== 0) {
          newItems = newItems.map((item) => ({
            ...item,
            x: (Number(item.x) || 0) + shiftX,
            y: (Number(item.y) || 0) + shiftY,
          }));
        }
        if (DEBUG_CLONE_GROUP_ALIGNMENT) {
          console.log("[CLONE-GROUP][MAIN-CLAMP-SHIFT]", {
            stageWidth,
            stageHeight,
            groupMinX,
            groupMinY,
            groupMaxX,
            groupMaxY,
            shiftX,
            shiftY,
          });
        }
      }
      logCloneGroupSnapshot("MAIN-AFTER-CLAMP", newItems);
    }

    // Off-canvas clone clamp for selectedObjects group and single clones.
    // Keep the whole cloned group inside off-canvas stage bounds and out of the
    // main-column overlay band (the hidden area behind main canvas).
    if (targetCanvas === "offCanvas" && newItems.length > 0) {
      logCloneGroupSnapshot("OFFCANVAS-BEFORE-CLAMP", newItems);
      const offStage = offCanvasStageRef?.current;
      const offStageWidth = offStage?.width?.() || getOffCanvasStageWidthPx();
      const offStageHeight = offStage?.height?.() || canvasStageHeightPx;

      let groupMinX = Infinity;
      let groupMinY = Infinity;
      let groupMaxX = -Infinity;
      let groupMaxY = -Infinity;
      newItems.forEach((item) => {
        const b = getItemBounds(item);
        groupMinX = Math.min(groupMinX, b.minX);
        groupMinY = Math.min(groupMinY, b.minY);
        groupMaxX = Math.max(groupMaxX, b.maxX);
        groupMaxY = Math.max(groupMaxY, b.maxY);
      });

      if (
        Number.isFinite(groupMinX) &&
        Number.isFinite(groupMinY) &&
        Number.isFinite(groupMaxX) &&
        Number.isFinite(groupMaxY) &&
        offStageWidth > 0 &&
        offStageHeight > 0
      ) {
        const groupWidth = Math.max(0, groupMaxX - groupMinX);
        const groupHeight = Math.max(0, groupMaxY - groupMinY);

        const clampedGroupMinX = clampOffCanvasTopLeftXAwayFromMainColumn(
          groupMinX,
          groupWidth,
          offStageWidth,
          TRANSFORMER_ANCHOR_BUFFER_PX,
        );
        const maxGroupY = Math.max(
          TRANSFORMER_ANCHOR_BUFFER_PX,
          offStageHeight - groupHeight - TRANSFORMER_ANCHOR_BUFFER_PX,
        );
        const clampedGroupMinY = Math.max(
          TRANSFORMER_ANCHOR_BUFFER_PX,
          Math.min(maxGroupY, groupMinY),
        );

        const shiftX = clampedGroupMinX - groupMinX;
        const shiftY = clampedGroupMinY - groupMinY;

        if (shiftX !== 0 || shiftY !== 0) {
          newItems = newItems.map((item) => ({
            ...item,
            x: (Number(item.x) || 0) + shiftX,
            y: (Number(item.y) || 0) + shiftY,
          }));
        }
        if (DEBUG_CLONE_GROUP_ALIGNMENT) {
          console.log("[CLONE-GROUP][OFFCANVAS-CLAMP-SHIFT]", {
            offStageWidth,
            offStageHeight,
            groupMinX,
            groupMinY,
            groupMaxX,
            groupMaxY,
            groupWidth,
            groupHeight,
            clampedGroupMinX,
            clampedGroupMinY,
            shiftX,
            shiftY,
          });
        }
      }

      // Final image-level safety clamp after group shift.
      // Group clamp uses aggregate bounds; mixed selections can still leave image
      // center/top-left anchor slightly outside off-canvas strip due geometry differences.
      // Re-apply off-canvas image clamp here to guarantee image placement is valid.
      newItems = newItems.map((item) => {
        if (normalizeShapeType(item?.type) !== "Image") {
          return item;
        }
        const clamped = constrainInsideOffCanvasStage(item);
        if (
          (Number(clamped?.x) || 0) !== (Number(item?.x) || 0) ||
          (Number(clamped?.y) || 0) !== (Number(item?.y) || 0)
        ) {
          console.log("[GROUP-CLONE][OFFCANVAS][IMAGE-RECLAMP]", {
            id: item?.id,
            before: { x: item?.x, y: item?.y },
            after: { x: clamped?.x, y: clamped?.y },
            width: item?.width,
            height: item?.height,
            useTopLeftAnchor: item?.useTopLeftAnchor === true,
          });
        }
        return clamped;
      });
      logCloneGroupSnapshot("OFFCANVAS-AFTER-CLAMP", newItems);
    }
    logCloneGroupSnapshot("FINAL-BEFORE-SETTERS", newItems);

    // Helper function to generate unique name by checking existing names
    // Uses the same logic as pointerHandlers.js generateUniqueNameForClone
    const generateUniqueName = (
      baseName,
      existingNames,
      pendingNames = new Set(),
    ) => {
      // Extract base name and number pattern (e.g., "Rectangle 2" -> "Rectangle", 2)
      const nameMatch = baseName.match(/^(.+?)\s+(\d+)$/);
      const base = nameMatch ? nameMatch[1] : baseName;

      // When cloning, we ALWAYS generate a new name by finding the highest existing number
      // Start from 0 to ensure we find the true maximum across all existing and pending names
      // This matches the logic in pointerHandlers.js generateUniqueNameForClone
      let maxNumber = 0;

      // Check existing names
      existingNames.forEach((existingName) => {
        const existingMatch = existingName.match(
          new RegExp(
            `^${base.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\s+(\\d+)$`,
          ),
        );
        if (existingMatch) {
          const num = parseInt(existingMatch[1]);
          if (num > maxNumber) {
            maxNumber = num;
          }
        }
      });

      // Check pending names
      pendingNames.forEach((pendingName) => {
        const pendingMatch = pendingName.match(
          new RegExp(
            `^${base.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\s+(\\d+)$`,
          ),
        );
        if (pendingMatch) {
          const num = parseInt(pendingMatch[1]);
          if (num > maxNumber) {
            maxNumber = num;
          }
        }
      });

      // Use the next number after the highest existing number
      // This ensures we never reuse an existing name, even if the base name doesn't exist in the set
      // This matches the logic in pointerHandlers.js: nextNumber = maxNumber + 1
      const nextNumber = maxNumber + 1;
      const finalName = `${base} ${nextNumber}`;
      return finalName;
    };

    // Helper function to ensure name is unique in current state (used as final check in setters)
    // This function checks if the name exists, and if it does, finds the next available number
    const ensureUniqueName = (name, existingNames) => {
      // If name doesn't exist, use it as-is
      if (!existingNames.has(name)) {
        return name;
      }

      // Name exists, find the highest number for this base name and increment
      const nameMatch = name.match(/^(.+?)\s+(\d+)$/);
      const base = nameMatch ? nameMatch[1] : name;

      // Find the highest number for this base name in existing names
      let maxNumber = 0;
      existingNames.forEach((existingName) => {
        const existingMatch = existingName.match(
          new RegExp(
            `^${base.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\s+(\\d+)$`,
          ),
        );
        if (existingMatch) {
          const num = parseInt(existingMatch[1]);
          if (num > maxNumber) {
            maxNumber = num;
          }
        }
      });

      // Use the next number after the highest existing number
      const nextNumber = maxNumber + 1;
      const finalName = `${base} ${nextNumber}`;

      return finalName;
    };

    // Get current state to check existing names (use current state values, not prev in setters)
    // For unified elements (Arrow, DoubleSidedArrow stored in lines array), include them in the name check
    const getCurrentNamesForType = (elementType, targetCanvas) => {
      if (targetCanvas === "main") {
        switch (elementType) {
          case "Rect":
            return new Set(rectangles.map((r) => r.name));
          case "Circle":
            return new Set(circles.map((c) => c.name));
          case "Image":
            return new Set(images.map((i) => i.name));
          case "Arrow":
            // Unified arrows are stored in lines array, so check both arrows and lines arrays
            const mainArrowNames = new Set(arrows.map((a) => a.name));
            lines.forEach((l) => {
              const hasStartArrow =
                l.hasStartArrow === true || l.pointerAtBeginning === true;
              const hasEndArrow =
                l.hasEndArrow === true || l.pointerAtEnd === true;
              // If it's a unified arrow (has one arrow head), include its name
              if (
                (hasStartArrow || hasEndArrow) &&
                !(hasStartArrow && hasEndArrow)
              ) {
                if (l.name) mainArrowNames.add(l.name);
              }
            });
            return mainArrowNames;
          case "DoubleSidedArrow":
            // Unified double-sided arrows are stored in lines array, so check both arrays
            const mainDoubleArrowNames = new Set(
              doubleSidedArrows.map((a) => a.name),
            );
            lines.forEach((l) => {
              const hasStartArrow =
                l.hasStartArrow === true || l.pointerAtBeginning === true;
              const hasEndArrow =
                l.hasEndArrow === true || l.pointerAtEnd === true;
              // If it's a unified double-sided arrow (has both arrow heads), include its name
              if (hasStartArrow && hasEndArrow) {
                if (l.name) mainDoubleArrowNames.add(l.name);
              }
            });
            return mainDoubleArrowNames;
          case "Line":
            // Lines without arrow heads (pure lines)
            return new Set(
              lines
                .filter((l) => {
                  const hasStartArrow =
                    l.hasStartArrow === true || l.pointerAtBeginning === true;
                  const hasEndArrow =
                    l.hasEndArrow === true || l.pointerAtEnd === true;
                  return !hasStartArrow && !hasEndArrow;
                })
                .map((l) => l.name),
            );
          case "Text":
            return new Set(texts.map((t) => t.name));
          default:
            return new Set();
        }
      } else {
        switch (elementType) {
          case "Rect":
            return new Set(offCanvasRectangles.map((r) => r.name));
          case "Circle":
            return new Set(offCanvasCircles.map((c) => c.name));
          case "Image":
            return new Set(offCanvasImages.map((i) => i.name));
          case "Arrow":
            // Unified arrows are stored in offCanvasLines array, so check both arrays
            const offArrowNames = new Set(offCanvasArrows.map((a) => a.name));
            offCanvasLines.forEach((l) => {
              const hasStartArrow =
                l.hasStartArrow === true || l.pointerAtBeginning === true;
              const hasEndArrow =
                l.hasEndArrow === true || l.pointerAtEnd === true;
              // If it's a unified arrow (has one arrow head), include its name
              if (
                (hasStartArrow || hasEndArrow) &&
                !(hasStartArrow && hasEndArrow)
              ) {
                if (l.name) offArrowNames.add(l.name);
              }
            });
            return offArrowNames;
          case "DoubleSidedArrow":
            // Unified double-sided arrows are stored in offCanvasLines array, so check both arrays
            const offDoubleArrowNames = new Set(
              offCanvasDoubleSidedArrows.map((a) => a.name),
            );
            offCanvasLines.forEach((l) => {
              const hasStartArrow =
                l.hasStartArrow === true || l.pointerAtBeginning === true;
              const hasEndArrow =
                l.hasEndArrow === true || l.pointerAtEnd === true;
              // If it's a unified double-sided arrow (has both arrow heads), include its name
              if (hasStartArrow && hasEndArrow) {
                if (l.name) offDoubleArrowNames.add(l.name);
              }
            });
            return offDoubleArrowNames;
          case "Line":
            // Lines without arrow heads (pure lines)
            return new Set(
              offCanvasLines
                .filter((l) => {
                  const hasStartArrow =
                    l.hasStartArrow === true || l.pointerAtBeginning === true;
                  const hasEndArrow =
                    l.hasEndArrow === true || l.pointerAtEnd === true;
                  return !hasStartArrow && !hasEndArrow;
                })
                .map((l) => l.name),
            );
          case "Text":
            return new Set(offCanvasTexts.map((t) => t.name));
          default:
            return new Set();
        }
      }
    };

    // Track pending names for each type to avoid duplicates within the same batch
    const pendingNamesByType = new Map();

    // STEP 1: Generate all unique names synchronously BEFORE any state updates
    const itemsWithNames = newItems.map((item) => {
      const elementType = normalizeShapeType(item.type);

      // Initialize pending names set for this type if not exists
      if (!pendingNamesByType.has(elementType)) {
        pendingNamesByType.set(elementType, new Set());
      }
      const pendingNames = pendingNamesByType.get(elementType);

      // Get existing names from current state
      const existingNames = getCurrentNamesForType(elementType, targetCanvas);

      // Determine base name based on element type and properties
      let baseName = item.name;
      if (!baseName) {
        // Generate default base name based on type
        if (elementType === "Rect") {
          baseName = "Rectangle 1";
        } else if (elementType === "Circle") {
          baseName = "Circle 1";
        } else if (elementType === "Image") {
          baseName = "Image 1";
        } else if (elementType === "Arrow") {
          baseName = "Arrow 1";
        } else if (elementType === "DoubleSidedArrow") {
          baseName = "Double Sided Arrow 1";
        } else if (elementType === "Line") {
          // Check if it's a unified element (has arrow properties)
          const normalizedHasStartArrow =
            item.hasStartArrow === true || item.pointerAtBeginning === true;
          const normalizedHasEndArrow =
            item.hasEndArrow === true ||
            item.pointerAtEnd === true ||
            item.pointerAtEnding === true;

          if (normalizedHasStartArrow && normalizedHasEndArrow) {
            baseName = "Double Sided Arrow 1";
          } else if (normalizedHasStartArrow || normalizedHasEndArrow) {
            baseName = "Arrow 1";
          } else {
            baseName = "Stroke 1";
          }
        } else if (elementType === "Text") {
          baseName = "Text 1";
        } else {
          baseName = "Element 1";
        }
      } else {
        // If name exists but doesn't match the type, adjust it
        if (elementType === "Line") {
          const normalizedHasStartArrow =
            item.hasStartArrow === true || item.pointerAtBeginning === true;
          const normalizedHasEndArrow =
            item.hasEndArrow === true ||
            item.pointerAtEnd === true ||
            item.pointerAtEnding === true;

          if (normalizedHasStartArrow && normalizedHasEndArrow) {
            if (!baseName.includes("Double Sided Arrow")) {
              baseName = "Double Sided Arrow 1";
            }
          } else if (normalizedHasStartArrow || normalizedHasEndArrow) {
            if (
              !baseName.includes("Arrow") &&
              !baseName.includes("Double Sided Arrow")
            ) {
              baseName = "Arrow 1";
            }
          } else {
            if (!baseName.includes("Stroke")) {
              baseName = "Stroke 1";
            }
          }
        }
      }

      // Generate unique name
      const uniqueName = generateUniqueName(
        baseName,
        existingNames,
        pendingNames,
      );
      pendingNames.add(uniqueName); // Track this name so next item of same type gets a different name

      return {
        ...item,
        name: uniqueName,
      };
    });

    // STEP 2: Now add elements to state with pre-generated unique names
    itemsWithNames.forEach((item) => {
      // CRITICAL: Normalize element type to handle variations like "rect", "rectangle", "Rect", "img", "image", etc.
      const elementType = normalizeShapeType(item.type);

      if (targetCanvas === "main") {
        // Add to main canvas arrays
        if (elementType === "Image") {
          setImages((prev) => {
            // CRITICAL: Check if ID already exists to prevent duplicates
            const idExists = prev.some((img) => img.id === item.id);
            if (idExists) {
              console.error(
                "❌ [CLONE] Duplicate image ID detected, generating new ID",
                {
                  duplicateId: item.id,
                  existingIds: prev.map((i) => i.id).slice(-5),
                },
              );
              item.id = generateMarkerId();
            }
            // Double-check name uniqueness in case state has changed since name generation
            const existingNames = new Set(prev.map((el) => el.name));
            const finalName = ensureUniqueName(item.name, existingNames);
            const newElement = {
              ...item,
              name: finalName, // Use final unique name
            };
            return [...prev, newElement];
          });
        } else if (elementType === "Rect") {
          setRectangles((prev) => {
            // CRITICAL: Check if ID already exists to prevent duplicates
            const idExists = prev.some((rect) => rect.id === item.id);
            if (idExists) {
              console.error(
                "❌ [CLONE] Duplicate rectangle ID detected, generating new ID",
                {
                  duplicateId: item.id,
                  existingIds: prev.map((r) => r.id).slice(-5),
                },
              );
              // Generate a new ID if duplicate is found
              item.id = generateMarkerId();
            }
            // Double-check name uniqueness in case state has changed since name generation
            const existingNames = new Set(prev.map((r) => r.name));
            const finalName = ensureUniqueName(item.name, existingNames);
            const newElement = {
              ...item,
              name: finalName, // Use final unique name
            };
            return [...prev, newElement];
          });
        } else if (elementType === "Circle") {
          setCircles((prev) => {
            // CRITICAL: Check if ID already exists to prevent duplicates
            const idExists = prev.some((circle) => circle.id === item.id);
            if (idExists) {
              console.error(
                "❌ [CLONE] Duplicate circle ID detected, generating new ID",
                {
                  duplicateId: item.id,
                  existingIds: prev.map((c) => c.id).slice(-5),
                },
              );
              item.id = generateMarkerId();
            }
            // Double-check name uniqueness in case state has changed since name generation
            const existingNames = new Set(prev.map((c) => c.name));
            const finalName = ensureUniqueName(item.name, existingNames);
            const newElement = {
              ...item,
              name: finalName, // Use final unique name
            };
            return [...prev, newElement];
          });
        } else if (elementType === "Arrow") {
          // CRITICAL: Check where the original arrow is stored
          // Find the template that matches this item to determine source array
          const matchingTemplate = templatesToUse.find(
            (t) =>
              t.id === item.id || (item.originalId && t.id === item.originalId),
          );

          // Check if original exists in arrows array (legacy)
          const originalInArrows = matchingTemplate
            ? arrows.some((a) => a.id === matchingTemplate.id)
            : arrows.some((a) => a.id === item.id);

          // Check if original exists in lines array (unified)
          const originalInLines = matchingTemplate
            ? lines.some((l) => {
              if (l.id !== matchingTemplate.id) return false;
              const isArrow =
                l.hasStartArrow === true ||
                l.hasEndArrow === true ||
                l.pointerAtBeginning === true ||
                l.pointerAtEnd === true ||
                l.pointerAtEnding === true;
              return isArrow;
            })
            : lines.some((l) => {
              if (l.id !== item.id) return false;
              const isArrow =
                l.hasStartArrow === true ||
                l.hasEndArrow === true ||
                l.pointerAtBeginning === true ||
                l.pointerAtEnd === true ||
                l.pointerAtEnding === true;
              return isArrow;
            });

          // If original is in arrows array, clone to arrows array
          // Otherwise, if it's in lines array or has arrow properties, clone to lines array
          if (originalInArrows && !originalInLines) {
            // Clone to arrows array (legacy)
            setArrows((prev) => {
              const idExists = prev.some((arrow) => arrow.id === item.id);
              if (idExists) {
                console.error(
                  "❌ [CLONE] Duplicate arrow ID detected, generating new ID",
                  {
                    duplicateId: item.id,
                    existingIds: prev.map((a) => a.id).slice(-5),
                  },
                );
                item.id = generateMarkerId();
              }
              const existingNames = new Set(prev.map((el) => el.name));
              const finalName = ensureUniqueName(item.name, existingNames);
              const newElement = {
                ...item,
                name: finalName,
              };
              return [...prev, newElement];
            });
          } else {
            // Clone to lines array (unified)
            setLines((prev) => {
              const idExists = prev.some((line) => line.id === item.id);
              if (idExists) {
                console.error(
                  "❌ [CLONE] Duplicate line ID detected, generating new ID",
                  {
                    duplicateId: item.id,
                    existingIds: prev.map((l) => l.id).slice(-5),
                  },
                );
                item.id = generateMarkerId();
              }
              const existingNames = new Set(
                prev
                  .filter(
                    (l) =>
                      l.hasStartArrow === true ||
                      l.hasEndArrow === true ||
                      l.pointerAtBeginning === true ||
                      l.pointerAtEnd === true ||
                      l.pointerAtEnding === true,
                  )
                  .map((l) => l.name),
              );
              const finalName = ensureUniqueName(item.name, existingNames);
              const newElement = {
                ...item,
                name: finalName,
              };
              return [...prev, newElement];
            });
          }
        } else if (elementType === "DoubleSidedArrow") {
          // CRITICAL FIX: Check if this is a unified element (has hasStartArrow/hasEndArrow properties)
          // Unified double-sided arrows should be added to lines array, not doubleSidedArrows array
          // This matches the behavior of single-sided arrows
          const normalizedHasStartArrow =
            item.hasStartArrow === true || item.pointerAtBeginning === true;
          const normalizedHasEndArrow =
            item.hasEndArrow === true ||
            item.pointerAtEnd === true ||
            item.pointerAtEnding === true;
          const isUnifiedElement =
            normalizedHasStartArrow && normalizedHasEndArrow;

          // Check if original exists in doubleSidedArrows array (legacy)
          const matchingTemplate = templatesToUse.find(
            (t) =>
              t.id === item.id || (item.originalId && t.id === item.originalId),
          );
          const originalInDoubleSidedArrows = matchingTemplate
            ? doubleSidedArrows.some((a) => a.id === matchingTemplate.id)
            : doubleSidedArrows.some((a) => a.id === item.id);

          // Check if original exists in lines array (unified)
          const originalInLines = matchingTemplate
            ? lines.some((l) => {
              if (l.id !== matchingTemplate.id) return false;
              const hasStartArrow =
                l.hasStartArrow === true || l.pointerAtBeginning === true;
              const hasEndArrow =
                l.hasEndArrow === true ||
                l.pointerAtEnd === true ||
                l.pointerAtEnding === true;
              return hasStartArrow && hasEndArrow;
            })
            : lines.some((l) => {
              if (l.id !== item.id) return false;
              const hasStartArrow =
                l.hasStartArrow === true || l.pointerAtBeginning === true;
              const hasEndArrow =
                l.hasEndArrow === true ||
                l.pointerAtEnd === true ||
                l.pointerAtEnding === true;
              return hasStartArrow && hasEndArrow;
            });

          // If original is in doubleSidedArrows array (legacy) and NOT in lines array, clone to doubleSidedArrows
          // Otherwise, if it's a unified element or in lines array, clone to lines array
          if (
            originalInDoubleSidedArrows &&
            !originalInLines &&
            !isUnifiedElement
          ) {
            // Clone to doubleSidedArrows array (legacy)
            setDoubleSidedArrows((prev) => {
              const idExists = prev.some((arrow) => arrow.id === item.id);
              if (idExists) {
                console.error(
                  "❌ [CLONE] Duplicate double-sided arrow ID detected, generating new ID",
                  {
                    duplicateId: item.id,
                    existingIds: prev.map((a) => a.id).slice(-5),
                  },
                );
                item.id = generateMarkerId();
              }
              const existingNames = new Set(prev.map((el) => el.name));
              const finalName = ensureUniqueName(item.name, existingNames);
              const newElement = {
                ...item,
                name: finalName,
              };
              return [...prev, newElement];
            });
          } else {
            // Clone to lines array (unified) - same logic as single-sided arrows
            setLines((prev) => {
              const idExists = prev.some((line) => line.id === item.id);
              if (idExists) {
                console.error(
                  "❌ [CLONE] Duplicate line ID detected, generating new ID",
                  {
                    duplicateId: item.id,
                    existingIds: prev.map((l) => l.id).slice(-5),
                  },
                );
                item.id = generateMarkerId();
              }
              // Check if it's a unified element (has arrow properties)
              // Normalize arrow properties: prioritize new properties, fallback to legacy
              const normalizedHasStartArrow =
                item.hasStartArrow === true || item.pointerAtBeginning === true;
              const normalizedHasEndArrow =
                item.hasEndArrow === true ||
                item.pointerAtEnd === true ||
                item.pointerAtEnding === true;

              // Validate and normalize points array
              let normalizedPoints = item.points || [0, 0, 100, 0];
              if (
                !Array.isArray(normalizedPoints) ||
                normalizedPoints.length < 4
              ) {
                normalizedPoints = [0, 0, 100, 0];
              }

              // Check if start and end points are at the same location (invalid)
              const startX = normalizedPoints[0] || 0;
              const startY = normalizedPoints[1] || 0;
              const endX = normalizedPoints[normalizedPoints.length - 2] || 0;
              const endY = normalizedPoints[normalizedPoints.length - 1] || 0;

              if (startX === endX && startY === endY) {
                const defaultWidth = item.width || 100;
                normalizedPoints = [0, 0, defaultWidth, 0];
              }

              // Calculate width and height from normalized points
              let minX = Infinity,
                minY = Infinity,
                maxX = -Infinity,
                maxY = -Infinity;
              for (let i = 0; i < normalizedPoints.length; i += 2) {
                minX = Math.min(minX, normalizedPoints[i]);
                maxX = Math.max(maxX, normalizedPoints[i]);
                minY = Math.min(minY, normalizedPoints[i + 1]);
                maxY = Math.max(maxY, normalizedPoints[i + 1]);
              }
              const calculatedWidth = maxX - minX;
              const calculatedHeight = maxY - minY;

              // Create normalized element with only the new arrow properties
              const {
                pointerAtBeginning,
                pointerAtEnd,
                pointerAtEnding,
                ...itemWithoutLegacy
              } = item;
              const existingNames = new Set(
                prev
                  .filter(
                    (l) =>
                      l.hasStartArrow === true ||
                      l.hasEndArrow === true ||
                      l.pointerAtBeginning === true ||
                      l.pointerAtEnd === true ||
                      l.pointerAtEnding === true,
                  )
                  .map((l) => l.name),
              );
              const finalName = ensureUniqueName(item.name, existingNames);
              const newElement = {
                ...itemWithoutLegacy,
                name: finalName,
                hasStartArrow: normalizedHasStartArrow,
                hasEndArrow: normalizedHasEndArrow,
                points: normalizedPoints,
                width: calculatedWidth || item.width || 100,
                height: calculatedHeight || item.height || 0,
              };
              return [...prev, newElement];
            });
          }
        } else if (elementType === "Line") {
          setLines((prev) => {
            // CRITICAL: Check if ID already exists to prevent duplicates
            const idExists = prev.some((line) => line.id === item.id);
            if (idExists) {
              console.error(
                "❌ [CLONE] Duplicate line ID detected, generating new ID",
                {
                  duplicateId: item.id,
                  existingIds: prev.map((l) => l.id).slice(-5),
                },
              );
              item.id = generateMarkerId();
            }
            // Check if it's a unified element (has arrow properties)
            // Normalize arrow properties: prioritize new properties, fallback to legacy
            const normalizedHasStartArrow =
              item.hasStartArrow === true || item.pointerAtBeginning === true;
            const normalizedHasEndArrow =
              item.hasEndArrow === true ||
              item.pointerAtEnd === true ||
              item.pointerAtEnding === true;

            // Name is already generated uniquely in itemsWithNames

            // Validate and normalize points array
            // Points should be relative to the element's x, y position
            let normalizedPoints = item.points || [0, 0, 100, 0];

            // Ensure points array is valid (has at least 4 coordinates)
            if (
              !Array.isArray(normalizedPoints) ||
              normalizedPoints.length < 4
            ) {
              normalizedPoints = [0, 0, 100, 0];
            }

            // Check if start and end points are at the same location (invalid)
            const startX = normalizedPoints[0] || 0;
            const startY = normalizedPoints[1] || 0;
            const endX = normalizedPoints[normalizedPoints.length - 2] || 0;
            const endY = normalizedPoints[normalizedPoints.length - 1] || 0;

            // If start and end are at the same location, use default points
            if (startX === endX && startY === endY) {
              // Use the original width/height if available, or default to 100
              const defaultWidth = item.width || 100;
              normalizedPoints = [0, 0, defaultWidth, 0];
            }

            // Calculate width and height from normalized points
            let minX = Infinity,
              minY = Infinity,
              maxX = -Infinity,
              maxY = -Infinity;
            for (let i = 0; i < normalizedPoints.length; i += 2) {
              minX = Math.min(minX, normalizedPoints[i]);
              maxX = Math.max(maxX, normalizedPoints[i]);
              minY = Math.min(minY, normalizedPoints[i + 1]);
              maxY = Math.max(maxY, normalizedPoints[i + 1]);
            }
            const calculatedWidth = maxX - minX;
            const calculatedHeight = maxY - minY;

            // Create normalized element with only the new arrow properties
            // Destructure to exclude legacy properties, then add normalized ones
            const {
              pointerAtBeginning,
              pointerAtEnd,
              pointerAtEnding,
              ...itemWithoutLegacy
            } = item;
            // Double-check name uniqueness in case state has changed since name generation
            const existingNames = new Set(prev.map((l) => l.name));
            const finalName = ensureUniqueName(item.name, existingNames);
            const newElement = {
              ...itemWithoutLegacy,
              name: finalName, // Use final unique name
              // Ensure arrow properties are properly set
              hasStartArrow: normalizedHasStartArrow,
              hasEndArrow: normalizedHasEndArrow,
              // Ensure points are valid and normalized
              points: normalizedPoints,
              // Recalculate width and height from normalized points
              width: calculatedWidth || item.width || 100,
              height: calculatedHeight || item.height || 0,
            };
            return [...prev, newElement];
          });
        } else if (elementType === "Text") {
          setTexts((prev) => {
            // CRITICAL: Check if ID already exists to prevent duplicates
            const idExists = prev.some((text) => text.id === item.id);
            if (idExists) {
              console.error(
                "❌ [CLONE] Duplicate text ID detected, generating new ID",
                {
                  duplicateId: item.id,
                  existingIds: prev.map((t) => t.id).slice(-5),
                },
              );
              item.id = generateMarkerId();
            }
            // Double-check name uniqueness in case state has changed since name generation
            const existingNames = new Set(prev.map((t) => t.name));
            const finalName = ensureUniqueName(item.name, existingNames);
            const newElement = {
              ...item,
              name: finalName, // Use final unique name
            };
            return [...prev, newElement];
          });
        }
      } else {
        // Add to off-canvas arrays
        if (elementType === "Image") {
          setOffCanvasImages((prev) => {
            // CRITICAL: Check if ID already exists to prevent duplicates
            const idExists = prev.some((img) => img.id === item.id);
            if (idExists) {
              console.error(
                "❌ [CLONE] Duplicate off-canvas image ID detected, generating new ID",
                {
                  duplicateId: item.id,
                  existingIds: prev.map((i) => i.id).slice(-5),
                },
              );
              item.id = generateMarkerId();
            }
            // Double-check name uniqueness in case state has changed since name generation
            const existingNames = new Set(prev.map((el) => el.name));
            const finalName = ensureUniqueName(item.name, existingNames);
            const newElement = {
              ...item,
              name: finalName, // Use final unique name
            };
            return [...prev, newElement];
          });
        } else if (elementType === "Rect") {
          setOffCanvasRectangles((prev) => {
            // CRITICAL: Check if ID already exists to prevent duplicates
            const idExists = prev.some((rect) => rect.id === item.id);
            if (idExists) {
              console.error(
                "❌ [CLONE] Duplicate off-canvas rectangle ID detected, generating new ID",
                {
                  duplicateId: item.id,
                  existingIds: prev.map((r) => r.id).slice(-5),
                },
              );
              item.id = generateMarkerId();
            }
            // Double-check name uniqueness in case state has changed since name generation
            const existingNames = new Set(prev.map((el) => el.name));
            const finalName = ensureUniqueName(item.name, existingNames);
            const newElement = {
              ...item,
              name: finalName, // Use final unique name
            };
            return [...prev, newElement];
          });
        } else if (elementType === "Circle") {
          setOffCanvasCircles((prev) => {
            // CRITICAL: Check if ID already exists to prevent duplicates
            const idExists = prev.some((circle) => circle.id === item.id);
            if (idExists) {
              console.error(
                "❌ [CLONE] Duplicate off-canvas circle ID detected, generating new ID",
                {
                  duplicateId: item.id,
                  existingIds: prev.map((c) => c.id).slice(-5),
                },
              );
              item.id = generateMarkerId();
            }
            // Double-check name uniqueness in case state has changed since name generation
            const existingNames = new Set(prev.map((i) => i.name));
            const finalName = ensureUniqueName(item.name, existingNames);
            const newElement = {
              ...item,
              name: finalName, // Use final unique name
            };
            return [...prev, newElement];
          });
        } else if (elementType === "Arrow") {
          // Route off-canvas arrow clones to the correct storage:
          // - legacy arrows -> offCanvasArrows
          // - unified arrows (line-based) -> offCanvasLines
          const matchingTemplate = templatesToUse.find(
            (t) =>
              t.id === item.id || (item.originalId && t.id === item.originalId),
          );

          const originalInOffCanvasArrows = matchingTemplate
            ? offCanvasArrows.some((a) => a.id === matchingTemplate.id)
            : offCanvasArrows.some((a) => a.id === item.id);

          const originalInOffCanvasLines = matchingTemplate
            ? offCanvasLines.some((l) => {
              if (l.id !== matchingTemplate.id) return false;
              const hasStartArrow =
                l.hasStartArrow === true || l.pointerAtBeginning === true;
              const hasEndArrow =
                l.hasEndArrow === true ||
                l.pointerAtEnd === true ||
                l.pointerAtEnding === true;
              return (hasStartArrow || hasEndArrow) && !(hasStartArrow && hasEndArrow);
            })
            : offCanvasLines.some((l) => {
              if (l.id !== item.id) return false;
              const hasStartArrow =
                l.hasStartArrow === true || l.pointerAtBeginning === true;
              const hasEndArrow =
                l.hasEndArrow === true ||
                l.pointerAtEnd === true ||
                l.pointerAtEnding === true;
              return (hasStartArrow || hasEndArrow) && !(hasStartArrow && hasEndArrow);
            });

          const normalizedHasStartArrow =
            item.hasStartArrow === true || item.pointerAtBeginning === true;
          const normalizedHasEndArrow =
            item.hasEndArrow === true ||
            item.pointerAtEnd === true ||
            item.pointerAtEnding === true;
          const isUnifiedElement =
            (normalizedHasStartArrow || normalizedHasEndArrow) &&
            !(normalizedHasStartArrow && normalizedHasEndArrow);

          if (
            originalInOffCanvasArrows &&
            !originalInOffCanvasLines &&
            !isUnifiedElement
          ) {
            // Legacy arrow clone path
            setOffCanvasArrows((prev) => {
              const idExists = prev.some((arrow) => arrow.id === item.id);
              if (idExists) {
                console.error(
                  "❌ [CLONE] Duplicate off-canvas arrow ID detected, generating new ID",
                  {
                    duplicateId: item.id,
                    existingIds: prev.map((a) => a.id).slice(-5),
                  },
                );
                item.id = generateMarkerId();
              }
              const existingNames = new Set(prev.map((el) => el.name));
              const finalName = ensureUniqueName(item.name, existingNames);
              const newElement = {
                ...item,
                name: finalName,
              };
              return [...prev, newElement];
            });
          } else {
            // Unified arrow clone path (store in offCanvasLines so selection box/renderer can find it)
            setOffCanvasLines((prev) => {
              const idExists = prev.some((line) => line.id === item.id);
              if (idExists) {
                console.error(
                  "❌ [CLONE] Duplicate off-canvas line ID detected, generating new ID",
                  {
                    duplicateId: item.id,
                    existingIds: prev.map((l) => l.id).slice(-5),
                  },
                );
                item.id = generateMarkerId();
              }

              const existingNames = new Set(
                prev
                  .filter((l) => {
                    const hasStartArrow =
                      l.hasStartArrow === true || l.pointerAtBeginning === true;
                    const hasEndArrow =
                      l.hasEndArrow === true ||
                      l.pointerAtEnd === true ||
                      l.pointerAtEnding === true;
                    return (hasStartArrow || hasEndArrow) && !(hasStartArrow && hasEndArrow);
                  })
                  .map((l) => l.name),
              );
              const finalName = ensureUniqueName(item.name, existingNames);

              let normalizedPoints = item.points || [0, 0, 100, 0];
              if (!Array.isArray(normalizedPoints) || normalizedPoints.length < 4) {
                normalizedPoints = [0, 0, 100, 0];
              }

              const startX = normalizedPoints[0] || 0;
              const startY = normalizedPoints[1] || 0;
              const endX = normalizedPoints[normalizedPoints.length - 2] || 0;
              const endY = normalizedPoints[normalizedPoints.length - 1] || 0;
              if (startX === endX && startY === endY) {
                const defaultWidth = item.width || 100;
                normalizedPoints = [0, 0, defaultWidth, 0];
              }

              let minX = Infinity,
                minY = Infinity,
                maxX = -Infinity,
                maxY = -Infinity;
              for (let i = 0; i < normalizedPoints.length; i += 2) {
                minX = Math.min(minX, normalizedPoints[i]);
                maxX = Math.max(maxX, normalizedPoints[i]);
                minY = Math.min(minY, normalizedPoints[i + 1]);
                maxY = Math.max(maxY, normalizedPoints[i + 1]);
              }
              const calculatedWidth = maxX - minX;
              const calculatedHeight = maxY - minY;

              const {
                pointerAtBeginning,
                pointerAtEnd,
                pointerAtEnding,
                ...itemWithoutLegacy
              } = item;

              const newElement = {
                ...itemWithoutLegacy,
                name: finalName,
                hasStartArrow: normalizedHasStartArrow,
                hasEndArrow: normalizedHasEndArrow,
                points: normalizedPoints,
                width: calculatedWidth || item.width || 100,
                height: calculatedHeight || item.height || 0,
              };
              return [...prev, newElement];
            });
          }
        } else if (elementType === "DoubleSidedArrow") {
          // CRITICAL FIX: Check if this is a unified element (has hasStartArrow/hasEndArrow properties)
          // Unified double-sided arrows should be added to offCanvasLines array, not offCanvasDoubleSidedArrows array
          // This matches the behavior of single-sided arrows
          const normalizedHasStartArrow =
            item.hasStartArrow === true || item.pointerAtBeginning === true;
          const normalizedHasEndArrow =
            item.hasEndArrow === true ||
            item.pointerAtEnd === true ||
            item.pointerAtEnding === true;
          const isUnifiedElement =
            normalizedHasStartArrow && normalizedHasEndArrow;

          // Check if original exists in offCanvasDoubleSidedArrows array (legacy)
          const matchingTemplate = templatesToUse.find(
            (t) =>
              t.id === item.id || (item.originalId && t.id === item.originalId),
          );
          const originalInOffCanvasDoubleSidedArrows = matchingTemplate
            ? offCanvasDoubleSidedArrows.some(
              (a) => a.id === matchingTemplate.id,
            )
            : offCanvasDoubleSidedArrows.some((a) => a.id === item.id);

          // Check if original exists in offCanvasLines array (unified)
          const originalInOffCanvasLines = matchingTemplate
            ? offCanvasLines.some((l) => {
              if (l.id !== matchingTemplate.id) return false;
              const hasStartArrow =
                l.hasStartArrow === true || l.pointerAtBeginning === true;
              const hasEndArrow =
                l.hasEndArrow === true ||
                l.pointerAtEnd === true ||
                l.pointerAtEnding === true;
              return hasStartArrow && hasEndArrow;
            })
            : offCanvasLines.some((l) => {
              if (l.id !== item.id) return false;
              const hasStartArrow =
                l.hasStartArrow === true || l.pointerAtBeginning === true;
              const hasEndArrow =
                l.hasEndArrow === true ||
                l.pointerAtEnd === true ||
                l.pointerAtEnding === true;
              return hasStartArrow && hasEndArrow;
            });

          // If original is in offCanvasDoubleSidedArrows array (legacy) and NOT in offCanvasLines array, clone to offCanvasDoubleSidedArrows
          // Otherwise, if it's a unified element or in offCanvasLines array, clone to offCanvasLines array
          if (
            originalInOffCanvasDoubleSidedArrows &&
            !originalInOffCanvasLines &&
            !isUnifiedElement
          ) {
            // Clone to offCanvasDoubleSidedArrows array (legacy)
            setOffCanvasDoubleSidedArrows((prev) => {
              const idExists = prev.some((arrow) => arrow.id === item.id);
              if (idExists) {
                console.error(
                  "❌ [CLONE] Duplicate off-canvas double-sided arrow ID detected, generating new ID",
                  {
                    duplicateId: item.id,
                    existingIds: prev.map((a) => a.id).slice(-5),
                  },
                );
                item.id = generateMarkerId();
              }
              const existingNames = new Set(prev.map((el) => el.name));
              const finalName = ensureUniqueName(item.name, existingNames);
              const newElement = {
                ...item,
                name: finalName,
              };
              return [...prev, newElement];
            });
          } else {
            // Clone to offCanvasLines array (unified) - same logic as single-sided arrows
            setOffCanvasLines((prev) => {
              const idExists = prev.some((line) => line.id === item.id);
              if (idExists) {
                console.error(
                  "❌ [CLONE] Duplicate off-canvas line ID detected, generating new ID",
                  {
                    duplicateId: item.id,
                    existingIds: prev.map((l) => l.id).slice(-5),
                  },
                );
                item.id = generateMarkerId();
              }
              // Check if it's a unified element (has arrow properties)
              const normalizedHasStartArrow =
                item.hasStartArrow === true || item.pointerAtBeginning === true;
              const normalizedHasEndArrow =
                item.hasEndArrow === true ||
                item.pointerAtEnd === true ||
                item.pointerAtEnding === true;

              // Validate and normalize points array
              let normalizedPoints = item.points || [0, 0, 100, 0];
              if (
                !Array.isArray(normalizedPoints) ||
                normalizedPoints.length < 4
              ) {
                normalizedPoints = [0, 0, 100, 0];
              }

              // Check if start and end points are at the same location (invalid)
              const startX = normalizedPoints[0] || 0;
              const startY = normalizedPoints[1] || 0;
              const endX = normalizedPoints[normalizedPoints.length - 2] || 0;
              const endY = normalizedPoints[normalizedPoints.length - 1] || 0;

              if (startX === endX && startY === endY) {
                const defaultWidth = item.width || 100;
                normalizedPoints = [0, 0, defaultWidth, 0];
              }

              // Calculate width and height from normalized points
              let minX = Infinity,
                minY = Infinity,
                maxX = -Infinity,
                maxY = -Infinity;
              for (let i = 0; i < normalizedPoints.length; i += 2) {
                minX = Math.min(minX, normalizedPoints[i]);
                maxX = Math.max(maxX, normalizedPoints[i]);
                minY = Math.min(minY, normalizedPoints[i + 1]);
                maxY = Math.max(maxY, normalizedPoints[i + 1]);
              }
              const calculatedWidth = maxX - minX;
              const calculatedHeight = maxY - minY;

              // Create normalized element with only the new arrow properties
              const {
                pointerAtBeginning,
                pointerAtEnd,
                pointerAtEnding,
                ...itemWithoutLegacy
              } = item;
              const existingNames = new Set(
                prev
                  .filter(
                    (l) =>
                      l.hasStartArrow === true ||
                      l.hasEndArrow === true ||
                      l.pointerAtBeginning === true ||
                      l.pointerAtEnd === true ||
                      l.pointerAtEnding === true,
                  )
                  .map((l) => l.name),
              );
              const finalName = ensureUniqueName(item.name, existingNames);
              const newElement = {
                ...itemWithoutLegacy,
                name: finalName,
                hasStartArrow: normalizedHasStartArrow,
                hasEndArrow: normalizedHasEndArrow,
                points: normalizedPoints,
                width: calculatedWidth || item.width || 100,
                height: calculatedHeight || item.height || 0,
              };
              return [...prev, newElement];
            });
          }
        } else if (elementType === "Line") {
          setOffCanvasLines((prev) => {
            // CRITICAL: Check if ID already exists to prevent duplicates
            const idExists = prev.some((line) => line.id === item.id);
            if (idExists) {
              console.error(
                "❌ [CLONE] Duplicate off-canvas line ID detected, generating new ID",
                {
                  duplicateId: item.id,
                  existingIds: prev.map((l) => l.id).slice(-5),
                },
              );
              item.id = generateMarkerId();
            }
            // Check if it's a unified element (has arrow properties)
            // Normalize arrow properties: prioritize new properties, fallback to legacy
            const normalizedHasStartArrow =
              item.hasStartArrow === true || item.pointerAtBeginning === true;
            const normalizedHasEndArrow =
              item.hasEndArrow === true ||
              item.pointerAtEnd === true ||
              item.pointerAtEnding === true;

            // Double-check name uniqueness in case state has changed since name generation
            const existingNames = new Set(prev.map((l) => l.name));
            const finalName = ensureUniqueName(item.name, existingNames);

            // Validate and normalize points array
            // Points should be relative to the element's x, y position
            let normalizedPoints = item.points || [0, 0, 100, 0];

            // Ensure points array is valid (has at least 4 coordinates)
            if (
              !Array.isArray(normalizedPoints) ||
              normalizedPoints.length < 4
            ) {
              normalizedPoints = [0, 0, 100, 0];
            }

            // Check if start and end points are at the same location (invalid)
            const startX = normalizedPoints[0] || 0;
            const startY = normalizedPoints[1] || 0;
            const endX = normalizedPoints[normalizedPoints.length - 2] || 0;
            const endY = normalizedPoints[normalizedPoints.length - 1] || 0;

            // If start and end are at the same location, use default points
            if (startX === endX && startY === endY) {
              // Use the original width/height if available, or default to 100
              const defaultWidth = item.width || 100;
              normalizedPoints = [0, 0, defaultWidth, 0];
            }

            // Calculate width and height from normalized points
            let minX = Infinity,
              minY = Infinity,
              maxX = -Infinity,
              maxY = -Infinity;
            for (let i = 0; i < normalizedPoints.length; i += 2) {
              minX = Math.min(minX, normalizedPoints[i]);
              maxX = Math.max(maxX, normalizedPoints[i]);
              minY = Math.min(minY, normalizedPoints[i + 1]);
              maxY = Math.max(maxY, normalizedPoints[i + 1]);
            }
            const calculatedWidth = maxX - minX;
            const calculatedHeight = maxY - minY;

            // Create normalized element with only the new arrow properties
            // Destructure to exclude legacy properties, then add normalized ones
            const {
              pointerAtBeginning,
              pointerAtEnd,
              pointerAtEnding,
              ...itemWithoutLegacy
            } = item;
            const newElement = {
              ...itemWithoutLegacy,
              name: finalName, // Use final unique name
              // Ensure arrow properties are properly set
              hasStartArrow: normalizedHasStartArrow,
              hasEndArrow: normalizedHasEndArrow,
              // Ensure points are valid and normalized
              points: normalizedPoints,
              // Recalculate width and height from normalized points
              width: calculatedWidth || item.width || 100,
              height: calculatedHeight || item.height || 0,
            };
            return [...prev, newElement];
          });
        } else if (elementType === "Text") {
          setOffCanvasTexts((prev) => {
            // CRITICAL: Check if ID already exists to prevent duplicates
            const idExists = prev.some((text) => text.id === item.id);
            if (idExists) {
              console.error(
                "❌ [CLONE] Duplicate off-canvas text ID detected, generating new ID",
                {
                  duplicateId: item.id,
                  existingIds: prev.map((t) => t.id).slice(-5),
                },
              );
              item.id = generateMarkerId();
            }
            // Double-check name uniqueness in case state has changed since name generation
            const existingNames = new Set(prev.map((el) => el.name));
            const finalName = ensureUniqueName(item.name, existingNames);
            const newElement = {
              ...item,
              name: finalName, // Use final unique name
            };
            return [...prev, newElement];
          });
        }
      }
    });

    // Select the newly placed clones (duplicate to selected location), same spirit as paste
    const normalizeLineForSelection = (lineItem) => {
      if (lineItem.type !== "Line") {
        return lineItem;
      }
      const normalizedHasStartArrow =
        lineItem.hasStartArrow === true ||
        lineItem.pointerAtBeginning === true;
      const normalizedHasEndArrow =
        lineItem.hasEndArrow === true ||
        lineItem.pointerAtEnd === true ||
        lineItem.pointerAtEnding === true;

      let normalizedPoints = lineItem.points || [0, 0, 100, 0];
      if (!Array.isArray(normalizedPoints) || normalizedPoints.length < 4) {
        normalizedPoints = [0, 0, 100, 0];
      }
      const startX = normalizedPoints[0] || 0;
      const startY = normalizedPoints[1] || 0;
      const endX = normalizedPoints[normalizedPoints.length - 2] || 0;
      const endY = normalizedPoints[normalizedPoints.length - 1] || 0;
      if (startX === endX && startY === endY) {
        const defaultWidth = lineItem.width || 100;
        normalizedPoints = [0, 0, defaultWidth, 0];
      }

      let minX = Infinity,
        minY = Infinity,
        maxX = -Infinity,
        maxY = -Infinity;
      for (let i = 0; i < normalizedPoints.length; i += 2) {
        minX = Math.min(minX, normalizedPoints[i]);
        maxX = Math.max(maxX, normalizedPoints[i]);
        minY = Math.min(minY, normalizedPoints[i + 1]);
        maxY = Math.max(maxY, normalizedPoints[i + 1]);
      }
      const calculatedWidth = maxX - minX;
      const calculatedHeight = maxY - minY;

      const {
        pointerAtBeginning,
        pointerAtEnd,
        pointerAtEnding,
        ...itemWithoutLegacy
      } = lineItem;
      return {
        ...itemWithoutLegacy,
        hasStartArrow: normalizedHasStartArrow,
        hasEndArrow: normalizedHasEndArrow,
        points: normalizedPoints,
        width: calculatedWidth || lineItem.width || 100,
        height: calculatedHeight || lineItem.height || 0,
      };
    };

    const normalizeCloneSelectionItem = (item) => {
      const normalized = normalizeLineForSelection(item);
      const t = normalizeShapeType(normalized.type);
      if (t === "Rect") return { ...normalized, type: "Rect" };
      if (t === "Circle") return { ...normalized, type: "Circle" };
      if (t === "Image") return { ...normalized, type: "Image" };
      if (t === "Text") return { ...normalized, type: "Text" };
      return normalized;
    };
    const selectedClones = itemsWithNames.map(normalizeCloneSelectionItem);
    if (DEBUG_CLONE_GROUP_ALIGNMENT) {
      console.log(
        "[CLONE-GROUP][SELECTED-CLONES-BEFORE-SET]",
        selectedClones.map((it) => ({
          id: it.id,
          type: it.type,
          x: it.x,
          y: it.y,
          points: Array.isArray(it.points)
            ? [it.points[0], it.points[1], it.points[it.points.length - 2], it.points[it.points.length - 1]]
            : null,
        })),
      );
    }
    try {
      const selectedTextClones = selectedClones.filter(
        (item) => normalizeShapeType(item?.type) === "Text",
      );
      if (selectedTextClones.length > 0) {
      }
    } catch (_e) {}
    setSelectedObjects(selectedClones);
    setSelectedObject(selectedClones[0] || null);

    if (DEBUG_CLONE_GROUP_ALIGNMENT) {
      const plannedById = new Map(
        selectedClones.map((it) => [it.id, { x: Number(it.x) || 0, y: Number(it.y) || 0, type: it.type }]),
      );
      const cloneIds = selectedClones.map((it) => it.id);
      const logNodeSnapshot = (phaseLabel) => {
        const main = stageRef?.current;
        const off = offCanvasStageRef?.current;
        const rows = cloneIds.map((id) => {
          const planned = plannedById.get(id) || {};
          const mainNode = main?.findOne?.(`#${id}`) || null;
          const offNode = off?.findOne?.(`#${id}`) || null;
          const node = offNode || mainNode;
          return {
            id,
            type: planned.type,
            plannedX: planned.x,
            plannedY: planned.y,
            mainNodeXY: mainNode ? { x: mainNode.x(), y: mainNode.y() } : null,
            offNodeXY: offNode ? { x: offNode.x(), y: offNode.y() } : null,
            pickedNodeXY: node ? { x: node.x(), y: node.y() } : null,
          };
        });
        console.log(`[CLONE-GROUP][${phaseLabel}]`, rows);
      };

      // React state updates + Konva render can settle over successive frames.
      setTimeout(() => logNodeSnapshot("POST-SETTERS-T0"), 0);
      requestAnimationFrame(() => {
        logNodeSnapshot("POST-SETTERS-RAF1");
        requestAnimationFrame(() => {
          logNodeSnapshot("POST-SETTERS-RAF2");
        });
      });
    }

    // Save to history
    saveToHistory();
  };

  const cloneElementsAtPositionRef = useRef(cloneElementsAtPosition);
  cloneElementsAtPositionRef.current = cloneElementsAtPosition;

  /** Place duplicate(s) at the last known pointer location (same as a click-to-place clone). Used by Ctrl/Cmd+V. */
  const placeCloneAtPointerKeyboard = useCallback(() => {
    const templatesReady =
      (cloneTemplates && cloneTemplates.length > 0) || cloneTemplate;
    if (!isCloneMode || !templatesReady) {
      return false;
    }

    const clientX =
      typeof window.lastMouseX === "number"
        ? window.lastMouseX
        : window.innerWidth / 2;
    const clientY =
      typeof window.lastMouseY === "number"
        ? window.lastMouseY
        : window.innerHeight / 2;

    const mainStage = stageRef?.current;
    const offStage =
      offCanvasStageRef?.current ||
      (typeof window !== "undefined" && window.__offCanvasStageRef?.current);

    const clientToStage = (stage, cx, cy) => {
      try {
        const container = stage.container();
        if (!container) return null;
        const rect = container.getBoundingClientRect();
        const scaleX = stage.width() / rect.width;
        const scaleY = stage.height() / rect.height;
        return {
          x: (cx - rect.left) * scaleX,
          y: (cy - rect.top) * scaleY,
        };
      } catch {
        return null;
      }
    };

    const rectContains = (rect, cx, cy) =>
      !!rect &&
      cx >= rect.left &&
      cx <= rect.right &&
      cy >= rect.top &&
      cy <= rect.bottom;

    let mainRect = null;
    let offRect = null;
    try {
      mainRect = mainStage?.container?.()?.getBoundingClientRect() ?? null;
    } catch {
      mainRect = null;
    }
    try {
      offRect = offStage?.container?.()?.getBoundingClientRect() ?? null;
    } catch {
      offRect = null;
    }

    let stageCoords = null;
    let stageRefToPass = stageRef;
    let clientXUse = clientX;
    let clientYUse = clientY;

    if (mainStage && rectContains(mainRect, clientX, clientY)) {
      stageCoords = clientToStage(mainStage, clientX, clientY);
      stageRefToPass = stageRef;
    } else if (offStage && rectContains(offRect, clientX, clientY)) {
      stageCoords = clientToStage(offStage, clientX, clientY);
      stageRefToPass = offCanvasStageRef;
    } else if (mainStage && mainRect) {
      const cx = Math.max(mainRect.left, Math.min(mainRect.right, clientX));
      const cy = Math.max(mainRect.top, Math.min(mainRect.bottom, clientY));
      clientXUse = cx;
      clientYUse = cy;
      stageCoords = clientToStage(mainStage, cx, cy);
      stageRefToPass = stageRef;
    }

    if (!stageCoords) {
      return false;
    }

    cloneElementsAtPositionRef.current(
      stageCoords.x,
      stageCoords.y,
      stageRefToPass,
      clientXUse,
      clientYUse,
    );
    return true;
  }, [
    isCloneMode,
    cloneTemplate,
    cloneTemplates,
    stageRef,
    offCanvasStageRef,
  ]);

  // Clipboard operations - modified to enter paste mode
  // Ref to store handleCopyGroupBetweenCanvases from Canvas
  const handleCopyGroupBetweenCanvasesRef = useRef(null);

  const handleCopy = () => {
    const itemsToCopy =
      selectedObjects && selectedObjects.length > 0
        ? selectedObjects
        : selectedObject
          ? [selectedObject]
          : [];
    if (itemsToCopy.length === 0) return;

    // Prepare clipboard data with relative positions
    const clipboardData = prepareClipboardData(itemsToCopy);
    if (!clipboardData) return;

    // Store in pending clipboard and enter paste mode
    setPendingClipboard(clipboardData);
    setPasteModeType("copy");
    setIsWaitingForPaste(true);

    // Also store in regular clipboard for compatibility
    setClipboard(
      clipboardData.map((item) => ({
        ...item,
        id: `copy_${Date.now()}_${item.originalId}`,
      })),
    );
  };

  const handleCut = () => {
    if (selectedObjects.length === 0) return;

    // Prepare clipboard data with relative positions
    const clipboardData = prepareClipboardData(selectedObjects);
    if (!clipboardData) return;

    // Store in pending clipboard and enter paste mode
    setPendingClipboard(clipboardData);
    setPasteModeType("cut");
    setIsWaitingForPaste(true);

    // Also store in regular clipboard for compatibility
    setClipboard(
      clipboardData.map((item) => ({
        ...item,
        id: `cut_${Date.now()}_${item.originalId}`,
      })),
    );
  };

  // Element editing functions (needed for deleteSelectedObject creation)
  const endElementEditing = createEndElementEditing(
    isElementBeingEditedRef,
    pendingChangesRef,
    isTextEditingModeRef, // Pass text editing mode ref to prevent saves when in text editing mode
    latestUpdatedImagesRef, // Pass ref to store latest updated images from transform operations
    latestUpdatedTextsRef, // Pass ref to store latest updated texts from transform operations
    offCanvasImagesRef, // Pass ref with current offCanvasImages (updated each render) so drag position is captured
    setRectangles, // Pass setter to read current rectangles state (for text property)
    setOffCanvasRectangles, // Pass setter to read current off-canvas rectangles state
    setCircles, // Pass setter to read current circles state (for text property)
    setOffCanvasCircles, // Pass setter to read current off-canvas circles state
    saveToHistory,
    setIsElementBeingEdited,
    setPendingChanges,
    setInitialEditState,
    isSavingRef, // Pass ref to prevent duplicate element_edit_complete calls
    () => selectedObject, // Pass function to get current selectedObject (to check if element is selected)
    isTransformingRef, // Pass ref to clear transform initial state after save
    setImages, // Optional: apply ref and save when selection cleared after crop/transform
    setOffCanvasImages,
    lastHistoryOperationTypeRef,
    setTexts, // Optional: read current texts in delayed save (e.g. text stroke change then unselect)
    setOffCanvasTexts,
    latestImageEditorRefUrlRef,
  );

  // Clone mode exit safeguard - ensure deferred clone operations are saved
  // This handles the case where clone operations were deferred but element_edit_complete wasn't called
  useEffect(() => {
    // Only run when clone mode is disabled and there are pending changes
    if (
      !isCloneMode &&
      pendingChanges &&
      !isElementBeingEdited &&
      selectedObject === null
    ) {
      // Temporarily set editing state to trigger element_edit_complete
      setIsElementBeingEdited(true);
      isElementBeingEditedRef.current = true;
      // Call endElementEditing to save deferred operations
      setTimeout(() => {
        endElementEditing();
      }, 10); // Small delay to ensure state is updated
    }
  }, [
    isCloneMode,
    pendingChanges,
    isElementBeingEdited,
    selectedObject,
    endElementEditing,
  ]);

  // Deletion operations
  const deleteSelectedObject = createDeleteSelectedObject({
    selectedObject,
    isElementBeingEdited,
    endElementEditing,
    setAction,
    selectAction: ACTIONS.SELECT,
    findAndRemoveFromState,
    mainStates: {
      rectangles,
      setRectangles,
      setRemovedRectangles,
      circles,
      setCircles,
      setRemovedCircles,
      arrows,
      setArrows,
      setRemovedArrows,
      doubleSidedArrows,
      setDoubleSidedArrows,
      setRemovedDoubleSidedArrows,
      scribbles,
      setScribbles,
      setRemovedScribbles,
      texts,
      setTexts,
      setRemovedTexts,
      images,
      setImages,
      setRemovedImages,
      videos,
      setVideos,
      setRemovedVideos,
      lines,
      setLines,
      setRemovedLines,
    },
    offCanvasStates: {
      offCanvasRectangles,
      setOffCanvasRectangles,
      setRemovedOffCanvasRectangles,
      offCanvasCircles,
      setOffCanvasCircles,
      setRemovedOffCanvasCircles,
      offCanvasArrows,
      setOffCanvasArrows,
      setRemovedOffCanvasArrows,
      offCanvasDoubleSidedArrows,
      setOffCanvasDoubleSidedArrows,
      setRemovedOffCanvasDoubleSidedArrows,
      offCanvasScribbles,
      setOffCanvasScribbles,
      setRemovedOffCanvasScribbles,
      offCanvasTexts,
      setOffCanvasTexts,
      offCanvasTexts,
      setRemovedOffCanvasTexts,
      offCanvasImages,
      setOffCanvasImages,
      setRemovedOffCanvasImages,
      offCanvasVideos,
      setOffCanvasVideos,
      setRemovedOffCanvasVideos,
      offCanvasLines,
      setOffCanvasLines,
      setRemovedOffCanvasLines,
    },
    setSelectedText,
    setSelectedTextId,
    setSelectedObject,
    setSelectedObjects,
    transformerRef,
    saveToHistory,
    setPendingChanges,
    pendingChangesRef,
  });

  // Function to delete all selected objects (for keyboard handler)
  const deleteAllSelectedObjects = useCallback(() => {
    // Combine selectedObjects and selectedObject into one array
    const allObjectsToDelete = [...(selectedObjects || [])];
    if (
      selectedObject &&
      !allObjectsToDelete.some((obj) => obj.id === selectedObject.id)
    ) {
      allObjectsToDelete.push(selectedObject);
    }

    if (allObjectsToDelete.length === 0) return;

    // End element editing before deletion
    if (isElementBeingEdited) {
      endElementEditing();
    }

    // CRITICAL: Save the current state BEFORE deletion to history
    // This ensures undo can restore the state before the elements were deleted
    const stateBeforeDeletion = {
      rectangles: JSON.parse(JSON.stringify(rectangles || [])),
      circles: JSON.parse(JSON.stringify(circles || [])),
      arrows: JSON.parse(JSON.stringify(arrows || [])),
      doubleSidedArrows: JSON.parse(JSON.stringify(doubleSidedArrows || [])),
      scribbles: JSON.parse(JSON.stringify(scribbles || [])),
      texts: JSON.parse(JSON.stringify(texts || [])),
      images: JSON.parse(JSON.stringify(images || [])),
      videos: JSON.parse(JSON.stringify(videos || [])),
      lines: JSON.parse(JSON.stringify(lines || [])),
    };

    // Helper function to check if an element is in main canvas arrays
    const isElementInMainCanvas = (elementId, elementType) => {
      if (!elementId || !elementType) return false;
      switch (elementType) {
        case "Rect":
          return rectangles.some((r) => r.id === elementId);
        case "Circle":
          return circles.some((c) => c.id === elementId);
        case "Image":
          return images.some((i) => i.id === elementId);
        case "Text":
          return texts.some((t) => t.id === elementId);
        case "Arrow":
          // Check both arrows array and lines array (for unified elements)
          return (
            arrows.some((a) => a.id === elementId) ||
            lines.some((l) => {
              if (l.id !== elementId) return false;
              const hasStartArrow =
                l.hasStartArrow === true ||
                l.pointerAtBeginning === true ||
                l.pointerAtBeginning === "true";
              const hasEndArrow =
                l.hasEndArrow === true ||
                l.pointerAtEnd === true ||
                l.pointerAtEnding === true ||
                l.pointerAtEnd === "true" ||
                l.pointerAtEnding === "true";
              return (
                (hasStartArrow || hasEndArrow) &&
                !(hasStartArrow && hasEndArrow)
              );
            })
          );
        case "Line":
          return lines.some((l) => l.id === elementId);
        case "DoubleSidedArrow":
          // Check both doubleSidedArrows array and lines array (for unified elements)
          return (
            doubleSidedArrows.some((d) => d.id === elementId) ||
            lines.some((l) => {
              if (l.id !== elementId) return false;
              const hasStartArrow =
                l.hasStartArrow === true ||
                l.pointerAtBeginning === true ||
                l.pointerAtBeginning === "true";
              const hasEndArrow =
                l.hasEndArrow === true ||
                l.pointerAtEnd === true ||
                l.pointerAtEnding === true ||
                l.pointerAtEnd === "true" ||
                l.pointerAtEnding === "true";
              return hasStartArrow && hasEndArrow;
            })
          );
        default:
          return false;
      }
    };

    const classifyUnifiedLineType = (line) => {
      if (!line) return "Line";
      const hasStartArrow =
        line.hasStartArrow === true ||
        line.pointerAtBeginning === true ||
        line.pointerAtBeginning === "true";
      const hasEndArrow =
        line.hasEndArrow === true ||
        line.pointerAtEnd === true ||
        line.pointerAtEnding === true ||
        line.pointerAtEnd === "true" ||
        line.pointerAtEnding === "true";
      if (hasStartArrow && hasEndArrow) return "DoubleSidedArrow";
      if (hasStartArrow || hasEndArrow) return "Arrow";
      return "Line";
    };

    const resolveDeleteTypeById = (obj) => {
      const explicitType = obj?.type;
      const id = obj?.id;
      if (!id) return null;
      if (explicitType === "Rect" || explicitType === "Circle" || explicitType === "Image" || explicitType === "Text") {
        return explicitType;
      }
      if (explicitType === "Arrow" || explicitType === "DoubleSidedArrow") {
        return explicitType;
      }
      if (explicitType === "Line") {
        const unified =
          lines.find((l) => l.id === id) || offCanvasLines.find((l) => l.id === id);
        return unified ? classifyUnifiedLineType(unified) : "Line";
      }
      if (rectangles.some((r) => r.id === id) || offCanvasRectangles.some((r) => r.id === id)) {
        return "Rect";
      }
      if (circles.some((c) => c.id === id) || offCanvasCircles.some((c) => c.id === id)) {
        return "Circle";
      }
      if (images.some((i) => i.id === id) || offCanvasImages.some((i) => i.id === id)) {
        return "Image";
      }
      if (texts.some((t) => t.id === id) || offCanvasTexts.some((t) => t.id === id)) {
        return "Text";
      }
      if (arrows.some((a) => a.id === id) || offCanvasArrows.some((a) => a.id === id)) {
        return "Arrow";
      }
      if (
        doubleSidedArrows.some((d) => d.id === id) ||
        offCanvasDoubleSidedArrows.some((d) => d.id === id)
      ) {
        return "DoubleSidedArrow";
      }
      const unified =
        lines.find((l) => l.id === id) || offCanvasLines.find((l) => l.id === id);
      if (unified) {
        return classifyUnifiedLineType(unified);
      }
      return explicitType || null;
    };

    // Delete each object
    allObjectsToDelete.forEach((obj) => {
      if (!obj || !obj.id) return;
      const resolvedType = resolveDeleteTypeById(obj);
      if (!resolvedType) return;

      const isMainCanvas = isElementInMainCanvas(obj.id, resolvedType);

      switch (resolvedType) {
        case "Rect":
          if (isMainCanvas && setRectangles) {
            setRectangles((prev) => {
              const filtered = prev.filter((r) => r.id !== obj.id);
              if (filtered.length < prev.length && setRemovedRectangles) {
                setRemovedRectangles((removed) => [...removed, obj.id]);
              }
              return filtered;
            });
          } else if (!isMainCanvas && setOffCanvasRectangles) {
            setOffCanvasRectangles((prev) => {
              const filtered = prev.filter((r) => r.id !== obj.id);
              if (
                filtered.length < prev.length &&
                setRemovedOffCanvasRectangles
              ) {
                setRemovedOffCanvasRectangles((removed) => [
                  ...removed,
                  obj.id,
                ]);
              }
              return filtered;
            });
          }
          break;
        case "Circle":
          if (isMainCanvas && setCircles) {
            setCircles((prev) => {
              const filtered = prev.filter((c) => c.id !== obj.id);
              if (filtered.length < prev.length && setRemovedCircles) {
                setRemovedCircles((removed) => [...removed, obj.id]);
              }
              return filtered;
            });
          } else if (!isMainCanvas && setOffCanvasCircles) {
            setOffCanvasCircles((prev) => {
              const filtered = prev.filter((c) => c.id !== obj.id);
              if (filtered.length < prev.length && setRemovedOffCanvasCircles) {
                setRemovedOffCanvasCircles((removed) => [...removed, obj.id]);
              }
              return filtered;
            });
          }
          break;
        case "Image":
          if (isMainCanvas && setImages) {
            setImages((prev) => {
              const filtered = prev.filter((i) => i.id !== obj.id);
              if (filtered.length < prev.length && setRemovedImages) {
                setRemovedImages((removed) => [...removed, obj.id]);
              }
              return filtered;
            });
          } else if (!isMainCanvas && setOffCanvasImages) {
            setOffCanvasImages((prev) => {
              const filtered = prev.filter((i) => i.id !== obj.id);
              if (filtered.length < prev.length && setRemovedOffCanvasImages) {
                setRemovedOffCanvasImages((removed) => [...removed, obj.id]);
              }
              return filtered;
            });
          }
          break;
        case "Text":
          // Defensive text delete: remove from both stores by id.
          // Text ids can appear in stale main/off-canvas stores during return merges;
          // deleting from both prevents ghost rehydration on reopen.
          if (setTexts) {
            setTexts((prev) => {
              const filtered = prev.filter((t) => t.id !== obj.id);
              if (filtered.length < prev.length && setRemovedTexts) {
                setRemovedTexts((removed) => [...removed, obj.id]);
              }
              return filtered;
            });
          }
          if (setOffCanvasTexts) {
            setOffCanvasTexts((prev) => {
              const filtered = prev.filter((t) => t.id !== obj.id);
              if (filtered.length < prev.length && setRemovedOffCanvasTexts) {
                setRemovedOffCanvasTexts((removed) => [...removed, obj.id]);
              }
              return filtered;
            });
          }
          break;
        case "Arrow":
          // Check if this is a unified arrow element (stored in lines array)
          const arrowInLines = isMainCanvas
            ? lines.find((l) => l.id === obj.id)
            : offCanvasLines.find((l) => l.id === obj.id);

          if (arrowInLines) {
            // Unified arrow element - delete from lines array
            if (isMainCanvas && setLines) {
              setLines((prev) => {
                const filtered = prev.filter((l) => l.id !== obj.id);
                if (filtered.length < prev.length && setRemovedLines) {
                  setRemovedLines((removed) => [...removed, obj.id]);
                }
                return filtered;
              });
            } else if (!isMainCanvas && setOffCanvasLines) {
              setOffCanvasLines((prev) => {
                const filtered = prev.filter((l) => l.id !== obj.id);
                if (filtered.length < prev.length && setRemovedOffCanvasLines) {
                  setRemovedOffCanvasLines((removed) => [...removed, obj.id]);
                }
                return filtered;
              });
            }
          } else {
            // Legacy arrow element - delete from arrows array
            if (isMainCanvas && setArrows) {
              setArrows((prev) => {
                const filtered = prev.filter((a) => a.id !== obj.id);
                if (filtered.length < prev.length && setRemovedArrows) {
                  setRemovedArrows((removed) => [...removed, obj.id]);
                }
                return filtered;
              });
            } else if (!isMainCanvas && setOffCanvasArrows) {
              setOffCanvasArrows((prev) => {
                const filtered = prev.filter((a) => a.id !== obj.id);
                if (
                  filtered.length < prev.length &&
                  setRemovedOffCanvasArrows
                ) {
                  setRemovedOffCanvasArrows((removed) => [...removed, obj.id]);
                }
                return filtered;
              });
            }
          }
          break;
        case "Line":
          if (isMainCanvas && setLines) {
            setLines((prev) => {
              const filtered = prev.filter((l) => l.id !== obj.id);
              if (filtered.length < prev.length && setRemovedLines) {
                setRemovedLines((removed) => [...removed, obj.id]);
              }
              return filtered;
            });
          } else if (!isMainCanvas && setOffCanvasLines) {
            setOffCanvasLines((prev) => {
              const filtered = prev.filter((l) => l.id !== obj.id);
              if (filtered.length < prev.length && setRemovedOffCanvasLines) {
                setRemovedOffCanvasLines((removed) => [...removed, obj.id]);
              }
              return filtered;
            });
          }
          break;
        case "DoubleSidedArrow":
          // Check if this is a unified double-sided arrow element (stored in lines array)
          const doubleArrowInLines = isMainCanvas
            ? lines.find((l) => l.id === obj.id)
            : offCanvasLines.find((l) => l.id === obj.id);

          if (doubleArrowInLines) {
            // Unified double-sided arrow element - delete from lines array
            if (isMainCanvas && setLines) {
              setLines((prev) => {
                const filtered = prev.filter((l) => l.id !== obj.id);
                if (filtered.length < prev.length && setRemovedLines) {
                  setRemovedLines((removed) => [...removed, obj.id]);
                }
                return filtered;
              });
            } else if (!isMainCanvas && setOffCanvasLines) {
              setOffCanvasLines((prev) => {
                const filtered = prev.filter((l) => l.id !== obj.id);
                if (filtered.length < prev.length && setRemovedOffCanvasLines) {
                  setRemovedOffCanvasLines((removed) => [...removed, obj.id]);
                }
                return filtered;
              });
            }
          } else {
            // Legacy double-sided arrow element - delete from doubleSidedArrows array
            if (isMainCanvas && setDoubleSidedArrows) {
              setDoubleSidedArrows((prev) => {
                const filtered = prev.filter((d) => d.id !== obj.id);
                if (
                  filtered.length < prev.length &&
                  setRemovedDoubleSidedArrows
                ) {
                  setRemovedDoubleSidedArrows((removed) => [
                    ...removed,
                    obj.id,
                  ]);
                }
                return filtered;
              });
            } else if (!isMainCanvas && setOffCanvasDoubleSidedArrows) {
              setOffCanvasDoubleSidedArrows((prev) => {
                const filtered = prev.filter((d) => d.id !== obj.id);
                if (
                  filtered.length < prev.length &&
                  setRemovedOffCanvasDoubleSidedArrows
                ) {
                  setRemovedOffCanvasDoubleSidedArrows((removed) => [
                    ...removed,
                    obj.id,
                  ]);
                }
                return filtered;
              });
            }
          }
          break;
        default:
      }
    });

    // Clear all selection states
    setSelectedText(null);
    setSelectedTextId(null);
    setSelectedObject(null);
    setSelectedObjects([]);
    transformerRef.current?.nodes([]);

    // Exit multi-select mode after deletion
    if (setIsMultiSelectMode) {
      setIsMultiSelectMode(false);
    }
    if (setIndividuallySelectedIds) {
      setIndividuallySelectedIds(new Set());
    }

    // Save to history with the state BEFORE deletion
    // This ensures undo can restore the deleted objects
    saveToHistory(
      "delete_multiple_objects",
      stateBeforeDeletion.images,
      stateBeforeDeletion.arrows,
      stateBeforeDeletion.doubleSidedArrows,
      stateBeforeDeletion.lines,
      stateBeforeDeletion.texts,
      null, // currentSelectedTextOverride
      null, // currentSelectedTextIdOverride
      stateBeforeDeletion.rectangles,
      null, // currentOffCanvasRectanglesOverride
      stateBeforeDeletion.circles,
      null, // currentOffCanvasCirclesOverride
      null, // currentOffCanvasArrowsOverride
      null, // currentOffCanvasDoubleSidedArrowsOverride
      null, // currentOffCanvasLinesOverride
      null, // currentOffCanvasTextsOverride
      null, // currentOffCanvasImagesOverride
      allObjectsToDelete.map((obj) => obj.id).join(","), // deletedObjectIds - comma-separated IDs
      allObjectsToDelete.map((obj) => obj.type).join(","), // deletedObjectTypes - comma-separated types
    );
  }, [
    selectedObjects,
    selectedObject,
    isElementBeingEdited,
    endElementEditing,
    rectangles,
    circles,
    arrows,
    doubleSidedArrows,
    lines,
    texts,
    images,
    videos,
    offCanvasRectangles,
    offCanvasCircles,
    offCanvasArrows,
    offCanvasDoubleSidedArrows,
    offCanvasLines,
    offCanvasTexts,
    offCanvasImages,
    setRectangles,
    setCircles,
    setArrows,
    setDoubleSidedArrows,
    setLines,
    setTexts,
    setImages,
    setOffCanvasRectangles,
    setOffCanvasCircles,
    setOffCanvasArrows,
    setOffCanvasDoubleSidedArrows,
    setOffCanvasLines,
    setOffCanvasTexts,
    setOffCanvasImages,
    setRemovedRectangles,
    setRemovedCircles,
    setRemovedArrows,
    setRemovedDoubleSidedArrows,
    setRemovedLines,
    setRemovedTexts,
    setRemovedImages,
    setRemovedOffCanvasRectangles,
    setRemovedOffCanvasCircles,
    setRemovedOffCanvasArrows,
    setRemovedOffCanvasDoubleSidedArrows,
    setRemovedOffCanvasLines,
    setRemovedOffCanvasTexts,
    setRemovedOffCanvasImages,
    setSelectedText,
    setSelectedTextId,
    setSelectedObject,
    setSelectedObjects,
    transformerRef,
    setIsMultiSelectMode,
    setIndividuallySelectedIds,
    saveToHistory,
  ]);

  const nudgeSelectedElements = useCallback(
    (dx, dy) => {
      if (dx === 0 && dy === 0) return;
      if (isCloneMode || lockedAction) return;

      const allObjectsToNudge = [...(selectedObjects || [])];
      if (
        selectedObject &&
        !allObjectsToNudge.some((obj) => obj.id === selectedObject.id)
      ) {
        allObjectsToNudge.push(selectedObject);
      }
      if (allObjectsToNudge.length === 0) return;

      const state = {
        rectangles,
        offCanvasRectangles,
        circles,
        offCanvasCircles,
        arrows,
        offCanvasArrows,
        doubleSidedArrows,
        offCanvasDoubleSidedArrows,
        lines,
        offCanvasLines,
        texts,
        offCanvasTexts,
        images,
        offCanvasImages,
      };

      const offStage =
        offCanvasStageRef?.current ||
        (typeof window !== "undefined" && window.__offCanvasStageRef?.current);
      const constraintOptions = {
        mainStageWidth:
          stageRef?.current?.width?.() ?? window.innerWidth * 0.45,
        mainStageHeight: stageRef?.current?.height?.() ?? window.innerHeight,
        offCanvasStageWidth: offStage?.width?.() ?? window.innerWidth,
        offCanvasStageHeight: offStage?.height?.() ?? window.innerHeight,
      };

      const next = computeNudgedCanvasState(
        allObjectsToNudge,
        dx,
        dy,
        state,
        constraintOptions,
      );

      if (!next.didChange) return;

      setRectangles(next.rectangles);
      setOffCanvasRectangles(next.offCanvasRectangles);
      setCircles(next.circles);
      setOffCanvasCircles(next.offCanvasCircles);
      setArrows(next.arrows);
      setOffCanvasArrows(next.offCanvasArrows);
      setDoubleSidedArrows(next.doubleSidedArrows);
      setOffCanvasDoubleSidedArrows(next.offCanvasDoubleSidedArrows);
      setLines(next.lines);
      setOffCanvasLines(next.offCanvasLines);
      setTexts(next.texts);
      setOffCanvasTexts(next.offCanvasTexts);
      setImages(next.images);
      setOffCanvasImages(next.offCanvasImages);

      if (selectedObjects && selectedObjects.length > 0) {
        setSelectedObjects(next.selectionSnapshots);
        setSelectedObject((prev) => {
          if (!prev) return null;
          const match = next.selectionSnapshots.find((s) => s.id === prev.id);
          return match ? { ...match, type: prev.type } : prev;
        });
      } else if (selectedObject) {
        const match = next.selectionSnapshots.find(
          (s) => s.id === selectedObject.id,
        );
        if (match) {
          setSelectedObject({ ...match, type: selectedObject.type });
        }
      }

      saveToHistory(
        "nudge_selected_elements",
        next.images,
        next.arrows,
        next.doubleSidedArrows,
        next.lines,
        next.texts,
        null,
        null,
        next.rectangles,
        next.offCanvasRectangles,
        next.circles,
        next.offCanvasCircles,
        next.offCanvasArrows,
        next.offCanvasDoubleSidedArrows,
        next.offCanvasLines,
        next.offCanvasTexts,
        next.offCanvasImages,
      );
    },
    [
      isCloneMode,
      lockedAction,
      selectedObjects,
      selectedObject,
      rectangles,
      offCanvasRectangles,
      circles,
      offCanvasCircles,
      arrows,
      offCanvasArrows,
      doubleSidedArrows,
      offCanvasDoubleSidedArrows,
      lines,
      offCanvasLines,
      texts,
      offCanvasTexts,
      images,
      offCanvasImages,
      setRectangles,
      setOffCanvasRectangles,
      setCircles,
      setOffCanvasCircles,
      setArrows,
      setOffCanvasArrows,
      setDoubleSidedArrows,
      setOffCanvasDoubleSidedArrows,
      setLines,
      setOffCanvasLines,
      setTexts,
      setOffCanvasTexts,
      setImages,
      setOffCanvasImages,
      setSelectedObjects,
      setSelectedObject,
      saveToHistory,
      stageRef,
      offCanvasStageRef,
    ],
  );

  // Shape text operations
  const onAddText = createOnAddText(
    selectedObject,
    setRectangles,
    setCircles,
    setOffCanvasRectangles,
    setOffCanvasCircles,
    setSelectedObject,
    saveToHistory,
    isElementBeingEditedRef, // Pass ref to check if element is being edited
    setPendingChanges, // Pass setter to mark pending changes when editing
    pendingChangesRef, // Pass ref to update synchronously
  );
  const onRemoveText = createOnRemoveText(
    selectedObject,
    setRectangles,
    setCircles,
    setOffCanvasRectangles,
    setOffCanvasCircles,
    setSelectedObject,
    saveToHistory,
    isElementBeingEditedRef, // Pass ref to check if element is being edited
    setPendingChanges, // Pass setter to mark pending changes when editing
    pendingChangesRef, // Pass ref to update synchronously
  );

  // Custom always-visible scrollbar refs and state
  const containerRef = useRef(null);
  // scrollAreaRef is declared earlier (needed for createHandleZoom at line 1842)
  const vTrackRef = useRef(null);
  const hTrackRef = useRef(null);
  const [scrollMetrics, setScrollMetrics] = useState({
    scrollTop: 0,
    scrollLeft: 0,
    scrollHeight: 1,
    scrollWidth: 1,
    clientHeight: 1,
    clientWidth: 1,
  });

  // Throttle scroll updates for better performance
  const updateScrollMetrics = useUpdateScrollMetrics(
    scrollAreaRef,
    setScrollMetrics,
  );

  // Throttled version for scroll events
  const throttledUpdateScrollMetrics =
    useThrottledUpdateScrollMetrics(updateScrollMetrics);

  useEffect(() => {
    // Prevent page-level scrollbars behind the canvas while active
    const prevOverflow = document.documentElement.style.overflow;
    const prevBodyOverflow = document.body.style.overflow;
    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
    const el = scrollAreaRef.current;
    if (!el) return;
    updateScrollMetrics();
    const onScroll = createScrollHandler(throttledUpdateScrollMetrics);
    el.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", updateScrollMetrics);
    return () => {
      el.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", updateScrollMetrics);
      document.documentElement.style.overflow = prevOverflow;
      document.body.style.overflow = prevBodyOverflow;
    };
  }, [updateScrollMetrics]);

  // Recalculate when zoom/layout changes
  useEffect(() => {
    const id = setTimeout(updateScrollMetrics, 0);
    return () => clearTimeout(id);
  }, [zoomLevel, canvasHeight, canvasResizeTrigger, updateScrollMetrics]);

  // Zoom in: preserve horizontal scrollbar ratio. Zoom out: center canvas in viewport (H + V).
  // scrollWidth/clientHeight often lag one frame behind `transform: scale()` + spacer updates;
  // re-apply after layout settles.
  useLayoutEffect(() => {
    const snap = preZoomHScrollRef.current;
    preZoomHScrollRef.current = null;
    if (!scrollAreaRef.current || !snap) {
      if (zoomLevel > 1 && !snap) {
        logCanvasHScroll("preserveHScroll:afterZoom:noSnapshot", {
          zoomLevel,
          note: "Zoom >100% but no pre-zoom snapshot (not from +/- or manual field?)",
        });
      }
      return;
    }

    if (snap.mode === "center") {
      const applyCenter = (label) => {
        const el = scrollAreaRef.current;
        if (!el) return;
        settleScrollViewportOnMainStage(el, stageRef, mainStageContainerRef);
        logCanvasHScroll("preserveHScroll:zoomOutCenter", {
          label,
          zoomLevel,
          scrollLeft: el.scrollLeft,
          scrollTop: el.scrollTop,
          scrollWidth: el.scrollWidth,
          scrollHeight: el.scrollHeight,
          clientWidth: el.clientWidth,
          clientHeight: el.clientHeight,
        });
        updateScrollMetrics();
      };

      applyCenter("layout");
      const raf1 = requestAnimationFrame(() => {
        applyCenter("raf1");
        requestAnimationFrame(() => applyCenter("raf2"));
      });
      const t0 = window.setTimeout(() => applyCenter("t0"), 0);
      const t48 = window.setTimeout(() => applyCenter("t48"), 48);
      const t120 = window.setTimeout(() => applyCenter("t120"), 120);
      const t200 = window.setTimeout(() => applyCenter("t200"), 200);
      const t400 = window.setTimeout(() => applyCenter("t400"), 400);

      return () => {
        cancelAnimationFrame(raf1);
        window.clearTimeout(t0);
        window.clearTimeout(t48);
        window.clearTimeout(t120);
        window.clearTimeout(t200);
        window.clearTimeout(t400);
      };
    }

    if (snap.mode === "pointer") {
      const el = scrollAreaRef.current;
      if (el) {
        applyScrollZoomToPoint(
          el,
          snap.clientX,
          snap.clientY,
          snap.oldScale,
          snap.newScale,
        );
        logCanvasHScroll("preserveHScroll:zoomPointer", {
          zoomLevel,
          scrollLeft: el.scrollLeft,
          scrollTop: el.scrollTop,
          scrollWidth: el.scrollWidth,
          scrollHeight: el.scrollHeight,
          clientWidth: el.clientWidth,
          clientHeight: el.clientHeight,
        });
        updateScrollMetrics();
      }
      return undefined;
    }

    const applyRatio = (label) => {
      const el = scrollAreaRef.current;
      if (!el) return;
      const newMaxL = Math.max(0, el.scrollWidth - el.clientWidth);
      let appliedLeft = el.scrollLeft;
      let mode = "unchanged";

      if (snap.maxL > 0 && newMaxL > 0) {
        const ratio = snap.left / snap.maxL;
        appliedLeft = Math.round(
          Math.min(1, Math.max(0, ratio)) * newMaxL,
        );
        el.scrollLeft = appliedLeft;
        mode = "ratio";
      } else if (snap.maxL <= 0 && newMaxL > 0) {
        appliedLeft = computeHorizontallyCenteredScrollLeft(
          el,
          stageRef,
          mainStageContainerRef,
        );
        el.scrollLeft = appliedLeft;
        mode = "centerNewOverflow";
      }

      logCanvasHScroll("preserveHScroll:afterZoom", {
        label,
        zoomLevel,
        mode,
        snap,
        newMaxL,
        appliedLeft,
        scrollWidth: el.scrollWidth,
        clientWidth: el.clientWidth,
      });
      updateScrollMetrics();
    };

    applyRatio("layout");
    const raf1 = requestAnimationFrame(() => {
      applyRatio("raf1");
      requestAnimationFrame(() => applyRatio("raf2"));
    });
    const t0 = window.setTimeout(() => applyRatio("t0"), 0);
    const t48 = window.setTimeout(() => applyRatio("t48"), 48);
    const t120 = window.setTimeout(() => applyRatio("t120"), 120);

    return () => {
      cancelAnimationFrame(raf1);
      window.clearTimeout(t0);
      window.clearTimeout(t48);
      window.clearTimeout(t120);
    };
  }, [zoomLevel, updateScrollMetrics, stageRef, mainStageContainerRef]);

  // On route re-entry, allow initial horizontal centering to run again.
  useEffect(() => {
    hasCenteredInitialHorizontalScrollRef.current = false;
  }, [location.key, pageId]);

  // On initial load, horizontally center the canvas viewport so the main stage is visible.
  useEffect(() => {
    if (hasCenteredInitialHorizontalScrollRef.current) return;

    let cancelled = false;
    let attempt = 0;
    const maxAttempts = 120;

    const tryCenterHorizontally = () => {
      if (cancelled || hasCenteredInitialHorizontalScrollRef.current) return;
      const el = scrollAreaRef.current;
      if (!el) return;

      const maxScrollLeft = Math.max(0, el.scrollWidth - el.clientWidth);
      if (maxScrollLeft <= 0) {
        if (attempt < maxAttempts) {
          attempt += 1;
          requestAnimationFrame(tryCenterHorizontally);
        }
        return;
      }

      const stage = stageRef?.current;
      const container =
        stage && typeof stage.container === "function"
          ? stage.container()
          : null;
      const canMeasure =
        container && typeof el.contains === "function" && el.contains(container);
      if (!canMeasure && attempt < maxAttempts) {
        attempt += 1;
        requestAnimationFrame(tryCenterHorizontally);
        return;
      }

      settleScrollViewportOnMainStage(el, stageRef, mainStageContainerRef);
      hasCenteredInitialHorizontalScrollRef.current = true;
      updateScrollMetrics();
    };

    requestAnimationFrame(tryCenterHorizontally);

    return () => {
      cancelled = true;
    };
  }, [updateScrollMetrics, stageRef, mainStageContainerRef, location.key, pageId]);

  // Measure content and create spacer to ensure scrollable area matches zoomed content
  const mainCanvasWidthPx = Math.max(0, Math.floor(getMainCanvasWidthPx()));
  const [effectiveContentSize, setEffectiveContentSize] = useState({
    width: 0,
    height: 0,
  });

  // Extra padding needed when zoom origin is centered so top/left become reachable
  const [zoomOverflowPadding, setZoomOverflowPadding] = useState({
    top: 0,
    left: 0,
    right: 0,
  });

  // Keep track of previous zoom and padding to compute stable center across zooms
  const prevZoomRef = useRef(zoomLevel);
  const prevPadRef = useRef({ top: 0, left: 0 });

  const updateEffectiveContentSize = useUpdateEffectiveContentSize(
    contentRef,
    scaledContentRef,
    scrollAreaRef,
    setEffectiveContentSize,
    canvasHeight,
    zoomLevel,
  );

  // Catch late async layout/data updates after returning from CanvasForm full-screen edit.
  // Run the same one-time settle logic used on initial load when content width becomes available.
  useEffect(() => {
    if (hasCenteredInitialHorizontalScrollRef.current) return;
    if (!effectiveContentSize.width || effectiveContentSize.width <= 0) return;

    let cancelled = false;
    const applySettle = () => {
      if (cancelled || hasCenteredInitialHorizontalScrollRef.current) return;
      const el = scrollAreaRef.current;
      if (!el) return;
      const maxScrollLeft = Math.max(0, el.scrollWidth - el.clientWidth);
      if (maxScrollLeft <= 0) return;
      settleScrollViewportOnMainStage(el, stageRef, mainStageContainerRef);
      hasCenteredInitialHorizontalScrollRef.current = true;
      updateScrollMetrics();
    };

    const rafId = requestAnimationFrame(applySettle);
    const t1 = window.setTimeout(applySettle, 80);
    const t2 = window.setTimeout(applySettle, 220);

    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, [
    effectiveContentSize.width,
    updateScrollMetrics,
    stageRef,
    mainStageContainerRef,
    location.key,
    pageId,
  ]);

  useEffect(() => {
    const id = setTimeout(() => {
      updateEffectiveContentSize();
      updateScrollMetrics();
    }, 0);
    return () => clearTimeout(id);
  }, [
    zoomLevel,
    canvasHeight,
    canvasResizeTrigger,
    updateEffectiveContentSize,
    updateScrollMetrics,
  ]);

  // Compute top/left overflow introduced by centered zoom so we can pad scroll area
  const [dynamicBottomPadding, setDynamicBottomPadding] = useState(30); // Default padding for resize handle visibility

  useEffect(() => {
    try {
      const canvasHeightPercent = parseInt(canvasHeight, 10);
      if (!isNaN(canvasHeightPercent)) {
        const stageHeightPx = canvasStageHeightPx;
        // Use full horizontal span (left off-canvas strip + main + right span) so
        // zoom > 100% keeps both horizontal boundaries reachable.
        const stageWidthPx = Math.floor(
          getOffCanvasStageWidthPx() + getMainCanvasWidthPx(),
        );
        const zoom = Number(zoomLevel) || 1;
        // Simple overflow padding - just ensure enough space for scaled content
        // Provide consistent small top padding to prevent content cutoff
        const extraTop = zoom > 1 ? 20 : 0; // Buffer when zoomed in (scale > 1)
        const extraHorizontal = Math.max(
          0,
          Math.ceil((stageWidthPx * (zoom - 1)) / 2),
        );

        // Calculate dynamic bottom padding based on canvas height and zoom
        // Resize handle sits at the bottom of the fixed-height stage minus offset
        const resizeHandleY = canvasStageHeightPx - 10;
        const resizeHandleBottom = resizeHandleY + 10; // handle height is 10px
        const scaledResizeHandleBottom = resizeHandleBottom * zoom;

        // Calculate the extra space needed due to zoom with reduced factor
        // For zoom > 100%, use an even smaller factor to reduce white space
        const zoomFactor = zoom > 1 ? 0 : 0.3; // Zero factor for zoom > 100% to eliminate extra space
        const extraBottom = Math.max(
          0,
          (zoom - 1) * resizeHandleBottom * zoomFactor,
        );

        // Minimum padding to ensure resize handle is visible, with minimal buffer for zoom > 100% only
        const minPadding = zoom > 1 ? 40 : 20; // Zero padding for zoom > 100%
        const calculatedPadding = Math.max(
          minPadding,
          extraBottom + minPadding,
        );

        setZoomOverflowPadding({
          top: extraTop,
          left: extraHorizontal,
          right: extraHorizontal,
        });
        setDynamicBottomPadding(calculatedPadding);
      } else {
        setZoomOverflowPadding({ top: 0, left: 0, right: 0 });
        setDynamicBottomPadding(30); // fallback - normal padding
      }
    } catch (e) {
      setZoomOverflowPadding({ top: 0, left: 0, right: 0 });
      setDynamicBottomPadding(30); // fallback - reduced from 50
    }
  }, [zoomLevel, canvasHeight, canvasResizeTrigger, canvasStageHeightPx]);

  // Disabled automatic viewport center stabilization - now handled directly in zoom functions
  // to prevent conflicts with manual scroll position preservation
  /*
  // Keep viewport center stable across zoom changes (both in and out)
  useEffect(() => {
    const el = scrollAreaRef.current;
    if (!el) {
      prevZoomRef.current = zoomLevel;
      prevPadRef.current = {
        top: zoomOverflowPadding.top || 0,
        left: zoomOverflowPadding.left || 0,
      };
      return;
    }
    const previous = prevZoomRef.current ?? 1;
    const current = zoomLevel ?? 1;
    if (!previous || previous <= 0) {
      prevZoomRef.current = current || 1;
      prevPadRef.current = {
        top: zoomOverflowPadding.top || 0,
        left: zoomOverflowPadding.left || 0,
      };
      return;
    }
    const ratio = (current || 1) / previous;
    if (ratio === 1) {
      prevPadRef.current = {
        top: zoomOverflowPadding.top || 0,
        left: zoomOverflowPadding.left || 0,
      };
      return;
    }
    const prevPad = prevPadRef.current || { top: 0, left: 0 };
    const currPad = {
      top: zoomOverflowPadding.top || 0,
      left: zoomOverflowPadding.left || 0,
    };
    // Calculate center in content space excluding previous padding, then apply scale and add current padding
    const cxScrollable = el.scrollLeft - prevPad.left + el.clientWidth / 2;
    const cyScrollable = el.scrollTop - prevPad.top + el.clientHeight / 2;
    const newLeft = Math.max(
      0,
      Math.round(cxScrollable * ratio - el.clientWidth / 2 + currPad.left)
    );
    const newTop = Math.max(
      0,
      Math.round(cyScrollable * ratio - el.clientHeight / 2 + currPad.top)
    );
    requestAnimationFrame(() => {
      el.scrollTo({ left: newLeft, top: newTop, behavior: "auto" });
    });
    prevZoomRef.current = current;
    prevPadRef.current = currPad;
  }, [zoomLevel, zoomOverflowPadding.top, zoomOverflowPadding.left]);
  */

  // After effective content size updates (e.g., spacer width/height changed),
  // measure scroll metrics on the next animation frame to ensure DOM has applied sizes
  useEffect(() => {
    const rafId = requestAnimationFrame(() => {
      updateScrollMetrics();
    });
    return () => cancelAnimationFrame(rafId);
  }, [
    effectiveContentSize.width,
    effectiveContentSize.height,
    updateScrollMetrics,
  ]);

  /**
   * `useLayoutEffect` centers when `preZoomHScrollRef` fires, but `zoomOverflowPadding` and spacer
   * `effectiveContentSize` update in later effects — `scrollWidth` / `maxL` change after that pass
   * (noticeable above ~150% when horizontal overflow padding grows). Re-center when those
   * dimensions settle so the main stage stays in the scroll viewport.
   * Omit `effectiveContentSize` from deps — it updates often via ResizeObserver and would fight
   * manual panning; staggered timeouts still run after spacer width catches up to zoom padding.
   */
  useEffect(() => {
    if (zoomLevel <= 1) return;

    let cancelled = false;
    const timeoutIds = [];

    const applyCenterOnLayout = () => {
      if (cancelled) return;
      const el = scrollAreaRef.current;
      if (!el) return;
      settleScrollViewportOnMainStage(el, stageRef, mainStageContainerRef);
      updateScrollMetrics();
    };

    const rafId = requestAnimationFrame(() => {
      if (cancelled) return;
      applyCenterOnLayout();
      // Keep follow-up recenters short-lived so manual horizontal scroll
      // (especially to the far left boundary) is not overridden.
      [0, 48, 120].forEach((ms) => {
        timeoutIds.push(window.setTimeout(applyCenterOnLayout, ms));
      });
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
      timeoutIds.forEach((id) => clearTimeout(id));
    };
  }, [
    zoomLevel,
    updateScrollMetrics,
    stageRef,
    mainStageContainerRef,
  ]);

  useEffect(() => {
    const ro = new ResizeObserver(() => {
      updateEffectiveContentSize();
      updateScrollMetrics();
    });
    if (contentRef.current) ro.observe(contentRef.current);
    if (scaledContentRef.current) ro.observe(scaledContentRef.current);
    return () => ro.disconnect();
  }, [updateEffectiveContentSize, updateScrollMetrics]);

  useEffect(() => {
    const updateViewportWidth = () => {
      const measuredWidth =
        scaledContentRef.current?.getBoundingClientRect?.().width || 0;
      setCanvasViewportWidth(Math.max(0, measuredWidth || window.innerWidth));
    };

    updateViewportWidth();

    const ro = new ResizeObserver(updateViewportWidth);
    if (scaledContentRef.current) {
      ro.observe(scaledContentRef.current);
    }

    window.addEventListener("resize", updateViewportWidth);
    window.addEventListener("orientationchange", updateViewportWidth);

    return () => {
      ro.disconnect();
      window.removeEventListener("resize", updateViewportWidth);
      window.removeEventListener("orientationchange", updateViewportWidth);
    };
  }, []);

  useEffect(() => {
    const mainStageHeightPx = getStageHeightPxFromCanvasHeight(canvasHeight);
    setCanvasStageHeightPx(mainStageHeightPx);
  }, [canvasHeight, canvasResizeTrigger]);

  // Derived sizes/positions for custom thumbs
  const vTrackSize = vTrackRef.current
    ? vTrackRef.current.clientHeight
    : scrollMetrics.clientHeight;
  const hTrackSize = hTrackRef.current
    ? hTrackRef.current.clientWidth
    : scrollMetrics.clientWidth;

  // Use the same scroll extents for thumb **size** and **position** (`hScrollable` / `vScrollable`).
  // `useStableScrollExtent` lagged `scrollWidth` by up to 14px while `scrollLeft/hScrollable` used
  // fresh `scrollMetrics`, so after zoom the thumb ratio drifted left on the horizontal track.
  const verticalVisibleRatio = Math.min(
    1,
    scrollMetrics.clientHeight / Math.max(1, scrollMetrics.scrollHeight),
  );
  const horizontalVisibleRatio = Math.min(
    1,
    scrollMetrics.clientWidth / Math.max(1, scrollMetrics.scrollWidth),
  );

  // More responsive thumb sizing with better minimum size
  const vThumbHeight = useMemo(() => {
    const minSize = Math.max(16, Math.floor(vTrackSize * 0.05)); // 5% of track size or 16px minimum
    const calculatedSize = Math.floor(verticalVisibleRatio * vTrackSize);
    return Math.max(minSize, calculatedSize);
  }, [verticalVisibleRatio, vTrackSize]);

  const hThumbWidth = useMemo(() => {
    const minSize = Math.max(16, Math.floor(hTrackSize * 0.05)); // 5% of track size or 16px minimum
    const calculatedSize = Math.floor(horizontalVisibleRatio * hTrackSize);
    return Math.max(minSize, calculatedSize);
  }, [horizontalVisibleRatio, hTrackSize]);

  const vScrollable = Math.max(
    0,
    scrollMetrics.scrollHeight - scrollMetrics.clientHeight,
  );
  const hScrollable = Math.max(
    0,
    scrollMetrics.scrollWidth - scrollMetrics.clientWidth,
  );

  const vMaxThumbOffset = Math.max(0, vTrackSize - vThumbHeight);
  const hMaxThumbOffset = Math.max(0, hTrackSize - hThumbWidth);

  // More precise thumb positioning calculations
  const vThumbTop = useMemo(() => {
    if (vScrollable <= 0 || vMaxThumbOffset <= 0) return 0;
    const ratio = Math.max(
      0,
      Math.min(1, scrollMetrics.scrollTop / vScrollable),
    );
    return Math.round(ratio * vMaxThumbOffset);
  }, [scrollMetrics.scrollTop, vScrollable, vMaxThumbOffset]);

  const hThumbLeft = useMemo(() => {
    if (hScrollable <= 0 || hMaxThumbOffset <= 0) return 0;
    const ratio = Math.max(
      0,
      Math.min(1, scrollMetrics.scrollLeft / hScrollable),
    );
    return Math.round(ratio * hMaxThumbOffset);
  }, [scrollMetrics.scrollLeft, hScrollable, hMaxThumbOffset]);



  // Drag handling for thumbs
  const [dragState, setDragState] = useState(null);

  const handleVerticalThumbMouseDown = useVerticalThumbMouseDown(
    scrollAreaRef,
    setDragState,
  );

  const handleHorizontalThumbMouseDown = useHorizontalThumbMouseDown(
    scrollAreaRef,
    setDragState,
  );

  const handleVerticalTrackMouseDown = useVerticalTrackMouseDown(
    vTrackRef,
    scrollAreaRef,
  );

  const handleHorizontalTrackMouseDown = useHorizontalTrackMouseDown(
    hTrackRef,
    scrollAreaRef,
  );

  useEffect(() => {
    if (!dragState) return;

    const onMove = createMoveHandler(
      dragState,
      scrollAreaRef,
      vTrackRef,
      hTrackRef,
    );
    const onUp = createUpHandler(setDragState);

    document.addEventListener("mousemove", onMove, { passive: false });
    document.addEventListener("mouseup", onUp);

    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
  }, [dragState]);
  const [isPartTable2Updating, setIsPartTable2Updating] = useState(false); // Add this new state
  const [newRow, setNewRow] = useState(null);
  const [editingRowId, setEditingRowId] = useState(null);
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "ascending",
  });
  const { units } = useSelector((state) => state.units);
  const [part, setPart] = useState("");
  const partNumbers = useMemo(() => {
    return Array.isArray(location.state?.partNumbers)
      ? location.state.partNumbers
      : [];
  }, [location.state?.partNumbers]);

  const filterOptions = [
    { value: "all", label: "All parts" },
    { value: "bom_step", label: "Parts in BOM for step" },
    { value: "bom_procedure", label: "Parts in BOM for Procedure" },
    { value: "modules", label: "Parts that are Modules" },
    { value: "procedures", label: "Parts that are Procedure" },
    { value: "flowchart", label: "Parts that are Flow Chart" },
  ];
  const handleCancelPartEdit = createCancelPartEditHandler(
    setEditingPartId,
    setEditedPartData,
  );

  const [newPart, setNewPart] = useState({
    type_id_ref: "",
    unit_type_id_ref: "",
    part_number: "",
    description: "",
    table_note: "",
    textbox_note: "",
    default_unit: "",
    url: "",
  });
  const handleInputChange = createInputChangeHandler(newPart, setNewPart);

  // Helper function for shallow comparison of part arrays based on part_id
  // Wrapped in useCallback to prevent it from causing re-renders itself if passed as dependency
  const arePartsArraysEqualCallback = useCallback(arePartsArraysEqual, []);
  const [isDrawingArrow, setIsDrawingArrow] = useState(false);
  const [arrowStartPoint, setArrowStartPoint] = useState(null);
  const [isDrawingDoubleArrow, setIsDrawingDoubleArrow] = useState(false);
  const [doubleArrowStartPoint, setDoubleArrowStartPoint] = useState(null);

  // Keep top-layer state stable during drawing to prevent image remount/blink.

  const [filterType, setFilterType] = useState("bom_step");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedColor, setSelectedColor] = useState("#000000");
  const [searchType, setSearchType] = useState("all_columns");
  const [selectedColumns, setSelectedColumns] = useState({
    type: true,
    part_number: true,
    short_description: true,
    table_note: true,
    textbox_note: true,
    default_unit: true,
    url: true,
  });
  // clipboard is declared earlier (needed for createHandleCopy at line 1952)
  // getTextStyle is now imported from utils

  const [searchQuery, setSearchQuery] = useState("");
  const dispatch = useDispatch();
  const debouncedSearchQuery = useDebounce(searchQuery, 500); // 500ms debounce delay
  // --- NEW MULTI-PAGE STATES ---
  const [canvasPages, setCanvasPages] = useState(() => {
    const savedPages = localStorage.getItem("canvasPages");
    if (savedPages) {
      try {
        return JSON.parse(savedPages);
      } catch (e) { }
    }

    // Fallback to default initial page
    return [];
  });

  // deduplicateByName is now imported from utils
  useEffect(() => {
    if (location.state?.canvasPages) {
      // Remove duplicate named elements from each page
      const dedupedPages = location.state.canvasPages.map((page) => ({
        ...page,
        arrows: page.arrows ? deduplicateByName(page.arrows) : [],
        circles: page.circles ? deduplicateByName(page.circles) : [],
        rectangles: page.rectangles ? deduplicateByName(page.rectangles) : [],
        images: page.images ? deduplicateByName(page.images) : [],
        texts: page.texts ? deduplicateByName(page.texts) : [],
        scribbles: page.scribbles ? deduplicateByName(page.scribbles) : [],
        lines: page.lines ? deduplicateByName(page.lines) : [],
        doubleSidedArrows: page.doubleSidedArrows
          ? deduplicateByName(page.doubleSidedArrows)
          : [],
      }));
      setCanvasPages(dedupedPages);
    }
  }, [location.state?.canvasPages]);

  const [currentPageIndex, setCurrentPageIndex] = useState(0); // Index of the current page

  // Helper to create a new blank page object

  // Set step ID when parts modal opens or stepId changes
  useEffect(() => {
    if (isPartsModalOpen && location.state?.stepId) {
      dispatch(setStepTreeNodeId(location.state.stepId));
    }
  }, [isPartsModalOpen, location.state?.stepId, dispatch]);

  // 1. Consolidated Effect for Initial Load / Filter Changes when Modal is Open
  useEffect(() => {
    // Only fetch these initial datasets if the parts modal is open
    // This prevents unnecessary fetches when the modal is closed

    if (isPartsModalOpen) {
      // dispatch(getAllParts({ limit: 100, offset: 0, filterType }));
      dispatch(getAllUnits({ limit: 1000, offset: 0 }));
      dispatch(getAllTypes({ limit: 1000, offset: 0 }));
      // Set step ID if available from location state
      if (location.state?.stepId) {
        dispatch(setStepTreeNodeId(location.state.stepId));
      }
    }
  }, [dispatch, filterType, isPartsModalOpen, location.state?.stepId]); // Include stepId in dependencies
  // Example: If isPartsModalOpen controls the visibility of the Item Code Table
  // This useEffect will reset searchQuery when the modal closes or opens
  useEffect(() => {
    if (!isPartsModalOpen) {
      // Or when it opens, depending on desired behavior
      setSearchQuery("");
      // You might also want to reset filterType or selectedColumns here
      // setFilterType("bom_step");
      // setSelectedColumns({ type: false, part_number: true });
    }
  }, [isPartsModalOpen]);
  useEffect(() => {
    const locationState = location?.state || {};
    const pageId = locationState.pageId || "currentCanvas";

    const savedSnapshot =
      getCanvasSnapshotFromStorage() ||
      locationState.currentSnapshot ||
      null;
    const legacyState = localStorage.getItem("canvasState");

    if (savedSnapshot) {
      setSnapshot(savedSnapshot);
    }

    const loadCanvasElements = async (
      version = DB_VERSION,
      maxVersion = DB_VERSION + 5,
    ) => {
      // Sanitize the pageId to match what we save
      const sanitizeIdLocal = (id) => sanitizeId(id, "currentCanvas");

      const sanitizedPageId = sanitizeIdLocal(pageId);

      try {
        const db = await openCanvasDB(version);

        let elementsResult = null;
        if (sanitizedPageId) {
          elementsResult = await loadFromIndexedDB(
            "canvasElements",
            sanitizedPageId,
          );
        }
        if (!elementsResult) {
          elementsResult = await loadFromIndexedDB(
            "canvasElements",
            "currentCanvas",
          );
        }

        let elements = elementsResult?.data || elementsResult || {};

        // Check if the loaded data has template elements nested under an 'elements' property
        // This happens when loading template data saved from CanvasForm.js
        if (elements.elements && typeof elements.elements === "object") {
          elements = elements.elements;
        }

        // 🔁 Retry with newer version if elements missing
        if (!elements || Object.keys(elements).length === 0) {
          if (version < maxVersion) {
            return await loadCanvasElements(version + 1, maxVersion);
          } else {
            elements = {};
          }
        }

        // IMPORTANT: Prioritize navigation state canvas height first (specific to this page)
        // Then fallback to IndexedDB elements, then default
        // This ensures each page maintains its own independent canvas height

        if (
          location.state?.canvasHeight != null &&
          !Number.isNaN(parseInt(location.state?.canvasHeight))
        ) {
          // Use canvasHeight passed via navigation state for selected page (highest priority)
          setCanvasHeight(String(parseInt(location.state.canvasHeight)));
        } else if (
          elements?.canvasHeight != null &&
          !Number.isNaN(parseInt(elements.canvasHeight))
        ) {
          // Fallback to saved canvasHeight from IndexedDB
          setCanvasHeight(String(parseInt(elements.canvasHeight)));
        }

        // ✅ Normalize legacy `.shapes` - merge by shape_id (don't duplicate when API has 2 texts)
        const legacyExistingByShapeId = new Map();
        (elements.texts || []).forEach((t) => {
          const sid = t.shape_id || t.id;
          if (sid) legacyExistingByShapeId.set(sid, t);
        });
        if (elements?.shapes?.length) {
          elements.shapes.forEach((shape) => {
            const normalizedShape = {
              ...shape,
              type: normalizeShapeType(shape.type),
            };

            switch (normalizedShape.type) {
              case "Text": {
                if (
                  isMarkerId(normalizedShape.shape_id) ||
                  (!normalizedShape.shape_id &&
                    normalizedShape.shape_properties?.id &&
                    isMarkerId(normalizedShape.shape_properties.id))
                )
                  break;
                const textIdLegacy =
                  normalizedShape.shape_id ||
                  (normalizedShape.shape_properties?.id &&
                    !isMarkerId(normalizedShape.shape_properties.id)
                    ? normalizedShape.shape_properties.id
                    : null) ||
                  normalizedShape.id;
                if (textIdLegacy && legacyExistingByShapeId.has(textIdLegacy))
                  break;
                if (!textIdLegacy || isMarkerId(textIdLegacy)) break;
                const textData = normalizedShape.shape_properties
                  ? {
                    ...normalizedShape.shape_properties,
                    ...normalizedShape,
                    id: textIdLegacy,
                    shape_id: normalizedShape.shape_id || textIdLegacy,
                  }
                  : normalizedShape;
                (elements.texts = elements.texts || []).push(textData);
                if (textIdLegacy)
                  legacyExistingByShapeId.set(textIdLegacy, textData);
                break;
              }
              case "Rect": {
                // Extract rectangle properties from shape_properties or use shape directly
                const rectData = normalizedShape.shape_properties
                  ? {
                    ...normalizedShape.shape_properties,
                    ...normalizedShape,
                    id:
                      normalizedShape.shape_properties.id ||
                      normalizedShape.shape_id ||
                      normalizedShape.id,
                  }
                  : normalizedShape;
                (elements.rectangles = elements.rectangles || []).push(
                  rectData,
                );
                break;
              }
              case "Circle": {
                // Extract circle properties from shape_properties
                const circleData = normalizedShape.shape_properties
                  ? {
                    ...normalizedShape.shape_properties,
                    ...normalizedShape,
                    id:
                      normalizedShape.shape_properties.id ||
                      normalizedShape.shape_id ||
                      normalizedShape.id,
                  }
                  : normalizedShape;
                (elements.circles = elements.circles || []).push(circleData);
                break;
              }
              case "Scribble": {
                // Extract scribble properties from shape_properties
                const scribbleData = normalizedShape.shape_properties
                  ? {
                    ...normalizedShape.shape_properties,
                    ...normalizedShape,
                    id:
                      normalizedShape.shape_properties.id ||
                      normalizedShape.shape_id ||
                      normalizedShape.id,
                  }
                  : normalizedShape;
                (elements.scribbles = elements.scribbles || []).push(
                  scribbleData,
                );
                break;
              }
              case "Arrow": {
                // Extract arrow properties from shape_properties
                const arrowData = normalizedShape.shape_properties
                  ? {
                    ...normalizedShape.shape_properties,
                    ...normalizedShape,
                    id:
                      normalizedShape.shape_properties.id ||
                      normalizedShape.shape_id ||
                      normalizedShape.id,
                  }
                  : normalizedShape;
                // Convert to unified format: Arrow has arrow at end by default (1 pointer)
                // Push to lines array with hasEndArrow: true, hasStartArrow: false
                arrowData.hasStartArrow =
                  arrowData.hasStartArrow !== undefined
                    ? arrowData.hasStartArrow
                    : arrowData.pointerAtBeginning || false;
                arrowData.hasEndArrow =
                  arrowData.hasEndArrow !== undefined
                    ? arrowData.hasEndArrow
                    : arrowData.pointerAtEnd !== undefined
                      ? arrowData.pointerAtEnd
                      : true;
                // CRITICAL: Set type to "Arrow" so click handler can identify it correctly
                arrowData.type = "Arrow";
                // CRITICAL: Push to lines array for unified rendering with UnifiedLineArrowElement
                (elements.lines = elements.lines || []).push(arrowData);
                break;
              }
              case "Line": {
                // Extract line properties from shape_properties
                const lineData = normalizedShape.shape_properties
                  ? {
                    ...normalizedShape.shape_properties,
                    ...normalizedShape,
                    id:
                      normalizedShape.shape_properties.id ||
                      normalizedShape.shape_id ||
                      normalizedShape.id,
                  }
                  : normalizedShape;
                const spTypeLine2 = normalizedShape.shape_properties?.type != null
                  ? normalizeShapeType(normalizedShape.shape_properties.type)
                  : null;
                const hasBothArrowsLine2 =
                  lineData.hasStartArrow === true && lineData.hasEndArrow === true;
                if (spTypeLine2 === "DoubleSidedArrow" || (hasBothArrowsLine2 && spTypeLine2 !== "Arrow")) {
                  lineData.type = "DoubleSidedArrow";
                  lineData.hasStartArrow = true;
                  lineData.hasEndArrow = true;
                }
                if (
                  (lineData.hasStartArrow || lineData.hasEndArrow) &&
                  Array.isArray(lineData.points) &&
                  lineData.points.length >= 4
                ) {
                  const [xa, ya, xb, yb] = lineData.points;
                  if (Math.sqrt((xb - xa) ** 2 + (yb - ya) ** 2) < 1) {
                    lineData.points = [0, 0, lineData.width > 0 ? lineData.width : 100, 0];
                  }
                }
                (elements.lines = elements.lines || []).push(lineData);
                break;
              }
              case "DoubleSidedArrow": {
                // Extract double-sided arrow properties from shape_properties
                const doubleArrowData = normalizedShape.shape_properties
                  ? {
                    ...normalizedShape.shape_properties,
                    ...normalizedShape,
                    id:
                      normalizedShape.shape_properties.id ||
                      normalizedShape.shape_id ||
                      normalizedShape.id,
                  }
                  : normalizedShape;
                // Convert to unified format: DoubleSidedArrow has arrows at both ends
                doubleArrowData.hasStartArrow =
                  doubleArrowData.hasStartArrow !== undefined
                    ? doubleArrowData.hasStartArrow
                    : doubleArrowData.pointerAtBeginning !== undefined
                      ? doubleArrowData.pointerAtBeginning
                      : true;
                doubleArrowData.hasEndArrow =
                  doubleArrowData.hasEndArrow !== undefined
                    ? doubleArrowData.hasEndArrow
                    : doubleArrowData.pointerAtEnd !== undefined
                      ? doubleArrowData.pointerAtEnd
                      : true;
                // CRITICAL: Set type to "DoubleSidedArrow" so click handler can identify it correctly
                doubleArrowData.type = "DoubleSidedArrow";
                // CRITICAL: Push to lines array for unified rendering with UnifiedLineArrowElement
                (elements.lines = elements.lines || []).push(doubleArrowData);
                break;
              }
              case "Image":
                (elements.images = elements.images || []).push(normalizedShape);
                break;
            }
          });
        }

        // ✅ Merge router state shapeData
        if (shapeData?.length > 0) {
        }

        shapeData?.forEach((shape) => {
          const normalizedShape = {
            ...shape,
            type: normalizeShapeType(shape.type),
          };

          switch (normalizedShape.type) {
            case "Text": {
              if (
                isMarkerId(normalizedShape.shape_id) ||
                (!normalizedShape.shape_id &&
                  normalizedShape.shape_properties?.id &&
                  isMarkerId(normalizedShape.shape_properties.id))
              )
                break;
              const textIdFromState =
                normalizedShape.shape_id ||
                (normalizedShape.shape_properties?.id &&
                  !isMarkerId(normalizedShape.shape_properties.id)
                  ? normalizedShape.shape_properties.id
                  : null) ||
                normalizedShape.id;
              if (
                textIdFromState &&
                legacyExistingByShapeId.has(textIdFromState)
              )
                break;
              if (!textIdFromState || isMarkerId(textIdFromState)) break;
              const textData = normalizedShape.shape_properties
                ? {
                  ...normalizedShape.shape_properties,
                  ...normalizedShape,
                  id: textIdFromState,
                  shape_id: normalizedShape.shape_id || textIdFromState,
                }
                : normalizedShape;

              if (
                normalizedShape.canvas_page_location_and_size?.rotation !==
                undefined
              ) {
                textData.rotation =
                  normalizedShape.canvas_page_location_and_size.rotation;
              } else if (
                normalizedShape.shape_properties?.canvas_page_location_and_size
                  ?.rotation !== undefined
              ) {
                textData.rotation =
                  normalizedShape.shape_properties.canvas_page_location_and_size.rotation;
              } else if (
                normalizedShape.shape_info?.canvas_page_location_and_size
                  ?.rotation !== undefined
              ) {
                textData.rotation =
                  normalizedShape.shape_info.canvas_page_location_and_size.rotation;
              }
              if (textData.rotation === undefined) {
                textData.rotation = normalizedShape.rotation ?? 0;
              }

              (elements.texts = elements.texts || []).push(textData);
              if (textIdFromState)
                legacyExistingByShapeId.set(textIdFromState, textData);
              break;
            }
            case "Rect": {
              // Extract rectangle properties from shape_properties
              const rectData = normalizedShape.shape_properties
                ? {
                  ...normalizedShape.shape_properties,
                  ...normalizedShape,
                  id:
                    normalizedShape.shape_properties.id ||
                    normalizedShape.shape_id ||
                    normalizedShape.id,
                }
                : normalizedShape;
              (elements.rectangles = elements.rectangles || []).push(rectData);
              break;
            }
            case "Circle": {
              // Extract circle properties from shape_properties
              const circleData = normalizedShape.shape_properties
                ? {
                  ...normalizedShape.shape_properties,
                  ...normalizedShape,
                  id:
                    normalizedShape.shape_properties.id ||
                    normalizedShape.shape_id ||
                    normalizedShape.id,
                }
                : normalizedShape;
              (elements.circles = elements.circles || []).push(circleData);
              break;
            }
            case "Scribble": {
              // Extract scribble properties from shape_properties
              const scribbleData = normalizedShape.shape_properties
                ? {
                  ...normalizedShape.shape_properties,
                  ...normalizedShape,
                  id:
                    normalizedShape.shape_properties.id ||
                    normalizedShape.shape_id ||
                    normalizedShape.id,
                }
                : normalizedShape;
              (elements.scribbles = elements.scribbles || []).push(
                scribbleData,
              );
              break;
            }
            case "Arrow": {
              // Extract arrow properties from shape_properties
              const arrowData = normalizedShape.shape_properties
                ? {
                  ...normalizedShape.shape_properties,
                  ...normalizedShape,
                  id:
                    normalizedShape.shape_properties.id ||
                    normalizedShape.shape_id ||
                    normalizedShape.id,
                }
                : normalizedShape;
              // Convert to unified format: Arrow has arrow at end by default (1 pointer)
              // Push to lines array with hasEndArrow: true, hasStartArrow: false
              arrowData.hasStartArrow =
                arrowData.hasStartArrow !== undefined
                  ? arrowData.hasStartArrow
                  : arrowData.pointerAtBeginning || false;
              arrowData.hasEndArrow =
                arrowData.hasEndArrow !== undefined
                  ? arrowData.hasEndArrow
                  : arrowData.pointerAtEnd !== undefined
                    ? arrowData.pointerAtEnd
                    : true;
              // CRITICAL: Set type to "Arrow" so click handler can identify it correctly
              arrowData.type = "Arrow";
              // CRITICAL: Push to lines array for unified rendering with UnifiedLineArrowElement
              (elements.lines = elements.lines || []).push(arrowData);
              break;
            }
            case "Line": {
              // Extract line properties from shape_properties
              const lineData = normalizedShape.shape_properties
                ? {
                  ...normalizedShape.shape_properties,
                  ...normalizedShape,
                  id:
                    normalizedShape.shape_properties.id ||
                    normalizedShape.shape_id ||
                    normalizedShape.id,
                }
                : normalizedShape;
              const spTypeLine3 = normalizedShape.shape_properties?.type != null
                ? normalizeShapeType(normalizedShape.shape_properties.type)
                : null;
              const hasBothArrowsLine3 =
                lineData.hasStartArrow === true && lineData.hasEndArrow === true;
              if (spTypeLine3 === "DoubleSidedArrow" || (hasBothArrowsLine3 && spTypeLine3 !== "Arrow")) {
                lineData.type = "DoubleSidedArrow";
                lineData.hasStartArrow = true;
                lineData.hasEndArrow = true;
              }
              if (
                (lineData.hasStartArrow || lineData.hasEndArrow) &&
                Array.isArray(lineData.points) &&
                lineData.points.length >= 4
              ) {
                const [xa, ya, xb, yb] = lineData.points;
                if (Math.sqrt((xb - xa) ** 2 + (yb - ya) ** 2) < 1) {
                  lineData.points = [0, 0, lineData.width > 0 ? lineData.width : 100, 0];
                }
              }
              (elements.lines = elements.lines || []).push(lineData);
              break;
            }
            case "DoubleSidedArrow": {
              // Preserve original IDs before spreading shape_properties
              // which may contain marker IDs that should not override the real shape_id
              const originalShapeId = normalizedShape.shape_id;
              const originalId = normalizedShape.id;

              // Extract double-sided arrow properties from shape_properties
              const doubleArrowData = normalizedShape.shape_properties
                ? {
                  ...normalizedShape.shape_properties,
                  ...normalizedShape,
                  // CRITICAL: Prioritize shape_id from API over id from shape_properties
                  // shape_id is the authoritative ID from the database
                  // shape_properties.id may contain marker IDs that should be ignored
                  id:
                    originalShapeId ||
                    (originalId && !isMarkerId(originalId)
                      ? originalId
                      : null) ||
                    normalizedShape.shape_properties.id ||
                    normalizedShape.id,
                }
                : normalizedShape;
              // Convert to unified format: DoubleSidedArrow has arrows at both ends (2 pointers)
              // Push to lines array with hasStartArrow: true, hasEndArrow: true
              doubleArrowData.hasStartArrow =
                doubleArrowData.hasStartArrow !== undefined
                  ? doubleArrowData.hasStartArrow
                  : true;
              doubleArrowData.hasEndArrow =
                doubleArrowData.hasEndArrow !== undefined
                  ? doubleArrowData.hasEndArrow
                  : true;
              // CRITICAL: Set type to "DoubleSidedArrow" so click handler can identify it correctly
              doubleArrowData.type = "DoubleSidedArrow";
              // CRITICAL: Push to lines array for unified rendering with UnifiedLineArrowElement
              (elements.lines = elements.lines || []).push(doubleArrowData);
              break;
            }
          }
        });

        // ✅ Merge router state imageData: merge into existing image so originalCropRotation/originalWidth/originalHeight from image_properties are kept (fixes crop cut in MyCanvas)
        imageData?.forEach((img) => {
          const src = img.image_path || img.src;
          const id = img.id || img.image_id || img.shape_id;
          const imageProps = img.image_properties || {};
          const existingIndex = (elements.images || []).findIndex(
            (e) =>
              (e.id || e.image_id || e.shape_id) === id ||
              (e.src || e.image_path) === src,
          );
          if (existingIndex >= 0) {
            const existing = elements.images[existingIndex];
            elements.images[existingIndex] = {
              ...existing,
              ...img,
              ...imageProps,
              src: src || existing.src || existing.image_path,
              image_properties: {
                ...(existing.image_properties || {}),
                ...imageProps,
              },
            };
          } else {
            (elements.images = elements.images || []).push({
              ...img,
              ...imageProps,
              src,
            });
          }
        });

        // Helper to normalize loaded arrows to invariant: points are RELATIVE to (x, y)
        // normalizeLoadedArrow is now imported from utils

        // Include API text shapes and user-created texts (createdOnCanvas or marker ids in loaded data)
        let textsForDedupInner = elements.texts || [];
        const validTextShapeIdsInner =
          location.state?.validTextShapeIds ??
          (elements?.validTextShapeIds || []);
        const loadedMarkerTextIdsInner = new Set(
          (elements.texts || [])
            .filter((t) => {
              const sid = t.shape_id || t.id;
              return sid && isMarkerId(sid);
            })
            .map((t) => t.shape_id || t.id),
        );
        const nonMarkerOrUserCreatedInner = textsForDedupInner.filter((t) => {
          const sid = t.shape_id || t.id;
          return (
            sid &&
            (!isMarkerId(sid) ||
              t.createdOnCanvas === true ||
              loadedMarkerTextIdsInner.has(sid))
          );
        });
        if (
          Array.isArray(validTextShapeIdsInner) &&
          validTextShapeIdsInner.length > 0
        ) {
          textsForDedupInner = textsForDedupInner.filter((t) => {
            if (t.createdOnCanvas === true) return true;
            const sid = t.shape_id || t.id;
            if (sid && loadedMarkerTextIdsInner.has(sid)) return true;
            return sid && validTextShapeIdsInner.includes(sid);
          });
          if (
            textsForDedupInner.length < nonMarkerOrUserCreatedInner.length &&
            nonMarkerOrUserCreatedInner.length > 0
          ) {
            textsForDedupInner = nonMarkerOrUserCreatedInner;
          }
        } else if (nonMarkerOrUserCreatedInner.length > 0) {
          textsForDedupInner = nonMarkerOrUserCreatedInner;
        }
        textsForDedupInner = textsForDedupInner.filter((t) => {
          if (t.createdOnCanvas === true) return true;
          const sid = t.shape_id || t.id;
          if (sid && loadedMarkerTextIdsInner.has(sid)) return true;
          const idStr = t.id != null ? String(t.id) : "";
          const sidStr = t.shape_id != null ? String(t.shape_id) : "";
          if (
            idStr.includes("marker") &&
            (!sidStr || sidStr.includes("marker"))
          )
            return false;
          return true;
        });

        // CRITICAL: Deduplicate texts by shape_id so one shape never appears twice (avoids merged props)
        const textsDedupedByShapeIdInner = dedupeCanvasTextsByEntity(
          textsForDedupInner.reduce(
            (acc, t) => {
              const sid = t.shape_id || t.id;
              if (!sid) {
                acc.push(t);
                return acc;
              }
              const existing = acc.find((a) => (a.shape_id || a.id) === sid);
              if (!existing) {
                acc.push(t);
              } else if (t.id === sid && existing.id !== sid) {
                const idx = acc.findIndex((a) => (a.shape_id || a.id) === sid);
                acc[idx] = t;
              }
              return acc;
            },
            [],
          ),
        );

        // ⬇️ Set shape states
        const processedTexts =
          textsDedupedByShapeIdInner.length > 0
            ? textsDedupedByShapeIdInner.map((r) => {
              // Preserve original IDs before spreading shape_properties/shape_info
              // which may contain marker IDs that should not override the real shape_id
              const originalShapeId = r.shape_id;
              const originalId = r.id;

              const shapeProperties = r.shape_properties || {};
              const shapeInfo = r.shape_info || {};

              // CRITICAL: Check for partsByType in multiple locations
              // Priority order: top-level > shape_properties > shape_info
              const partsByType =
                r.partsByType !== null && r.partsByType !== undefined
                  ? r.partsByType
                  : shapeProperties.partsByType !== null &&
                    shapeProperties.partsByType !== undefined
                    ? shapeProperties.partsByType
                    : shapeInfo.partsByType;

              // Debug logging for partsByType preservation
              if (
                partsByType &&
                (shapeProperties.partsByType || r.partsByType)
              ) {
              }

              const text = {
                ...r,
                ...shapeProperties,
                ...shapeInfo,
                // Override with preserved partsByType if it exists (prevents null from overwriting)
                ...(partsByType !== undefined && partsByType !== null
                  ? { partsByType }
                  : {}),
              };

              // Verify partsByType was preserved correctly
              if (partsByType && !text.partsByType) {
              }

              // Extract rotation from canvas_page_location_and_size if present (from API)
              // Check multiple possible locations where rotation might be stored
              if (
                r.text_box_properties?.canvas_page_location_and_size
                  ?.rotation !== undefined
              ) {
                text.rotation =
                  r.text_box_properties.canvas_page_location_and_size.rotation;
              } else if (
                r.canvas_page_location_and_size?.rotation !== undefined
              ) {
                // Top level canvas_page_location_and_size (from shapeData)
                text.rotation = r.canvas_page_location_and_size.rotation;
              } else if (
                r.shape_properties?.canvas_page_location_and_size
                  ?.rotation !== undefined
              ) {
                // shape_properties.canvas_page_location_and_size (from shapeData)
                text.rotation =
                  r.shape_properties.canvas_page_location_and_size.rotation;
              } else if (
                r.shape_info?.canvas_page_location_and_size?.rotation !==
                undefined
              ) {
                // shape_info.canvas_page_location_and_size (from shapeData)
                text.rotation =
                  r.shape_info.canvas_page_location_and_size.rotation;
              } else if (
                r.shape_properties?.text_box_properties
                  ?.canvas_page_location_and_size?.rotation !== undefined
              ) {
                text.rotation =
                  r.shape_properties.text_box_properties.canvas_page_location_and_size.rotation;
              } else if (
                r.shape_info?.text_box_properties
                  ?.canvas_page_location_and_size?.rotation !== undefined
              ) {
                text.rotation =
                  r.shape_info.text_box_properties.canvas_page_location_and_size.rotation;
              }
              // If rotation is not set, default to existing rotation or 0
              if (text.rotation === undefined) {
                text.rotation = r.rotation ?? 0;
              }

              // CRITICAL: Loaded element (r) is the source of truth for identity, position, and content.
              // API can nest wrong data in shape_properties.shape_info (e.g. first shape's position/content in second shape),
              // so we must not let ...shapeInfo overwrite these fields.
              const fromR = {
                id: r.id,
                shape_id: r.shape_id,
                x: r.x,
                y: r.y,
                content: r.content,
                note: r.note,
                task: r.task,
                name: r.name,
                plainText: r.plainText,
                notePlainText: r.notePlainText,
                taskPlainText: r.taskPlainText,
                partsByType: r.partsByType,
                text_box_content_data: r.text_box_content_data,
                text_box_id: r.text_box_id,
              };
              Object.keys(fromR).forEach((k) => {
                if (fromR[k] !== undefined && fromR[k] !== null) {
                  text[k] = fromR[k];
                }
              });
              if (r.x != null || r.y != null) {
                text.x = r.x ?? text.x;
                text.y = r.y ?? text.y;
                if (text.canvas_page_location_and_size) {
                  text.canvas_page_location_and_size = {
                    ...text.canvas_page_location_and_size,
                    x: text.x,
                    y: text.y,
                  };
                }
              }

              const preferredId =
                resolveTextElementId(r, text) ||
                (originalShapeId !== undefined && originalShapeId !== null
                  ? String(originalShapeId)
                  : null) ||
                (originalId !== undefined &&
                  originalId !== null &&
                  !String(originalId).toLowerCase().includes("marker")
                  ? String(originalId)
                  : null);

              if (preferredId) {
                text.id = preferredId;
                text.shape_id = preferredId;
              } else {
                const currentId =
                  text.id === undefined || text.id === null
                    ? null
                    : typeof text.id === "number"
                      ? String(text.id)
                      : String(text.id);

                if (
                  !currentId ||
                  currentId.toLowerCase().includes("marker")
                ) {
                  const fallbackId = `text_${Date.now()}_${Math.random()}`;
                  text.id = fallbackId;
                  text.shape_id = fallbackId;
                }
              }
              return text;
            })
            : [];

        const refreshedTexts =
          await refreshTextPartsByTypeWithLatestParts(processedTexts, dispatch);
        setTexts(refreshedTexts);
        elements.rectangles &&
          setRectangles(
            elements.rectangles
              .map((r) => {
                // Preserve original IDs before spreading shape_properties/shape_info
                // which may contain marker IDs that should not override the real shape_id
                const originalShapeId = r.shape_id;
                const originalId = r.id;

                // Extract properties from shape_properties or shape_info if they exist
                const rect = {
                  ...r,
                  ...(r.shape_properties || {}),
                  ...(r.shape_info || {}),
                };

                // CRITICAL: Prioritize shape_id from API over id from shape_properties
                // shape_id is the authoritative ID from the database
                // shape_properties.id may contain marker IDs that should be ignored
                if (originalShapeId) {
                  rect.id = originalShapeId;
                } else if (originalId && !isMarkerId(originalId)) {
                  rect.id = originalId;
                } else if (!rect.id) {
                  rect.id = `rect_${Date.now()}_${Math.random()}`;
                }
                // Convert corner positioning to center positioning for existing rectangles
                // This ensures backward compatibility with rectangles stored with corner positioning
                // Only convert if we have valid coordinates and they look like corner coordinates
                if (
                  rect.x !== undefined &&
                  rect.y !== undefined &&
                  rect.width !== undefined &&
                  rect.height !== undefined &&
                  // Check if coordinates might be corner-based (this is a heuristic)
                  // If width/height are reasonable and coordinates are small, likely corner-based
                  rect.width > 0 &&
                  rect.height > 0
                ) {
                  // Check if this looks like corner positioning by checking if it's in the expected range
                  // For center positioning, x and y would typically be larger values
                  // This is a heuristic - we convert if it seems like corner positioning
                  const isLikelyCornerBased =
                    rect.x < rect.width * 2 && rect.y < rect.height * 2;
                  if (isLikelyCornerBased) {
                    return {
                      ...rect,
                      x: rect.x + rect.width / 2,
                      y: rect.y + rect.height / 2,
                    };
                  }
                }
                return rect;
              })
              // Filter out rectangles that don't have valid dimensions
              .filter(
                (rect) =>
                  rect.width !== undefined &&
                  rect.height !== undefined &&
                  rect.width > 0 &&
                  rect.height > 0 &&
                  rect.x !== undefined &&
                  rect.y !== undefined,
              ),
          );
        const processedCircles = elements.circles
          ? elements.circles.map((c) => {
            // Preserve original IDs before spreading shape_properties/shape_info
            // which may contain marker IDs that should not override the real shape_id
            const originalShapeId = c.shape_id;
            const originalId = c.id;

            const circle = {
              ...c,
              ...(c.shape_properties || {}),
              ...(c.shape_info || {}),
            };

            // CRITICAL: Prioritize shape_id from API over id from shape_properties
            // shape_id is the authoritative ID from the database
            // shape_properties.id may contain marker IDs that should be ignored
            if (originalShapeId) {
              circle.id = originalShapeId;
            } else if (originalId && !isMarkerId(originalId)) {
              circle.id = originalId;
            } else if (!circle.id) {
              circle.id = `circle_${Date.now()}_${Math.random()}`;
            }
            return circle;
          })
          : [];

        setCircles(processedCircles);

        const processedScribbles = elements.scribbles
          ? elements.scribbles.map((s) => ({
            ...s,
            ...(s.shape_properties || {}),
            ...(s.shape_info || {}),
          }))
          : [];
        setScribbles(processedScribbles);
        // CRITICAL: Convert legacy arrows to unified format and merge into lines array
        const processedArrows = elements.arrows
          ? elements.arrows.map(mapLegacyLoadedArrowToUnifiedLine)
          : [];
        // Keep arrows array empty for backward compatibility (but don't use it)
        setArrows([]);

        const processedLines = elements.lines
          ? elements.lines
              .map(processLoadedUnifiedLineLike)
              .filter(isValidLoadedUnifiedLineLike)
          : [];

        // CRITICAL: Convert legacy doubleSidedArrows to unified format and merge into lines array
        const processedDoubleSidedArrows = elements.doubleSidedArrows
          ? elements.doubleSidedArrows.map(
              mapLegacyLoadedDoubleSidedToUnifiedLine,
            )
          : [];
        // Keep doubleSidedArrows array empty for backward compatibility (but don't use it)
        setDoubleSidedArrows([]);

        // Merge all unified elements (lines, arrows, doubleSidedArrows) into lines array
        const allUnifiedLines = [
          ...processedLines,
          ...processedArrows,
          ...processedDoubleSidedArrows,
        ];
        console.log("[MyCanvasLoadDebug][branch-B][allUnifiedLines]", {
          total: allUnifiedLines.length,
          sample: allUnifiedLines.slice(0, 8).map((l) => ({
            id: l?.id || l?.shape_id,
            type: l?.type,
            x: l?.x,
            y: l?.y,
            pointsLen: Array.isArray(l?.points) ? l.points.length : null,
            firstPoints: Array.isArray(l?.points) ? l.points.slice(0, 6) : null,
          })),
        });
        try {
          const unresolved = (elements.lines || []).filter((raw) => {
            const rootPts = raw?.points;
            const infoPts = raw?.shape_info?.points;
            const propsPts = raw?.shape_properties?.points;
            return (
              !Array.isArray(rootPts) &&
              !Array.isArray(infoPts) &&
              !Array.isArray(propsPts)
            );
          });
          if (unresolved.length > 0) {
            console.warn(
              "[MyCanvasLoadDebug][unresolved-line-points][branch-B]",
              {
                count: unresolved.length,
                ids: unresolved.map((r) => r?.shape_id || r?.id).filter(Boolean),
              },
            );
          }
        } catch (e) {}
        setLines(allUnifiedLines);

        // ⬇️ Load image elements
        if (elements.images?.length) {
          const loadImagePromises = elements.images.map((imgData) => {
            return new Promise((resolve) => {
              const info = imgData.shape_info || imgData.image_properties || {};
              // CRITICAL: For edited images (cropped/flipped), prioritize imageDataUrl over src
              // imageDataUrl contains the latest edited version, while src might be stale
              const base64Src =
                imgData.imageDataUrl ||
                info.imageDataUrl ||
                imgData.src ||
                imgData.image_path ||
                "";

              if (!base64Src) {
                resolve({
                  ...info,
                  ...imgData,
                  id: imgData.shape_id || imgData.id || imgData.image_id,
                  x: imgData.x ?? info.x ?? 0,
                  y: imgData.y ?? info.y ?? 0,
                  width: imgData.width ?? info.width ?? 100,
                  height: imgData.height ?? info.height ?? 100,
                  src: "",
                  image: null,
                  maintainAspectRatio: imgData.maintainAspectRatio ?? true,
                  // compress: imgData.compress || false,
                  originalImage: null,
                  compressedImage: null,
                  referenceUrl:
                    imgData.referenceUrl ||
                    info.referenceUrl ||
                    imgData.image_properties?.referenceUrl ||
                    "",
                });
                return;
              }

              const image = new Image();
              image.crossOrigin = "anonymous";

              image.onerror = (error) => {
                resolve({
                  ...info,
                  ...imgData,
                  id: imgData.shape_id || imgData.id || imgData.image_id,
                  x: imgData.x ?? info.x ?? 0,
                  y: imgData.y ?? info.y ?? 0,
                  width: imgData.width ?? info.width ?? 100,
                  height: imgData.height ?? info.height ?? 100,
                  src: base64Src,
                  image: null,
                  maintainAspectRatio: imgData.maintainAspectRatio ?? true,
                  // compress: imgData.compress || false,
                  originalImage: null,
                  compressedImage: null,
                  // Preserve flip and rotation data even on error
                  flipHorizontal:
                    imgData.flipHorizontal ?? info.flipHorizontal ?? false,
                  flipVertical:
                    imgData.flipVertical ?? info.flipVertical ?? false,
                  hasBakedTransforms:
                    imgData.hasBakedTransforms ??
                    info.hasBakedTransforms ??
                    false,
                  rotation: imgData.rotation ?? info.rotation ?? 0,
                  // CRITICAL FIX: API images ALWAYS need flip via scale, even if hasBakedTransforms is true
                  scaleX: (() => {
                    const hasBaked =
                      imgData.hasBakedTransforms ??
                      info.hasBakedTransforms ??
                      false;
                    const isApiImage =
                      base64Src.startsWith("http://") ||
                      base64Src.startsWith("https://");
                    const shouldApplyFlipViaScale = isApiImage || !hasBaked;
                    const flipH =
                      imgData.flipHorizontal ?? info.flipHorizontal ?? false;

                    if (shouldApplyFlipViaScale && flipH) {
                      return -Math.abs(imgData.scaleX ?? info.scaleX ?? 1);
                    }
                    return Math.abs(imgData.scaleX ?? info.scaleX ?? 1);
                  })(),
                  scaleY: (() => {
                    const hasBaked =
                      imgData.hasBakedTransforms ??
                      info.hasBakedTransforms ??
                      false;
                    const isApiImage =
                      base64Src.startsWith("http://") ||
                      base64Src.startsWith("https://");
                    const shouldApplyFlipViaScale = isApiImage || !hasBaked;
                    const flipV =
                      imgData.flipVertical ?? info.flipVertical ?? false;

                    if (shouldApplyFlipViaScale && flipV) {
                      return -Math.abs(imgData.scaleY ?? info.scaleY ?? 1);
                    }
                    return Math.abs(imgData.scaleY ?? info.scaleY ?? 1);
                  })(),
                  referenceUrl:
                    imgData.referenceUrl ||
                    info.referenceUrl ||
                    imgData.image_properties?.referenceUrl ||
                    "",
                });
              };

              // Add timeout for image loading
              const timeout = setTimeout(() => {
                image.onload = null;
                image.onerror = null;
                resolve({
                  ...info,
                  ...imgData,
                  id: imgData.shape_id || imgData.id || imgData.image_id,
                  x: imgData.x ?? info.x ?? 0,
                  y: imgData.y ?? info.y ?? 0,
                  width: imgData.width ?? info.width ?? 100,
                  height: imgData.height ?? info.height ?? 100,
                  src: base64Src,
                  image: null,
                  maintainAspectRatio: imgData.maintainAspectRatio ?? true,
                  // compress: imgData.compress || false,
                  originalImage: null,
                  compressedImage: null,
                  referenceUrl:
                    imgData.referenceUrl ||
                    info.referenceUrl ||
                    imgData.image_properties?.referenceUrl ||
                    "",
                });
              }, 10000); // 10 second timeout

              image.onload = async () => {
                clearTimeout(timeout);

                // Check if this is an API image (S3 URL)
                // Only check the current src - if it's a data URL, transforms are already baked
                const originalImageDataUrl =
                  imgData.originalImageDataUrl || info.originalImageDataUrl;
                const isApiImage =
                  base64Src.startsWith("http://") ||
                  base64Src.startsWith("https://");
                const hasBakedTransforms =
                  imgData.hasBakedTransforms ??
                  info.hasBakedTransforms ??
                  false;

                const flipH =
                  imgData.flipHorizontal ?? info.flipHorizontal ?? false;
                const flipV =
                  imgData.flipVertical ?? info.flipVertical ?? false;
                const rotationAngle =
                  imgData.originalCropRotation ??
                  info.originalCropRotation ??
                  0;

                // For cropped images, we need to load the original image separately
                // if originalImageDataUrl exists and is different from the current src
                const uncompressedCroppedImageDataUrl =
                  imgData.uncompressedCroppedImageDataUrl ||
                  info.uncompressedCroppedImageDataUrl;
                const hasCropData = imgData.cropData || info.cropData;
                const isCroppedImage =
                  hasCropData &&
                  originalImageDataUrl &&
                  originalImageDataUrl !== base64Src;

                // CRITICAL FIX: Convert ALL API images with transforms to base64
                // This prevents scale inversion issues with Konva Transformer
                // Even if hasBakedTransforms=true in DB, API images need rebaking
                const hasTransforms = flipH || flipV || rotationAngle !== 0;

                // CRITICAL FIX: Bake ALL API images with transforms, even if they have cropData
                // Cropped API images also need transforms baked to avoid Konva scale issues
                if (isApiImage && hasTransforms) {

                  // Convert API image to base64 with baked transforms
                  try {
                    const canvas = document.createElement("canvas");
                    const ctx = canvas.getContext("2d");

                    // Calculate dimensions considering rotation
                    const rotationRadians = (rotationAngle * Math.PI) / 180;
                    const cos = Math.abs(Math.cos(rotationRadians));
                    const sin = Math.abs(Math.sin(rotationRadians));

                    let canvasWidth = image.naturalWidth;
                    let canvasHeight = image.naturalHeight;

                    if (rotationAngle !== 0) {
                      canvasWidth =
                        image.naturalWidth * cos + image.naturalHeight * sin;
                      canvasHeight =
                        image.naturalWidth * sin + image.naturalHeight * cos;
                    }

                    canvas.width = canvasWidth;
                    canvas.height = canvasHeight;

                    // Apply transforms
                    ctx.save();
                    ctx.translate(canvasWidth / 2, canvasHeight / 2);
                    ctx.scale(flipH ? -1 : 1, flipV ? -1 : 1);
                    ctx.rotate(rotationRadians);
                    ctx.drawImage(
                      image,
                      -image.naturalWidth / 2,
                      -image.naturalHeight / 2,
                      image.naturalWidth,
                      image.naturalHeight,
                    );
                    ctx.restore();

                    // Convert to base64
                    const bakedImageUrl = canvas.toDataURL("image/png");
                    const bakedImage = new Image();
                    bakedImage.src = bakedImageUrl;

                    await new Promise((resolveImg) => {
                      bakedImage.onload = () => resolveImg();
                    });


                    // Return with baked transforms
                    resolve({
                      ...info,
                      ...imgData,
                      id: imgData.shape_id || imgData.id || imgData.image_id,
                      x: imgData.x ?? info.x ?? 0,
                      y: imgData.y ?? info.y ?? 0,
                      width: imgData.width ?? info.width ?? 100,
                      height: imgData.height ?? info.height ?? 100,
                      src: bakedImageUrl,
                      image: bakedImage,
                      maintainAspectRatio: imgData.maintainAspectRatio ?? true,
                      originalImage: image,
                      originalImageDataUrl: base64Src,
                      compressedImage: null,
                      cropData: imgData.cropData || info.cropData, // Preserve crop data
                      originalWidth:
                        imgData.originalWidth ?? info.originalWidth,
                      originalHeight:
                        imgData.originalHeight ?? info.originalHeight,
                      flipHorizontal: flipH,
                      flipVertical: flipV,
                      hasBakedTransforms: true,
                      rotation: imgData.rotation ?? info.rotation ?? 0,
                      originalCropRotation: rotationAngle,
                      scaleX: 1,
                      scaleY: 1,
                      referenceUrl:
                        imgData.referenceUrl ||
                        info.referenceUrl ||
                        imgData.image_properties?.referenceUrl ||
                        "",
                    });
                    return;
                  } catch (error) {
                    console.error(
                      `[MyCanvas] ❌ ERROR baking transforms for API image ${imgData.shape_id || imgData.id
                      }:`,
                      error,
                    );
                    console.error(
                      "[MyCanvas] Possible CORS issue? Image will load with negative scale instead.",
                    );
                    // Fall through to normal loading
                  }
                }

                if (isCroppedImage) {
                  // Load the original image for uncropping functionality
                  const originalImage = new Image();
                  originalImage.crossOrigin = "anonymous";

                  // Load uncompressed cropped version if available
                  const loadUncompressedCropped = new Promise(
                    (resolveUncropped) => {
                      if (
                        uncompressedCroppedImageDataUrl &&
                        uncompressedCroppedImageDataUrl !== base64Src
                      ) {
                        const uncompressedCroppedImage = new Image();
                        uncompressedCroppedImage.crossOrigin = "anonymous";
                        uncompressedCroppedImage.onload = () =>
                          resolveUncropped(uncompressedCroppedImage);
                        uncompressedCroppedImage.onerror = () =>
                          resolveUncropped(image); // Fallback to cropped image
                        uncompressedCroppedImage.src =
                          uncompressedCroppedImageDataUrl;
                      } else {
                        resolveUncropped(image); // No uncompressed version, use current
                      }
                    },
                  );

                  originalImage.onload = async () => {
                    const uncompressedCroppedImage =
                      await loadUncompressedCropped;

                    resolve({
                      ...info,
                      ...imgData,
                      id: imgData.shape_id || imgData.id || imgData.image_id,
                      x: imgData.x ?? info.x ?? 0,
                      y: imgData.y ?? info.y ?? 0,
                      width: imgData.width ?? info.width ?? 100,
                      height: imgData.height ?? info.height ?? 100,
                      src: base64Src, // This is the cropped image URL
                      image, // This is the cropped image (potentially compressed)
                      maintainAspectRatio: imgData.maintainAspectRatio ?? true,
                      compress: imgData.compress !== false ? true : false,
                      originalImage: originalImage, // This is the original uncropped image
                      originalImageDataUrl: originalImageDataUrl,
                      compressedImage: null,
                      uncompressedCroppedImage: uncompressedCroppedImage, // Store uncompressed cropped version
                      cropData: imgData.cropData || info.cropData, // Pass crop data
                      // Preserve flip and rotation data
                      flipHorizontal:
                        imgData.flipHorizontal ?? info.flipHorizontal ?? false,
                      flipVertical:
                        imgData.flipVertical ?? info.flipVertical ?? false,
                      originalCropRotation:
                        imgData.originalCropRotation ??
                        info.originalCropRotation ??
                        0,
                      hasBakedTransforms:
                        imgData.hasBakedTransforms ??
                        info.hasBakedTransforms ??
                        false,
                      rotation: imgData.rotation ?? info.rotation ?? 0,
                      // CRITICAL FIX: API images ALWAYS need flip via scale, even if hasBakedTransforms is true
                      // Only use positive scale for LOCAL BLOBS with hasBakedTransforms
                      scaleX: (() => {
                        const hasBaked =
                          imgData.hasBakedTransforms ??
                          info.hasBakedTransforms ??
                          false;
                        const isApiImage =
                          base64Src.startsWith("http://") ||
                          base64Src.startsWith("https://");
                        const shouldApplyFlipViaScale = isApiImage || !hasBaked;
                        const flipH =
                          imgData.flipHorizontal ??
                          info.flipHorizontal ??
                          false;

                        if (shouldApplyFlipViaScale && flipH) {
                          return -Math.abs(imgData.scaleX ?? info.scaleX ?? 1);
                        }
                        return Math.abs(imgData.scaleX ?? info.scaleX ?? 1);
                      })(),
                      scaleY: (() => {
                        const hasBaked =
                          imgData.hasBakedTransforms ??
                          info.hasBakedTransforms ??
                          false;
                        const isApiImage =
                          base64Src.startsWith("http://") ||
                          base64Src.startsWith("https://");
                        const shouldApplyFlipViaScale = isApiImage || !hasBaked;
                        const flipV =
                          imgData.flipVertical ?? info.flipVertical ?? false;

                        if (shouldApplyFlipViaScale && flipV) {
                          return -Math.abs(imgData.scaleY ?? info.scaleY ?? 1);
                        }
                        return Math.abs(imgData.scaleY ?? info.scaleY ?? 1);
                      })(),
                      // Calculate original aspect ratio from saved data or current dimensions
                      originalAspectRatio:
                        imgData.originalAspectRatio ||
                        (imgData.width ?? info.width ?? 100) /
                        (imgData.height ?? info.height ?? 100),
                      // Preserve original dimensions
                      originalWidth:
                        imgData.originalWidth ??
                        info.originalWidth ??
                        originalImage.naturalWidth ??
                        100,
                      originalHeight:
                        imgData.originalHeight ??
                        info.originalHeight ??
                        originalImage.naturalHeight ??
                        100,
                      referenceUrl:
                        imgData.referenceUrl ||
                        info.referenceUrl ||
                        imgData.image_properties?.referenceUrl ||
                        "",
                    });
                  };
                  originalImage.onerror = async () => {
                    const uncompressedCroppedImage =
                      await loadUncompressedCropped;

                    // If original image fails to load, use the current image as both
                    resolve({
                      ...info,
                      ...imgData,
                      id: imgData.shape_id || imgData.id || imgData.image_id,
                      x: imgData.x ?? info.x ?? 0,
                      y: imgData.y ?? info.y ?? 0,
                      width: imgData.width ?? info.width ?? 100,
                      height: imgData.height ?? info.height ?? 100,
                      src: base64Src,
                      image,
                      maintainAspectRatio: imgData.maintainAspectRatio ?? true,
                      compress: imgData.compress !== false ? true : false,
                      originalImage: image,
                      originalImageDataUrl: originalImageDataUrl,
                      compressedImage: null,
                      uncompressedCroppedImage: uncompressedCroppedImage,
                      cropData: imgData.cropData || info.cropData, // Pass crop data
                      // Preserve flip and rotation data
                      flipHorizontal:
                        imgData.flipHorizontal ?? info.flipHorizontal ?? false,
                      flipVertical:
                        imgData.flipVertical ?? info.flipVertical ?? false,
                      originalCropRotation:
                        imgData.originalCropRotation ??
                        info.originalCropRotation ??
                        0,
                      hasBakedTransforms:
                        imgData.hasBakedTransforms ??
                        info.hasBakedTransforms ??
                        false,
                      rotation: imgData.rotation ?? info.rotation ?? 0,
                      // CRITICAL FIX: API images ALWAYS need flip via scale, even if hasBakedTransforms is true
                      // Only use positive scale for LOCAL BLOBS with hasBakedTransforms
                      scaleX: (() => {
                        const hasBaked =
                          imgData.hasBakedTransforms ??
                          info.hasBakedTransforms ??
                          false;
                        const isApiImage =
                          base64Src.startsWith("http://") ||
                          base64Src.startsWith("https://");
                        const shouldApplyFlipViaScale = isApiImage || !hasBaked;
                        const flipH =
                          imgData.flipHorizontal ??
                          info.flipHorizontal ??
                          false;

                        if (shouldApplyFlipViaScale && flipH) {
                          return -Math.abs(imgData.scaleX ?? info.scaleX ?? 1);
                        }
                        return Math.abs(imgData.scaleX ?? info.scaleX ?? 1);
                      })(),
                      scaleY: (() => {
                        const hasBaked =
                          imgData.hasBakedTransforms ??
                          info.hasBakedTransforms ??
                          false;
                        const isApiImage =
                          base64Src.startsWith("http://") ||
                          base64Src.startsWith("https://");
                        const shouldApplyFlipViaScale = isApiImage || !hasBaked;
                        const flipV =
                          imgData.flipVertical ?? info.flipVertical ?? false;

                        if (shouldApplyFlipViaScale && flipV) {
                          return -Math.abs(imgData.scaleY ?? info.scaleY ?? 1);
                        }
                        return Math.abs(imgData.scaleY ?? info.scaleY ?? 1);
                      })(),
                      referenceUrl:
                        imgData.referenceUrl ||
                        info.referenceUrl ||
                        imgData.image_properties?.referenceUrl ||
                        "",
                      // Calculate original aspect ratio from saved data or current dimensions
                      originalAspectRatio:
                        imgData.originalAspectRatio ||
                        (imgData.width ?? info.width ?? 100) /
                        (imgData.height ?? info.height ?? 100),
                      // Preserve original dimensions
                      originalWidth:
                        imgData.originalWidth ??
                        info.originalWidth ??
                        imgData.width ??
                        info.width ??
                        100,
                      originalHeight:
                        imgData.originalHeight ??
                        info.originalHeight ??
                        imgData.height ??
                        info.height ??
                        100,
                    });
                  };
                  originalImage.src = originalImageDataUrl;
                } else {
                  // Not a cropped image, use the same image for both
                  resolve({
                    ...info,
                    ...imgData,
                    id: imgData.shape_id || imgData.id || imgData.image_id,
                    x: imgData.x ?? info.x ?? 0,
                    y: imgData.y ?? info.y ?? 0,
                    width: imgData.width ?? info.width ?? 100,
                    height: imgData.height ?? info.height ?? 100,
                    src: base64Src,
                    image,
                    maintainAspectRatio: imgData.maintainAspectRatio ?? true,
                    compress: imgData.compress !== false ? true : false,
                    originalImage: image,
                    compressedImage: imgData.compressedImage || null,
                    uncompressedCroppedImage:
                      imgData.uncompressedCroppedImage || null,
                    cropData: imgData.cropData || info.cropData, // Pass crop data
                    originalCropRotation:
                      imgData.originalCropRotation ??
                      info.originalCropRotation ??
                      0, // Preserve straightening angle
                    // Preserve flip and rotation data
                    flipHorizontal:
                      imgData.flipHorizontal ?? info.flipHorizontal ?? false,
                    flipVertical:
                      imgData.flipVertical ?? info.flipVertical ?? false,
                    hasBakedTransforms:
                      imgData.hasBakedTransforms ??
                      info.hasBakedTransforms ??
                      false,
                    rotation: imgData.rotation ?? info.rotation ?? 0,
                    // CRITICAL FIX: API images ALWAYS need flip via scale, even if hasBakedTransforms is true
                    scaleX: (() => {
                      const hasBaked =
                        imgData.hasBakedTransforms ??
                        info.hasBakedTransforms ??
                        false;
                      const isApiImage =
                        base64Src.startsWith("http://") ||
                        base64Src.startsWith("https://");
                      const shouldApplyFlipViaScale = isApiImage || !hasBaked;
                      const flipH =
                        imgData.flipHorizontal ?? info.flipHorizontal ?? false;

                      if (shouldApplyFlipViaScale && flipH) {
                        return -Math.abs(imgData.scaleX ?? info.scaleX ?? 1);
                      }
                      return Math.abs(imgData.scaleX ?? info.scaleX ?? 1);
                    })(),
                    scaleY: (() => {
                      const hasBaked =
                        imgData.hasBakedTransforms ??
                        info.hasBakedTransforms ??
                        false;
                      const isApiImage =
                        base64Src.startsWith("http://") ||
                        base64Src.startsWith("https://");
                      const shouldApplyFlipViaScale = isApiImage || !hasBaked;
                      const flipV =
                        imgData.flipVertical ?? info.flipVertical ?? false;

                      if (shouldApplyFlipViaScale && flipV) {
                        return -Math.abs(imgData.scaleY ?? info.scaleY ?? 1);
                      }
                      return Math.abs(imgData.scaleY ?? info.scaleY ?? 1);
                    })(),
                    // Calculate original aspect ratio from saved data or current dimensions
                    originalAspectRatio:
                      imgData.originalAspectRatio ||
                      (imgData.width ?? info.width ?? 100) /
                      (imgData.height ?? info.height ?? 100),
                    // Preserve original dimensions from stored data or use current dimensions as fallback
                    originalWidth:
                      imgData.originalWidth ??
                      info.originalWidth ??
                      imgData.width ??
                      info.width ??
                      100,
                    originalHeight:
                      imgData.originalHeight ??
                      info.originalHeight ??
                      imgData.height ??
                      info.height ??
                      100,
                    referenceUrl:
                      imgData.referenceUrl ||
                      info.referenceUrl ||
                      imgData.image_properties?.referenceUrl ||
                      "",
                  });
                }
              };

              image.onerror = (error) => {
                clearTimeout(timeout);

                resolve({
                  ...info,
                  ...imgData,
                  id: imgData.shape_id || imgData.id || imgData.image_id,
                  x: imgData.x ?? info.x ?? 0,
                  y: imgData.y ?? info.y ?? 0,
                  width: imgData.width ?? info.width ?? 100,
                  height: imgData.height ?? info.height ?? 100,
                  src: base64Src,
                  image: null,
                  maintainAspectRatio: imgData.maintainAspectRatio ?? true,
                  // compress: imgData.compress || false,
                  originalImage: null,
                  // Preserve flip and rotation data even on error
                  flipHorizontal:
                    imgData.flipHorizontal ?? info.flipHorizontal ?? false,
                  flipVertical:
                    imgData.flipVertical ?? info.flipVertical ?? false,
                  hasBakedTransforms:
                    imgData.hasBakedTransforms ??
                    info.hasBakedTransforms ??
                    false,
                  rotation: imgData.rotation ?? info.rotation ?? 0,
                  // CRITICAL FIX: API images ALWAYS need flip via scale, even if hasBakedTransforms is true
                  scaleX: (() => {
                    const hasBaked =
                      imgData.hasBakedTransforms ??
                      info.hasBakedTransforms ??
                      false;
                    const isApiImage =
                      base64Src.startsWith("http://") ||
                      base64Src.startsWith("https://");
                    const shouldApplyFlipViaScale = isApiImage || !hasBaked;
                    const flipH =
                      imgData.flipHorizontal ?? info.flipHorizontal ?? false;

                    if (shouldApplyFlipViaScale && flipH) {
                      return -Math.abs(imgData.scaleX ?? info.scaleX ?? 1);
                    }
                    return Math.abs(imgData.scaleX ?? info.scaleX ?? 1);
                  })(),
                  scaleY: (() => {
                    const hasBaked =
                      imgData.hasBakedTransforms ??
                      info.hasBakedTransforms ??
                      false;
                    const isApiImage =
                      base64Src.startsWith("http://") ||
                      base64Src.startsWith("https://");
                    const shouldApplyFlipViaScale = isApiImage || !hasBaked;
                    const flipV =
                      imgData.flipVertical ?? info.flipVertical ?? false;

                    if (shouldApplyFlipViaScale && flipV) {
                      return -Math.abs(imgData.scaleY ?? info.scaleY ?? 1);
                    }
                    return Math.abs(imgData.scaleY ?? info.scaleY ?? 1);
                  })(),
                  compressedImage: null,
                });
              };

              image.src = base64Src;
            });
          });

          const loadedImages = await Promise.all(loadImagePromises);

          // Filter out images that failed to load and log them
          const successfulImages = loadedImages.filter(
            (img) => img.image !== null,
          );
          const failedImages = loadedImages.filter((img) => img.image === null);

          if (failedImages.length > 0) {
            failedImages.forEach((img) => { });
          }

          const recoveryImages = await Promise.all(
            successfulImages.map((img) => {
              if (!img.image && img.src?.startsWith("data:image/")) {
                return new Promise((resolve) => {
                  const fallback = new Image();
                  fallback.crossOrigin = "anonymous";
                  fallback.onload = () => {
                    resolve({
                      ...img,
                      image: fallback,
                      originalImage: fallback,
                    });
                  };
                  fallback.onerror = () => {
                    resolve(img);
                  };
                  fallback.src = img.src;
                });
              }
              return Promise.resolve(img);
            }),
          );

          // Deduplicate images by ID before setting state
          const uniqueImages = [];
          const seenIds = new Set();
          for (const img of recoveryImages) {
            if (!seenIds.has(img.id)) {
              seenIds.add(img.id);
              uniqueImages.push(img);
            }
          }

          setImages(uniqueImages);

          // POST-PROCESSING: Bake API images with transforms after cache load
          // This ensures API images are converted to base64 even when loaded from IndexedDB
          setTimeout(async () => {
            const imagesToBake = uniqueImages.filter((img) => {
              const isApiImage =
                img.src &&
                (img.src.startsWith("http://") ||
                  img.src.startsWith("https://"));
              const hasFlips = img.flipHorizontal || img.flipVertical;
              const hasRotation = (img.originalCropRotation || 0) !== 0;
              return isApiImage && (hasFlips || hasRotation);
            });

            if (imagesToBake.length > 0) {

              const bakedImages = await Promise.all(
                imagesToBake.map(async (img) => {
                  try {
                    const canvas = document.createElement("canvas");
                    const ctx = canvas.getContext("2d");

                    // Apply transforms
                    const flipH = img.flipHorizontal || false;
                    const flipV = img.flipVertical || false;
                    const rotation =
                      (img.originalCropRotation || 0) * (Math.PI / 180);

                    canvas.width = img.image.naturalWidth;
                    canvas.height = img.image.naturalHeight;

                    ctx.save();
                    ctx.translate(canvas.width / 2, canvas.height / 2);
                    ctx.scale(flipH ? -1 : 1, flipV ? -1 : 1);
                    ctx.rotate(rotation);
                    ctx.drawImage(
                      img.image,
                      -img.image.naturalWidth / 2,
                      -img.image.naturalHeight / 2,
                      img.image.naturalWidth,
                      img.image.naturalHeight,
                    );
                    ctx.restore();

                    const bakedUrl = canvas.toDataURL("image/png");
                    const bakedImg = new Image();
                    bakedImg.src = bakedUrl;

                    await new Promise((r) => (bakedImg.onload = r));


                    return {
                      ...img,
                      src: bakedUrl,
                      image: bakedImg,
                      originalImage: img.image,
                      originalImageDataUrl: img.src,
                      hasBakedTransforms: true,
                      scaleX: 1,
                      scaleY: 1,
                    };
                  } catch (error) {
                    console.error(
                      `[MyCanvas] ❌ Failed to post-bake image ${img.id}:`,
                      error,
                    );
                    return img;
                  }
                }),
              );

              // Update images state with baked versions
              setImages((prevImages) =>
                prevImages.map((img) => {
                  const baked = bakedImages.find((b) => b.id === img.id);
                  return baked || img;
                }),
              );
            }
          }, 100);
        }

        // Load videos (similar to images)
        if (elements.videos?.length) {
          const loadVideoPromises = elements.videos.map((videoData) => {
            return new Promise((resolve) => {
              const info =
                videoData.shape_info || videoData.video_properties || {};
              const videoSrc =
                videoData.src ||
                videoData.videoDataUrl ||
                info.videoDataUrl ||
                videoData.video_path ||
                "";

              if (!videoSrc) {
                resolve({
                  ...info,
                  ...videoData,
                  id: videoData.shape_id || videoData.id || videoData.video_id,
                  x: videoData.x ?? info.x ?? 0,
                  y: videoData.y ?? info.y ?? 0,
                  width: videoData.width ?? info.width ?? 320,
                  height: videoData.height ?? info.height ?? 180,
                  src: "",
                  duration: videoData.duration || 0,
                  videoNumber: videoData.videoNumber || 1,
                  name: videoData.name || "Video",
                });
                return;
              }

              const video = document.createElement("video");
              video.crossOrigin = "anonymous";
              video.preload = "metadata";

              video.onloadedmetadata = async () => {
                // Generate thumbnail for loaded video
                const thumbnail = await generateVideoThumbnail(
                  videoSrc,
                  videoData.width ?? info.width ?? 320,
                  videoData.height ?? info.height ?? 180,
                );

                resolve({
                  ...info,
                  ...videoData,
                  id: videoData.shape_id || videoData.id || videoData.video_id,
                  x: videoData.x ?? info.x ?? 0,
                  y: videoData.y ?? info.y ?? 0,
                  width: videoData.width ?? info.width ?? 320,
                  height: videoData.height ?? info.height ?? 180,
                  src: videoSrc,
                  duration: isFinite(video.duration)
                    ? video.duration
                    : videoData.duration || 0,
                  videoNumber: videoData.videoNumber || 1,
                  name: videoData.name || "Video",
                  thumbnail: thumbnail,
                });
              };

              video.onerror = (error) => {
                resolve({
                  ...info,
                  ...videoData,
                  id: videoData.shape_id || videoData.id || videoData.video_id,
                  x: videoData.x ?? info.x ?? 0,
                  y: videoData.y ?? info.y ?? 0,
                  width: videoData.width ?? info.width ?? 320,
                  height: videoData.height ?? info.height ?? 180,
                  src: videoSrc,
                  duration: videoData.duration || 0,
                  videoNumber: videoData.videoNumber || 1,
                  name: videoData.name || "Video",
                });
              };

              // Add timeout for video loading
              const timeout = setTimeout(() => {
                video.onloadedmetadata = null;
                video.onerror = null;
                resolve({
                  ...info,
                  ...videoData,
                  id: videoData.shape_id || videoData.id || videoData.video_id,
                  x: videoData.x ?? info.x ?? 0,
                  y: videoData.y ?? info.y ?? 0,
                  width: videoData.width ?? info.width ?? 320,
                  height: videoData.height ?? info.height ?? 180,
                  src: videoSrc,
                  duration: videoData.duration || 0,
                  videoNumber: videoData.videoNumber || 1,
                  name: videoData.name || "Video",
                });
              }, 10000); // 10 second timeout

              video.src = videoSrc;
            });
          });

          const loadedVideos = await Promise.all(loadVideoPromises);

          // Filter out videos that failed to load and log them
          const successfulVideos = loadedVideos.filter(
            (video) => video.src && video.src !== "",
          );
          const failedVideos = loadedVideos.filter(
            (video) => !video.src || video.src === "",
          );

          if (failedVideos.length > 0) {
            failedVideos.forEach((video) => { });
          }

          setVideos(successfulVideos);
        }

        // Load markers
        const markersResult = await loadFromIndexedDB(
          "canvasMarkers",
          sanitizedPageId,
        );
        const markers = markersResult?.data || markersResult;

        // setLocationMarkers(markers); // Uncomment if needed
        // Note: History initialization moved to a single useEffect to prevent duplicates
      } catch (error) { }
    };

    // Only load elements on initialization if we're not coming from EditCanvasPage
    if (!location.state?.fromCanvasPage) {
      if (savedSnapshot || legacyState) {
        const version =
          Number(localStorage.getItem("canvasDBVersion")) || DB_VERSION;
        loadCanvasElements(version);
      }
      // Note: History initialization moved to a single useEffect to prevent duplicates
    }
    // Note: History initialization moved to a single useEffect to prevent duplicates
  }, []);

  useEffect(() => {
    // Don't clear selectedObject if clone mode is active or tool is locked
    // This allows the editor to stay open when clone mode is enabled
    // Only clear when action changes AND clone mode is not active
    if (isCloneMode || lockedAction) {
      // Don't clear selectedObject when clone mode is active - keep editor open
      return;
    }
    // Only clear when action changes away from SELECT (not when selectedObject changes)
    // This prevents clearing when clones are created (which might cause selectedObject reference to change)
  }, [action, isCloneMode, lockedAction]);

  // Create undo and redo functions using factories (needed before keyboard shortcuts useEffect)
  offCanvasImagesRef.current = offCanvasImages; // So undo reads latest when add_image entry had empty offCanvasImages
  const undo = createUndo({
    history,
    setHistory,
    setRedoStack,
    isUndoingRedoing,
    lastUndoRedoCompletionTime,
    fillColor,
    setSelectedObject,
    setSelectedText,
    setSelectedTextId,
    setSelectedImage,
    setSelectedArrow,
    setSelectedLine,
    setSelectedObjects,
    setArrows,
    setLines,
    setRectangles,
    setCircles,
    setDoubleSidedArrows,
    setTexts,
    setVideos,
    setScribbles,
    setOffCanvasTexts,
    offCanvasTexts,
    setOffCanvasRectangles,
    setOffCanvasCircles,
    setOffCanvasArrows,
    setOffCanvasDoubleSidedArrows,
    setOffCanvasImages,
    setOffCanvasScribbles,
    setOffCanvasLines,
    setOffCanvasVideos,
    setImages,
    images, // Pass current images state to preserve image objects
    offCanvasImages,
    offCanvasImagesRef, // Read at undo time so we get latest (avoids stale closure from Toolbar)
    rectangles, // Pass current rectangles state to preserve rectangles
    circles, // Pass current circles state to preserve circles
    offCanvasRectangles, // Fallback when history saved offCanvasRectangles empty (e.g. add_rectangle off-canvas)
    offCanvasCircles, // Fallback when history saved offCanvasCircles empty (e.g. add_circle off-canvas)
    setCanvasHeight,
  });

  const redo = createRedo({
    redoStack,
    history, // Pass history for state comparison
    setHistory,
    setRedoStack,
    isUndoingRedoing,
    lastUndoRedoCompletionTime,
    fillColor,
    setSelectedObject,
    setSelectedText,
    setSelectedTextId,
    setSelectedImage,
    setSelectedArrow,
    setSelectedLine,
    setSelectedObjects,
    setArrows,
    setLines,
    setRectangles,
    setCircles,
    setDoubleSidedArrows,
    setTexts,
    setVideos,
    setScribbles,
    setOffCanvasTexts,
    offCanvasTexts,
    setOffCanvasRectangles,
    setOffCanvasCircles,
    setOffCanvasArrows,
    setOffCanvasDoubleSidedArrows,
    setOffCanvasImages,
    setOffCanvasScribbles,
    setOffCanvasLines,
    setOffCanvasVideos,
    setImages,
    images, // Pass current images state to preserve image objects
    setCanvasHeight,
  });

  // Add keyboard shortcuts for undo/redo/select all
  useEffect(() => {
    const handleKeyDown = createKeyboardHandler({
      undo: () => undo(offCanvasImages),
      redo,
      selectedObject,
      deleteSelectedObject,
      selectedObjects,
      setSelectedObjects,
      setSelectedObject,
      setIsMultiSelectMode,
      deleteAllSelectedObjects,
      nudgeSelectedElements,
      stageRef,
      offCanvasStageRef: {
        current: window.__offCanvasStageRef?.current || null,
      },
      texts,
      rectangles,
      circles,
      arrows,
      lines,
      doubleSidedArrows,
      images,
      offCanvasTexts,
      offCanvasRectangles,
      offCanvasCircles,
      offCanvasArrows,
      offCanvasLines,
      offCanvasDoubleSidedArrows,
      offCanvasImages,
    });

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [
    undo,
    redo,
    selectedObject,
    deleteSelectedObject,
    selectedObjects,
    setSelectedObjects,
    setSelectedObject,
    setIsMultiSelectMode,
    deleteAllSelectedObjects,
    nudgeSelectedElements,
    stageRef,
    texts,
    rectangles,
    circles,
    arrows,
    lines,
    doubleSidedArrows,
    images,
    offCanvasTexts,
    offCanvasRectangles,
    offCanvasCircles,
    offCanvasArrows,
    offCanvasLines,
    offCanvasDoubleSidedArrows,
    offCanvasImages,
  ]);

  // Debounced version of saveToHistory to prevent too many entries during dragging
  const initialEmptyState = {
    texts: [],
    rectangles: [],
    circles: [],
    arrows: [],
    doubleSidedArrows: [],
    images: [],
    scribbles: [],
    lines: [],
    // Offcanvas elements
    offCanvasTexts: [],
    offCanvasRectangles: [],
    offCanvasCircles: [],
    offCanvasArrows: [],
    offCanvasDoubleSidedArrows: [],
    offCanvasImages: [],
    offCanvasScribbles: [],
    offCanvasLines: [],
    offCanvasVideos: [],
  };

  // Function to restore images with HTML elements from serialized data
  // restoreImagesFromHistory is now imported from utils

  // Add new function to handle image updates
  // ⚠️ PERFORMANCE NOTE: This function is EXPENSIVE - it updates all images and queues history saves
  // DO NOT call this on every drag move event - only on drag END or discrete property changes
  // During drag move, only update selectedObject state for immediate UI feedback
  // updateImageProperties is now imported from utils

  // updateVideoProperties is now imported from utils

  // handleImageDragEnd is now imported from utils

  // handleImageResize is now imported from utils

  // Function to ensure arrows are saved with absolute coordinates
  // This helper function is crucial and should be defined somewhere accessible

  // clearSavedCanvasState is now imported from utils

  // prepareAndSaveCanvasData is now imported from utils/saveUtils
  // Must be defined before handleLeaveCanvasLocal since it uses it
  const prepareAndSaveCanvasData = useCallback(
    async (snapshot, overrides) => {
      return await prepareAndSaveCanvasDataUtil({
        snapshot,
        images: overrides?.images ?? images,
        videos: overrides?.videos ?? videos,
        texts: overrides?.texts ?? texts,
        rectangles: overrides?.rectangles ?? rectangles,
        circles: overrides?.circles ?? circles,
        arrows: overrides?.arrows ?? arrows,
        lines: overrides?.lines ?? lines,
        doubleSidedArrows: overrides?.doubleSidedArrows ?? doubleSidedArrows,
        scribbles: overrides?.scribbles ?? scribbles,
        offCanvasTexts: overrides?.offCanvasTexts ?? offCanvasTexts,
        offCanvasRectangles:
          overrides?.offCanvasRectangles ?? offCanvasRectangles,
        offCanvasCircles: overrides?.offCanvasCircles ?? offCanvasCircles,
        offCanvasArrows: overrides?.offCanvasArrows ?? offCanvasArrows,
        offCanvasLines: overrides?.offCanvasLines ?? offCanvasLines,
        offCanvasDoubleSidedArrows:
          overrides?.offCanvasDoubleSidedArrows ?? offCanvasDoubleSidedArrows,
        offCanvasScribbles: overrides?.offCanvasScribbles ?? offCanvasScribbles,
        offCanvasImages: overrides?.offCanvasImages ?? offCanvasImages,
        offCanvasVideos: overrides?.offCanvasVideos ?? offCanvasVideos,
        canvasHeight: overrides?.canvasHeight ?? canvasHeight,
        pageId,
        fromCanvasTemplate,
        setCanvasPages,
        setHasUnsavedChanges,
        saveCanvasElementsToIndexedDB,
        validTextShapeIds:
          overrides?.validTextShapeIds ?? location.state?.validTextShapeIds,
      });
    },
    [
      images,
      videos,
      texts,
      rectangles,
      circles,
      arrows,
      lines,
      doubleSidedArrows,
      scribbles,
      offCanvasTexts,
      offCanvasRectangles,
      offCanvasCircles,
      offCanvasArrows,
      offCanvasLines,
      offCanvasDoubleSidedArrows,
      offCanvasScribbles,
      offCanvasImages,
      offCanvasVideos,
      canvasHeight,
      pageId,
      fromCanvasTemplate,
      setCanvasPages,
      setHasUnsavedChanges,
      saveCanvasElementsToIndexedDB,
      location.state?.validTextShapeIds,
    ],
  );

  // handleLeaveCanvas is now imported from utils/canvasLeaveHandler
  const handleLeaveCanvasLocal = useCallback(async () => {
    if (isLeavingCanvas) return;

    // Clear history and redo stack when leaving canvas
    setHistory([]);
    setRedoStack([]);
    // CRITICAL: Reset historyInitialized flag and set keepHistoryEmpty flag
    // This prevents history from being re-initialized until the user makes their first change
    historyInitialized.current = false;
    keepHistoryEmpty.current = true;
    // Show leave loader immediately while we fetch latest part details.
    setIsLeavingCanvas(true);

    let latestTextsForLeave = texts || [];
    let latestOffCanvasTextsForLeave = offCanvasTexts || [];
    try {
      const textElementsWithParts = [
        ...(latestTextsForLeave || []),
        ...(latestOffCanvasTextsForLeave || []),
      ].filter((text) => text?.partsByType && typeof text.partsByType === "object");

      const partSearchTerms = [];
      textElementsWithParts.forEach((text) => {
        Object.values(text.partsByType || {}).forEach((group) => {
          (group?.parts || []).forEach((partItem) => {
            const pn =
              partItem?.part_number ||
              partItem?.partNumber ||
              extractPartNumberFromTextLabel(partItem?.part);
            if (pn) partSearchTerms.push(pn);
          });
        });
      });

      const cleanedRefs = [
        ...new Set((partSearchTerms || []).filter(Boolean).map((id) => String(id))),
      ];

      if (cleanedRefs.length > 0) {
        const searchQuery = new URLSearchParams({
          search_type:
            "name,part_number,description,table_note,textbox_note,unit_name,url",
          search: cleanedRefs.join(","),
          limit: String(Math.max(cleanedRefs.length, 10)),
          offset: "0",
        }).toString();

        const response = await dispatch(fetchAllParts({ searchQuery })).unwrap();
        const payload = response?.results || response || {};
        const latestParts =
          payload?.data ||
          payload?.results ||
          payload?.parts ||
          (Array.isArray(payload) ? payload : []);

        const latestPartMap = new Map();
        (latestParts || []).forEach((part) => {
          if (part?.part_id != null && part.part_id !== "") {
            latestPartMap.set(String(part.part_id), part);
          }
        });

        if (latestPartMap.size > 0) {
          const nextMainTexts = (latestTextsForLeave || []).map((text) =>
            updateImportedTextWithLatestPartDetails(text, latestPartMap),
          );
          const nextOffCanvasTexts = (latestOffCanvasTextsForLeave || []).map((text) =>
            updateImportedTextWithLatestPartDetails(text, latestPartMap),
          );

          const mainChanged = nextMainTexts.some(
            (text, idx) => text !== latestTextsForLeave[idx],
          );
          const offCanvasChanged = nextOffCanvasTexts.some(
            (text, idx) => text !== latestOffCanvasTextsForLeave[idx],
          );

          if (mainChanged) {
            latestTextsForLeave = nextMainTexts;
            setTexts(nextMainTexts);
          }
          if (offCanvasChanged) {
            latestOffCanvasTextsForLeave = nextOffCanvasTexts;
            setOffCanvasTexts(nextOffCanvasTexts);
          }
        }
      }
    } catch (error) {
      // Continue leave flow even if part refresh fails.
    }

    const prepareAndSaveCanvasDataForLeave = async (snapshot, overrides = {}) => {
      const removedMainTextIds = new Set(
        (removedTexts || []).filter(Boolean).map((id) => String(id)),
      );
      const removedOffTextIds = new Set(
        (removedOffCanvasTexts || []).filter(Boolean).map((id) => String(id)),
      );
      const baseMainTexts = overrides?.texts ?? latestTextsForLeave;
      const baseOffTexts = overrides?.offCanvasTexts ?? latestOffCanvasTextsForLeave;
      const filteredMainTexts = Array.isArray(baseMainTexts)
        ? baseMainTexts.filter((t) => !removedMainTextIds.has(String(t?.id)))
        : [];
      const filteredOffTexts = Array.isArray(baseOffTexts)
        ? baseOffTexts.filter((t) => !removedOffTextIds.has(String(t?.id)))
        : [];
      return prepareAndSaveCanvasData(snapshot, {
        ...overrides,
        texts: filteredMainTexts,
        offCanvasTexts: filteredOffTexts,
      });
    };

    await handleLeaveCanvas({
      pageId,
      isLeavingCanvas: false,
      setIsLeavingCanvas,
      setSnapshot,
      stageRef,
      topLayerStageRef,
      transformerRef,
      setSelectedTextId,
      setSelectedArrow,
      setSelectedObject,
      setShowTextBox,
      setSelectedImage,
      setSelectedObjects,
      setAction,
      ACTIONS_SELECT: ACTIONS.SELECT,
      fromCanvasTemplate,
      texts: latestTextsForLeave,
      images,
      rectangles,
      circles,
      arrows,
      lines,
      scribbles,
      doubleSidedArrows,
      prepareAndSaveCanvasData: prepareAndSaveCanvasDataForLeave,
      navigate,
      location, // Pass location for navigation state preservation
      removedShapeIds: {
        removedRectangles,
        removedCircles,
        removedArrows,
        removedDoubleSidedArrows,
        removedScribbles,
        removedTexts,
        removedImages,
        removedVideos,
        removedLines,
        removedOffCanvasRectangles,
        removedOffCanvasCircles,
        removedOffCanvasArrows,
        removedOffCanvasDoubleSidedArrows,
        removedOffCanvasScribbles,
        removedOffCanvasTexts,
        removedOffCanvasImages,
        removedOffCanvasVideos,
        removedOffCanvasLines,
        removedPartReferences, // Track removed part references
      },
      initialArraysState: initialArraysStateRef.current, // Pass initial state for change detection
      userMadeChanges: userMadeChangesRef.current, // Pass flag indicating if user made changes
      canvasHeight, // Pass current canvasHeight for change detection
      openCanvasDB,
    });
  }, [
    pageId,
    isLeavingCanvas,
    setIsLeavingCanvas,
    setSnapshot,
    stageRef,
    transformerRef,
    setSelectedTextId,
    setSelectedArrow,
    setSelectedObject,
    setShowTextBox,
    setSelectedImage,
    setSelectedObjects,
    setAction,
    fromCanvasTemplate,
    texts,
    offCanvasTexts,
    setTexts,
    setOffCanvasTexts,
    images,
    rectangles,
    circles,
    arrows,
    lines,
    prepareAndSaveCanvasData,
    navigate,
    location, // Add location to dependencies
    removedRectangles,
    removedCircles,
    removedArrows,
    removedDoubleSidedArrows,
    removedScribbles,
    removedTexts,
    removedImages,
    removedVideos,
    removedLines,
    removedOffCanvasRectangles,
    removedOffCanvasCircles,
    removedOffCanvasArrows,
    removedOffCanvasDoubleSidedArrows,
    removedOffCanvasScribbles,
    removedOffCanvasTexts,
    removedOffCanvasImages,
    removedOffCanvasVideos,
    removedOffCanvasLines,
    removedPartReferences,
    scribbles,
    doubleSidedArrows,
    canvasHeight, // Add canvasHeight to dependencies
    setHistory, // Add for clearing history on leave
    setRedoStack, // Add for clearing redo stack on leave
    openCanvasDB,
    dispatch,
  ]);

  // saveCanvasElementsToIndexedDB is now imported from utils/indexedDBStorageUtils

  // Clean up transformer and reset selections

  // Get dynamic bounds that include ALL canvas elements

  // prepareAndSaveCanvasData is now defined above (before handleLeaveCanvasLocal)

  // Navigate back with state
  // navigateBack is now imported from utils

  // Helper functions

  // deduplicate is now imported from utils

  // Optimized SVG snapshot generation

  // calculateDrawingBounds is now imported from utils

  // Optimize SVG attributes

  // processImagesForStorage is now imported from utils

  // deduplicateByIdOrMarkerId is now imported from utils

  // prepareCanvasData is now imported from utils

  // updateCanvasState is now imported from utils/canvasStateManagementUtils
  const updateCanvasStateLocal = useCallback(
    async (canvasData, snapshot) => {
      await updateCanvasState({
        canvasData,
        snapshot,
        setCanvasPages,
        setHasUnsavedChanges,
        pageId,
        fromCanvasTemplate,
        texts,
        rectangles,
        circles,
        arrows,
        lines,
        doubleSidedArrows,
        scribbles,
      });
    },
    [
      pageId,
      fromCanvasTemplate,
      texts,
      rectangles,
      circles,
      arrows,
      lines,
      doubleSidedArrows,
      scribbles,
      setCanvasPages,
      setHasUnsavedChanges,
    ],
  );

  // navigateWithState is now imported from utils
  // handleSort is now imported from utils
  // handleEditPart is now imported from utils
  // showSnackbar is now imported from utils
  const presetColors = [
    "#000000", // Black
    "#FF0000", // Red
    "#800080", // Purple
    "#0000FF", // Blue
    "#008000", // Green
  ];
  // handleReplacePart is now imported from utils

  // Create handleSelectPart using factory
  const handleSelectPart = createHandleSelectPart({
    generateMarkerId,
    selectedTextId,
    texts,
    setTexts,
    selectedText,
    setSelectedText,
    setPartsModalOpen: setPartsModalOpen, // Only used to close modal in createHandleSelectPart
    saveToHistory,
    isElementBeingEdited,
    setPendingChanges,
    pendingChangesRef,
    offCanvasTexts, // Add off-canvas texts array
    setOffCanvasTexts, // Add off-canvas texts setter
  });

  const handlePartUpdatedInPopup = useCallback((updatedPart) => {
    if (!updatedPart?.part_id) return;
    const partRefKey = String(updatedPart.part_id);
    const nextPartLabel = buildPartLabel(updatedPart);
    const nextTextboxNote =
      updatedPart?.textbox_note !== undefined && updatedPart?.textbox_note !== null
        ? updatedPart.textbox_note
        : "";
    const nextDefaultUnit = getDefaultUnitFromPart(updatedPart);
    const nextUnitId = updatedPart?.units?.[0]?.unit_id || "";
    const nextTypeName = getPartTypeName(updatedPart, "Other");
    const nextTypeOrder =
      updatedPart?.type_details?.orders ??
      updatedPart?.type_info?.order ??
      9999;

    const applyOnText = (text) => {
      if (!text?.partsByType || typeof text.partsByType !== "object") return text;
      let changed = false;
      const nextPartsByType = {};

      Object.entries(text.partsByType).forEach(([typeName, group]) => {
        const parts = Array.isArray(group?.parts) ? group.parts : [];
        parts.forEach((partItem) => {
          const isTarget = String(partItem?.part_ref || "") === partRefKey;
          const targetType = isTarget ? nextTypeName : typeName;
          const targetOrder = isTarget ? nextTypeOrder : group?.order;
          const nextPartItem = isTarget
            ? {
                ...partItem,
                part: nextPartLabel || partItem.part || "",
                itemCodeNote: nextTextboxNote,
                textbox_note: nextTextboxNote,
                defaultUnit: nextDefaultUnit,
                units: nextUnitId || partItem.units || "",
                unit_type_id:
                  updatedPart?.unit_type_id_ref || partItem.unit_type_id || null,
                typeName: nextTypeName,
                type_name: nextTypeName,
                unit:
                  !partItem?.unit || partItem.unit === (partItem.defaultUnit || "")
                    ? nextDefaultUnit
                    : partItem.unit,
              }
            : partItem;

          if (isTarget) changed = true;
          if (!nextPartsByType[targetType]) {
            nextPartsByType[targetType] = { order: targetOrder, parts: [] };
          }
          nextPartsByType[targetType].parts.push(nextPartItem);
        });
      });

      return changed ? { ...text, partsByType: nextPartsByType } : text;
    };

    setTexts((prev) => prev.map(applyOnText));
    setOffCanvasTexts((prev) => prev.map(applyOnText));
    setSelectedText((prev) => (prev ? applyOnText(prev) : prev));
  }, [setTexts, setOffCanvasTexts, setSelectedText]);

  // Old handleSelectPart implementation removed - now using factory function above
  /*
  const handleSelectPart = async (selectedPart) => {
    const partNumber = selectedPart?.part_number || "";
    const description = selectedPart?.description || "";

    let defaultUnit = "";
    let defaultUnitId = "";
    if (
      selectedPart?.units &&
      selectedPart.units.length > 0 &&
      selectedPart.units[0]?.unit_name
    ) {
      defaultUnit = selectedPart.units[0].unit_name;
      defaultUnitId = selectedPart.units[0].unit_id || "";
    }

    const newPart = {
      id: generateMarkerId(), // 🔐 Unique ID for tracking this specific instance
      part: `(${partNumber}) ${description}`,
      qty: "",
      unit: "",
      note: "",
      task: "",
      part_ref: selectedPart.part_id,
      textbox_note: selectedPart.textbox_note,
      defaultUnit,
      units: defaultUnitId, // Add unit_id
      unit_type_id: selectedPart?.unit_type_id_ref || null, // Add unit type ID for proper unit filtering
    };

    const type = selectedPart?.type_details?.name || "Other";
    const order = parseInt(selectedPart?.type_details?.orders || 9999);

    const updatedTexts = texts.map((text) => {
      if (text.id === selectedTextId) {
        const updatedPartsByType = { ...text.partsByType };

        const existingParts = updatedPartsByType?.[type]?.parts || [];
        const updatedParts = [...existingParts, newPart]
          .map((part) => ({
            ...part,
            id: part.id || uuidv4(), // Ensure every part has a unique id
          }))
          .sort((a, b) =>
            a.part.toLowerCase().localeCompare(b.part.toLowerCase())
          );

        updatedPartsByType[type] = {
          order: updatedPartsByType?.[type]?.order || order,
          parts: updatedParts,
        };

        return {
          ...text,
          partsByType: updatedPartsByType,
          selectedContent: {
            id: newPart.id,
            type: "part",
            content: `${newPart.part} - Qty-${newPart.qty} ${newPart.unit}`,
            qty: newPart.qty,
            unit: newPart.unit,
            typeKey: type,
            part_ref: newPart.part_ref,
            unit_type_id: newPart.unit_type_id, // Include unit type ID in selected content
          },
        };
      }
      return text;
    });

    setTexts(updatedTexts);

    // Update selectedText state first
    const updatedSelectedText = {
      ...selectedText,
      partsByType: {
        ...selectedText.partsByType,
        [type]: {
          order: selectedText.partsByType?.[type]?.order || order,
          parts: [
            ...(selectedText.partsByType?.[type]?.parts || []),
            newPart,
          ].sort((a, b) =>
            a.part.toLowerCase().localeCompare(b.part.toLowerCase())
          ),
        },
      },
      selectedContent: {
        id: newPart.id,
        type: "part",
        content: `${newPart.part} - Qty-${newPart.qty} ${newPart.unit}`,
        qty: newPart.qty,
        unit: newPart.unit,
        typeKey: type,
        part_ref: newPart.part_ref,
        unit_type_id: newPart.unit_type_id,
      },
    };

    setSelectedText(updatedSelectedText);

    // Now save to history with the updated state

    saveToHistory("add_part", null, null, null, null, updatedTexts);

    setPartsModalOpen(false);
  };

  // 3. Effect for Updating Local Parts from Redux 'parts' (No dispatch here)
  useEffect(() => {
    if (parts) {
      // Ensure `parts` is not null/undefined
      const filteredParts =
        filterType === "bom_step"
          ? parts.filter(
              (part) =>
                Array.isArray(partNumbers) &&
                partNumbers.includes(part.part_number)
            )
          : parts;
      setLocalParts(filteredParts);
    }
  }, [parts, filterType, partNumbers]); // Dependencies remain the same

  // CustomColorPicker is now imported from utils

  // Remove the problematic useEffect that causes infinite loops
  // Instead, we'll call saveToHistory explicitly when needed

  // undo and redo functions are created earlier (before keyboard shortcuts useEffect at line 3450)
  // Old implementation removed - see lines 3450-3514 for current implementation
  /*
  // Create undo function using factory
  const undo = createUndo({
    history,
    setHistory,
    setRedoStack,
    isUndoingRedoing,
    lastUndoRedoCompletionTime,
    fillColor,
    setSelectedObject,
    setSelectedText,
    setSelectedTextId,
    setSelectedImage,
    setSelectedArrow,
    setSelectedLine,
    setSelectedObjects,
    setArrows,
    setLines,
    setRectangles,
    setCircles,
    setDoubleSidedArrows,
    setTexts,
    setVideos,
    setScribbles,
    setOffCanvasTexts,
        offCanvasTexts,
    setOffCanvasRectangles,
    setOffCanvasCircles,
    setOffCanvasArrows,
    setOffCanvasDoubleSidedArrows,
    setOffCanvasImages,
    setOffCanvasScribbles,
    setOffCanvasLines,
    setOffCanvasVideos,
    setImages,
  });

  // Old undo implementation removed - now using factory function above
  /*
  async function undo() {
    if (history.length <= 1) {
      return;
    }

    // Prevent recursive calls
    if (isUndoingRedoing.current) {
      return;
    }

    isUndoingRedoing.current = true;

    try {
      // Get the previous state
      const currentState = history[history.length - 1];
      const previousState = history[history.length - 2];

      // Move current state to redo stack with size limit
      setRedoStack((prev) => {
        const newStack = [...prev, currentState];
        // No limit on redo stack size
        return newStack;
      });

      // Update history by removing the current state
      setHistory((prev) => prev.slice(0, -1));

      // Restore all canvas elements atomically
      const restoreCanvasState = async () => {
        // Clear all selections first
        setSelectedObject(null);
        setSelectedText(null);
        setSelectedTextId(null);
        setSelectedImage(null);
        setSelectedArrow(null);
        setSelectedLine(null);
        setSelectedObjects([]);

        // Restore all elements synchronously first

        setArrows(JSON.parse(JSON.stringify(previousState.arrows || [])));

        // Ensure all lines have startDot and endDot properties when restored
        const restoredLines = JSON.parse(
          JSON.stringify(previousState.lines || [])
        ).map((line) => {
          // Ensure line has the required dot properties
          const lineWithDots = {
            ...line,
            startDot: line.startDot || {
              radius: 4,
              fill: line.strokeColor || line.stroke || fillColor || "black",
              stroke: line.strokeColor || line.stroke || fillColor || "black",
              strokeWidth: 1,
            },
            endDot: line.endDot || {
              radius: 4,
              fill: line.strokeColor || line.stroke || fillColor || "black",
              stroke: line.strokeColor || line.stroke || fillColor || "black",
              strokeWidth: 1,
            },
          };
          return lineWithDots;
        });
        setLines(restoredLines);
        // Ensure restored shapes are not treated as 'new' and have full opacity defaults
        const restoredRectangles = JSON.parse(
          JSON.stringify(previousState.rectangles || [])
        ).map((rect) => ({
          ...rect,
          isNew: false,
          fillOpacity: rect.fillOpacity ?? 1,
          strokeOpacity: rect.strokeOpacity ?? 1,
          opacity: 1,
        }));
        setRectangles(restoredRectangles);
        const restoredCircles = JSON.parse(
          JSON.stringify(previousState.circles || [])
        ).map((circle) => ({
          ...circle,
          isNew: false,
          fillOpacity: circle.fillOpacity ?? 1,
          strokeOpacity: circle.strokeOpacity ?? 1,
          opacity: 1,
        }));
        setCircles(restoredCircles);
        setDoubleSidedArrows(
          JSON.parse(JSON.stringify(previousState.doubleSidedArrows || []))
        );
        setTexts(JSON.parse(JSON.stringify(previousState.texts || [])));
        setVideos(JSON.parse(JSON.stringify(previousState.videos || []))); // Restore videos
        setScribbles(JSON.parse(JSON.stringify(previousState.scribbles || [])));

        // Restore offcanvas elements
        setOffCanvasTexts(
          JSON.parse(JSON.stringify(previousState.offCanvasTexts || []))
        );
        setOffCanvasRectangles(
          JSON.parse(JSON.stringify(previousState.offCanvasRectangles || []))
        );
        setOffCanvasCircles(
          JSON.parse(JSON.stringify(previousState.offCanvasCircles || []))
        );
        setOffCanvasArrows(
          JSON.parse(JSON.stringify(previousState.offCanvasArrows || []))
        );
        setOffCanvasDoubleSidedArrows(
          JSON.parse(
            JSON.stringify(previousState.offCanvasDoubleSidedArrows || [])
          )
        );
        setOffCanvasImages(
          JSON.parse(JSON.stringify(previousState.offCanvasImages || []))
        );
        setOffCanvasScribbles(
          JSON.parse(JSON.stringify(previousState.offCanvasScribbles || []))
        );
        setOffCanvasLines(
          JSON.parse(JSON.stringify(previousState.offCanvasLines || []))
        );
        setOffCanvasVideos(
          JSON.parse(JSON.stringify(previousState.offCanvasVideos || []))
        );

        // Restore selectedText state if it exists in history
        if (previousState.selectedText) {
          setSelectedText(
            JSON.parse(JSON.stringify(previousState.selectedText))
          );
        }

        // Restore selectedTextId if it exists in history
        if (previousState.selectedTextId) {
          setSelectedTextId(previousState.selectedTextId);
        }

        // Handle images separately due to async loading
        if (previousState.images && previousState.images.length > 0) {
          try {
            const restoredImages = await restoreImagesFromHistory(
              previousState.images
            );
            setImages(restoredImages);
          } catch (error) {
            // Try to restore images with basic data even if full restoration fails
            const fallbackImages = await Promise.all(
              previousState.images.map(async (img) => {
                try {
                  // Try to create a basic image from the available data
                  const newImg = new Image();
                  newImg.crossOrigin = "anonymous";

                  // Use the best available source
                  let imageSrc = img.src;
                  if (
                    img.originalImageDataUrl &&
                    img.originalImageDataUrl.startsWith("data:")
                  ) {
                    imageSrc = img.originalImageDataUrl;
                  } else if (
                    img.imageDataUrl &&
                    img.imageDataUrl.startsWith("data:")
                  ) {
                    imageSrc = img.imageDataUrl;
                  }

                  return new Promise((resolve) => {
                    newImg.onload = () => {
                      resolve({
                        ...img,
                        image: newImg,
                        originalImage: newImg,
                        compressedImage: null,
                        src: imageSrc,
                      });
                    };
                    newImg.onerror = () => {
                      // If loading fails, create a placeholder
                      const placeholderImage = new Image();
                      placeholderImage.src =
                        "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgZmlsbD0iI2YwZjBmMCIvPjx0ZXh0IHg9IjUwIiB5PSI1MCIgZm9udC1mYW1pbHk9IkFyaWFsIiBmb250LXNpemU9IjEyIiBmaWxsPSIjOTk5IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSI+SW1hZ2U8L3RleHQ+PC9zdmc+";
                      placeholderImage.onload = () => {
                        resolve({
                          ...img,
                          image: placeholderImage,
                          originalImage: placeholderImage,
                          compressedImage: null,
                          src: placeholderImage.src,
                          name: img.name || "Placeholder",
                        });
                      };
                    };
                    newImg.src = imageSrc;
                  });
                } catch (error) {
                  return {
                    ...img,
                    image: null,
                    originalImage: null,
                    compressedImage: null,
                  };
                }
              })
            );
            setImages(fallbackImages);
          }
        } else {
          setImages([]);
        }
      };

      // Execute the restoration and wait until it's done
      await restoreCanvasState();

      // Note: isUndoingRedoing will be reset in the finally block
    } catch (error) {
    } finally {
      // Reset after next paint to avoid queued saveToHistory calls during restore
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          isUndoingRedoing.current = false;
        });
      });
    }
  }
  */

  // redo function is now created earlier (before keyboard shortcuts useEffect at line 4821)
  // Old redo implementation removed - now using factory function above (created earlier)
  /*
  async function redo() {
    if (redoStack.length === 0) {
      return;
    }

    // Prevent recursive calls
    if (isUndoingRedoing.current) {
      return;
    }

    isUndoingRedoing.current = true;

    try {
      // Get the state to redo
      const stateToRedo = redoStack[redoStack.length - 1];

      // Add state back to history
      setHistory((prev) => [...prev, stateToRedo]);

      // Remove from redo stack
      setRedoStack((prev) => {
        const newStack = prev.slice(0, -1);

        return newStack;
      });

      // Restore all canvas elements atomically
      const restoreCanvasState = async () => {
        // Clear all selections first
        setSelectedObject(null);
        setSelectedText(null);
        setSelectedTextId(null);
        setSelectedImage(null);
        setSelectedArrow(null);
        setSelectedLine(null);
        setSelectedObjects([]);

        // Restore all elements synchronously first
        const restoredArrows = JSON.parse(
          JSON.stringify(stateToRedo.arrows || [])
        ).map((arrow) => ({
          ...arrow,
          isRestored: true,
          isFromHistory: true,
        }));
        setArrows(restoredArrows);

        // Clean up restoration flags after a longer delay to ensure components are fully initialized
        setTimeout(() => {
          setArrows((prev) =>
            prev.map((arrow) => {
              const { isRestored, isFromHistory, ...cleanArrow } = arrow;

              return cleanArrow;
            })
          );
          setDoubleSidedArrows((prev) =>
            prev.map((arrow) => {
              const { isRestored, isFromHistory, ...cleanArrow } = arrow;

              return cleanArrow;
            })
          );
        }, 500);
        // Ensure all lines have startDot and endDot properties when restored
        const restoredLines = JSON.parse(
          JSON.stringify(stateToRedo.lines || [])
        ).map((line) => {
          // Ensure line has the required dot properties
          const lineWithDots = {
            ...line,
            startDot: line.startDot || {
              radius: 4,
              fill: line.strokeColor || line.stroke || fillColor || "black",
              stroke: line.strokeColor || line.stroke || fillColor || "black",
              strokeWidth: 1,
            },
            endDot: line.endDot || {
              radius: 4,
              fill: line.strokeColor || line.stroke || fillColor || "black",
              stroke: line.strokeColor || line.stroke || fillColor || "black",
              strokeWidth: 1,
            },
          };
          return lineWithDots;
        });
        setLines(restoredLines);
        const redoRectangles = JSON.parse(
          JSON.stringify(stateToRedo.rectangles || [])
        ).map((rect) => ({
          ...rect,
          isNew: false,
          fillOpacity: rect.fillOpacity ?? 1,
          strokeOpacity: rect.strokeOpacity ?? 1,
          opacity: 1,
        }));
        setRectangles(redoRectangles);
        const redoCircles = JSON.parse(
          JSON.stringify(stateToRedo.circles || [])
        ).map((circle) => ({
          ...circle,
          isNew: false,
          fillOpacity: circle.fillOpacity ?? 1,
          strokeOpacity: circle.strokeOpacity ?? 1,
          opacity: 1,
        }));
        setCircles(redoCircles);
        const restoredDoubleSidedArrows = JSON.parse(
          JSON.stringify(stateToRedo.doubleSidedArrows || [])
        ).map((arrow) => ({
          ...arrow,
          isRestored: true,
          isFromHistory: true,
        }));
        setDoubleSidedArrows(restoredDoubleSidedArrows);
        setTexts(JSON.parse(JSON.stringify(stateToRedo.texts || [])));
        setVideos(JSON.parse(JSON.stringify(stateToRedo.videos || []))); // Restore videos
        setScribbles(JSON.parse(JSON.stringify(stateToRedo.scribbles || [])));

        // Restore offcanvas elements
        setOffCanvasTexts(
          JSON.parse(JSON.stringify(stateToRedo.offCanvasTexts || []))
        );
        setOffCanvasRectangles(
          JSON.parse(JSON.stringify(stateToRedo.offCanvasRectangles || []))
        );
        setOffCanvasCircles(
          JSON.parse(JSON.stringify(stateToRedo.offCanvasCircles || []))
        );
        setOffCanvasArrows(
          JSON.parse(JSON.stringify(stateToRedo.offCanvasArrows || []))
        );
        setOffCanvasDoubleSidedArrows(
          JSON.parse(
            JSON.stringify(stateToRedo.offCanvasDoubleSidedArrows || [])
          )
        );
        setOffCanvasImages(
          JSON.parse(JSON.stringify(stateToRedo.offCanvasImages || []))
        );
        setOffCanvasScribbles(
          JSON.parse(JSON.stringify(stateToRedo.offCanvasScribbles || []))
        );
        setOffCanvasLines(
          JSON.parse(JSON.stringify(stateToRedo.offCanvasLines || []))
        );
        setOffCanvasVideos(
          JSON.parse(JSON.stringify(stateToRedo.offCanvasVideos || []))
        );

        // Restore selectedText state if it exists in history
        if (stateToRedo.selectedText) {
          setSelectedText(JSON.parse(JSON.stringify(stateToRedo.selectedText)));
        }

        // Restore selectedTextId if it exists in history
        if (stateToRedo.selectedTextId) {
          setSelectedTextId(stateToRedo.selectedTextId);
        }

        // Handle images separately due to async loading
        if (stateToRedo.images && stateToRedo.images.length > 0) {
          try {
            const restoredImages = await restoreImagesFromHistory(
              stateToRedo.images
            );

            if (restoredImages.length > 0) {
            }
            setImages(restoredImages);
          } catch (error) {
            // Try to restore images with basic data even if full restoration fails

            const fallbackImages = await Promise.all(
              stateToRedo.images.map(async (img) => {
                try {
                  // Try to create a basic image from the available data
                  const newImg = new Image();
                  newImg.crossOrigin = "anonymous";

                  // Use the best available source
                  let imageSrc = img.src;
                  if (
                    img.originalImageDataUrl &&
                    img.originalImageDataUrl.startsWith("data:")
                  ) {
                    imageSrc = img.originalImageDataUrl;
                  } else if (
                    img.imageDataUrl &&
                    img.imageDataUrl.startsWith("data:")
                  ) {
                    imageSrc = img.imageDataUrl;
                  }

                  return new Promise((resolve) => {
                    newImg.onload = () => {
                      resolve({
                        ...img,
                        image: newImg,
                        originalImage: newImg,
                        compressedImage: null,
                        src: imageSrc,
                      });
                    };
                    newImg.onerror = () => {
                      // If loading fails, create a placeholder
                      const placeholderImage = new Image();
                      placeholderImage.src =
                        "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgZmlsbD0iI2YwZjBmMCIvPjx0ZXh0IHg9IjUwIiB5PSI1MCIgZm9udC1mYW1pbHk9IkFyaWFsIiBmb250LXNpemU9IjEyIiBmaWxsPSIjOTk5IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSI+SW1hZ2U8L3RleHQ+PC9zdmc+";
                      placeholderImage.onload = () => {
                        resolve({
                          ...img,
                          image: placeholderImage,
                          originalImage: placeholderImage,
                          compressedImage: null,
                          src: placeholderImage.src,
                          name: img.name || "Placeholder",
                        });
                      };
                    };
                    newImg.src = imageSrc;
                  });
                } catch (error) {
                  return {
                    ...img,
                    image: null,
                    originalImage: null,
                    compressedImage: null,
                  };
                }
              })
            );
            setImages(fallbackImages);
          }
        } else {
          setImages([]);
        }
      };

      // Execute the restoration and wait until it's done
      await restoreCanvasState();
    } catch (error) {
      // Error handled
    } finally {
      // Reset after next paint to avoid queued saveToHistory calls during restore
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          isUndoingRedoing.current = false;
        });
      });
    }
  }
  */

  // Create toggleImageCompression function using factory
  const toggleImageCompression = createToggleImageCompression({
    images,
    setImages,
    offCanvasImages,
    setOffCanvasImages,
    selectedObject,
    setSelectedObject,
    selectedImage,
    setSelectedImage,
    saveToHistory,
  });

  // Old toggleImageCompression implementation removed - now using factory function above
  /*
  async function toggleImageCompression(imageId, enableCompression) {
    // ... implementation removed - now in utils/partImageUtils.js ...
    saveToHistory();
  }
  */

  // Initialize history with the first fully loaded state - SINGLE SOURCE OF TRUTH
  useEffect(() => {
    // CRITICAL: Once history is initialized, NEVER reset it, even if dependencies change
    // This prevents history from being cleared when texts/selectedText changes (e.g., when unselecting)
    if (historyInitialized.current) {
      return;
    }

    // CRITICAL: If keepHistoryEmpty flag is set, don't initialize history
    // This allows us to keep history empty after intentional clearing (e.g., when opening canvas for editing)
    if (keepHistoryEmpty.current) {
      return;
    }

    // If history already has entries, mark as initialized and return
    // This prevents re-initialization if history was already set
    if (history.length > 0) {
      historyInitialized.current = true;
      return;
    }

    if (initialDataLoadInProgress.current) {
      return;
    }

    if (isLoadingFromIndexedDB) {
      return;
    }

    const cloneDeep = (value) => JSON.parse(JSON.stringify(value || []));

    const serializedImages = (images || []).map((img) => ({
      id: img.id,
      x: img.x ?? 0,
      y: img.y ?? 0,
      width: img.width ?? 100,
      height: img.height ?? 100,
      src: img.src,
      maintainAspectRatio:
        img.maintainAspectRatio !== undefined ? img.maintainAspectRatio : true,
      compress: img.compress || false,
      imageNumber: img.imageNumber,
      name: img.name,
      rotation: img.rotation || 0,
      scaleX: img.scaleX || 1,
      scaleY: img.scaleY || 1,
      isOnTopLayer: !!img.isOnTopLayer,
      originalImageDataUrl: img.originalImageDataUrl || null,
      imageDataUrl: img.imageDataUrl || null,
      cropData: img.cropData,
      originalCropRotation: img.originalCropRotation || 0,
      flipHorizontal: img.flipHorizontal || false,
      flipVertical: img.flipVertical || false,
      hasBakedTransforms: img.hasBakedTransforms || false,
      originalWidth: img.originalWidth,
      originalHeight: img.originalHeight,
      originalAspectRatio: img.originalAspectRatio,
    }));

    const initialState = {
      texts: cloneDeep(texts),
      rectangles: cloneDeep(rectangles),
      circles: cloneDeep(circles),
      arrows: cloneDeep(arrows),
      doubleSidedArrows: cloneDeep(doubleSidedArrows),
      images: serializedImages,
      scribbles: cloneDeep(scribbles),
      lines: cloneDeep(lines),
      videos: cloneDeep(videos),
      // Offcanvas elements
      offCanvasTexts: cloneDeep(offCanvasTexts),
      offCanvasRectangles: cloneDeep(offCanvasRectangles),
      offCanvasCircles: cloneDeep(offCanvasCircles),
      offCanvasArrows: cloneDeep(offCanvasArrows),
      offCanvasDoubleSidedArrows: cloneDeep(offCanvasDoubleSidedArrows),
      offCanvasImages: cloneDeep(offCanvasImages),
      offCanvasScribbles: cloneDeep(offCanvasScribbles),
      offCanvasLines: cloneDeep(offCanvasLines),
      offCanvasVideos: cloneDeep(offCanvasVideos),
      selectedText: selectedText
        ? JSON.parse(JSON.stringify(selectedText))
        : null,
      selectedTextId: selectedTextId || null,
      canvasHeight: canvasHeight || "100", // Include canvas height in initial state
      operationType: "initial",
      timestamp: Date.now(),
    };

    setHistory([initialState]);
    historyInitialized.current = true;
  }, [
    arrows,
    circles,
    doubleSidedArrows,
    // CRITICAL: Removed 'history' from dependencies to prevent re-initialization
    // when history changes. The guard checks above are sufficient.
    images,
    initialDataLoadInProgress,
    isLoadingFromIndexedDB,
    lines,
    offCanvasArrows,
    offCanvasCircles,
    offCanvasDoubleSidedArrows,
    offCanvasImages,
    offCanvasLines,
    offCanvasRectangles,
    offCanvasScribbles,
    offCanvasTexts,
    offCanvasVideos,
    rectangles,
    scribbles,
    // CRITICAL: Removed 'selectedText', 'selectedTextId', and 'texts' from dependencies
    // to prevent re-initialization when text selection changes or selectedContent is cleared.
    // These changes should not trigger history re-initialization.
    videos,
    canvasHeight, // Include canvasHeight so initial state has the correct value
  ]);

  // Monitor arrows state changes
  useEffect(() => {
    // Check if arrows are being cleared
    if (arrows.length === 0) {
    }
  }, [arrows]);

  // Monitor double sided arrows state changes
  useEffect(() => { }, [doubleSidedArrows]);

  // addText is now imported from utils

  // updateTextContent is now imported from utils

  useEffect(() => {
    if (quillRef.current) return;

    const editorContainer = document.getElementById("editor-container");
    if (!editorContainer) return; // Ensure the container exists before initializing Quill

    quillRef.current = new ReactQuill(editorContainer, {
      modules: {
        toolbar: [
          ["bold", "italic", "underline"],
          [{ color: [] }],
          [{ font: [] }],
          [{ size: [] }],
        ],
      },
      theme: "snow",
    });
  }, []);

  // handleZoom is now imported from utils

  // handleZoomInputChange is now imported from utils

  // handleZoomInputKeyPress is now imported from utils

  // handleManualZoom is now imported from utils

  // resetZoom is now imported from utils

  // Handle keyboard shortcuts and mouse wheel for zoom
  useEffect(() => {
    const handleKeyDown = createZoomKeyHandler(resetZoom, (factor) =>
      handleZoom(factor, zoomLevel),
    );

    // handleWheel is now imported from utils

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("wheel", handleWheel, { passive: false });
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("wheel", handleWheel);
    };
  }, [zoomLevel]);

  // updateText is now imported from utils

  // addImage is now imported from utils

  // addVideo is now imported from utils

  // handleImageUpload is now imported from utils

  // handleSvgExport is now imported from utils/exportHandlers
  const handleSvgExportCallback = useCallback(() => {
    handleSvgExportUtil({ stageRef, transformerRef });
  }, [stageRef, transformerRef]);

  // handleExportPDF is now imported from utils/exportHandlers
  const handleExportPDFCallback = useCallback(() => {
    handleExportPDFUtil(stageRef);
  }, [stageRef]);

  const strokeColor = "#000";
  const isPaining = useRef();
  const currentShapeId = useRef();
  // transformerRef is declared earlier (needed for function creators)

  // Separate refs for each element type to avoid race conditions during rapid creation
  const currentRectId = useRef();
  const currentCircleId = useRef();
  const currentTextId = useRef();
  const currentArrowId = useRef();
  const currentDoubleArrowId = useRef();
  const currentLineId = useRef();
  const currentImageId = useRef();
  const imageConstraintRef = useRef(null); // Track last used constraint for smooth image preview
  const previewImageDataRef = useRef(null); // Store preview image data for perfect alignment

  // pendingSelection is declared earlier (needed for createAddText at line 1800)

  const isDraggable = action === ACTIONS.SELECT;

  const markerCount = useRef(0); // Keeps track of marker numbers
  const [locationMarkers, setLocationMarkers] = useState([]);
  const initialPointerPosition = useRef(null); // Track initial pointer position for click detection

  // Handle pending selection after state updates
  useEffect(() => {
    if (pendingSelection.current) {
      const { elementId, elementType, elementArray } = pendingSelection.current;

      pendingSelection.current = null;

      // Skip selection if clone mode is active AND we're cloning existing elements
      // But allow selection for newly drawn elements (like images drawn from scratch)
      // Check if this is a multi-clone operation (cloning existing elements)
      const isMultiCloneOperation =
        isCloneMode && cloneTemplates && cloneTemplates.length > 1;

      if (isMultiCloneOperation) {
        return;
      }

      // Find the element in the provided array or search through all arrays if elementArray is null
      let element;
      let foundInArray = null;

      if (elementArray) {
        // Use the provided array (original behavior)
        element = elementArray.find((el) => el.id === elementId);
        foundInArray = "provided";
      } else {
        // Search through all relevant arrays based on elementType
        switch (elementType) {
          case "Image":
            element = images.find((el) => el.id === elementId);
            if (element) foundInArray = "images";
            if (!element) {
              element = offCanvasImages.find((el) => el.id === elementId);
              if (element) foundInArray = "offCanvasImages";
            }
            break;
          case "Rect":
            element = rectangles.find((el) => el.id === elementId);
            if (element) foundInArray = "rectangles";
            if (!element) {
              element = offCanvasRectangles.find((el) => el.id === elementId);
              if (element) foundInArray = "offCanvasRectangles";
            }
            break;
          case "Circle":
            element = circles.find((el) => el.id === elementId);
            if (element) foundInArray = "circles";
            if (!element) {
              element = offCanvasCircles.find((el) => el.id === elementId);
              if (element) foundInArray = "offCanvasCircles";
            }
            break;
          case "Arrow":
            element = arrows.find((el) => el.id === elementId);
            if (element) foundInArray = "arrows";
            if (!element) {
              element = offCanvasArrows.find((el) => el.id === elementId);
              if (element) foundInArray = "offCanvasArrows";
            }
            break;
          case "DoubleSidedArrow":
            element = doubleSidedArrows.find((el) => el.id === elementId);
            if (element) foundInArray = "doubleSidedArrows";
            if (!element) {
              element = offCanvasDoubleSidedArrows.find(
                (el) => el.id === elementId,
              );
              if (element) foundInArray = "offCanvasDoubleSidedArrows";
            }
            break;
          case "Line":
            element = lines.find((el) => el.id === elementId);
            if (element) foundInArray = "lines";
            if (!element) {
              element = offCanvasLines.find((el) => el.id === elementId);
              if (element) foundInArray = "offCanvasLines";
            }
            break;
          case "Text":
            element = texts.find((el) => el.id === elementId);
            if (element) foundInArray = "texts";
            if (!element) {
              element = offCanvasTexts.find((el) => el.id === elementId);
              if (element) foundInArray = "offCanvasTexts";
            }
            break;
          default:
        }
      }

      if (element) {
        let resolvedType = elementType;
        if (elementType === "Line") {
          const hs =
            element.hasStartArrow === true ||
            element.pointerAtBeginning === true;
          const he =
            element.hasEndArrow === true ||
            element.pointerAtEnd === true ||
            element.pointerAtEnding === true;
          if (hs && he) resolvedType = "DoubleSidedArrow";
          else if (hs || he) resolvedType = "Arrow";
          else resolvedType = "Line";
        }
        setSelectedObject({
          ...element,
          type: resolvedType,
        });

        // Also set the specific selection state based on element type
        if (elementType === "Image") {
          setSelectedImage(element);
        } else if (elementType === "Text") {
          setSelectedText(element);
          setSelectedTextId(elementId);
          setShowTextBox(true);
        }

        // Ensure showTextBox is true for Text elements
        if (elementType === "Text") {
          setShowTextBox(true);
        }
      } else {
      }
    }
  }, [
    rectangles,
    circles,
    arrows,
    doubleSidedArrows,
    lines,
    texts,
    images, // Add images to trigger selection when images are updated
    offCanvasRectangles, // Add off-canvas arrays to trigger selection when off-canvas elements are created
    offCanvasCircles,
    offCanvasArrows,
    offCanvasDoubleSidedArrows,
    offCanvasLines,
    offCanvasTexts,
    offCanvasImages,
    isCloneMode,
    lockedAction,
    cloneTemplates, // Add cloneTemplates to detect multi-clone operations
  ]);

  // undo and redo functions are created earlier (before keyboard shortcuts useEffect at line 3450)
  /*
  // Create undo function using factory
  const undo = createUndo({
    history,
    setHistory,
    setRedoStack,
    isUndoingRedoing,
    lastUndoRedoCompletionTime,
    fillColor,
    setSelectedObject,
    setSelectedText,
    setSelectedTextId,
    setSelectedImage,
    setSelectedArrow,
    setSelectedLine,
    setSelectedObjects,
    setArrows,
    setLines,
    setRectangles,
    setCircles,
    setDoubleSidedArrows,
    setTexts,
    setVideos,
    setScribbles,
    setOffCanvasTexts,
        offCanvasTexts,
    setOffCanvasRectangles,
    setOffCanvasCircles,
    setOffCanvasArrows,
    setOffCanvasDoubleSidedArrows,
    setOffCanvasImages,
    setOffCanvasScribbles,
    setOffCanvasLines,
    setOffCanvasVideos,
    setImages,
  });

  const redo = createRedo({
    redoStack,
    setHistory,
    setRedoStack,
    isUndoingRedoing,
    lastUndoRedoCompletionTime,
    fillColor,
    setSelectedObject,
    setSelectedText,
    setSelectedTextId,
    setSelectedImage,
    setSelectedArrow,
    setSelectedLine,
    setSelectedObjects,
    setArrows,
    setLines,
    setRectangles,
    setCircles,
    setDoubleSidedArrows,
    setTexts,
    setVideos,
    setScribbles,
    setOffCanvasTexts,
        offCanvasTexts,
    setOffCanvasRectangles,
    setOffCanvasCircles,
    setOffCanvasArrows,
    setOffCanvasDoubleSidedArrows,
    setOffCanvasImages,
    setOffCanvasScribbles,
    setOffCanvasLines,
    setOffCanvasVideos,
    setImages,
  });
  */

  // toggleImageCompression is now created using factory function (see line 4597)

  const blurCanvasNumericInputs = useCallback(() => {
    const activeEl = document.activeElement;
    if (!activeEl || typeof activeEl.blur !== "function") return;
    const isZoomInput =
      typeof activeEl.classList?.contains === "function" &&
      activeEl.classList.contains("zoom-input");
    const isCanvasHeightInput = activeEl.id === "canvas-height-input";
    if (isZoomInput || isCanvasHeightInput) {
      activeEl.blur();
    }
  }, []);

  // onPointerDown is now created using factory function from utils/pointerHandlers
  const onPointerDown = useMemo(
    () => {
      const pointerDownHandler = createOnPointerDown({
        action,
        stageRef,
        markerCount,
        currentShapeId,
        isPaining,
        setLocationMarkers,
        setShowTextBox,
        setSelectedObject,
        setSelectedObjects,
        pageId,
        setIsDrawingRect,
        setNewRectStart,
        currentRectId,
        setRectangles,
        setIsDrawingCircle,
        setNewCircleStart,
        currentCircleId,
        setCircles,
        setIsDrawingArrow,
        setArrowStartPoint,
        currentArrowId,
        setArrows,
        setIsDrawingDoubleArrow,
        setDoubleArrowStartPoint,
        currentDoubleArrowId,
        setDoubleSidedArrows,
        isDrawingLine,
        setIsDrawingLine,
        setLineStartPoint,
        currentLineId,
        setLines,
        lines,
        setIsDrawingText,
        setNewTextStart,
        currentTextId,
        setTexts,
        texts,
        fillColor,
        applyDrawingRestrictions,
        saveToHistory,
        ACTIONS_SELECT: ACTIONS.SELECT,
        ACTIONS_RECTANGLE: ACTIONS.RECTANGLE,
        ACTIONS_CIRCLE: ACTIONS.CIRCLE,
        ACTIONS_ARROW: ACTIONS.ARROW,
        ACTIONS_DOUBLE_SIDED_ARROW: ACTIONS.DOUBLE_SIDED_ARROW,
        ACTIONS_LINE: ACTIONS.LINE,
        ACTIONS_TEXT: ACTIONS.TEXT,
        ACTIONS_IMAGE: ACTIONS.IMAGE,
        initialPointerPosition,
        lockedAction,
        cloneTemplate,
        cloneTemplates,
        isCloneMode,
        pendingImageData,
        setIsDrawingImage,
        setImageStartPoint,
        setImages,
        currentImageId,
        imageConstraintRef,
        previewImageDataRef,
        generateMarkerId,
        // Pass off-canvas state setters and stage ref for off-canvas drawing support
        offCanvasStageRef: window.__offCanvasStageRef || null, // Get from global (set by Canvas callback)
        setOffCanvasRectangles,
        setOffCanvasCircles,
        setOffCanvasArrows,
        setOffCanvasDoubleSidedArrows,
        setOffCanvasLines,
        offCanvasLines,
        setOffCanvasTexts,
        offCanvasTexts,
        setOffCanvasImages,
        deferDrawingClampUntilPointerUp: true,
      });
      return (e) => {
        blurCanvasNumericInputs();
        return pointerDownHandler(e);
      };
    },
    [
      action,
      stageRef,
      markerCount,
      currentShapeId,
      isPaining,
      setLocationMarkers,
      setShowTextBox,
      setSelectedObject,
      setSelectedObjects,
      pageId,
      setIsDrawingRect,
      setNewRectStart,
      currentRectId,
      setRectangles,
      setIsDrawingCircle,
      setNewCircleStart,
      currentCircleId,
      setCircles,
      setIsDrawingArrow,
      setArrowStartPoint,
      currentArrowId,
      setArrows,
      setIsDrawingDoubleArrow,
      setDoubleArrowStartPoint,
      currentDoubleArrowId,
      setDoubleSidedArrows,
      isDrawingLine,
      setIsDrawingLine,
      setLineStartPoint,
      currentLineId,
      setLines,
      isDrawingText,
      setIsDrawingText,
      setNewTextStart,
      currentTextId,
      setTexts,
      texts,
      fillColor,
      applyDrawingRestrictions,
      saveToHistory,
      initialPointerPosition,
      lockedAction,
      cloneTemplate,
      cloneTemplates,
      isCloneMode,
      pendingImageData,
      isDrawingImage,
      imageStartPoint,
      setImages,
      currentImageId,
      imageConstraintRef,
      previewImageDataRef,
      generateMarkerId,
      setOffCanvasRectangles,
      setOffCanvasCircles,
      setOffCanvasArrows,
      setOffCanvasDoubleSidedArrows,
      setOffCanvasLines,
      setOffCanvasTexts,
      offCanvasTexts,
      setOffCanvasImages,
      blurCanvasNumericInputs,
    ],
  );

  // storeMarkerInIndexedDB is now imported from utils/markerStorageUtils

  // Utility function to apply drawing restrictions

  // applyDrawingRestrictions is now imported from utils

  // onPointerMove is now created using factory function from utils/pointerHandlers
  const onPointerMove = useMemo(
    () =>
      createOnPointerMove({
        action,
        stageRef,
        isPaining,
        setHoverPoint,
        isDrawingRect,
        newRectStart,
        isDrawingRectRef,
        newRectStartRef,
        applyDrawingRestrictions,
        setRectangles,
        isDrawingCircle,
        newCircleStart,
        setCircles,
        isDrawingArrow,
        arrowStartPoint,
        currentShapeId,
        setArrows,
        isDrawingDoubleArrow,
        doubleArrowStartPoint,
        setDoubleSidedArrows,
        setScribbles,
        isDrawingLine,
        lineStartPoint,
        setLines,
        lines,
        arrows, // Current arrows array (needed for name generation for legacy arrows)
        doubleSidedArrows, // Current doubleSidedArrows array (needed for name generation)
        isDrawingText,
        newTextStart,
        currentTextId,
        setTexts,
        ACTIONS_SELECT: ACTIONS.SELECT,
        ACTIONS_RECTANGLE: ACTIONS.RECTANGLE,
        ACTIONS_CIRCLE: ACTIONS.CIRCLE,
        ACTIONS_ARROW: ACTIONS.ARROW,
        ACTIONS_DOUBLE_SIDED_ARROW: ACTIONS.DOUBLE_SIDED_ARROW,
        ACTIONS_SCRIBBLE: ACTIONS.SCRIBBLE,
        ACTIONS_LINE: ACTIONS.LINE,
        ACTIONS_TEXT: ACTIONS.TEXT,
        ACTIONS_IMAGE: ACTIONS.IMAGE,
        isDrawingImage,
        imageStartPoint,
        pendingImageData,
        setImages,
        currentImageId,
        imageConstraintRef,
        previewImageDataRef,
        isCloneMode,
        cloneTemplate,
        currentLineId,
        lockedAction,
        currentRectId,
        currentCircleId,
        // Pass off-canvas state setters for off-canvas drawing support
        offCanvasStageRef: window.__offCanvasStageRef || null, // Get from global (set by Canvas callback)
        setOffCanvasRectangles,
        setOffCanvasCircles,
        setOffCanvasArrows,
        setOffCanvasDoubleSidedArrows,
        setOffCanvasLines,
        offCanvasLines,
        setOffCanvasTexts,
        offCanvasTexts,
        setOffCanvasImages,
        deferDrawingClampUntilPointerUp: true,
      }),
    [
      action,
      stageRef,
      isPaining,
      setHoverPoint,
      isDrawingRect,
      newRectStart,
      applyDrawingRestrictions,
      setRectangles,
      isDrawingCircle,
      newCircleStart,
      setCircles,
      isDrawingArrow,
      arrowStartPoint,
      currentShapeId,
      setArrows,
      isDrawingDoubleArrow,
      doubleArrowStartPoint,
      setDoubleSidedArrows,
      setScribbles,
      isDrawingLine,
      lineStartPoint,
      setLines,
      isDrawingText,
      newTextStart,
      currentTextId,
      setTexts,
      isCloneMode,
      cloneTemplate,
      currentLineId,
      lockedAction,
      currentRectId,
      applyDrawingRestrictions,
      isDrawingImage,
      imageStartPoint,
      pendingImageData,
      setImages,
      setOffCanvasRectangles,
      setOffCanvasCircles,
      setOffCanvasArrows,
      setOffCanvasDoubleSidedArrows,
      setOffCanvasLines,
      setOffCanvasTexts,
      offCanvasTexts,
      setOffCanvasImages,
      currentImageId,
      imageConstraintRef,
      previewImageDataRef,
    ],
  );

  // onPointerUp is now created using factory function from utils/pointerHandlers
  const onPointerUp = useMemo(
    () =>
      createOnPointerUp({
        isPaining,
        isDrawingRect,
        isDrawingRectRef,
        setIsDrawingRect,
        setNewRectStart,
        newRectStart,
        newRectStartRef,
        currentRectId,
        setRectangles,
        fillColor,
        setAction,
        saveToHistory,
        action,
        isDrawingCircle,
        setIsDrawingCircle,
        setNewCircleStart,
        newCircleStart,
        currentCircleId,
        setCircles,
        isDrawingArrow,
        setIsDrawingArrow,
        setArrowStartPoint,
        currentArrowId,
        currentShapeId,
        setArrows,
        isDrawingDoubleArrow,
        setIsDrawingDoubleArrow,
        setDoubleArrowStartPoint,
        currentDoubleArrowId,
        setDoubleSidedArrows,
        isDrawingLine,
        setIsDrawingLine,
        currentLineId,
        stageRef,
        setLines,
        setLineStartPoint,
        lineStartPoint,
        lines, // Current lines array (needed for name generation)
        arrows, // Current arrows array (needed for name generation for legacy arrows)
        doubleSidedArrows, // Current doubleSidedArrows array (needed for name generation)
        isDrawingText,
        setIsDrawingText,
        newTextStart,
        setNewTextStart,
        currentTextId,
        setTexts,
        texts,
        setSelectedText,
        setSelectedTextId,
        setShowTextBox,
        setSelectedObject,
        setSelectedObjects,
        setEditTextBoxContent,
        transformerRef,
        ACTIONS_SELECT: ACTIONS.SELECT,
        ACTIONS_RECTANGLE: ACTIONS.RECTANGLE,
        ACTIONS_CIRCLE: ACTIONS.CIRCLE,
        selectedObject,
        rectangles,
        circles,
        arrows,
        doubleSidedArrows,
        lines,
        ACTIONS_ARROW: ACTIONS.ARROW,
        ACTIONS_DOUBLE_SIDED_ARROW: ACTIONS.DOUBLE_SIDED_ARROW,
        ACTIONS_LINE: ACTIONS.LINE,
        ACTIONS_TEXT: ACTIONS.TEXT,
        ACTIONS_SCRIBBLE: ACTIONS.SCRIBBLE,
        ACTIONS_IMAGE: ACTIONS.IMAGE,
        pendingSelection,
        lockedAction,
        initialPointerPosition,
        applyDrawingRestrictions,
        markerCount,
        setLocationMarkers,
        pageId,
        cloneTemplate,
        cloneTemplates,
        isDrawingImage,
        setIsDrawingImage,
        imageStartPoint,
        setImageStartPoint,
        pendingImageData,
        setPendingImageData,
        addImage,
        setImages,
        currentImageId,
        imageConstraintRef,
        previewImageDataRef,
        isCloneMode,
        // Pass off-canvas state setters for off-canvas drawing support
        offCanvasStageRef: window.__offCanvasStageRef || null, // Get from global (set by Canvas callback)
        setOffCanvasRectangles,
        setOffCanvasCircles,
        setOffCanvasArrows,
        setOffCanvasDoubleSidedArrows,
        setOffCanvasLines,
        offCanvasLines,
        setOffCanvasTexts,
        offCanvasTexts,
        setOffCanvasImages,
        isCropModalOpen,
        isCropModalOpenRef,
        cloneTemplateElementIdRef,
        justFinishedShapeDrawRef,
      }),
    [
      isPaining,
      isDrawingRect,
      setIsDrawingRect,
      setNewRectStart,
      currentRectId,
      setRectangles,
      fillColor,
      setAction,
      saveToHistory,
      action,
      lockedAction,
      isDrawingCircle,
      setIsDrawingCircle,
      setNewCircleStart,
      currentCircleId,
      setCircles,
      isDrawingArrow,
      setIsDrawingArrow,
      setArrowStartPoint,
      currentArrowId,
      currentShapeId,
      setArrows,
      isDrawingDoubleArrow,
      setIsDrawingDoubleArrow,
      setDoubleArrowStartPoint,
      currentDoubleArrowId,
      setDoubleSidedArrows,
      isDrawingLine,
      setIsDrawingLine,
      currentLineId,
      stageRef,
      setLines,
      setLineStartPoint,
      lineStartPoint,
      isDrawingText,
      setIsDrawingText,
      newTextStart,
      setNewTextStart,
      currentTextId,
      setTexts,
      texts,
      setSelectedText,
      setSelectedTextId,
      setShowTextBox,
      setSelectedObject,
      setSelectedObjects,
      setEditTextBoxContent,
      transformerRef,
      selectedObject,
      pendingSelection,
      initialPointerPosition,
      applyDrawingRestrictions,
      markerCount,
      setLocationMarkers,
      pageId,
      cloneTemplate,
      cloneTemplates,
      isDrawingImage,
      setIsDrawingImage,
      imageStartPoint,
      setImageStartPoint,
      pendingImageData,
      setPendingImageData,
      addImage,
      setImages,
      currentImageId,
      isCloneMode,
      setOffCanvasRectangles,
      setOffCanvasCircles,
      setOffCanvasArrows,
      setOffCanvasDoubleSidedArrows,
      setOffCanvasLines,
      setOffCanvasTexts,
      offCanvasTexts,
      setOffCanvasImages,
      isCropModalOpen,
      isCropModalOpenRef,
      justFinishedShapeDrawRef,
    ],
  );
  const definedMarkers = new Set(); // Prevent duplicate <defs>

  // Helper to get SVG transform matrix string from Konva node's absolute transform
  // getSvgTransform, processNode, processTextElement, and convertStageToSVG are now imported from utils/svgConversionUtils

  // Helper wrapper for convertStageToSVG that maintains backward compatibility
  const convertStageToSVG = (stage) => {
    return convertStageToSVGUtil({
      stage,
      arrows,
      doubleSidedArrows,
      texts,
      circles,
      rectangles,
      videos,
      images,
      processHtmlContentWithFormatting,
    });
  };

  // Local processNode wrapper to pass processHtmlContentWithFormatting if needed in the future
  // For now, processNode from utils works directly for Konva nodes

  // processNodeLocal is now processNode from utils/svgConversionUtils - using imported version
  // processNode is now imported from utils/svgConversionUtils

  // processTextElement is now imported from utils/svgConversionUtils and called with processHtmlContentWithFormatting

  // processTextElementLocal wrapper - using imported function instead
  // processTextElement is now imported and will be called with processHtmlContentWithFormatting

  // convertStageToSVG is now a wrapper function that uses convertStageToSVGUtil from utils

  // Helper function to extract links from text element HTML content with position information
  // extractLinksFromText is now imported from utils

  async function handleExport(format) {
    const stage = stageRef.current;
    const transformer = transformerRef.current;
    if (!stage) return;

    const transformerExists =
      transformer && typeof transformer.nodes === "function";
    const currentNodes = transformerExists ? transformer.nodes() : [];

    if (transformerExists) {
      transformer.nodes([]); // Hide transformer
      transformer.getLayer()?.batchDraw();
    }
    const svgStringExport = convertStageToSVG(stage);

    // rasterizeSvg is now imported from utils

    // restoreTransformer is now imported from utils

    if (format === "pdf") {
      // Set loading state
      setIsExportingPdf(true);

      cleanupTransformerAndSelections(
        transformerRef,
        setSelectedTextId,
        setSelectedArrow,
        setSelectedObject,
        setShowTextBox,
        setSelectedImage,
        setSelectedObjects,
        setAction,
        ACTIONS.SELECT,
      );

      // Use the same screenshot approach as PNG export for consistency
      // This ensures HTML text elements (including highlighting) are captured correctly

      // 1️⃣ Temporarily make text-box backgrounds transparent so they don't hide shapes
      const textRects = stage.find(
        (n) =>
          n.getClassName &&
          n.getClassName() === "Rect" &&
          n.name() === "text-box-container",
      );
      const prevFills = textRects.map((r) => r.fill());
      textRects.forEach((r) => r.fill("rgba(0,0,0,0)"));
      stage.batchDraw();

      // 2️⃣ Hide resize handle and control points during export
      const resizeHandle = stage.findOne(".resize-handle");
      if (resizeHandle) {
        resizeHandle.hide();
      }

      const controlCircles = stage
        .find("Circle")
        ?.filter((node) => ["blue", "red"].includes(node.fill()));
      if (controlCircles && controlCircles.length > 0) {
        controlCircles.forEach((circle) => circle.hide());
      }

      // Redraw after hiding controls
      stage.batchDraw();
      stage.draw();
      await new Promise((resolve) => setTimeout(resolve, 100));

      // 3️⃣ Get the main canvas container (same as PNG export and handleLeaveCanvas)
      const mainStageContainer = document.getElementById(
        "main-canvas-stage-container",
      );
      if (!mainStageContainer) {
        console.error("Main stage container not found for PDF export");
        restoreTransformer(transformer, currentNodes, transformerExists);
        setIsExportingPdf(false);
        return;
      }

      // 3.5️⃣ Capture link positions BEFORE any transforms (critical for correct coordinates)
      const linkAnnotations = [];
      const exportElements = mainStageContainer.querySelectorAll(
        '[data-export="true"]',
      );
      const originalMainStageRect = mainStageContainer.getBoundingClientRect();
      const originalStageRect = stage.container().getBoundingClientRect();
      // Konva stage padding is typically set as a property or defaults to 20
      const stagePadding = 20; // Default padding value

      exportElements.forEach((element) => {
        const links = element.querySelectorAll("a[href]");

        links.forEach((link) => {
          const range = document.createRange();

          try {
            range.selectNodeContents(link);
            const rects = range.getClientRects();

            Array.from(rects).forEach((rect) => {
              // Get coordinates relative to main stage container BEFORE transforms
              const x = rect.left - originalMainStageRect.left;
              const y = rect.top - originalMainStageRect.top;
              const width = rect.width;
              const height = rect.height;

              if (width >= 2 && height >= 2 && link.href) {
                linkAnnotations.push({
                  x,
                  y,
                  width,
                  height,
                  url: link.href,
                });
              }
            });
          } catch (error) {
            // Fallback to simple bounding rect
            try {
              const linkRect = link.getBoundingClientRect();
              const x = linkRect.left - originalMainStageRect.left;
              const y = linkRect.top - originalMainStageRect.top;
              const width = linkRect.width;
              const height = linkRect.height;

              if (width >= 2 && height >= 2 && link.href) {
                linkAnnotations.push({
                  x,
                  y,
                  width,
                  height,
                  url: link.href,
                });
              }
            } catch (fallbackError) {
            }
          }
        });
      });

      // Store original styles
      const originalTransform = mainStageContainer.style.transform;
      const originalLeft = mainStageContainer.style.left;

      // Get the stage container element and store its styles
      const stageContainer = stage.container();
      const originalBorder = stageContainer?.style.border;
      const originalBoxShadow = stageContainer?.style.boxShadow;

      // Temporarily remove border and box-shadow from stage container
      if (stageContainer) {
        stageContainer.style.border = "none";
        stageContainer.style.boxShadow = "none";
      }

      // Do NOT hide parent with opacity: 0 - it makes HTML overlays invisible (see canvasLeaveHandler).
      const parentContainer = mainStageContainer.parentElement;
      let originalParentPosition = null;
      let originalParentLeft = null;
      let originalParentTop = null;
      let originalParentZIndex = null;
      if (parentContainer) {
        originalParentPosition = parentContainer.style.position;
        originalParentLeft = parentContainer.style.left;
        originalParentTop = parentContainer.style.top;
        originalParentZIndex = parentContainer.style.zIndex;
        parentContainer.style.position = "fixed";
        parentContainer.style.left = "-9999px";
        parentContainer.style.top = "0";
        parentContainer.style.zIndex = "-1";
        void parentContainer.offsetHeight;
      }

      // Force visibility on HTML overlay elements so they appear in the export
      const exportElementsPdf = mainStageContainer.querySelectorAll(
        '[data-export="true"]',
      );
      const exportOriginalStylesPdf = Array.from(exportElementsPdf).map(
        (el) => ({
          visibility: el.style.visibility,
          opacity: el.style.opacity,
        }),
      );
      exportElementsPdf.forEach((el) => {
        el.style.visibility = "visible";
        el.style.opacity = "1";
      });

      // Temporarily reset transform and position for clean capture
      mainStageContainer.style.transform = "none";
      mainStageContainer.style.left = "0";
      void mainStageContainer.offsetHeight; // Force reflow

      // Ensure stage is fully drawn
      stage.batchDraw();
      stage.draw();

      // Wait for DOM to settle
      await new Promise((resolve) => setTimeout(resolve, 500));

      try {
        // 4️⃣ Capture screenshot using toPng (same as PNG export and handleLeaveCanvas)
        const PIXEL_RATIO = 2; // Match the pixelRatio used in toPng
        let imgDataUrl;
        try {
          imgDataUrl = await toPng(mainStageContainer, {
            cacheBust: true,
            backgroundColor: "#ffffff",
            pixelRatio: PIXEL_RATIO,
            quality: 0.95,
            skipFonts: false, // Ensure fonts are included
          });
        } catch (pngError) {
          // Fallback to html2canvas if toPng fails (e.g., due to tainted canvas from CORS)
          // This can happen if images were loaded without proper CORS headers
          try {
            // Clone the container to avoid modifying the original
            const clonedContainer = mainStageContainer.cloneNode(true);
            clonedContainer.style.position = "absolute";
            clonedContainer.style.left = "-9999px";
            clonedContainer.style.top = "0";
            document.body.appendChild(clonedContainer);

            // Try to convert tainted images to data URLs before html2canvas
            // This handles images that were loaded without CORS
            const konvaCanvases = clonedContainer.querySelectorAll("canvas");
            for (const canvas of konvaCanvases) {
              try {
                // Try to get data URL from Konva canvas
                const dataUrl = canvas.toDataURL("image/png");
                // If successful, replace canvas with an img element
                const img = document.createElement("img");
                img.src = dataUrl;
                img.style.width = canvas.width + "px";
                img.style.height = canvas.height + "px";
                canvas.parentNode?.replaceChild(img, canvas);
              } catch (e) {
                // Canvas is tainted, skip it - html2canvas will handle it
              }
            }

            // Use html2canvas with allowTaint: false to skip tainted content gracefully
            const canvas = await html2canvas(clonedContainer, {
              useCORS: true,
              allowTaint: false, // Changed to false - will skip tainted content instead of failing
              backgroundColor: "#ffffff",
              scale: PIXEL_RATIO,
              logging: false,
              ignoreElements: (element) => {
                // Skip elements that might be tainted
                return false;
              },
            });

            // Clean up cloned container
            document.body.removeChild(clonedContainer);

            // Try to get data URL, but if it fails, we'll use the canvas as-is
            try {
              imgDataUrl = canvas.toDataURL("image/png", 0.95);
            } catch (dataUrlError) {
              // Even html2canvas's canvas might be tainted if images were in the DOM
              // Try using Konva's stage.toDataURL as a last resort
              try {
                imgDataUrl = stage.toDataURL({
                  pixelRatio: PIXEL_RATIO,
                  quality: 0.95,
                  mimeType: "image/png",
                });
              } catch (konvaError) {
                throw new Error(
                  `Failed to export PDF: All export methods failed. Canvas contains images that cannot be exported due to CORS restrictions. Please ensure images are loaded with proper CORS headers or use data URLs.`,
                );
              }
            }
          } catch (html2canvasError) {
            // If html2canvas also fails, try Konva stage.toDataURL as final fallback
            try {
              imgDataUrl = stage.toDataURL({
                pixelRatio: PIXEL_RATIO,
                quality: 0.95,
                mimeType: "image/png",
              });
            } catch (konvaError) {
              throw new Error(
                `Failed to export PDF: Canvas may contain images from untrusted sources. Please ensure images support CORS or are loaded as data URLs. Original error: ${pngError.message}`,
              );
            }
          }
        }

        // Convert PNG to Image to get dimensions
        const img = new Image();
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
          img.src = imgDataUrl;
        });

        // Get the actual container dimensions (not the screenshot dimensions which are 2x)
        const containerRect = mainStageContainer.getBoundingClientRect();
        const containerWidth = containerRect.width;
        const containerHeight = containerRect.height;

        // Create PDF with actual container dimensions (not screenshot dimensions)
        const pdf = new jsPDF({
          orientation:
            containerWidth > containerHeight ? "landscape" : "portrait",
          unit: "px",
          format: [containerWidth, containerHeight],
          compress: true,
        });

        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = pdf.internal.pageSize.getHeight();

        // Add the screenshot image to PDF (scaled down from 2x to 1x)
        pdf.addImage(
          imgDataUrl,
          "PNG",
          0,
          0,
          pdfWidth,
          pdfHeight,
          undefined,
          "FAST",
        );

        // 5️⃣ Add clickable link annotations using pre-captured positions
        linkAnnotations.forEach((annotation) => {
          try {
            // Coordinates are already relative to container, and PDF is container-sized
            // So we can use them directly (they're in 1x pixels, PDF is in 1x pixels)
            pdf.link(
              annotation.x,
              annotation.y,
              annotation.width,
              annotation.height,
              { url: annotation.url },
            );
          } catch (error) {
          }
        });

        // Add image reference links
        // Images are positioned from center in Konva, coordinates are relative to stage
        // Calculate position relative to main stage container using pre-captured positions
        images.forEach((imageElement) => {
          if (imageElement.referenceUrl && imageElement.referenceUrl.trim()) {
            try {
              // Stage position relative to main container (before transforms)
              const stageOffsetX =
                originalStageRect.left - originalMainStageRect.left;
              const stageOffsetY =
                originalStageRect.top - originalMainStageRect.top;

              // Image position in Konva is from center, convert to top-left
              // Add stage padding and stage offset to get container-relative position
              const imgX =
                imageElement.x -
                imageElement.width / 2 +
                stagePadding +
                stageOffsetX;
              const imgY =
                imageElement.y -
                imageElement.height / 2 +
                stagePadding +
                stageOffsetY;
              const imgW = imageElement.width;
              const imgH = imageElement.height;

              pdf.link(imgX, imgY, imgW, imgH, {
                url: imageElement.referenceUrl,
              });
            } catch (error) {
            }
          }
        });

        pdf.save("canvas.pdf");
      } catch (error) {
        console.error("Error capturing PDF:", error);
      } finally {
        // Restore border and box-shadow
        if (stageContainer) {
          stageContainer.style.border = originalBorder || "";
          stageContainer.style.boxShadow = originalBoxShadow || "";
        }

        // Restore original styles
        mainStageContainer.style.transform = originalTransform || "";
        mainStageContainer.style.left = originalLeft || "";

        // Restore HTML overlay elements' visibility/opacity
        exportElementsPdf.forEach((el, i) => {
          const orig = exportOriginalStylesPdf[i];
          if (orig) {
            el.style.visibility = orig.visibility || "";
            el.style.opacity = orig.opacity != null ? orig.opacity : "";
          }
        });

        // Restore parent position if we modified it
        if (parentContainer && originalParentPosition !== null) {
          parentContainer.style.position = originalParentPosition || "";
          parentContainer.style.left = originalParentLeft || "";
          parentContainer.style.top = originalParentTop || "";
          parentContainer.style.zIndex =
            originalParentZIndex != null ? originalParentZIndex : "";
        }

        // Restore resize handle visibility
        if (resizeHandle) {
          resizeHandle.show();
        }

        // Restore control circles
        if (controlCircles && controlCircles.length > 0) {
          controlCircles.forEach((circle) => circle.show());
        }

        // Restore fills
        textRects.forEach((r, idx) => r.fill(prevFills[idx]));
        stage.batchDraw();
        restoreTransformer(transformer, currentNodes, transformerExists);

        // Clear loading state
        setIsExportingPdf(false);
      }
    } else if (format === "svg") {
      setIsExportingSvg(true);
      // Same screenshot approach as PDF export: capture main-canvas-stage-container
      // so alignment, HTML content, and visuals match exactly what's on screen.
      const textRectsSvg = stage.find(
        (n) =>
          n.getClassName &&
          n.getClassName() === "Rect" &&
          n.name() === "text-box-container",
      );
      const prevFillsSvg = textRectsSvg.map((r) => r.fill());
      textRectsSvg.forEach((r) => r.fill("rgba(0,0,0,0)"));
      stage.batchDraw();

      const resizeHandleSvg = stage.findOne(".resize-handle");
      if (resizeHandleSvg) resizeHandleSvg.hide();
      const controlCirclesSvg = stage
        .find("Circle")
        ?.filter((node) => ["blue", "red"].includes(node.fill()));
      if (controlCirclesSvg?.length) {
        controlCirclesSvg.forEach((c) => c.hide());
      }
      stage.batchDraw();
      stage.draw();
      await new Promise((resolve) => setTimeout(resolve, 100));

      const mainStageContainerSvg = document.getElementById(
        "main-canvas-stage-container",
      );
      if (!mainStageContainerSvg) {
        setIsExportingSvg(false);
        restoreTransformer(transformer, currentNodes, transformerExists);
        textRectsSvg.forEach((r, i) => r.fill(prevFillsSvg[i]));
        stage.batchDraw();
        return;
      }

      // Capture link positions BEFORE any transforms (same as PDF export)
      const linkAnnotationsSvg = [];
      const exportElementsForLinks = mainStageContainerSvg.querySelectorAll(
        '[data-export="true"]',
      );
      const originalMainStageRectSvg =
        mainStageContainerSvg.getBoundingClientRect();
      const originalStageRectSvg = stage.container().getBoundingClientRect();
      const stagePaddingSvg = 20;

      exportElementsForLinks.forEach((element) => {
        const links = element.querySelectorAll("a[href]");
        links.forEach((link) => {
          try {
            const range = document.createRange();
            range.selectNodeContents(link);
            const rects = range.getClientRects();
            Array.from(rects).forEach((rect) => {
              const x = rect.left - originalMainStageRectSvg.left;
              const y = rect.top - originalMainStageRectSvg.top;
              const width = rect.width;
              const height = rect.height;
              if (width >= 2 && height >= 2 && link.href) {
                linkAnnotationsSvg.push({
                  x,
                  y,
                  width,
                  height,
                  url: link.href,
                });
              }
            });
          } catch (error) {
            try {
              const linkRect = link.getBoundingClientRect();
              const x = linkRect.left - originalMainStageRectSvg.left;
              const y = linkRect.top - originalMainStageRectSvg.top;
              const width = linkRect.width;
              const height = linkRect.height;
              if (width >= 2 && height >= 2 && link.href) {
                linkAnnotationsSvg.push({
                  x,
                  y,
                  width,
                  height,
                  url: link.href,
                });
              }
            } catch (fallbackError) {
            }
          }
        });
      });

      // Image reference links (same as PDF)
      const imageLinkAnnotationsSvg = [];
      images.forEach((imageElement) => {
        if (imageElement.referenceUrl && imageElement.referenceUrl.trim()) {
          try {
            const stageOffsetXSvg =
              originalStageRectSvg.left - originalMainStageRectSvg.left;
            const stageOffsetYSvg =
              originalStageRectSvg.top - originalMainStageRectSvg.top;
            const imgX =
              imageElement.x -
              imageElement.width / 2 +
              stagePaddingSvg +
              stageOffsetXSvg;
            const imgY =
              imageElement.y -
              imageElement.height / 2 +
              stagePaddingSvg +
              stageOffsetYSvg;
            imageLinkAnnotationsSvg.push({
              x: imgX,
              y: imgY,
              width: imageElement.width,
              height: imageElement.height,
              url: imageElement.referenceUrl,
            });
          } catch (error) {
          }
        }
      });

      const originalTransformSvg = mainStageContainerSvg.style.transform;
      const originalLeftSvg = mainStageContainerSvg.style.left;
      const stageContainerSvg = stage.container();
      const originalBorderSvg = stageContainerSvg?.style.border;
      const originalBoxShadowSvg = stageContainerSvg?.style.boxShadow;
      if (stageContainerSvg) {
        stageContainerSvg.style.border = "none";
        stageContainerSvg.style.boxShadow = "none";
      }

      const parentContainerSvg = mainStageContainerSvg.parentElement;
      let originalParentPositionSvg = null;
      let originalParentLeftSvg = null;
      let originalParentTopSvg = null;
      let originalParentZIndexSvg = null;
      if (parentContainerSvg) {
        originalParentPositionSvg = parentContainerSvg.style.position;
        originalParentLeftSvg = parentContainerSvg.style.left;
        originalParentTopSvg = parentContainerSvg.style.top;
        originalParentZIndexSvg = parentContainerSvg.style.zIndex;
        parentContainerSvg.style.position = "fixed";
        parentContainerSvg.style.left = "-9999px";
        parentContainerSvg.style.top = "0";
        parentContainerSvg.style.zIndex = "-1";
        void parentContainerSvg.offsetHeight;
      }

      const exportElementsSvg = mainStageContainerSvg.querySelectorAll(
        '[data-export="true"]',
      );
      const exportOriginalStylesSvg = Array.from(exportElementsSvg).map(
        (el) => ({
          visibility: el.style.visibility,
          opacity: el.style.opacity,
        }),
      );
      exportElementsSvg.forEach((el) => {
        el.style.visibility = "visible";
        el.style.opacity = "1";
      });

      mainStageContainerSvg.style.transform = "none";
      mainStageContainerSvg.style.left = "0";
      void mainStageContainerSvg.offsetHeight;
      stage.batchDraw();
      stage.draw();
      await new Promise((resolve) => setTimeout(resolve, 500));

      const PIXEL_RATIO_SVG = 2;
      let imgDataUrlSvg;
      try {
        imgDataUrlSvg = await toPng(mainStageContainerSvg, {
          cacheBust: true,
          backgroundColor: "#ffffff",
          pixelRatio: PIXEL_RATIO_SVG,
          quality: 0.95,
          skipFonts: false,
        });
      } catch (pngErr) {
        try {
          const cloned = mainStageContainerSvg.cloneNode(true);
          cloned.style.position = "absolute";
          cloned.style.left = "-9999px";
          cloned.style.top = "0";
          document.body.appendChild(cloned);
          const konvaCanvases = cloned.querySelectorAll("canvas");
          for (const canvas of konvaCanvases) {
            try {
              const dataUrl = canvas.toDataURL("image/png");
              const img = document.createElement("img");
              img.src = dataUrl;
              img.style.width = canvas.width + "px";
              img.style.height = canvas.height + "px";
              canvas.parentNode?.replaceChild(img, canvas);
            } catch (_) { }
          }
          const canvas = await html2canvas(cloned, {
            useCORS: true,
            allowTaint: false,
            backgroundColor: "#ffffff",
            scale: PIXEL_RATIO_SVG,
            logging: false,
          });
          document.body.removeChild(cloned);
          imgDataUrlSvg = canvas.toDataURL("image/png", 0.95);
        } catch (hcErr) {
          try {
            imgDataUrlSvg = stage.toDataURL({
              pixelRatio: PIXEL_RATIO_SVG,
              quality: 0.95,
              mimeType: "image/png",
            });
          } catch (_) {
            imgDataUrlSvg = null;
          }
        }
      }

      if (stageContainerSvg) {
        stageContainerSvg.style.border = originalBorderSvg || "";
        stageContainerSvg.style.boxShadow = originalBoxShadowSvg || "";
      }
      mainStageContainerSvg.style.transform = originalTransformSvg || "";
      mainStageContainerSvg.style.left = originalLeftSvg || "";
      exportElementsSvg.forEach((el, i) => {
        const o = exportOriginalStylesSvg[i];
        if (o) {
          el.style.visibility = o.visibility || "";
          el.style.opacity = o.opacity != null ? o.opacity : "";
        }
      });
      if (parentContainerSvg && originalParentPositionSvg != null) {
        parentContainerSvg.style.position = originalParentPositionSvg || "";
        parentContainerSvg.style.left = originalParentLeftSvg || "";
        parentContainerSvg.style.top = originalParentTopSvg || "";
        parentContainerSvg.style.zIndex =
          originalParentZIndexSvg != null ? originalParentZIndexSvg : "";
      }
      if (resizeHandleSvg) resizeHandleSvg.show();
      if (controlCirclesSvg?.length) {
        controlCirclesSvg.forEach((c) => c.show());
      }
      textRectsSvg.forEach((r, i) => r.fill(prevFillsSvg[i]));
      stage.batchDraw();
      restoreTransformer(transformer, currentNodes, transformerExists);
      setIsExportingSvg(false);

      if (imgDataUrlSvg) {
        const rectSvg = mainStageContainerSvg.getBoundingClientRect();
        const w = Math.round(rectSvg.width) || 800;
        const h = Math.round(rectSvg.height) || 600;
        const escapeSvgUrl = (str) =>
          String(str)
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;")
            .replace(/'/g, "&apos;");
        const linkElements = [...linkAnnotationsSvg, ...imageLinkAnnotationsSvg]
          .map(
            (ann) =>
              `<a xmlns:xlink="http://www.w3.org/1999/xlink" xlink:href="${escapeSvgUrl(ann.url)}" target="_blank" rel="noopener noreferrer">` +
              `<rect x="${ann.x}" y="${ann.y}" width="${ann.width}" height="${ann.height}" fill="transparent" style="cursor:pointer"/>` +
              `</a>`,
          )
          .join("");
        const svgStr =
          `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}">` +
          `<image x="0" y="0" width="${w}" height="${h}" href="${imgDataUrlSvg}" preserveAspectRatio="xMidYMid meet"/>` +
          linkElements +
          `</svg>`;
        const blob = new Blob([svgStr], { type: "image/svg+xml" });
        const url = URL.createObjectURL(blob);
        downloadFile(url, "canvas.svg");
      }
    } else if (format === "png" || format === "jpg" || format === "jpeg") {
      // Use the same screenshot approach as handleLeaveCanvas for consistency
      // This ensures HTML text elements (including radio buttons and numbering) are captured correctly

      // Set loading state
      setIsExportingPng(true);

      // 1️⃣ Temporarily make text-box backgrounds transparent so they don't hide shapes
      const textRects = stage.find(
        (n) =>
          n.getClassName &&
          n.getClassName() === "Rect" &&
          n.name() === "text-box-container",
      );
      const prevFills = textRects.map((r) => r.fill());
      textRects.forEach((r) => r.fill("rgba(0,0,0,0)"));
      stage.batchDraw();

      // 2️⃣ Hide resize handle and control points during export
      const resizeHandle = stage.findOne(".resize-handle");
      if (resizeHandle) {
        resizeHandle.hide();
      }

      const controlCircles = stage
        .find("Circle")
        ?.filter((node) => ["blue", "red"].includes(node.fill()));
      if (controlCircles && controlCircles.length > 0) {
        controlCircles.forEach((circle) => circle.hide());
      }

      // Redraw after hiding controls
      stage.batchDraw();
      stage.draw();
      await new Promise((resolve) => setTimeout(resolve, 100));

      // 3️⃣ Get the main canvas container (same as handleLeaveCanvas)
      const mainStageContainer = document.getElementById(
        "main-canvas-stage-container",
      );
      if (!mainStageContainer) {
        console.error("Main stage container not found for PNG export");
        restoreTransformer(transformer, currentNodes, transformerExists);
        setIsExportingPng(false);
        return;
      }

      // Store original styles
      const originalTransform = mainStageContainer.style.transform;
      const originalLeft = mainStageContainer.style.left;

      // Get the stage container element and store its styles
      const stageContainer = stage.container();
      const originalBorder = stageContainer?.style.border;
      const originalBoxShadow = stageContainer?.style.boxShadow;

      // Temporarily remove border and box-shadow from stage container
      if (stageContainer) {
        stageContainer.style.border = "none";
        stageContainer.style.boxShadow = "none";
      }

      // Do NOT hide parent with opacity: 0 - it makes HTML overlays invisible in the snapshot (see canvasLeaveHandler).
      const parentContainer = mainStageContainer.parentElement;
      let originalParentPosition = null;
      let originalParentLeft = null;
      let originalParentTop = null;
      let originalParentZIndex = null;
      if (parentContainer) {
        originalParentPosition = parentContainer.style.position;
        originalParentLeft = parentContainer.style.left;
        originalParentTop = parentContainer.style.top;
        originalParentZIndex = parentContainer.style.zIndex;
        parentContainer.style.position = "fixed";
        parentContainer.style.left = "-9999px";
        parentContainer.style.top = "0";
        parentContainer.style.zIndex = "-1";
        void parentContainer.offsetHeight;
      }

      // Force visibility on HTML overlay elements so they appear in the export
      const exportElements = mainStageContainer.querySelectorAll(
        '[data-export="true"]',
      );
      const exportOriginalStyles = Array.from(exportElements).map((el) => ({
        visibility: el.style.visibility,
        opacity: el.style.opacity,
      }));
      exportElements.forEach((el) => {
        el.style.visibility = "visible";
        el.style.opacity = "1";
      });

      // Temporarily reset transform and position for clean capture
      mainStageContainer.style.transform = "none";
      mainStageContainer.style.left = "0";
      void mainStageContainer.offsetHeight; // Force reflow

      // Ensure stage is fully drawn
      stage.batchDraw();
      stage.draw();

      // Wait for DOM to settle
      await new Promise((resolve) => setTimeout(resolve, 500));

      try {
        // Use toPng (same as handleLeaveCanvas) for consistent HTML text capture
        const dataUrl = await toPng(mainStageContainer, {
          cacheBust: true,
          backgroundColor: "#ffffff",
          pixelRatio: 2,
          quality: 0.95,
          skipFonts: false, // Ensure fonts are included
        });

        // For JPG format, convert PNG to JPG
        if (format.startsWith("jp")) {
          // Convert to canvas first, then to JPG
          const img = new Image();
          img.crossOrigin = "anonymous";
          await new Promise((resolve) => {
            img.onload = () => {
              const canvas = document.createElement("canvas");
              canvas.width = img.width;
              canvas.height = img.height;
              const ctx = canvas.getContext("2d");
              ctx.fillStyle = "#ffffff";
              ctx.fillRect(0, 0, canvas.width, canvas.height);
              ctx.drawImage(img, 0, 0);
              const jpgDataUrl = canvas.toDataURL("image/jpeg", 0.92);
              downloadFile(jpgDataUrl, `canvas.${format}`);
              resolve();
            };
            img.onerror = resolve;
            img.src = dataUrl;
          });
        } else {
          downloadFile(dataUrl, `canvas.${format}`);
        }
      } catch (pngError) {

        // Fallback 1: Try html2canvas with allowTaint: false
        try {
          const clonedContainer = mainStageContainer.cloneNode(true);
          clonedContainer.style.position = "absolute";
          clonedContainer.style.left = "-9999px";
          clonedContainer.style.top = "0";
          document.body.appendChild(clonedContainer);

          // Try to convert tainted Konva canvases to images
          const konvaCanvases = clonedContainer.querySelectorAll("canvas");
          for (const canvas of konvaCanvases) {
            try {
              const dataUrl = canvas.toDataURL("image/png");
              const img = document.createElement("img");
              img.src = dataUrl;
              img.style.width = canvas.width + "px";
              img.style.height = canvas.height + "px";
              canvas.parentNode?.replaceChild(img, canvas);
            } catch (e) {
              // Canvas is tainted, skip it
            }
          }

          const html2canvasCanvas = await html2canvas(clonedContainer, {
            useCORS: true,
            allowTaint: false, // Skip tainted content instead of failing
            backgroundColor: "#ffffff",
            scale: 2,
            logging: false,
          });

          document.body.removeChild(clonedContainer);

          let dataUrl;
          try {
            dataUrl = html2canvasCanvas.toDataURL(
              format.startsWith("jp") ? "image/jpeg" : "image/png",
              format.startsWith("jp") ? 0.92 : 0.95,
            );
          } catch (dataUrlError) {
            // html2canvas canvas also tainted, try Konva fallback
            throw new Error("html2canvas canvas tainted");
          }

          downloadFile(dataUrl, `canvas.${format}`);
        } catch (html2canvasError) {
          // Fallback 2: Try Konva stage.toDataURL
          try {
            if (stageRef.current) {
              const fallbackDataUrl = stageRef.current.toDataURL({
                pixelRatio: 2,
                quality: 1.0,
                mimeType: format.startsWith("jp") ? "image/jpeg" : "image/png",
              });
              downloadFile(fallbackDataUrl, `canvas.${format}`);
            } else {
              throw new Error("Stage reference not available");
            }
          } catch (konvaError) {
            // All methods failed
            console.error("Error capturing PNG - all methods failed:", {
              toPng: pngError.message,
              html2canvas: html2canvasError.message,
              konva: konvaError.message,
            });
            alert(
              "Failed to export image. Canvas contains images that cannot be exported due to CORS restrictions. Please ensure images support CORS or are loaded as data URLs.",
            );
          }
        }
      } finally {
        // Restore border and box-shadow
        if (stageContainer) {
          stageContainer.style.border = originalBorder || "";
          stageContainer.style.boxShadow = originalBoxShadow || "";
        }

        // Restore original styles
        mainStageContainer.style.transform = originalTransform || "";
        mainStageContainer.style.left = originalLeft || "";

        // Restore HTML overlay elements' visibility/opacity
        exportElements.forEach((el, i) => {
          const orig = exportOriginalStyles[i];
          if (orig) {
            el.style.visibility = orig.visibility || "";
            el.style.opacity = orig.opacity != null ? orig.opacity : "";
          }
        });

        // Restore parent position if we modified it
        if (parentContainer && originalParentPosition !== null) {
          parentContainer.style.position = originalParentPosition || "";
          parentContainer.style.left = originalParentLeft || "";
          parentContainer.style.top = originalParentTop || "";
          parentContainer.style.zIndex =
            originalParentZIndex != null ? originalParentZIndex : "";
        }

        // Restore resize handle visibility
        if (resizeHandle) {
          resizeHandle.show();
        }

        // Restore control circles
        if (controlCircles && controlCircles.length > 0) {
          controlCircles.forEach((circle) => circle.show());
        }

        // Restore fills
        textRects.forEach((r, idx) => r.fill(prevFills[idx]));
        stage.batchDraw();
        restoreTransformer(transformer, currentNodes, transformerExists);

        // Clear loading state
        setIsExportingPng(false);
      }
    } else if (format === "cjson") {
      // Convert canvas to exportable JSON
      const canvasData = {
        texts,
        images: await Promise.all(
          images.map(async (img) => {
            let src = img.src;
            let imageDataUrl = img.imageDataUrl;

            // Priority 1: Use existing imageDataUrl if available (from previous exports/saves)
            if (imageDataUrl && imageDataUrl.startsWith("data:")) {
              src = imageDataUrl;
            } else {
              // Priority 2: Try to convert img.image (HTMLImageElement) to data URL
              if (img.image instanceof HTMLImageElement) {
                try {
                  imageDataUrl = convertImageToDataUrl(img.image);
                  if (imageDataUrl) {
                    src = imageDataUrl; // Use data URL as src
                  }
                } catch (e) {
                }
              }

              // Priority 3: Try fallback data URLs
              if (!imageDataUrl) {
                if (img.originalImageDataUrl) {
                  imageDataUrl = img.originalImageDataUrl;
                  src = imageDataUrl;
                } else if (img.compressedImageDataUrl) {
                  imageDataUrl = img.compressedImageDataUrl;
                  src = imageDataUrl;
                }
              }

              // Priority 4: Convert blob to base64 if needed
              if (!imageDataUrl && src?.startsWith("blob:")) {
                try {
                  const blob = await fetch(src).then((res) => res.blob());
                  src = await new Promise((resolve) => {
                    const reader = new FileReader();
                    reader.onloadend = () => resolve(reader.result);
                    reader.readAsDataURL(blob);
                  });
                  imageDataUrl = src; // Store the converted data URL
                } catch (e) {
                }
              }

              // Priority 5: For HTTP/HTTPS URLs, fetch fresh with CORS to convert to data URL
              // This prevents tainted canvas issues on import
              if (!imageDataUrl && src && /^https?:/i.test(src)) {
                try {
                  const response = await fetch(src, {
                    mode: "cors",
                    credentials: "omit",
                  });
                  if (response.ok) {
                    const blob = await response.blob();
                    src = await new Promise((resolve, reject) => {
                      const reader = new FileReader();
                      reader.onloadend = () => resolve(reader.result);
                      reader.onerror = reject;
                      reader.readAsDataURL(blob);
                    });
                    imageDataUrl = src;
                  } else {
                  }
                } catch (fetchError) {
                  // If fetch fails, the image src will remain as HTTP/HTTPS URL
                  // This will cause tainted canvas on import, but at least the image is exported
                }
              }
            }

            return {
              ...img,
              image: undefined,
              originalImage: undefined,
              compressedImage: undefined,
              src,
              // Store imageDataUrl if we have it (for import to prefer data URLs)
              ...(imageDataUrl && { imageDataUrl }),
              // Explicitly preserve name and imageNumber for proper import
              // Always include name and imageNumber if they exist (even if empty string)
              ...(img.name !== undefined &&
                img.name !== null && { name: img.name }),
              ...(img.imageNumber !== undefined &&
                img.imageNumber !== null && { imageNumber: img.imageNumber }),
            };
          }),
        ),
        videos,
        rectangles,
        circles,
        arrows,
        lines,
        doubleSidedArrows,
      };

      const compressedData = LZString.compressToUint8Array(
        JSON.stringify(canvasData),
      );
      const blob = new Blob([compressedData], {
        type: "application/octet-stream",
      });

      downloadFile(URL.createObjectURL(blob), "canvas.cjson");
      restoreTransformer();
    } else {
      // fallback: raster png
      rasterizeSvg(svgStringExport, "image/png")
        .then(({ dataUrl }) => downloadFile(dataUrl, `canvas.png`))
        .finally(restoreTransformer);
    }
  }

  // downloadFile is now imported from utils/exportHandlers

  // onClick is now created using factory function from utils/clickHandler
  const onClick = useMemo(
    () => {
      const clickHandler = createOnClick({
        stageRef,
        offCanvasStageRef,
        topLayerStageRef,
        multiSelectCanvasClickDedupeRef,
        setSelectedImage,
        setSelectedLine,
        setSelectedArrow, // Add setSelectedArrow to clear arrow selections
        setSelectedDoubleSidedArrow, // Add setSelectedDoubleSidedArrow to clear double-sided arrow selections
        isElementBeingEdited,
        endElementEditing,
        setSelectedObjects,
        selectedObjectsRef,
        setSelectedObject,
        selectedObjects,
        selectedObject,
        setSelectedText,
        setShowTextBox,
        setSelectedTextId,
        setTexts,
        texts, // CRITICAL: Pass texts array to read selectedContent synchronously
        offCanvasTexts,
        isDrawingArrow,
        isDrawingDoubleArrow,
        isDrawingLine,
        setAction,
        action,
        rectangles,
        offCanvasRectangles,
        preMouseDownSelectionCountRef, // CRITICAL: Pass captured selection count ref to clickHandler
        circles,
        offCanvasCircles,
        images,
        offCanvasImages,
        videos,
        offCanvasVideos,
        arrows,
        offCanvasArrows,
        lines,
        offCanvasLines,
        doubleSidedArrows,
        offCanvasDoubleSidedArrows,
        ACTIONS_SELECT: ACTIONS.SELECT,
        lockedAction,
        isCloneMode, // Pass clone mode flag to click handler
        justFinishedSelectionRef, // Pass ref directly
        pendingChangesRef, // Only end editing when true so selection effect clears on first click
        isMultiSelectMode, // Pass multi-select mode flag
        isMultiSelectModeRef, // Pass ref to avoid stale closures
        setIsMultiSelectMode, // Pass setter for multi-select mode
        isCropModalOpen, // Don't clear selection when crop modal is open (prevents modal closing on rectangle change end)
        isCropModalOpenRef, // Ref set sync on Crop click so first-tick events see "open" (fixes first-time close)
      });
      return (e) => {
        blurCanvasNumericInputs();
        return clickHandler(e);
      };
    },
    [
      stageRef,
      offCanvasStageRef,
      topLayerStageRef,
      multiSelectCanvasClickDedupeRef,
      setSelectedImage,
      setSelectedLine,
      setSelectedArrow, // Add setSelectedArrow to dependencies
      setSelectedDoubleSidedArrow, // Add setSelectedDoubleSidedArrow to dependencies
      isElementBeingEdited,
      endElementEditing,
      setSelectedObjects,
      setSelectedObject,
      selectedObjects,
      selectedObject,
      setSelectedText,
      setShowTextBox,
      setSelectedTextId,
      setTexts,
      texts, // CRITICAL: Include texts in dependencies to read selectedContent
      offCanvasTexts,
      isDrawingArrow,
      isDrawingDoubleArrow,
      isDrawingLine,
      setAction,
      action,
      justFinishedSelectionRef, // Pass ref for checking if we just finished selection
      pendingChangesRef,
      rectangles,
      offCanvasRectangles,
      circles,
      offCanvasCircles,
      images,
      offCanvasImages,
      videos,
      offCanvasVideos,
      arrows,
      offCanvasArrows,
      lines,
      offCanvasLines,
      doubleSidedArrows,
      offCanvasDoubleSidedArrows,
      lockedAction,
      isCloneMode,
      isMultiSelectMode,
      setIsMultiSelectMode, // Add to dependencies
      isCropModalOpen,
      isCropModalOpenRef,
      blurCanvasNumericInputs,
    ],
  );

  // Element property update functions
  const updateLineProperties = createUpdateLineProperties({
    isElementBeingEditedRef, // Pass ref instead of value to avoid stale closures
    setPendingChanges,
    pendingChangesRef, // Pass ref to update synchronously
    setSelectedLine,
    setLines,
    setOffCanvasLines,
    setSelectedObject,
    saveToHistory,
  });

  const updateArrowProperties = createUpdateArrowProperties({
    isElementBeingEditedRef, // Pass ref instead of value to avoid stale closures
    setPendingChanges,
    pendingChangesRef, // Pass ref to update synchronously
    setSelectedArrow,
    setArrows,
    setOffCanvasArrows,
    setSelectedObject,
    saveToHistory,
  });

  const updateDoubleSidedArrowProperties =
    createUpdateDoubleSidedArrowProperties({
      isElementBeingEditedRef, // Pass ref instead of value to avoid stale closures
      setPendingChanges,
      pendingChangesRef, // Pass ref to update synchronously
      setSelectedDoubleSidedArrow,
      setDoubleSidedArrows,
      setOffCanvasDoubleSidedArrows,
      setSelectedObject,
      saveToHistory,
    });

  const updateSelectedShape = createUpdateSelectedShape({
    selectedObject,
    getSelectedObject: () => selectedObject, // Pass getter function to avoid stale closures
    isElementBeingEditedRef, // Pass ref instead of value to avoid stale closures
    setPendingChanges,
    pendingChangesRef, // Pass ref to update synchronously
    setSelectedObject,
    setLines,
    setRectangles,
    setCircles,
    setImages,
    setOffCanvasRectangles,
    setOffCanvasCircles,
    setOffCanvasImages,
    stageRef,
    offCanvasStageRef,
    saveToHistory,
    isTextEditingModeRef, // Pass the persistent ref
  });

  // Create a wrapped version of updateImageProperties with all necessary parameters bound
  const updateImagePropertiesWrapped = useCallback(
    (
      id,
      newProps,
      isDragging = false,
      skipHistorySave = false,
      isManualInput = false,
    ) => {
      return updateImageProperties(
        id,
        newProps,
        isDragging,
        images,
        offCanvasImages,
        setImages,
        setOffCanvasImages,
        setSelectedObject,
        setSelectedImage,
        setHasUnsavedChanges,
        saveToHistory,
        isUndoingRedoing,
        skipHistorySave,
        isElementBeingEditedRef, // Pass ref to check if element is being edited
        setPendingChanges, // Pass function to set pending changes
        latestUpdatedImagesRef, // Pass ref to store latest updated images
        pendingChangesRef, // Pass ref to track pending changes state
        isManualInput, // Pass isManualInput to bypass cooldown for manual input changes
      );
    },
    [
      images,
      offCanvasImages,
      setImages,
      setOffCanvasImages,
      setSelectedObject,
      setSelectedImage,
      setHasUnsavedChanges,
      saveToHistory,
      isUndoingRedoing,
      isElementBeingEditedRef,
      setPendingChanges,
      latestUpdatedImagesRef,
      pendingChangesRef,
    ],
  );

  // updateTextProperties is now imported from utils

  const fetchLatestPartsByRefs = useCallback(async (partSearchTerms = []) => {
    const cleanedRefs = [
      ...new Set((partSearchTerms || []).filter(Boolean).map((id) => String(id))),
    ];
    if (cleanedRefs.length === 0) return [];

    try {
      const searchQuery = new URLSearchParams({
        search_type:
          "name,part_number,description,table_note,textbox_note,unit_name,url",
        search: cleanedRefs.join(","),
        limit: String(Math.max(cleanedRefs.length, 10)),
        offset: "0",
      }).toString();
      const response = await dispatch(fetchAllParts({ searchQuery })).unwrap();
      const payload = response?.results || response || {};
      return (
        payload?.data ||
        payload?.results ||
        payload?.parts ||
        (Array.isArray(payload) ? payload : [])
      );
    } catch (error) {
      return [];
    }
  }, [dispatch]);

  const onImportedTextsReady = useCallback(
    async (importedTexts = []) => {
      const importedTextsWithParts = (importedTexts || []).filter(
        (text) => text?.partsByType && typeof text.partsByType === "object",
      );
      if (importedTextsWithParts.length === 0) return;

      const partRefs = [];
      importedTextsWithParts.forEach((text) => {
        Object.values(text.partsByType || {}).forEach((group) => {
          (group?.parts || []).forEach((partItem) => {
            const pn =
              partItem?.part_number ||
              partItem?.partNumber ||
              extractPartNumberFromTextLabel(partItem?.part);
            if (pn) partRefs.push(pn);
          });
        });
      });

      const latestParts = await fetchLatestPartsByRefs(partRefs);
      if (!Array.isArray(latestParts) || latestParts.length === 0) return;

      const latestPartMap = new Map();
      latestParts.forEach((part) => {
        if (part?.part_id != null && part.part_id !== "") {
          latestPartMap.set(String(part.part_id), part);
        }
      });
      if (latestPartMap.size === 0) return;

      const changeRows = [];
      importedTextsWithParts.forEach((text) => {
        Object.entries(text.partsByType || {}).forEach(([typeName, group]) => {
          (group?.parts || []).forEach((partItem) => {
            const latestPart = latestPartMap.get(
              partItem?.part_ref != null && partItem.part_ref !== ""
                ? String(partItem.part_ref)
                : "",
            );
            if (!latestPart) return;

            const nextSnapshot = {
              part: buildPartLabel(latestPart) || partItem.part || "",
              itemCodeNote:
                latestPart?.textbox_note !== undefined &&
                latestPart?.textbox_note !== null
                  ? latestPart.textbox_note
                  : partItem.itemCodeNote || "",
              defaultUnit: getDefaultUnitFromPart(latestPart),
              typeName: getPartTypeName(
                latestPart,
                partItem.typeName || typeName || "Other",
              ),
            };
            const previousSnapshot = {
              part: partItem.part || "",
              itemCodeNote: partItem.itemCodeNote || partItem.textbox_note || "",
              defaultUnit: partItem.defaultUnit || "",
              typeName: partItem.typeName || typeName,
            };

            const changedFields = PART_SEARCH_FIELDS.filter(
              (field) =>
                normalizeComparisonValue(previousSnapshot[field]) !==
                normalizeComparisonValue(nextSnapshot[field]),
            );
            if (changedFields.length === 0) return;

            changeRows.push({
              textId: text.id,
              partRef: partItem.part_ref,
              itemCode: latestPart.part_number || "",
              previousPart: previousSnapshot.part,
              nextPart: nextSnapshot.part,
              previousItemCodeNote: previousSnapshot.itemCodeNote,
              nextItemCodeNote: nextSnapshot.itemCodeNote,
              previousDefaultUnit: previousSnapshot.defaultUnit,
              nextDefaultUnit: nextSnapshot.defaultUnit,
              previousTypeName: previousSnapshot.typeName,
              nextTypeName: nextSnapshot.typeName,
              changedFields,
            });
          });
        });
      });

      if (changeRows.length === 0) return;

      const uniqueRows = Array.from(
        new Map(
          changeRows.map((row) => [
            `${String(row.textId ?? "")}-${String(row.partRef ?? "")}-${row.changedFields.join(",")}-${normalizeComparisonValue(row.previousPart)}-${normalizeComparisonValue(row.nextPart)}`,
            row,
          ]),
        ).values(),
      );

      latestImportedPartMapRef.current = latestPartMap;
      importedTextIdsWithPartChangesRef.current = new Set(
        uniqueRows.map((row) => row.textId).filter(Boolean),
      );
    },
    [fetchLatestPartsByRefs],
  );

  const applyImportedPartUpdates = useCallback(() => {
    if (latestImportedPartMapRef.current.size === 0) return;
    setIsApplyingImportedPartUpdates(true);
    setTexts((prevTexts) =>
      prevTexts.map((text) => {
        if (!importedTextIdsWithPartChangesRef.current.has(text.id)) return text;
        return updateImportedTextWithLatestPartDetails(
          text,
          latestImportedPartMapRef.current,
        );
      }),
    );
    setTimeout(() => {
      setIsApplyingImportedPartUpdates(false);
    }, 0);
  }, [setTexts]);

  // Canvas import/export operations
  const handleImportCanvas = createHandleImportCanvas({
    setAction,
    selectAction: ACTIONS.SELECT,
    generateMarkerId,
    setTexts,
    setImages,
    setVideos,
    setRectangles,
    setCircles,
    setArrows,
    setLines,
    setDoubleSidedArrows,
    saveToHistory,
    setIsImportingCanvas,
    onImportedTextsReady,
    texts, // Pass current state arrays for calculating combined state
    images,
    videos,
    rectangles,
    circles,
    arrows,
    lines,
    doubleSidedArrows,
  });

  // Drag End Handler
  // handleRectangleDragEnd is now imported from utils

  // handleCircleDragEnd is now imported from utils
  // handleArrowDragEnd is now imported from utils

  // handleDoubleSidedArrowDragEnd is now imported from utils

  const clearCanvas = createClearCanvas({
    setTexts,
    setRectangles,
    setCircles,
    setArrows,
    setScribbles,
    setLines,
    setImages,
    setVideos,
    setSelectedText,
    setSelectedTextId,
    setSelectedObject,
    setSelectedArrow,
    transformerRef,
    saveToHistory,
    setHasUnsavedChanges,
    pageId,
    stageRef,
    setSnapshot,
  });

  // Add useEffect to check for image upload from location state
  // useEffect(() => {
  //   // Check if we have an action in location state
  //   if (
  //     location.state &&
  //     location.state.action === "ADD_IMAGE" &&
  //     location.state.imageSrc
  //   ) {
  //     // Call addImage with the provided image source
  //     addImage(location.state.imageSrc);

  //     // Clear the location state to prevent re-adding the image on refresh
  //     window.history.replaceState({}, document.title);
  //   }
  // }, [location.state]);

  // Add useEffect for parts scroll handler
  useEffect(() => {
    if (!isPartsModalOpen || allPartsLoaded || isLoadingMoreParts) return;

    const handlePartsScroll = () => {
      if (allPartsLoaded || isLoadingMoreParts) return;

      const container = partTableContnrRef.current;
      if (!container) return;

      const { scrollTop, scrollHeight, clientHeight } = container;
      setPartsScrollPosition(scrollTop);

      // Check if scrolled to the bottom (with a 200px threshold)
      if (scrollTop + clientHeight >= scrollHeight - 200) {
        setIsLoadingMoreParts(true);
        setPartsOffset((prevOffset) => prevOffset + partsLimit);
      }
    };

    const tableContainer = partTableContnrRef.current;
    if (tableContainer) {
      tableContainer.addEventListener("scroll", handlePartsScroll);
    }

    return () => {
      if (tableContainer) {
        tableContainer.removeEventListener("scroll", handlePartsScroll);
      }
    };
  }, [isPartsModalOpen, allPartsLoaded, isLoadingMoreParts, partsLimit]);

  // Add effect to initialize local parts array and process newly loaded parts
  useEffect(() => {
    // Scenario 1: Initial load of parts
    // This condition runs when 'parts' data is loaded and 'localParts' is initially empty,
    // and we are not currently in a "loading more" state (to avoid conflict with scenario 2).
    if (
      parts &&
      parts.length > 0 &&
      localParts.length === 0 &&
      !isLoadingMoreParts
    ) {
      const filteredParts =
        filterType === "bom_step" && Array.isArray(partNumbers)
          ? parts.filter((part) => partNumbers.includes(part.part_number))
          : parts;

      // Crucial: Only update localParts if the filtered result is actually different
      // from the current localParts state to prevent infinite loops.
      if (!arePartsArraysEqual(localParts, filteredParts)) {
        setLocalParts(filteredParts);
      }
      // If no parts are found after filtering, signal that all parts are loaded.
      if (filteredParts.length === 0) {
        setAllPartsLoaded(true);
      }
    }

    // Scenario 2: Processing newly loaded parts when loading more
    // This runs when `isLoadingMoreParts` is true and the modal is open.
    if (isLoadingMoreParts && isPartsModalOpen) {
      // Removed setTimeout to simplify debugging. Reintroduce if specific timing issues arise.
      if (parts.length > 0) {
        // Identify parts that are truly new and not already in localParts
        const existingIds = new Set(localParts.map((part) => part.part_id));
        let newPartsToAdd = parts.filter(
          (part) => !existingIds.has(part.part_id),
        );

        // Apply additional filtering if it's a 'bom_step' type
        if (filterType === "bom_step" && Array.isArray(partNumbers)) {
          newPartsToAdd = newPartsToAdd.filter((part) =>
            partNumbers.includes(part.part_number),
          );
        }

        // Only update localParts if there are genuinely new parts to add
        if (newPartsToAdd.length > 0) {
          setLocalParts((prev) => {
            const mergedParts = [...prev, ...newPartsToAdd];
            // An additional check to ensure the merged array is truly different,
            // though for adding new unique items, it almost always will be.
            if (arePartsArraysEqual(prev, mergedParts)) {
              return prev; // Return previous state if no effective change
            }
            return mergedParts;
          });
        } else {
          // If no new parts are added from the latest fetch, assume all parts are loaded
          setAllPartsLoaded(true);
        }
      } else {
        // If the 'parts' array itself is empty (e.g., no more data from API)
        setAllPartsLoaded(true);
      }

      // Restore scroll position
      const container = partTableContnrRef.current;
      if (container) {
        container.scrollTop = partsScrollPosition;
      }

      // After processing, set `isLoadingMoreParts` to false.
      // This is a critical state update that triggers re-renders, ensure it's managed correctly.
      setIsLoadingMoreParts(false);
    }
  }, [
    parts, // Dependency: when `parts` from Redux changes
    isLoadingMoreParts, // Dependency: when the loading more state changes
    isPartsModalOpen, // Dependency: when the parts modal's open state changes
    localParts, // Dependency: when the component's internal `localParts` state changes
    partsScrollPosition, // Dependency: when the scroll position state changes
    filterType, // Dependency: used in filtering logic
    partNumbers, // Dependency: used in filtering logic
    setAllPartsLoaded, // State setter, included for completeness (React guarantees stability for these)
    setLocalParts, // State setter, included for completeness
    setIsLoadingMoreParts, // State setter, included for completeness
    partTableContnrRef, // Ref, included for completeness as it's used
    arePartsArraysEqual, // Callback function, included as a dependency
  ]);

  // Reset parts state when modal is opened/closed
  useEffect(() => {
    if (isPartsModalOpen) {
      // Reset state when modal is opened
      setPartsOffset(0);
      setAllPartsLoaded(false);
      setLocalParts([]);
    }
  }, [isPartsModalOpen]);

  // Image-specific clipboard operations
  const copySelectedImage = createCopySelectedImage({
    selectedObject,
    images,
    offCanvasImages,
    generateMarkerId,
    setImages,
    setOffCanvasImages,
    setSelectedObject,
    addImage,
    saveToHistory,
  });

  const cutSelectedImage = createCutSelectedImage({
    selectedObject,
    images,
    offCanvasImages,
    setClipboard,
    setImages,
    setOffCanvasImages,
    setSelectedImage,
    setSelectedObject,
    saveToHistory,
  });

  // Paste elements handler (needed for handlePaste)
  const handlePasteElements = createHandlePasteElements({
    clipboard,
    setImages,
    setRectangles,
    setCircles,
    setArrows,
    setDoubleSidedArrows,
    setLines,
    setTexts,
    setSelectedObjects,
    setSelectedObject,
    saveToHistory,
  });

  // Paste handler
  const handlePaste = createHandlePaste({
    clipboard,
    handlePasteElements,
    addImage: addImageAtPastePosition,
    showError: showErrorSnackbar,
  });

  const handleToolbarPaste = useCallback(async () => {
    isToolbarPasteInProgressRef.current = true;
    setIsPastingFromClipboard(true);
    try {
      await new Promise((resolve) => requestAnimationFrame(() => resolve()));
      await handlePaste();
    } finally {
      isToolbarPasteInProgressRef.current = false;
      setIsPastingFromClipboard(false);
    }
  }, [handlePaste]);

  // Native paste handler
  const handleNativePaste = createHandleNativePaste({
    setIsImageLoading,
    setImageLoadingProgress,
    addImage: addImageAtPastePosition,
    showError: showErrorSnackbar,
  });

  useEffect(() => {
    window.addEventListener("paste", handleNativePaste);
    return () => window.removeEventListener("paste", handleNativePaste);
  }, [handleNativePaste]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        if (showShortcutsModal) {
          e.preventDefault();
          setShowShortcutsModal(false);
          return;
        }

        const inChrome = isInsideCanvasChrome(e.target);
        if (inChrome) {
          e.preventDefault();
          window.dispatchEvent(new CustomEvent("canvasEscape"));
          if (
            document.activeElement &&
            typeof document.activeElement.blur === "function"
          ) {
            document.activeElement.blur();
          }
        }

        if (
          isTextInputOrContentEditable(e.target) &&
          !inChrome
        ) {
          return;
        }

        const exitSpecialModes =
          isWaitingForPaste ||
          isCloneMode ||
          !!cloneTemplate ||
          (cloneTemplates && cloneTemplates.length > 0) ||
          lockedAction != null ||
          action !== ACTIONS.SELECT;

        if (exitSpecialModes) {
          e.preventDefault();
          setIsWaitingForPaste(false);
          setPendingClipboard([]);
          setIsCloneMode(false);
          setCloneTemplate(null);
          setCloneTemplates([]);
          setLockedAction(null);
          setAction(ACTIONS.SELECT);
        }

        const cropBlocksClear =
          isCropModalOpen || isCropModalOpenRef?.current;
        const hasSelection =
          (selectedObjects && selectedObjects.length > 0) ||
          selectedObject != null;

        if (!cropBlocksClear && hasSelection) {
          e.preventDefault();
          if (pendingSelection.current) {
            pendingSelection.current = null;
          }
          transformerRef?.current?.nodes([]);
          setSelectedText(null);
          setShowTextBox(false);
          setSelectedTextId(null);
          setSelectedArrow(null);
          setSelectedLine(null);
          setSelectedDoubleSidedArrow(null);
          setSelectedObject(null);
          setSelectedImage(null);
          setSelectedObjects([]);
          setIsMultiSelectMode(false);
          setIndividuallySelectedIds(new Set());
        }
        return;
      }

      // Don't hijack keys while typing or editing rich text (outside canvas chrome).
      if (isTextInputOrContentEditable(e.target)) return;

      const isModifierPressed = e.ctrlKey || e.metaKey;
      const key = (e.key || "").toLowerCase();

      if (isModifierPressed) {
        if (key === "/" || key === "?") {
          e.preventDefault();
          setShowShortcutsModal((prev) => !prev);
          return;
        }
        if (key === "c") {
          const itemsToClone =
            selectedObjects && selectedObjects.length > 0
              ? selectedObjects
              : selectedObject
                ? [selectedObject]
                : [];
          if (itemsToClone.length === 0 || isCloneMode) {
            return;
          }
          e.preventDefault();
          handleToggleCloneMode(
            selectedObjects && selectedObjects.length > 0
              ? selectedObjects
              : null,
          );
          return;
        }
        if (key === "v") {
          const hasClonePayload =
            isCloneMode &&
            (cloneTemplate ||
              (cloneTemplates && cloneTemplates.length > 0));
          if (hasClonePayload) {
            e.preventDefault();
            placeCloneAtPointerKeyboard();
            return;
          }
          // In-app copy/cut: handle here and block default.
          if (clipboard.length > 0) {
            e.preventDefault();
            handlePaste();
            return;
          }
          // OS clipboard (e.g. image from browser/screenshot): do not preventDefault on
          // keydown — that suppresses the `paste` event in Chromium, so `handleNativePaste`
          // never receives clipboardData. Rely on the window `paste` listener instead.
          return;
        }
        if (key === "r") {
          e.preventDefault();
          window.location.reload();
        }
        return;
      }

      if (key === "insert") {
        const hasClonePayload =
          isCloneMode &&
          (cloneTemplate ||
            (cloneTemplates && cloneTemplates.length > 0));
        if (hasClonePayload) {
          e.preventDefault();
          placeCloneAtPointerKeyboard();
          return;
        }
        if (clipboard.length > 0) {
          e.preventDefault();
          handlePaste();
        }
        return;
      }

      const activateToolShortcut = (toolAction) => {
        const now = Date.now();
        const prev = toolShortcutPressRef.current[toolAction] || 0;
        const isDoublePress = now - prev <= 350;

        if (isDoublePress) {
          // Match toolbar behavior: double action toggles lock state.
          if (lockedAction === toolAction) {
            setLockedAction(null);
            setAction(ACTIONS.SELECT);
          } else {
            setLockedAction(toolAction);
            setAction(toolAction);
          }
        } else {
          setLockedAction(null);
          setAction(toolAction);
        }

        toolShortcutPressRef.current[toolAction] = now;
      };

      if (key === "r") {
        activateToolShortcut(ACTIONS.RECTANGLE);
        return;
      }
      if (key === "c") {
        activateToolShortcut(ACTIONS.CIRCLE);
        return;
      }
      if (key === "a" || key === "l") {
        activateToolShortcut(ACTIONS.LINE);
        return;
      }
      if (key === "t") {
        setLockedAction(null);
        setAction(ACTIONS.TEXT);
        return;
      }
      if (key === "h") {
        setLockedAction(null);
        setAction(ACTIONS.PAN);
        return;
      }
      if (key === "s") {
        setLockedAction(null);
        setAction(ACTIONS.SELECT);
        return;
      }
      if (key === "i") {
        const imageInput = document.getElementById("imageUpload");
        if (imageInput && typeof imageInput.click === "function") {
          setLockedAction(null);
          setAction(ACTIONS.IMAGE);
          imageInput.click();
        }
        return;
      }

      if (key === "p") {
        setShowWebcamPopup(true);
        return;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    handlePaste,
    clipboard,
    setAction,
    setLockedAction,
    lockedAction,
    action,
    selectedObject,
    selectedObjects,
    isCloneMode,
    cloneTemplate,
    cloneTemplates,
    isWaitingForPaste,
    handleToggleCloneMode,
    placeCloneAtPointerKeyboard,
    setIsWaitingForPaste,
    setPendingClipboard,
    setIsCloneMode,
    setCloneTemplate,
    setCloneTemplates,
    showShortcutsModal,
  ]);

  const handleDrop = createHandleDrop({
    setIsImageLoading,
    setImageLoadingProgress,
    addImage,
    saveToHistory,
    showErrorSnackbar,
    stageRef, // Pass stageRef for coordinate conversion
  });
  const sanitizeElements = sanitizeElementsUtil;

  // Inside MyCanvas.js, within the MyCanvas functional component

  // Line manipulation functions
  const handleDotDragMove = createHandleDotDragMove(
    circles,
    setLines,
    setCircles,
  );
  const addLine = createAddLine(
    setLines,
    setCircles,
    saveToHistory,
    fillColor,
    generateMarkerId,
  );

  const handleImageClick = createHandleImageClick(
    images,
    setSelectedImage,
    setSelectedObject,
  );

  const handleImageDragEnd = createHandleImageDragEnd(
    images,
    setImages,
    saveToHistory,
    isUndoingRedoing,
  );

  // Removed skipNextHistory (no longer used). Undo/redo uses isUndoingRedoing instead.

  // Grouped undo/redo functions
  const startElementEditing = createStartElementEditing(
    isElementBeingEdited,
    selectedObject,
    setIsElementBeingEdited,
    setPendingChanges,
    pendingChangesRef,
    pendingChanges, // Add pendingChanges to read current value
    setInitialEditState,
    texts,
    rectangles,
    circles,
    arrows,
    doubleSidedArrows,
    images,
    scribbles,
    lines,
  );

  // endElementEditing is created earlier (needed for createDeleteSelectedObject at line 1972)
  // Monitor selectedObject changes for grouped undo/redo
  useEffect(() => {
    if (!selectedObject && (selectedObjects?.length || 0) === 0) {
    }

    if (selectedObject && !isElementBeingEdited) {
      // Element was just selected - set editing mode so that property changes (e.g. stroke width in
      // UnifiedLineArrowEditor) are saved on unselect via endElementEditing. Otherwise we only set
      // isElementBeingEdited when hasPendingChanges, so changing stroke then clicking background
      // never calls endElementEditing and undo removes the element.
      const hasPendingChanges =
        pendingChanges || (pendingChangesRef && pendingChangesRef.current);

      setIsElementBeingEdited(true);
      isElementBeingEditedRef.current = true;

      if (!hasPendingChanges) {
        setPendingChanges(false);
        pendingChangesRef.current = false;
      } else {
      }

      // startElementEditing(); // REMOVED: Don't save initial state on selection
      // Reset text editing mode when selecting a new element
      isTextEditingModeRef.current = false;
    } else if (!selectedObject && isElementBeingEdited) {
      // Element was just unselected, end editing mode
      // Reset text editing mode ref when unselecting
      isTextEditingModeRef.current = false;

      // CRITICAL FIX: Only call endElementEditing if there are pending changes
      // Otherwise just clear the editing state
      const hasPendingChanges =
        pendingChanges || (pendingChangesRef && pendingChangesRef.current);
      if (hasPendingChanges) {
        // Defer endElementEditing so that onTransformEnd (rectangle/circle/image resize) has a chance to run
        // and set selectedObject back. If selection is restored within 120ms, do not call endElementEditing.
        const timeoutId = setTimeout(() => {
          if (selectedObjectRef.current == null) {
            endElementEditing();
          }
          // else: selection was restored (e.g. by onTransformEnd), leave selection and editing state as-is
        }, 120);
        return () => clearTimeout(timeoutId);
      } else {
        // No pending changes - just clear editing state without saving
        setIsElementBeingEdited(false);
        isElementBeingEditedRef.current = false;
      }
    }
  }, [selectedObject, isElementBeingEdited]);

  useEffect(() => {
    const prevLen = prevSelectedObjectsLenRef.current;
    const nextLen = selectedObjects?.length || 0;
    if (prevLen > 1 && nextLen === 0) {
    }
    prevSelectedObjectsLenRef.current = nextLen;
  }, [selectedObjects]);

  // CRITICAL: Also set isElementBeingEditedRef immediately when selectedObject changes
  // This prevents race conditions where property changes happen before useEffect runs
  // CRITICAL FIX: Only set the ref when there are pending changes, matching the state update logic
  useEffect(() => {
    if (selectedObject) {
      // Element is selected - only set editing flag if there are pending changes
      const hasPendingChanges =
        pendingChanges || (pendingChangesRef && pendingChangesRef.current);
      if (hasPendingChanges && !isElementBeingEditedRef.current) {
        isElementBeingEditedRef.current = true;
      } else if (!hasPendingChanges && isElementBeingEditedRef.current) {
        // Clear the ref if no pending changes (shouldn't normally happen, but for safety)
        isElementBeingEditedRef.current = false;
      }
    } else {
      // Element is not selected - clear editing flag
      if (isElementBeingEditedRef.current) {
        isElementBeingEditedRef.current = false;
      }
    }
  }, [selectedObject, pendingChanges]);

  useEffect(() => {
    setArrows((prev) => deduplicateByName(prev));
    setLines((prev) => deduplicateByName(prev));
    setRectangles((prev) => deduplicateByName(prev));
    setCircles((prev) => deduplicateByName(prev));
    setDoubleSidedArrows((prev) => deduplicateByName(prev));
    setScribbles((prev) => deduplicateByName(prev));
    setTexts((prev) => deduplicateByName(prev));
    setImages((prev) => deduplicateByName(prev));
  }, []);

  // Helper function to generate marker IDs for new elements
  // generateMarkerId is now imported from utils/lineUtils

  // All removed* states are declared earlier (needed for createDeleteSelectedObject at line 1982)
  // isCropModalOpen is declared earlier (needed for createOnClick and modal UI)
  // isImageLoading and imageLoadingProgress are declared earlier (needed for createAddImage at line 1797)

  // Keep fixed UI elements (zoom controls, leave button) sticky on screen during page zoom
  const fixedOffsetPx = getRootFontSizePx();
  const fixedPosStyles = {
    zoomControls: {
      bottom: `${fixedOffsetPx}px`,
      left: `80px`,
    },
    leaveCanvas: {
      bottom: `${fixedOffsetPx}px`,
      right: `${fixedOffsetPx}px`,
    },
  };

  // Only block by tool mode when a non-Select tool is active (not lockedAction while Select is selected).
  const isNonSelectToolActive = action !== ACTIONS.SELECT;

  const bumpLeaveChromeSync = useCallback(() => {
    setLeaveChromeSync((n) => n + 1);
  }, []);

  const chromeUiBlocksLeaveCanvas = useMemo(() => {
    if (typeof document === "undefined") return false;
    const el = document.activeElement;
    if (!el) return false;
    return isNodeInsideBlockLeaveRegion(el, action);
  }, [leaveChromeSync, action]);

  const leaveCanvasControlDisabled =
    isLeavingCanvas ||
    isNonSelectToolActive ||
    chromeUiBlocksLeaveCanvas || selectedObject !== null || selectedObjects.length > 0;

  // Recompute when tool changes (toolbar exception depends on action).
  useLayoutEffect(() => {
    bumpLeaveChromeSync();
  }, [action, bumpLeaveChromeSync]);

  useEffect(() => {
    const bumpAfterPaint = () => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          bumpLeaveChromeSync();
        });
      });
    };

    const onFocusIn = () => {
      bumpLeaveChromeSync();
    };

    const onFocusOut = () => {
      bumpAfterPaint();
    };

    const onPointerDown = () => {
      bumpAfterPaint();
    };

    const onPointerUp = () => {
      bumpAfterPaint();
    };

    document.addEventListener("focusin", onFocusIn, true);
    document.addEventListener("focusout", onFocusOut, true);
    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("pointerup", onPointerUp, true);

    return () => {
      document.removeEventListener("focusin", onFocusIn, true);
      document.removeEventListener("focusout", onFocusOut, true);
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("pointerup", onPointerUp, true);
    };
  }, [bumpLeaveChromeSync]);

  return (
    <div
      className={`canvas-outer ${zoomLevel !== 1 ? "zoomed" : ""}`}
      style={{
        overflow: "hidden",
        width: "100%",
        position: "relative",
        boxSizing: "border-box",
      }}
    >
      {/* Display Loader while loading data from IndexedDB */}
      {isLoadingFromIndexedDB && <Loader text={loadingMessage} />}

      {/* Display Loader while leaving canvas (hides stage movement during screenshot) */}
      {isLeavingCanvas && <Loader text="Leaving Canvas..." />}

      {/* Display Loader while exporting PNG/JPG */}
      {isExportingPng && <Loader text="Exporting Canvas PNG..." />}

      {/* Display Loader while exporting PDF */}
      {isExportingPdf && <Loader text="Exporting Canvas PDF..." />}

      {/* Display Loader while exporting SVG */}
      {isExportingSvg && <Loader text="Exporting SVG..." />}

      {/* Display Loader while importing canvas */}
      {isImportingCanvas && <Loader text="Importing Canvas..." />}

      {/* Display Loader while pasting from clipboard */}
      {isPastingFromClipboard && <Loader text="Pasting from Clipboard..." />}

      {/* Full-screen overlay when crop modal is open: blocks ALL pointer events to canvas/sidebar so resize/drag in modal cannot clear selection */}
      {(isCropModalOpen || isCropModalOpenRef?.current) &&
        createPortal(
          <div
            aria-hidden
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 9998,
              pointerEvents: "auto",
              background: "transparent",
            }}
          />,
          document.body,
        )}

      <div
        ref={containerRef}
        className="canvas-container"
        style={{
          position: "absolute",
          inset: 0,
          overflow: "hidden",
          // Hide canvas during leave so user doesn't see stage move for screenshot
          ...(isLeavingCanvas && {
            opacity: 0,
            pointerEvents: "none",
          }),
        }}
      >
        <div
          ref={scrollAreaRef}
          className="canvas-scroll-area"
          style={{
            position: "absolute",
            inset: 0,
            overflow: "auto",
            // Ensure content can scroll past the overlay custom scrollbars
            paddingTop: zoomOverflowPadding.top, // push content down so top area is scrollable when zoomed
            paddingLeft:
              CANVAS_SCROLL_HORIZONTAL_GUTTER_PX + zoomOverflowPadding.left,
            paddingBottom: dynamicBottomPadding, // dynamic padding based on canvas height and zoom for resize handle visibility
            paddingRight:
              CANVAS_SCROLL_HORIZONTAL_GUTTER_PX +
              (zoomOverflowPadding.right || 0),
          }}
        >
          {/* Wrapper to measure effective content size at current zoom */}
          <div ref={contentRef} className="editor-container">
            {/* Controls */}
            {!isCropModalOpen && !isLeavingCanvas && (
              <div className="main-toolbar-wrapper" data-canvas-chrome>
                <div className="toolbar-container">
                  <Toolbar
                    action={action}
                    setAction={(e) => {
                      setAction(e);
                      setShowTextBox(false);
                      // Clear pending image data if switching away from IMAGE action
                      if (e !== ACTIONS.IMAGE && pendingImageData) {
                        setPendingImageData(null);
                        setIsDrawingImage(false);
                        setImageStartPoint(null);
                      }
                      // Don't clear selectedObject if clone mode is active
                      if (!isCloneMode && !lockedAction) {
                        setSelectedObject(null);
                        setSelectedText(null);
                        setSelectedTextId(null);
                      }
                      // Clear pendingSelection to prevent useEffect from setting showTextBox back to true
                      if (pendingSelection.current) {
                        pendingSelection.current = null;
                      }
                      // transformerRef.current.nodes([]);
                    }}
                    history={history}
                    ACTIONS={ACTIONS}
                    addText={addText}
                    deleteSelectedObject={deleteSelectedObject}
                    fillColor={fillColor}
                    setFillColor={setFillColor}
                    handleExportPNG={handleExport}
                    handleImageUpload={handleImageUpload}
                    setShowWebcamPopup={setShowWebcamPopup}
                    handleExportSVG={handleSvgExportCallback}
                    handleExportPDF={handleExportPDFCallback}
                    handleExport={handleExport}
                    handleImportCanvas={handleImportCanvas}
                    handleRedo={redo}
                    handleUndo={() => undo(offCanvasImages)}
                    clearCanvas={clearCanvas}
                    handlePaste={handleToolbarPaste}
                    redoStack={redoStack}
                    zoomLevel={zoomLevel}
                    setShowTextBox={setShowTextBox}
                    setSelectedText={setSelectedText}
                    setSelectedTextId={setSelectedTextId}
                    lockedAction={lockedAction}
                    setLockedAction={setLockedAction}
                    images={images}
                    setSelectedObject={setSelectedObject}
                    setSelectedObjects={setSelectedObjects}
                    selectedObjects={selectedObjects}
                    isMultiSelectMode={isMultiSelectMode}
                    isPastingFromClipboard={isPastingFromClipboard}
                  />
                </div>
              </div>
            )}

            {/* Webcam Preview */}
            {showWebcamPopup && !isLeavingCanvas && (
              <WebcamPopup
                setShowWebcamPopup={setShowWebcamPopup}
                setCapturedImage={setCapturedImage}
                addImage={(imageSrc) =>
                  addImage(
                    imageSrc,
                    "",
                    null,
                    null,
                    null,
                    null,
                    null,
                    false,
                    { isOnTopLayer: false }
                  )
                }
                addVideo={addVideo}
                showErrorSnackbar={showErrorSnackbar}
              />
            )}

            {/* Shape Editor */}
            {!selectedObject &&
              !showWebcamPopup &&
              !isCropModalOpen &&
              !showTextBox &&
              !isImageLoading &&
              !isLoadingFromIndexedDB &&
              !isLeavingCanvas &&
              !isExportingPng &&
              !isExportingPdf &&
              !isExportingSvg &&
              !isImportingCanvas && (
                <div>
                  <CanvasHeightEditor
                    editorWidth={280}
                    canvasHeight={canvasHeight}
                    setCanvasHeight={setCanvasHeight}
                    applyCanvasHeight={applyCanvasHeight}
                    onAutoSizeCanvas={autoSizeMainCanvasToContent}
                    setImages={setImages}
                    images={images}
                    setRectangles={setRectangles}
                    rectangles={rectangles}
                    setCircles={setCircles}
                    circles={circles}
                    setLines={setLines}
                    lines={lines}
                    addImage={addImage}
                    setCanvasResizeTrigger={setCanvasResizeTrigger}
                    setSelectedObject={setSelectedObject}
                    setSelectedImage={setSelectedImage}
                    setSelectedObjects={setSelectedObjects}
                    setSelectedText={setSelectedText}
                    setSelectedTextId={setSelectedTextId}
                    setSelectedArrow={setSelectedArrow}
                    setSelectedLine={setSelectedLine}
                    setSelectedDoubleSidedArrow={setSelectedDoubleSidedArrow}
                    setAction={(e) => {
                      setAction(e);
                      setShowTextBox(false);
                      if (e !== ACTIONS.IMAGE && pendingImageData) {
                        setPendingImageData(null);
                        setIsDrawingImage(false);
                        setImageStartPoint(null);
                      }
                      if (!isCloneMode && !lockedAction) {
                        setSelectedObject(null);
                        setSelectedText(null);
                        setSelectedTextId(null);
                      }
                      if (pendingSelection.current) {
                        pendingSelection.current = null;
                      }
                    }}
                    ACTIONS={ACTIONS}
                    setLockedAction={setLockedAction}
                    setShowTextBox={setShowTextBox}
                    setPendingImageData={setPendingImageData}
                    transformerRef={transformerRef}
                    saveToHistory={saveToHistory}
                    onUserChange={markUserChanges}
                    onHeightChangeCommit={(heightChangeObj) => {
                      heightChangeCommitRef.current = heightChangeObj;
                    }}
                    getMinCanvasHeightPercent={computeMinCanvasHeightPercent}
                  />
                </div>
              )}

            {/* Show TextBoxWithButtons when:
                1. selectedText exists OR selectedObject is a Text type (handles timing issue)
                2. Not leaving canvas
                3. Multi-select mode is NOT enabled (hide when multi-select is on)
                4. Either selectedObjects is empty OR (selectedObjects has only 1 element AND it's the same text)
                5. selectedObject is a Text type (or null)
            */}
            {(() => {
              // Debug the condition
              const hasSelectedText = !!selectedText;
              const hasTextObject =
                selectedObject !== null &&
                (selectedObject.type === "Text" ||
                  selectedObject.type?.includes("Text"));
              // CRITICAL FIX: Check both selectedText and selectedObject (handles timing issue where selectedObject is set before selectedText)
              const hasTextSelected = hasSelectedText || hasTextObject;
              const notLeavingCanvas = !isLeavingCanvas;
              const notMultiSelect = !isMultiSelectMode;
              const selectedObjectsCheck =
                selectedObjects.length === 0 ||
                (selectedObjects.length === 1 &&
                  (selectedObjects[0]?.id === selectedText?.id ||
                    selectedObjects[0]?.id === selectedObject?.id) &&
                  selectedObjects[0]?.type === "Text");
              const selectedObjectCheck =
                !selectedObject || selectedObject.type?.includes("Text");
              const shouldShow =
                hasTextSelected &&
                notLeavingCanvas &&
                notMultiSelect &&
                selectedObjectsCheck &&
                selectedObjectCheck;

              if (hasSelectedText || hasTextObject) {
              }

              return shouldShow;
            })() && (
                <div
                  data-canvas-chrome
                  style={{
                    position: "fixed",
                    top: "0",
                    right: "0",
                    height: "100vh",
                    width: "300px",
                    padding: "20px",
                    zIndex: 1003,
                    overflowY: "auto",
                    pointerEvents: "auto",
                  }}
                >
                  <TextBoxWithButtons
                    saveToHistory={saveToHistory}
                    text={(() => {
                      const resolveCanonicalTextById = (targetId) => {
                        if (!targetId) return null;
                        const textFromOffCanvas = offCanvasTexts?.find(
                          (t) => t.id === targetId,
                        );
                        const textFromMain = texts?.find((t) => t.id === targetId);
                        // Prefer off-canvas match first to avoid stale main-array copies
                        // during cross-stage/creation transitions.
                        return textFromOffCanvas || textFromMain || null;
                      };

                      // CRITICAL FIX: Prefer canonical array-backed text over transient selectedText
                      // so width/x/y reflect the finalized element (especially for off-canvas draws).
                      if (selectedText) {
                        // Allow selectedText if it matches selectedObject (or if selectedObject is null/undefined during transition)
                        if (
                          !selectedObject ||
                          selectedText.id === selectedObject.id
                        ) {
                          return (
                            resolveCanonicalTextById(selectedText.id) ||
                            selectedText
                          );
                        }
                      }

                      // Fallback: If selectedText is missing (timing issue) or mismatch, look up in arrays
                      if (
                        selectedObject &&
                        (selectedObject.type === "Text" ||
                          selectedObject.type?.includes("Text"))
                      ) {
                        return (
                          resolveCanonicalTextById(selectedObject.id) ||
                          selectedObject
                        );
                      }

                      return (
                        resolveCanonicalTextById(selectedText?.id) ||
                        selectedText
                      );
                    })()}
                    setPartsModalOpen={handleOpenPartsModal}
                    getTextStyle={getTextStyle}
                    setSelectedTextId={setSelectedTextId}
                    updateTextContent={updateTextContent}
                    setTexts={setTextsForSelection}
                    selectedTextId={selectedTextId}
                    setSelectedText={setSelectedText}
                    showEditTask={showEditTask}
                    isElementBeingEdited={isElementBeingEdited}
                    setPendingChanges={setPendingChanges}
                    pendingChangesRef={pendingChangesRef}
                    showEditNote={showEditNote}
                    setShowEditTask={setShowEditTask}
                    setShowEditNote={setShowEditNote}
                    showQtyUnitOptions={showQtyUnitOptions}
                    setShowQtyUnitOptions={setShowQtyUnitOptions}
                    partNumbers={partNumbers}
                    editTextBoxContent={editTextBoxContent}
                    setEditTextBoxContent={setEditTextBoxContent}
                    setShowTextBox={setShowTextBox}
                    canvasHeight={canvasHeight}
                    setRemovedTexts={setRemovedTexts}
                    // Add offcanvas text props
                    setOffCanvasTexts={setOffCanvasTexts}
                    offCanvasTexts={offCanvasTexts}
                    setRemovedOffCanvasTexts={setRemovedOffCanvasTexts}
                    texts={texts}
                    // Transformer props for cleanup on delete
                    setSelectedObject={setSelectedObject}
                    transformerRef={transformerRef}
                    // Clear selectedObjects on delete
                    setSelectedObjects={setSelectedObjects}
                    // Callback to track removed part references
                    onPartRemoved={(removedPartData) => {
                      setRemovedPartReferences((prev) => [
                        ...prev,
                        removedPartData,
                      ]);
                    }}
                  />
                </div>
              )}

            {/* Fixed Text Editor on right */}
            {/* {editing && selectedText && (
          <div
            style={{
              position: "fixed",
              top: "0",
              right: "0",
              height: "100vh",
              width: "300px",
              backgroundColor: "#fff",
              boxShadow: "0 0 10px rgba(0,0,0,0.2)",
              padding: "20px",
              zIndex: 1000,
              overflowY: "auto",
            }}
          >
            <TextEditor
              selectedText={selectedText}
              updateTextProperties={updateTextProperties}
              setSelectedTextId={(id) => {
                setSelectedTextId(id);
                setEditing(false);
              }}
            />
          </div>
        )} */}

            {/* Shape Editor - Show when:
                1. Non-text element is selected, OR
                2. Multiple elements are selected (selectedObjects.length > 0), OR
                3. Multi-select mode is enabled (even if only text is selected)
                Rendered via portal to document.body so it stays on top and receives clicks when overlapping canvas on narrow screens.
            */}
            {((selectedObject && !selectedObject.type?.includes("Text")) ||
              selectedObjects.length > 0 ||
              isMultiSelectMode ||
              lockedAction != null ||
              isCloneMode) &&
              !isLeavingCanvas &&
              createPortal(
                <ShapeEditor
                  mainCanvasWidthPx={mainCanvasWidthPx}
                  updateVideoProperties={updateVideoProperties}
                  setVideos={setVideos}
                  selectedObject={selectedObject}
                  updateShapeProperties={updateSelectedShape}
                  updateArrowProperties={updateArrowProperties}
                  updateTextProperties={updateTextProperties}
                  updateLineProperties={updateLineProperties}
                  setSelectedObject={setSelectedObject}
                  setSelectedObjects={setSelectedObjects}
                  selectedObjects={selectedObjects}
                  groupDragDisplay={groupDragDisplay}
                  isMultiSelectMode={isMultiSelectMode}
                  setIsMultiSelectMode={setIsMultiSelectMode}
                  individuallySelectedIds={individuallySelectedIds}
                  setIndividuallySelectedIds={setIndividuallySelectedIds}
                  selectedText={selectedText}
                  deleteSelectedObject={deleteSelectedObject}
                  onAddText={onAddText}
                  onRemoveText={onRemoveText}
                  toggleImageCompression={toggleImageCompression}
                  cutSelectedImage={cutSelectedImage}
                  copySelectedImage={copySelectedImage}
                  transformerRef={transformerRef}
                  updateImageProperties={updateImagePropertiesWrapped}
                  canvasHeight={canvasHeight}
                  updateDoubleSidedArrowProperties={
                    updateDoubleSidedArrowProperties
                  }
                  setImages={setImages}
                  isCropModalOpen={isCropModalOpen}
                  setIsCropModalOpen={setIsCropModalOpen}
                  setCropModalOpenRefOnly={setCropModalOpenRefOnly}
                  setCropModalRootRef={setCropModalRootRef}
                  setIsImageLoading={setIsImageLoading}
                  setImageLoadingProgress={setImageLoadingProgress}
                  saveToHistory={saveToHistory}
                  latestUpdatedImagesRef={latestUpdatedImagesRef}
                  latestImageEditorRefUrlRef={latestImageEditorRefUrlRef}
                  setPendingChanges={setPendingChanges}
                  pendingChangesRef={pendingChangesRef}
                  isCloneMode={isCloneMode}
                  onToggleCloneMode={handleToggleCloneMode}
                  lockedAction={lockedAction}
                  onStopDuplicatingOrUnlockTool={
                    handleShapeEditorStopDuplicatingOrUnlock
                  }
                  handleCopy={handleCopy}
                  handleCut={handleCut}
                  rectangles={rectangles}
                  circles={circles}
                  images={images}
                  texts={texts}
                  arrows={arrows}
                  lines={lines}
                  doubleSidedArrows={doubleSidedArrows}
                  offCanvasRectangles={offCanvasRectangles}
                  offCanvasCircles={offCanvasCircles}
                  offCanvasImages={offCanvasImages}
                  offCanvasTexts={offCanvasTexts}
                  offCanvasArrows={offCanvasArrows}
                  offCanvasLines={offCanvasLines}
                  offCanvasDoubleSidedArrows={offCanvasDoubleSidedArrows}
                  setRectangles={setRectangles}
                  setCircles={setCircles}
                  setTexts={setTextsForSelection}
                  setArrows={setArrows}
                  setLines={setLines}
                  setDoubleSidedArrows={setDoubleSidedArrows}
                  setOffCanvasRectangles={setOffCanvasRectangles}
                  setOffCanvasCircles={setOffCanvasCircles}
                  setOffCanvasImages={setOffCanvasImages}
                  setOffCanvasTexts={setOffCanvasTexts}
                  setOffCanvasArrows={setOffCanvasArrows}
                  setOffCanvasLines={setOffCanvasLines}
                  setOffCanvasDoubleSidedArrows={setOffCanvasDoubleSidedArrows}
                  // Removed state setters for tracking deleted elements
                  setRemovedRectangles={setRemovedRectangles}
                  setRemovedCircles={setRemovedCircles}
                  setRemovedArrows={setRemovedArrows}
                  setRemovedDoubleSidedArrows={setRemovedDoubleSidedArrows}
                  setRemovedScribbles={setRemovedScribbles}
                  setRemovedTexts={setRemovedTexts}
                  setRemovedImages={setRemovedImages}
                  setRemovedVideos={setRemovedVideos}
                  setRemovedLines={setRemovedLines}
                  setRemovedOffCanvasRectangles={setRemovedOffCanvasRectangles}
                  setRemovedOffCanvasCircles={setRemovedOffCanvasCircles}
                  setRemovedOffCanvasImages={setRemovedOffCanvasImages}
                  setRemovedOffCanvasTexts={setRemovedOffCanvasTexts}
                  setRemovedOffCanvasArrows={setRemovedOffCanvasArrows}
                  setRemovedOffCanvasLines={setRemovedOffCanvasLines}
                  setRemovedOffCanvasDoubleSidedArrows={
                    setRemovedOffCanvasDoubleSidedArrows
                  }
                  handleCopyGroupBetweenCanvases={
                    handleCopyGroupBetweenCanvasesRef.current
                  }
                />,
                document.body,
              )}

            {/* Canvas Container with Zoom - disable pointer events when crop modal is open so resize/drag in modal doesn't hit Stage */}
            {/* z-index: 0 so fixed panels (ShapeEditor 1002, TextBox 1003) stay on top when overlapping */}
            <div
              ref={scaledContentRef}
              data-app-zoom={String(zoomLevel ?? 1)}
              style={{
                transform: `scale(${zoomLevel || 1})`,
                // center center: zoom out keeps stages visually centered; top center anchored
                // the scaled canvas to the top of the screen (empty space below).
                transformOrigin: "center center",
                width: "100%",
                height: "100%",
                position: "relative",
                minHeight: "100dvh",
                minWidth: "100vw",
                zIndex: 0,
                pointerEvents:
                  isCropModalOpen || isCropModalOpenRef?.current
                    ? "none"
                    : "auto",
              }}
            >
              <Canvas
                mainStageContainerRefFromParent={mainStageContainerRef}
                scrollAreaRef={scrollAreaRef}
                viewportWidth={canvasViewportWidth}
                texts={texts}
                videos={videos}
                setVideos={setVideos}
                isLeavingCanvas={isLeavingCanvas}
                doubleSidedArrows={doubleSidedArrows}
                updateLineProperties={updateLineProperties}
                images={images}
                rectangles={rectangles}
                circles={circles}
                arrows={arrows}
                scribbles={scribbles}
                lines={lines}
                strokeColor={fillColor}
                setSelectedTextId={setSelectedTextId}
                setImages={setImages}
                setTexts={setTexts}
                stageRef={stageRef}
                topLayerStageRefFromParent={topLayerStageRef}
                transformerRef={transformerRef}
                onClick={onClick}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                setSelectedArrow={setSelectedArrow}
                selectedArrow={selectedArrow}
                onDoubleClick={onDoubleClick}
                setArrows={setArrows}
                setWaypointCount={setWaypointCount}
                setSelectedText={setSelectedText}
                selectedText={selectedText}
                selectedTextId={selectedTextId}
                preMouseDownSelectionCountRef={preMouseDownSelectionCountRef} // CRITICAL: Pass captured selection count ref to Canvas
                saveToHistory={saveToHistory}
                handleRectangleDragEnd={handleRectangleDragEnd}
                handleCircleDragEnd={handleCircleDragEnd}
                handleArrowDragEnd={handleArrowDragEnd}
                handleDoubleSidedArrowDragEnd={handleDoubleSidedArrowDragEnd}
                handleTextDragEnd={handleTextDragEnd}
                handleScribbleDragEnd={handleScribbleDragEnd}
                handleImageDragEnd={handleImageDragEnd}
                handleLineDragEnd={handleLineDragEnd}
                setSelectedObject={setSelectedObject}
                canvasHeight={canvasHeight} // off-canvas to main on height increase: see Canvas.jsx (currently commented out)
                canvasResizeTrigger={canvasResizeTrigger}
                setCanvasResizeTrigger={setCanvasResizeTrigger}
                effectiveZoomLevel={effectiveZoomLevel}
                setShowTextBox={setShowTextBox}
                setShowEditTask={setShowEditTask}
                setShowEditNote={setShowEditNote}
                setShowQtyUnitOptions={setShowQtyUnitOptions}
                setRectangles={setRectangles}
                selectedObject={selectedObject}
                setCircles={setCircles}
                setEditing={setEditing}
                action={action}
                selectedImage={selectedImage}
                setSelectedImage={setSelectedImage}
                handleDrop={handleDrop}
                setLines={setLines}
                selectedLine={selectedLine}
                setSelectedLine={setSelectedLine}
                handleDotDragMove={handleDotDragMove}
                handleImageClick={handleImageClick}
                selectedDoubleSidedArrow={selectedDoubleSidedArrow}
                setSelectedDoubleSidedArrow={setSelectedDoubleSidedArrow}
                setDoubleSidedArrows={setDoubleSidedArrows}
                isDrawingLine={isDrawingLine}
                lineStartPoint={lineStartPoint}
                fillColor={fillColor}
                hoverPoint={hoverPoint}
                isDrawingArrow={isDrawingArrow}
                arrowStartPoint={arrowStartPoint}
                doubleArrowStartPoint={doubleArrowStartPoint}
                isDrawingDoubleArrow={isDrawingDoubleArrow}
                setAction={setAction}
                ACTIONS={ACTIONS}
                showEditTask={showEditTask}
                showEditNote={showEditNote}
                editTextBoxContent={editTextBoxContent}
                setEditTextBoxContent={setEditTextBoxContent}
                selectedObjects={selectedObjects}
                selectedObjectsRef={selectedObjectsRef}
                handleCut={handleCut}
                handleCopy={handleCopy}
                setSelectedObjects={debugSetSelectedObjects}
                setGroupDragDisplay={setGroupDragDisplay}
                isWaitingForPaste={isWaitingForPaste}
                pasteModeType={pasteModeType}
                pendingClipboard={pendingClipboard}
                pasteElementsAtPosition={pasteElementsAtPosition}
                setIsWaitingForPaste={setIsWaitingForPaste}
                setLockedAction={setLockedAction}
                setIsCloneMode={setIsCloneMode}
                setCloneTemplate={setCloneTemplate}
                setCloneTemplates={setCloneTemplates}
                isMultiSelectMode={isMultiSelectMode}
                cloneElementsAtPosition={cloneElementsAtPosition}
                cloneTemplates={cloneTemplates}
                cloneTemplate={cloneTemplate}
                setIsMultiSelectMode={setIsMultiSelectMode}
                individuallySelectedIds={individuallySelectedIds}
                setIndividuallySelectedIds={setIndividuallySelectedIds}
                justFinishedSelectionRef={justFinishedSelectionRef}
                justFinishedShapeDrawRef={justFinishedShapeDrawRef}
                pendingSelection={pendingSelection}
                getTextStyle={getTextStyle}
                updateArrowProperties={updateArrowProperties}
                updateDoubleSidedArrowProperties={
                  updateDoubleSidedArrowProperties
                }
                setCanvasHeight={setCanvasHeight}
                applyCanvasHeight={applyCanvasHeight}
                getMinCanvasHeightPercent={computeMinCanvasHeightPercent}
                isDrawingRect={isDrawingRect}
                isDrawingRectRef={isDrawingRectRef}
                isDrawingCircle={isDrawingCircle}
                isDrawingText={isDrawingText}
                isDrawingDoubleSidedArrow={isDrawingDoubleArrow}
                isDrawingImage={isDrawingImage}
                currentImageId={currentImageId}
                currentRectId={currentRectId}
                largeImageWarning={shouldShowLargeImageWarning}
                offCanvasRectangles={offCanvasRectangles}
                setOffCanvasRectangles={setOffCanvasRectangles}
                offCanvasCircles={offCanvasCircles}
                setOffCanvasCircles={setOffCanvasCircles}
                offCanvasArrows={offCanvasArrows}
                setOffCanvasArrows={setOffCanvasArrows}
                offCanvasLines={offCanvasLines}
                setOffCanvasLines={setOffCanvasLines}
                offCanvasDoubleSidedArrows={offCanvasDoubleSidedArrows}
                setOffCanvasDoubleSidedArrows={setOffCanvasDoubleSidedArrows}
                offCanvasTexts={offCanvasTexts}
                setOffCanvasTexts={setOffCanvasTexts}
                offCanvasImages={offCanvasImages}
                setOffCanvasImages={setOffCanvasImages}
                offCanvasVideos={offCanvasVideos}
                setOffCanvasVideos={setOffCanvasVideos}
                offCanvasScribbles={offCanvasScribbles}
                setOffCanvasScribbles={setOffCanvasScribbles}
                lockedAction={lockedAction}
                isCloneMode={isCloneMode}
                endElementEditing={endElementEditing}
                isElementBeingEdited={isElementBeingEdited}
                setPendingChanges={setPendingChanges}
                pendingChangesRef={pendingChangesRef}
                latestUpdatedImagesRef={latestUpdatedImagesRef}
                latestUpdatedTextsRef={latestUpdatedTextsRef}
                isTransformingRef={isTransformingRef}
                isCropModalOpen={isCropModalOpen}
                isCropModalOpenRef={isCropModalOpenRef}
                onCopyGroupBetweenCanvasesReady={(handler) => {
                  handleCopyGroupBetweenCanvasesRef.current = handler;
                }}
                offCanvasStageRefFromParent={offCanvasStageRef}
                onOffCanvasStageRefReady={(ref) => {
                  if (typeof window !== "undefined" && ref) {
                    window.__offCanvasStageRef = ref;
                  }
                }}
                showErrorSnackbar={showErrorSnackbar}
               
              />
            </div>

            {/* Zoom Controls - Moved to bottom left */}
            {!isCropModalOpen &&
              !showWebcamPopup &&
              !isImageLoading &&
              !isLoadingFromIndexedDB &&
              !isLeavingCanvas &&
              !isExportingPng &&
              !isExportingPdf &&
              !isExportingSvg &&
              !isImportingCanvas && (
                <div
                  className="zoom-controls-fixed"
                  data-canvas-chrome
                  style={fixedPosStyles.zoomControls}
                  onPointerDownCapture={() => {
                    // Sync after focus moves off e.g. canvas height input; double-rAF leave sync can lag behind +/- clicks.
                    queueMicrotask(() => {
                      bumpLeaveChromeSync();
                    });
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => handleZoom(0.9, zoomLevel)}
                      className="zoom-button"
                      title="Zoom out (Ctrl −)"
                    >
                      -
                    </button>
                    <input
                      type="text"
                      value={zoomInputValue}
                      onChange={handleZoomInputChange}
                      onBlur={(e) => handleManualZoom(e, zoomLevel)}
                      onKeyPress={(e) => handleZoomInputKeyPress(e, zoomLevel)}
                      step="10"
                      min="10"
                      max="500"
                      className="zoom-input"
                      data-block-leave-canvas
                      title="Zoom level (%) — Ctrl + or Ctrl − to zoom"
                    />

                    <button
                      type="button"
                      onClick={() => handleZoom(1.1, zoomLevel)}
                      className="zoom-button"
                      title="Zoom in (Ctrl +)"
                    >
                      +
                    </button>

                    {/* <button onClick={resetZoom} className="zoom-button" title="Reset to 100%">
              ↺
            </button>
            <div className="zoom-level-display" title="Current Zoom Level">
              {Math.round(zoomLevel * 100)}%
            </div> */}
                  </div>
                </div>
              )}

            {/* Leave Canvas */}
            {!isCropModalOpen &&
              !showWebcamPopup &&
              !isImageLoading &&
              !isLoadingFromIndexedDB &&
              !isLeavingCanvas &&
              !isExportingPng &&
              !isExportingPdf &&
              !isExportingSvg && (
                <div
                  className="leave-canvas"
                  style={fixedPosStyles.leaveCanvas}
                >
                  <div>
                    <button
                      onClick={handleLeaveCanvasLocal}
                      className="leave-canvas-button"
                      disabled={leaveCanvasControlDisabled}
                    >
                      {isLeavingCanvas ? "Leaving..." : "Leave Canvas"}
                    </button>
                  </div>
                </div>
              )}
          </div>
          {/* Spacer inside scroll area so vertical scroll can pass overlay tracks */}
          <div
            aria-hidden
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: `${effectiveContentSize.width +
                CANVAS_SCROLL_HORIZONTAL_GUTTER_PX +
                zoomOverflowPadding.left +
                CANVAS_SCROLL_HORIZONTAL_GUTTER_PX +
                (zoomOverflowPadding.right || 0)
                }px`,
              height: `${effectiveContentSize.height +
                (zoomLevel > 1 ? 20 : 30) +
                zoomOverflowPadding.top
                }px`,
              pointerEvents: "none",
              opacity: 0,
            }}
          />
        </div>
      </div>

      {isPartsModalOpen && !isLeavingCanvas && (
        <PartsPopup
          title="Item Code Table"
          items_type={filterType}
          disableFilter={false}
          disableBomOptions={fromCanvasTemplate}
          isMyCanvasCase
          setPartsModalOpen={setPartsModalOpen}
          handleSelectPart={(part) => {
            if (selectedText.selectedContent?.part) {
              handleReplacePart(part, selectedText);
            } else {
              handleSelectPart(part);
            }
          }}
          onPartUpdated={handlePartUpdatedInPopup}
        />
      )}

      {showShortcutsModal && (
        <div
          data-canvas-chrome
          onClick={() => setShowShortcutsModal(false)}
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0, 0, 0, 0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2000,
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            onClick={(evt) => evt.stopPropagation()}
            style={{
              width: "min(760px, 94vw)",
              maxHeight: "82vh",
              overflowY: "auto",
              background: "#fff",
              borderRadius: "12px",
              boxShadow: "0 20px 50px rgba(0, 0, 0, 0.25)",
              padding: "20px 22px",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "14px",
              }}
            >
              <h3 style={{ margin: 0, fontSize: "18px", fontWeight: 600 }}>
                Keyboard Shortcuts
              </h3>
              <button
                type="button"
                onClick={() => setShowShortcutsModal(false)}
                style={{
                  border: "1px solid #d1d5db",
                  borderRadius: "8px",
                  background: "#fff",
                  padding: "4px 10px",
                  cursor: "pointer",
                }}
              >
                Close
              </button>
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr auto",
                rowGap: "8px",
                columnGap: "16px",
                fontSize: "14px",
              }}
            >
              <span>Select mode</span><strong>S</strong>
              <span>Hand mode</span><strong>H</strong>
              <span>Add rectangle / lock tool on double-press</span><strong>R</strong>
              <span>Add circle / lock tool on double-press</span><strong>C</strong>
              <span>Add line or arrow / lock tool on double-press</span><strong>A or L</strong>
              <span>Add text</span><strong>T</strong>
              <span>Upload image</span><strong>I</strong>
              <span>Open camera</span><strong>P</strong>
              <span>Undo</span><strong>Ctrl/Cmd + Z</strong>
              <span>Redo</span><strong>Ctrl/Cmd + Shift + Z or Ctrl/Cmd + Y</strong>
              <span>Select all</span><strong>Ctrl/Cmd + A</strong>
              <span>Zoom in / out / reset</span><strong>Ctrl/Cmd + + / - / 0</strong>
              <span>Paste</span><strong>Ctrl/Cmd + V or Insert</strong>
              <span>Move selected object</span><strong>Arrow Keys</strong>
              <span>Pan canvas (no selection)</span><strong>Arrow Keys</strong>
              <span>Stop duplicate/continuous mode, dismiss panes</span><strong>Esc</strong>
              <span>Show this shortcuts help</span><strong>Ctrl/Cmd + /</strong>
            </div>
          </div>
        </div>
      )}

      {/* Spacer to ensure scrollable area covers the zoomed content */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: `${effectiveContentSize.width +
            CANVAS_SCROLL_HORIZONTAL_GUTTER_PX +
            zoomOverflowPadding.left +
            CANVAS_SCROLL_HORIZONTAL_GUTTER_PX +
            (zoomOverflowPadding.right || 0)}px`,
          height: `${effectiveContentSize.height +
            (zoomLevel > 1 ? 80 : 400) +
            zoomOverflowPadding.top
            }px`,
          pointerEvents: "none",
          opacity: 0,
        }}
      />

      {/* Always-visible custom scrollbars (overlay) */}
      <div
        ref={vTrackRef}
        data-canvas-chrome
        onMouseDown={handleVerticalTrackMouseDown}
        style={{
          position: "fixed",
          right: 0,
          top: 0,
          height: "100vh",
          width: 12,
          zIndex: 999,
          display: showWebcamPopup ? "none" : "flex",
          alignItems: "flex-start",
          justifyContent: "center",
          background: "transparent",
          cursor: "default",
        }}
      >
        <div
          onMouseDown={handleVerticalThumbMouseDown}
          style={{
            position: "relative",
            top: vThumbTop,
            width: 8,
            height: vThumbHeight,
            borderRadius: 4,
            background: "#9ca3af",
            cursor: "default",
            transition: "background 0.15s ease, opacity 0.15s ease",
            opacity: dragState?.axis === "v" ? 0.8 : 1,
            boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
          }}
        />
      </div>

      <div
        ref={hTrackRef}
        data-canvas-chrome
        onMouseDown={handleHorizontalTrackMouseDown}
        style={{
          position: "fixed",
          left: 0,
          bottom: 0,
          width: "100vw",
          height: 12,
          zIndex: 999,
          display: showWebcamPopup ? "none" : "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          background: "transparent",
          cursor: "default",
        }}
      >
        <div
          onMouseDown={handleHorizontalThumbMouseDown}
          style={{
            position: "relative",
            left: hThumbLeft,
            height: 8,
            width: hThumbWidth,
            borderRadius: 4,
            background: "#9ca3af",
            cursor: "default",
            transition: "background 0.15s ease, opacity 0.15s ease",
            opacity: dragState?.axis === "h" ? 0.8 : 1,
            boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
          }}
        />
      </div>

      {/* Hide native scrollbars; we render custom scrollbars */}
      <style>{`
        /* Global index.css forces .editor-container { width: 1600px } — narrower than the 2160px
           triple-stage layout, which skewed 50vw centering and left extra space on the right. */
        .canvas-scroll-area > .editor-container {
          width: 100% !important;
          min-width: ${getOffCanvasStageWidthPx() + getMainCanvasWidthPx()}px !important;
          max-width: none !important;
          margin-left: auto !important;
          margin-right: auto !important;
          box-sizing: border-box !important;
        }

        /* Keep native scrollbars hidden; custom ones are rendered */
        .canvas-scroll-area {
          /* Do not force native scrollbars */
          overflow: auto !important;
          overflow-x: auto;
          overflow-y: auto;
          /* Hide native scrollbars visually while keeping scroll functionality */
          scrollbar-width: none; /* Firefox */
          /* Improve scrolling performance */
          -webkit-overflow-scrolling: touch;
          overscroll-behavior: contain;
        }

        .canvas-scroll-area {
          -ms-overflow-style: none;
        }
        .canvas-scroll-area::-webkit-scrollbar {
          display: none;
        }

        /* Extra guard for WebKit overlay scrollbars on macOS */
        .canvas-scroll-area::-webkit-scrollbar,
        .canvas-scroll-area::-webkit-scrollbar-thumb,
        .canvas-scroll-area::-webkit-scrollbar-track {
          width: 0 !important;
          height: 0 !important;
          background: transparent !important;
        }

        /* Also hide native scrollbars on any nested scrollable elements inside canvas */
        .canvas-scroll-area * {
          -ms-overflow-style: none !important; /* IE/Edge */
          scrollbar-width: none !important; /* Firefox */
        }
        .canvas-scroll-area *::-webkit-scrollbar,
        .canvas-scroll-area *::-webkit-scrollbar-thumb,
        .canvas-scroll-area *::-webkit-scrollbar-track {
          width: 0 !important;
          height: 0 !important;
          background: transparent !important;
          display: none !important;
        }

        /* Ensure content doesn't create additional scrollbars */
        .canvas-container > * {
          max-width: 100%;
          max-height: 100%;
        }

        /* Prevent nested scrollable areas from interfering */
        .canvas-container .editor-container {
          overflow: visible;
        }

        /* Spinner animation for image loading */
        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
      `}</style>

      {/* Image Loading Overlay */}
      {isImageLoading && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            backdropFilter: "blur(2px)",
          }}
        >
          <div
            style={{
              width: "60px",
              height: "60px",
              border: "6px solid #f3f3f3",
              borderTop: "6px solid #3498db",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
            }}
          />
          <div
            style={{
              marginTop: "20px",
              color: "#fff",
              fontSize: "16px",
              fontWeight: "500",
            }}
          >
            Processing image...
          </div>
          <div
            style={{
              marginTop: "10px",
              width: "200px",
              height: "4px",
              backgroundColor: "#f3f3f3",
              borderRadius: "2px",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                width: `${imageLoadingProgress}%`,
                height: "100%",
                backgroundColor: "#3498db",
                transition: "width 0.3s ease",
              }}
            />
          </div>
        </div>
      )}

      {/* Error Snackbar */}
      <ErrorSnackbar
        message={errorSnackbar.message}
        isVisible={errorSnackbar.isVisible}
        onClose={() => setErrorSnackbar({ isVisible: false, message: "" })}
      />
    </div>
  );
};

export default MyCanvas;
